import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import axios from "axios";
import RouteManagementPage from "./RouteManagementPage";
import { Calendar, Truck, ClipboardCheck, CheckCircle, LogOut, Menu, X, AlertCircle, User, Phone, MessageSquare, Mail, ChevronUp, ChevronDown, ChevronRight, Filter, Upload, Eye, ArrowLeft, ArrowRight, Edit, LayoutDashboard, DollarSign, Radio, Clock, AlertTriangle, Clipboard, Users, Search, Archive, MapPin, MapPinOff, Building2, Plus, RotateCcw, Shield, Power, UserX, FileText, List, CalendarCheck, Lock, Database, GraduationCap, Flag, Camera, UserPlus, Loader2, Ban, Voicemail as VoicemailIcon, Compass, Repeat, Activity, Megaphone, TrendingDown } from "lucide-react";
import SchedulingOrderDetailPage from "./SchedulingOrderDetail";
import DashboardPage from "./DashboardPage";
import FinancePage from "./FinancePage";
import CheckReconcilePage from "./CheckReconcilePage";
import TeamPage from "./TeamPage";
import ProductivityPage from "./ProductivityPage";
import TransmissionPage from "./TransmissionPage";
import InspectorOrdersPage from "./InspectorOrdersPage";
// Angela 2026-05-10 — Removed standalone Inspector Field Updates mural.
// Inspector communications now surface inside the existing Scheduling
// History panel on the Scheduling order detail (single source of truth for
// office activity). The InspectorFieldUpdatesPage component file is kept on
// disk but no longer routed/imported.
import PropertyNotesPanel from "./PropertyNotesPanel";
import SchedulingKBPanel from "./SchedulingKBPanel";
import InspectorProfilePage from "./InspectorProfilePage";
import InspectorUploadPage from "./InspectorUploadPage";
import InspectorContractPage from "./InspectorContractPage";
import ClientPhotoUploadPage from "./ClientPhotoUploadPage";
import NationalPhotosPanel from "./NationalPhotosPanel";
import OrderReferenceBlock from "./OrderReferenceBlock";
import ReportProblemModal from "./components/ReportProblemModal";
import ArchivePage from "./ArchivePage";
import { RcSmsInbox, RcVoicemailInbox } from "./RcInboxView";
import ZoneCalendarPage from "./ZoneCalendarPage";
import OfficeHoursTrackingPage from "./OfficeHoursTrackingPage";
import NationalInspectorsUploadPage from "./NationalInspectorsUploadPage";
import ClosingHistoryPage from "./ClosingHistoryPage";
import DailyClosingPage from "./DailyClosingPage";
import CloseoutReviewPage from "./CloseoutReviewPage";
import AlyPortalPage from "./AlyPortalPage";
import AlyNationalViewPage from "./AlyNationalViewPage";
import AlyNationalCalendarPage from "./AlyNationalCalendarPage";
import AlyOrderDetailPage from "./AlyOrderDetailPage";
import AlyAllNationalOrdersPage from "./AlyAllNationalOrdersPage";
import InspectorWeeklyClosingPage from "./InspectorWeeklyClosingPage";
import AdminInspectionPage from "./AdminInspectionPage";
import AllCompanyOrdersPage from "./AllCompanyOrdersPage";
import TrainingGuide from "./TrainingGuide";
import InspectorMyOrdersPage from "./InspectorMyOrdersPage";
import InspectorPreRoutePage from "./InspectorPreRoutePage";
import InspectorDayClosingPage from "./InspectorDayClosingPage";
import AdminAnnouncementsPage from "./AdminAnnouncementsPage";
import AdminInspectorRatesPage from "./AdminInspectorRatesPage";
import OfficeWorkforcePage from "./OfficeWorkforcePage";
import WeekendCallPhotos from "./WeekendCallPhotos";
import HoldCallsSection from "./HoldCallsSection";
import OperationalMap from "./OperationalMap";
import { OperatorActivityButtons, RegisterActivityModal } from "./OperatorActivitySection";
import ReopenModal from "./ReopenModal";
import MorningUploadPage from "./MorningUpload";
import DispatchReturnsSection from "./DispatchReturnsSection";
import SchedulingQueueDrawer from "./SchedulingQueueDrawer";
import { SummaryCard, StatCard } from "./components/PageCards";
import DispatchFollowupQueueWidget from "./components/DispatchFollowupQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Auto-refresh token on 401: try /api/auth/refresh once, then retry the failed request
let isRefreshing = false;
let refreshQueue = [];
axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login') && !originalRequest.url?.includes('/auth/refresh')) {
      originalRequest._retry = true;
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await axios.post(`${API}/auth/refresh`);
          isRefreshing = false;
          refreshQueue.forEach(cb => cb());
          refreshQueue = [];
          return axios(originalRequest);
        } catch {
          isRefreshing = false;
          refreshQueue = [];
          return Promise.reject(error);
        }
      }
      return new Promise((resolve) => {
        refreshQueue.push(() => resolve(axios(originalRequest)));
      });
    }
    return Promise.reject(error);
  }
);

// Date display helper: YYYY-MM-DD → MM/DD/YYYY for user-facing display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '-';
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return dateStr;
};


// Convert 24h inspection time/range → US AM/PM for customer messages.
// Examples: "13:00" → "1:00 PM"; "09:00 - 13:00" → "9:00 AM - 1:00 PM"; "9:00-1:00" → "9:00 AM - 1:00 PM"
const _hourAmPm = (t) => {
  if (!t) return t;
  const s = String(t).trim().toUpperCase();
  if (s.includes("AM") || s.includes("PM")) return s.replace(/^0(\d)/, "$1");
  const m = s.match(/^(\d{1,2}):?(\d{0,2})$/);
  if (!m) return t;
  const h = parseInt(m[1], 10);
  const mm = (m[2] || "00").padStart(2, "0");
  if (h === 0) return `12:${mm} AM`;
  if (h === 12) return `12:${mm} PM`;
  if (h > 12) return `${h - 12}:${mm} PM`;
  if (h >= 1 && h <= 6) return `${h}:${mm} PM`;
  return `${h}:${mm} AM`;
};
const toAmPm24 = (timeStr) => {
  if (!timeStr) return timeStr || "";
  const s = String(timeStr).trim();
  if (s.includes("-")) {
    const parts = s.split("-").map(p => p.trim());
    return `${_hourAmPm(parts[0])} - ${_hourAmPm(parts[1])}`;
  }
  return _hourAmPm(s);
};


// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
    return response.data;
  };

  const clearPasswordFlag = () => {
    setUser(prev => prev ? { ...prev, must_set_password: false } : prev);
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    try { sessionStorage.removeItem("office_clock_gate_ok"); } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, clearPasswordFlag }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles, permission }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D1A054]"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_set_password && location.pathname !== "/set-password") return <Navigate to="/set-password" replace />;
  // Access is granted if EITHER the user's role is in allowedRoles OR the user
  // has the specific extra_permission flag. Used to give individual users
  // (e.g. Vitória) access to a screen without widening the whole role.
  const roleOk = !allowedRoles || allowedRoles.includes(user.role);
  const permOk = !!permission && Array.isArray(user.extra_permissions) && user.extra_permissions.includes(permission);
  if (!roleOk && !permOk) return <Navigate to="/" replace />;
  return <RequireClockInOrOffice>{children}</RequireClockInOrOffice>;
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(d => d.msg).join(" ") : detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="aarifi-login-bg flex items-center justify-center p-4">
      <div className="aarifi-login-card w-full max-w-sm p-8">
        <div className="flex justify-center mb-6">
          <img src="/aarifi-logo-full.png" alt="AARIFI Services LLC" className="h-36 w-auto object-contain" data-testid="login-logo" />
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-[#F5EDED] border border-[#DFC4C4] rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-[#8B4545]" />
            <p className="text-[#7A3B3B] text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A] focus:border-transparent placeholder-gray-400"
            placeholder="Email"
            required
            data-testid="login-email-input"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A] focus:border-transparent placeholder-gray-400"
            placeholder="Password"
            required
            data-testid="login-password-input"
          />
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white font-semibold rounded-lg transition-all disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Set Password Page (first login)
const SetPasswordPage = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user, clearPasswordFlag } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !user.must_set_password) navigate("/");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/set-password`, { current_password: currentPassword, new_password: newPassword });
      clearPasswordFlag();
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to set password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="aarifi-login-bg flex items-center justify-center p-4">
      <div className="aarifi-login-card w-full max-w-md p-8">
        <div className="flex justify-center mb-4">
          <img src="/aarifi-logo-full.png" alt="AARIFI" className="h-20 w-auto object-contain" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800 text-center mb-2">Create Your Password</h1>
        <p className="text-gray-500 text-center mb-1 text-sm">Welcome, {user?.name || user?.email}</p>
        <p className="text-gray-400 text-center mb-6 text-xs">Enter your current password and choose a new one</p>

        {error && (
          <div className="mb-4 p-3 bg-[#F5EDED] border border-[#DFC4C4] rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-[#8B4545]" />
            <p className="text-[#7A3B3B] text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A] placeholder-gray-400"
              placeholder="Enter current password"
              required
              autoFocus
              data-testid="set-password-current"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A] placeholder-gray-400"
              placeholder="Min. 6 characters"
              required
              data-testid="set-password-new"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A] placeholder-gray-400"
              placeholder="Repeat new password"
              required
              data-testid="set-password-confirm"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white font-semibold rounded-lg transition-all disabled:opacity-50"
            data-testid="set-password-submit"
          >
            {isLoading ? "Saving..." : "Set Password & Continue"}
          </button>
        </form>
      </div>
    </div>
  );
};

// RingCentral Phone Modal — Angela 2026-05-07.
// Operator self-service: configure the phone RingCentral will ring FIRST
// when clicking Call (RingOut leg 1). Office number stays as caller ID.
const RcPhoneModal = ({ onClose }) => {
  const [phone, setPhone] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/users/me/rc-phone`);
        if (!cancelled) setPhone(r.data?.ringcentral_phone || "");
      } catch { /* fall through with empty value */ }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setSaving(true);
    try {
      const r = await axios.put(`${API}/users/me/rc-phone`, { phone });
      setPhone(r.data?.ringcentral_phone || "");
      setSuccess(r.data?.ringcentral_phone ? "Phone saved." : "Phone cleared.");
      setTimeout(onClose, 1200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save phone.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="rc-phone-modal">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-xl p-6 border border-gray-200 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">RingCentral Phone</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600" data-testid="rc-phone-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          When you click <strong>Call</strong> on an order, RingCentral will ring this phone first, then bridge to the client.
          Office line <strong>(401) 404-6542</strong> remains the caller ID the client sees.
        </p>
        {error && (
          <div className="mb-3 p-3 bg-[#F5EDED] border border-[#DFC4C4] rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-[#8B4545]" />
            <p className="text-[#7A3B3B] text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-3 p-3 bg-[#EDF3EC] border border-[#C5D9C1] rounded-lg flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-[#6B8F62]" />
            <p className="text-[#4A7A56] text-sm">{success}</p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">My phone (US 10-digit)</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A] placeholder-gray-400"
              placeholder="(555) 123-4567"
              autoFocus={loaded}
              data-testid="rc-phone-input"
            />
            <p className="mt-1 text-[10px] text-gray-400">Examples accepted: 5551234567, +15551234567, (555) 123-4567.</p>
          </div>
          <button
            type="submit"
            disabled={saving || !loaded}
            className="w-full py-2 bg-[#2F5D8A] hover:bg-[#2F5D8A] text-white font-semibold rounded-lg transition-all disabled:opacity-50 text-sm"
            data-testid="rc-phone-submit"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Change Password Modal (for users who already set their password)
const ChangePasswordModal = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="change-password-modal">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-xl p-6 border border-gray-200 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">Change Password</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600" data-testid="change-password-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {success ? (
          <div className="p-4 bg-[#EDF3EC] border border-[#C5D9C1] rounded-lg flex items-center gap-2" data-testid="change-password-success">
            <CheckCircle className="h-5 w-5 text-[#6B8F62]" />
            <p className="text-[#4A7A56] text-sm font-medium">Password changed successfully!</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 p-3 bg-[#F5EDED] border border-[#DFC4C4] rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-[#8B4545]" />
                <p className="text-[#7A3B3B] text-sm">{error}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Current Password</label>
                <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A] placeholder-gray-400" placeholder="Enter current password" required autoFocus data-testid="change-password-current" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">New Password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A] placeholder-gray-400" placeholder="Min. 6 characters" required data-testid="change-password-new" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Confirm New Password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A] placeholder-gray-400" placeholder="Repeat new password" required data-testid="change-password-confirm" />
              </div>
              <button type="submit" disabled={isLoading} className="w-full py-2 bg-[#2F5D8A] hover:bg-[#2F5D8A] text-white font-semibold rounded-lg transition-all disabled:opacity-50 text-sm" data-testid="change-password-submit">
                {isLoading ? "Saving..." : "Change Password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// Sidebar
const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showRcPhone, setShowRcPhone] = useState(false);
  // National uploads pending counter — drives the red badge next to the
  // "National Inspectors Upload" nav item for Aly + Admin. Polls the same
  // live queue endpoint every 30s. Silent on network error.
  const [nationalUploadsPending, setNationalUploadsPending] = useState(0);
  useEffect(() => {
    if (!user || !["National Transmission", "Admin"].includes(user.role)) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/national-inspectors-upload-queue`);
        if (!cancelled) setNationalUploadsPending(Array.isArray(r.data) ? r.data.length : 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);
  const badgeCounts = { nationalUploadsPending };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Role-based access rules:
  // Admin -> all screens
  // Finance -> all screens (same as Admin)
  // Scheduling -> Scheduling + Dispatch only
  // Dispatch -> Scheduling + Dispatch + QA
  // Inspector -> Inspector only
  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["Admin", "Finance"] },
    { path: "/scheduling", label: "Scheduling", icon: Calendar, roles: ["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"] },
    { path: "/productivity", label: "Productivity", icon: Radio, roles: ["Admin", "Finance", "Operations"] },
    { path: "/dispatch", label: "Dispatch", icon: Truck, roles: ["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"] },
    { path: "/pre-route", label: "Pre-route", icon: Compass, roles: ["Inspector"] },
    { path: "/my-orders", label: "My Orders", icon: Clipboard, roles: ["Inspector"] },
    { path: "/day-closing", label: "Day Closing", icon: TrendingDown, roles: ["Inspector"] },
    { path: "/my-weekly-closing", label: "Weekly Closing", icon: Calendar, roles: ["Inspector"] },
    { path: "/inspector", label: "Inspector", icon: ClipboardCheck, roles: ["Admin", "Finance", "Operations", "Operator"] },
    { path: "/finance", label: "Finance", icon: DollarSign, roles: ["Admin", "Finance"] },
    { path: "/finance/check-reconcile", label: "Check Reconcile", icon: DollarSign, roles: ["Admin", "Finance"] },
    { path: "/team", label: "Team", icon: Users, roles: ["Admin", "Finance", "Operations"] },
    { path: "/upload-orders", label: "Upload", icon: Upload, roles: ["Admin", "Finance", "Operations"] },
    { path: "/archive", label: "Archive", icon: Archive, roles: ["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"] },
    { path: "/daily-closing", label: "Daily Closing", icon: FileText, roles: ["Admin", "Finance", "Operations"], permission: "daily_closing" },
    { path: "/closeout-review", label: "Closeout Review", icon: CheckCircle, roles: ["Admin", "Finance", "Office", "Operations", "Scheduling"] },
    { path: "/zone-calendar", label: "Zone Calendar", icon: MapPin, roles: ["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"] },
    { path: "/office", label: "Office Time", icon: Clock, roles: ["Admin", "Finance", "Operator"] },
    { path: "/office/hours-tracking", label: "Office Hours", icon: Clock, roles: ["Admin"] },
    { path: "/admin/announcements", label: "Announcements", icon: Megaphone, roles: ["Admin"] },
    { path: "/admin/inspector-rates", label: "Inspector Rates", icon: DollarSign, roles: ["Admin"] },
    // National module — Aly's UI consolidation (Apr 21, 2026):
    //   • Aly (National Transmission) sees ONLY a single nav item:
    //     "National Calendar". The calendar page itself embeds two big
    //     action cards at the top — Inspectors Upload (red badge) +
    //     Transmission counter — so he never has to hunt between menus.
    //   • Admin still sees the upload queue as a separate utility.
    //   • Angela / Finance see ONLY the Calendar (management visibility).
    // Keep routes for direct URL access (/aly-portal, /national-inspectors-upload,
    // /aly-order/:id) so the two action cards can still navigate to them.
    { path: "/national-inspectors-upload", label: "National Inspectors Upload", icon: Camera, roles: ["Admin"], badgeKey: "nationalUploadsPending" },
    { path: "/aly-calendar", label: "National Calendar", icon: Calendar, roles: ["National Transmission", "Admin", "Finance"] },
    // All National Orders — Aly's lookup/search view (Apr 22, 2026).
    // Lets her find ANY National order (past, archived, transmitted, or
    // active) so she can review photos that were already uploaded.
    // Server forces company="National" for her role — she cannot see
    // Safeguard orders from this screen.
    { path: "/aly-all-national", label: "All National Orders", icon: Search, roles: ["National Transmission", "Admin", "Finance"] },
  ];

  // Inspection tab for Angela/Aline — mirrors the real inspector UI exactly.
  // Label, path, and icon stay EXACTLY as the user originally set them;
  // the only change is that /inspection renders the same InspectorMyOrdersPage
  // a real inspector gets (no adminMode flag — fixed below in the route).
  if (user?.email?.toLowerCase() === "angela@aarifi.com" || user?.email?.toLowerCase() === "aline@aarifi.com") {
    navItems.push({ path: "/inspection", label: "Inspection", icon: MapPin, roles: [user.role] });
  }

  const filteredNavItems = navItems.filter(item =>
    item.roles.includes(user?.role)
    || (item.permission && Array.isArray(user?.extra_permissions) && user.extra_permissions.includes(item.permission))
  );

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setIsOpen(false)} />}
      <aside className={`fixed top-0 left-0 h-full w-56 aarifi-sidebar z-50 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 flex flex-col`}>
        {/* Logo area */}
        <div className="bg-white px-3 py-3 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-2" data-testid="sidebar-logo">
            <img src="/aarifi-icon-small.png" alt="AARIFI" className="h-10 w-10 object-contain" data-testid="sidebar-logo-img" />
            <div className="leading-none">
              <span className="text-[#2F5D8A] font-bold text-base tracking-wide">AARIFI</span>
              <span className="block text-[10px] text-gray-400 tracking-widest">SERVICES LLC</span>
            </div>
          </div>
          <button className="lg:hidden text-gray-400 hover:text-gray-600" onClick={() => setIsOpen(false)}><X className="h-5 w-5" /></button>
        </div>

        <nav className="flex-1 p-2.5 space-y-0.5 overflow-y-auto">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const badgeVal = item.badgeKey ? (badgeCounts[item.badgeKey] || 0) : 0;
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setIsOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${isActive ? 'bg-[#2F5D8A] text-white font-semibold' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
                {badgeVal > 0 && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#C0392B] text-white text-[11px] font-bold flex items-center justify-center shadow-sm ring-2 ring-[#C0392B]/30 animate-pulse"
                    data-testid={`nav-badge-${item.badgeKey}`}
                    title={`${badgeVal} pending`}
                  >
                    {badgeVal > 99 ? '99+' : badgeVal}
                  </span>
                )}
                {isActive && badgeVal === 0 && <div className="ml-auto w-1 h-4 rounded-full bg-[#D1A054]" />}
              </button>
            );
          })}
        </nav>

        <div className="p-2.5 border-t border-white/10">
          <div className="flex items-center gap-2 px-3 py-1.5 mb-1">
            <div className="h-7 w-7 rounded-full bg-white/15 flex items-center justify-center shrink-0">
              <User className="h-3.5 w-3.5 text-white/80" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-medium truncate">{user?.role === 'Scheduling' ? 'Team' : (user?.full_name || user?.name)}</p>
              <p className="text-white/50 text-[10px] truncate">{user?.role === 'Scheduling' ? 'Scheduling' : user?.role}</p>
            </div>
          </div>
          <button
            onClick={() => setShowRcPhone(true)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-white/60 hover:bg-white/8 hover:text-white rounded-lg text-xs"
            data-testid="rc-phone-btn"
          >
            <Phone className="h-3.5 w-3.5" />
            <span>RingCentral Phone</span>
          </button>
          <button
            onClick={() => setShowChangePassword(true)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-white/60 hover:bg-white/8 hover:text-white rounded-lg text-xs"
            data-testid="change-password-btn"
          >
            <Edit className="h-3.5 w-3.5" />
            <span>Change Password</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-white/60 hover:bg-white/10 hover:text-[#C07070] rounded-lg text-xs"
            data-testid="logout-btn"
          >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
      </aside>
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
      {showRcPhone && <RcPhoneModal onClose={() => setShowRcPhone(false)} />}
    </>
  );
};

// Layout
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="aarifi-layout">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="lg:ml-56">
        <header className="lg:hidden bg-[#0F1B2D] p-3 sticky top-0 z-30 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="text-white"><Menu className="h-5 w-5" /></button>
          <img src="/aarifi-icon.png" alt="AARIFI" className="h-7 w-7 object-contain" />
        </header>
        <main className="p-4">{children}</main>
      </div>
    </div>
  );
};

// Status Badge
const StatusBadge = ({ status }) => {
  const colors = {
    'New': 'bg-[#DCE4ED] text-[#2F5D8A]',
    'Scheduling': 'bg-[#DCE4ED] text-[#2F5D8A]',
    'Assigned': 'bg-[#E0DEE8] text-[#5A4F7A]',
    'In Field': 'bg-[#EDE5D6] text-[#7A6535]',
    'Inspection Completed': 'bg-[#D8E8D4] text-[#4A7A56]',
    'Returned': 'bg-[#E8D4D4] text-[#7A3B3B]',
    'QA': 'bg-[#E0DEE8] text-[#5A4F7A]',
    'Rework': 'bg-[#E8D4D4] text-[#7A3B3B]',
    'Completed': 'bg-[#D8E8D4] text-[#4A7A56]',
    'EOD Closed': 'bg-[#DCE4ED] text-[#2F5D8A]',
    'On Hold': 'bg-[#EDE5D6] text-[#7A6535]',
    'Follow-Up': 'bg-[#DCE4ED] text-[#2F5D8A]',
    'Waiting Review': 'bg-[#E0DEE8] text-[#5A4F7A]',
    'Approved': 'bg-[#D8E8D4] text-[#4A7A56]',
    'Removed': 'bg-[#E8D4D4] text-[#7A3B3B]',
    'Archived': 'bg-[#E2E4E7] text-[#4B5563]',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${colors[status] || 'bg-[#E2E4E7] text-[#4B5563]'}`}>
      {status}
    </span>
  );
};

// Action Button
const ActionBtn = ({ icon: Icon, label, onClick, variant = "default" }) => {
  const variants = {
    default: "bg-gray-100 hover:bg-gray-200 text-[#4B5563]",
    primary: "bg-[#2F5D8A]/10 hover:bg-[#2F5D8A]/15 text-[#2F5D8A]",
    success: "bg-[#A7C4A0]/20 hover:bg-[#A7C4A0]/30 text-[#4A7A56]",
    danger: "bg-[#C07070]/15 hover:bg-[#C07070]/20 text-[#8B4545]",
  };
  return (
    <button
      onClick={onClick}
      className={`p-1.5 rounded text-xs ${variants[variant]} transition-all`}
      title={label}
      data-testid={`action-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
    </button>
  );
};

// Order Table Component with row click and optional checkbox support
const OrderTable = ({ orders, columns, actions, onSort, sortBy, sortDir, loading, onRowClick, selectable, selectedIds, onSelectToggle, onSelectAll, onShiftSelect }) => {
  const navigate = useNavigate();
  const lastClickedRef = React.useRef(null);
  
  const handleRowClick = (order) => {
    if (onRowClick) {
      onRowClick(order);
    } else {
      navigate(`/order/${order.id}`);
    }
  };

  const handleCheckboxChange = (order, e) => {
    if (e.nativeEvent.shiftKey && lastClickedRef.current && onShiftSelect) {
      // Shift+Click range selection
      const lastIdx = orders.findIndex(o => o.id === lastClickedRef.current);
      const curIdx = orders.findIndex(o => o.id === order.id);
      if (lastIdx !== -1 && curIdx !== -1) {
        const start = Math.min(lastIdx, curIdx);
        const end = Math.max(lastIdx, curIdx);
        const rangeIds = orders.slice(start, end + 1).map(o => o.id);
        onShiftSelect(rangeIds);
        lastClickedRef.current = order.id;
        return;
      }
    }
    lastClickedRef.current = order.id;
    onSelectToggle && onSelectToggle(order.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D1A054]"></div>
      </div>
    );
  }

  if (orders.length === 0) {
    return <div className="text-center py-8 text-gray-500 text-sm">No orders found</div>;
  }

  const allSelected = orders.length > 0 && selectedIds && selectedIds.size === orders.length;
  const someSelected = selectedIds && selectedIds.size > 0 && selectedIds.size < orders.length;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="orders-table">
        <thead>
          <tr className="border-b border-gray-200">
            {selectable && (
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={(e) => onSelectAll && onSelectAll(e.target.checked)}
                  className="rounded border-gray-300 bg-gray-100 text-[#D1A054] focus:ring-[#D1A054]"
                  data-testid="select-all-checkbox"
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.sortable ? 'cursor-pointer hover:text-gray-700' : ''}`}
                onClick={() => col.sortable && onSort(col.key)}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  {col.sortable && sortBy === col.key && (
                    sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                  )}
                </div>
              </th>
            ))}
            {actions && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {orders.map((order) => (
            <tr 
              key={order.id} 
              className={`hover:bg-white/80 transition-colors cursor-pointer ${selectedIds && selectedIds.has(order.id) ? 'bg-[#D1A054]/10' : ''}`}
              data-testid={`order-row-${order.order_number}`}
              onClick={() => handleRowClick(order)}
            >
              {selectable && (
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds ? selectedIds.has(order.id) : false}
                    onChange={(e) => handleCheckboxChange(order, e)}
                    className="rounded border-gray-300 bg-gray-100 text-[#D1A054] focus:ring-[#D1A054]"
                    data-testid={`select-order-${order.order_number}`}
                  />
                </td>
              )}
              {columns.map((col) => (
                <td key={col.key} className={`px-3 py-2 text-gray-600 ${col.key === 'comments' || col.key === 'scheduling_result' ? 'max-w-[160px] truncate' : 'whitespace-nowrap'}`} title={col.key === 'comments' || col.key === 'scheduling_result' ? (order[col.key] || '') : undefined}>
                  {col.render ? col.render(order[col.key], order) : order[col.key] || '-'}
                </td>
              ))}
              {actions && (
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    {actions(order)}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Filters Component
const Filters = ({ filters, setFilters, inspectors, operators, showFlowType = true, showStatus = true, showInspector = true, showOperator = false }) => {
  const statuses = ['New', 'Assigned', 'In Field', 'Inspection Completed', 'Returned', 'On Hold', 'Rework'];
  const flowTypes = ['Internal', 'External', 'Regular'];

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <Filter className="h-4 w-4 text-gray-500" />
      {showFlowType && (
        <select
          value={filters.flow_type || ''}
          onChange={(e) => setFilters({ ...filters, flow_type: e.target.value })}
          className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
          data-testid="filter-flow-type"
        >
          <option value="">All Flow Types</option>
          {flowTypes.map(ft => <option key={ft} value={ft}>{ft}</option>)}
        </select>
      )}
      {showStatus && (
        <select
          value={filters.status || ''}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
          data-testid="filter-status"
        >
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      {showInspector && inspectors.length > 0 && (
        <select
          value={filters.assigned_inspector || ''}
          onChange={(e) => setFilters({ ...filters, assigned_inspector: e.target.value })}
          className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
          data-testid="filter-inspector"
        >
          <option value="">All Inspectors</option>
          {inspectors.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
        </select>
      )}
      {showOperator && operators && operators.length > 0 && (
        <select
          value={filters.assigned_operator || ''}
          onChange={(e) => setFilters({ ...filters, assigned_operator: e.target.value })}
          className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
          data-testid="filter-operator"
        >
          <option value="">All Operators</option>
          {operators.map(op => <option key={op} value={op}>{op}</option>)}
        </select>
      )}
      {(filters.flow_type || filters.status || filters.assigned_inspector || filters.assigned_operator) && (
        <button
          onClick={() => setFilters({})}
          className="px-2 py-1 text-xs text-gray-500 hover:text-gray-800"
        >
          Clear
        </button>
      )}
    </div>
  );
};

// Queue Tabs for Scheduling
const QueueTabs = ({ activeQueue, setActiveQueue, queueCounts }) => {
  const queues = [
    { id: 'all', label: 'All Orders' },
    { id: 'internal_due_today', label: 'Internal Due Today' },
    { id: 'external_call_today', label: 'External Call Today' },
    { id: 'urgent_calls', label: 'Urgent Calls' },
    { id: 'manual_review', label: 'Manual Review' },
    { id: 'admin_review', label: 'Admin Review' },
  ];

  return (
    <div className="flex flex-wrap gap-1 mb-4">
      {queues.map(q => {
        const count = queueCounts?.[q.id];
        const isUrgent = q.id === 'urgent_calls' && count > 0;
        return (
          <button
            key={q.id}
            onClick={() => setActiveQueue(q.id)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-all flex items-center gap-1.5 ${
              activeQueue === q.id
                ? 'bg-[#D1A054] text-[#1F2937]'
                : isUrgent
                ? 'bg-[#F5EDED]0/20 text-[#8B4545] border border-[#C07070]/30 hover:bg-[#F5EDED]0/30'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`queue-${q.id}`}
          >
            {q.label}
            {count != null && count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeQueue === q.id ? 'bg-gray-50/20 text-[#1F2937]' : isUrgent ? 'bg-[#E8D4D4] text-[#7A3B3B]' : 'bg-gray-200 text-gray-700'
              }`}>{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ─── PHASE 0 (Angela 2026-04-29) ─────────────────────────────────────
// AARIFI Internal windows (REAL) — these are the ONLY six legal windows.
// Operator picks Internal; Safeguard window is auto-derived from the
// mapping table in SchedulingOrderDetail.jsx / backend.
const SCHEDULING_WINDOWS = [
  { window: '8:00-9:00',   start: '08:00', end: '09:00' },
  { window: '8:00-12:00',  start: '08:00', end: '12:00' },   // Angela 2026-04-29
  { window: '9:00-1:00',   start: '09:00', end: '13:00' },
  { window: '9:30-1:30',   start: '09:30', end: '13:30' },
  { window: '10:00-2:00',  start: '10:00', end: '14:00' },
  { window: '11:00-3:00',  start: '11:00', end: '15:00' },
];
// Internal label → Safeguard label (display only — backend re-derives + validates).
const INTERNAL_TO_SG_LABEL = {
  '8:00-9:00':   '9:00-11:00',
  '8:00-12:00':  '12:00-4:00',   // Angela 2026-04-29
  '9:00-1:00':   '1:00-5:00',
  '9:30-1:30':   '1:30-5:30',
  '10:00-2:00':  '2:00-6:00',
  '11:00-3:00':  '3:00-7:00',
};

// Modals
const AppointmentModal = ({ order, onClose, onSave }) => {
  const [date, setDate] = useState(order?.appointment_sg_date || order?.appointment_real_date || '');
  const [selectedWindow, setSelectedWindow] = useState(order?.appointment_real_time || '');
  const [contactName, setContactName] = useState(order?.appointment_contact_name || '');
  const [contactPhone, setContactPhone] = useState(order?.appointment_contact_phone || '');

  const windowConfig = SCHEDULING_WINDOWS.find(w => w.window === selectedWindow);
  const sgLabel = INTERNAL_TO_SG_LABEL[selectedWindow] || '';
  // Explicit AM/PM table — 8-9 internal maps to 9-11 AM, all others to PM.
  const SG_LABEL_TO_AMPM = {
    '9:00-11:00': '9:00 AM – 11:00 AM',
    '12:00-4:00': '12:00 PM – 4:00 PM',  // Angela 2026-04-29
    '1:00-5:00':  '1:00 PM – 5:00 PM',
    '1:30-5:30':  '1:30 PM – 5:30 PM',
    '2:00-6:00':  '2:00 PM – 6:00 PM',
    '3:00-7:00':  '3:00 PM – 7:00 PM',
  };
  const sgLabelDisplay = SG_LABEL_TO_AMPM[sgLabel] || sgLabel;
  const internalDisplay = (() => {
    if (!windowConfig) return '';
    const fmt = (t) => {
      const [h, m] = t.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hh = h === 0 ? 12 : (h > 12 ? h - 12 : h);
      return `${hh}:${String(m).padStart(2,'0')} ${ampm}`;
    };
    return `${fmt(windowConfig.start)} – ${fmt(windowConfig.end)}`;
  })();

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(order.id, date, selectedWindow, windowConfig, contactName, contactPhone);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-gray-200 p-4 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Set Appointment</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
              required
              data-testid="appointment-date"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Time Window</label>
            <select
              value={selectedWindow}
              onChange={(e) => setSelectedWindow(e.target.value)}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
              required
              data-testid="appointment-window"
            >
              <option value="">Select window...</option>
              {SCHEDULING_WINDOWS.map(w => (
                <option key={w.window} value={w.window}>{w.window}</option>
              ))}
            </select>
          </div>
          {selectedWindow && windowConfig && (
            <div className="space-y-1.5 pt-1" data-testid="appointment-preview">
              <div className="bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 rounded px-3 py-1.5 flex items-center justify-between">
                <span className="text-[10px] text-[#6B8F62] font-semibold uppercase tracking-wider">Internal Window</span>
                <span className="text-sm text-[#6B8F62] font-medium">{windowConfig.start} – {windowConfig.end}</span>
              </div>
              <div className="bg-[#2F5D8A]/8 border border-[#2F5D8A]/25 rounded px-3 py-1.5 flex items-center justify-between">
                <span className="text-[10px] text-[#2F5D8A] font-semibold uppercase tracking-wider">Internal (AARIFI Real)</span>
                <span className="text-sm text-[#2F5D8A] font-bold">{internalDisplay || '—'}</span>
              </div>
              <div className="bg-gray-50 border border-gray-300 rounded px-3 py-1.5 flex items-center justify-between">
                <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Safeguard (Reported)</span>
                <span className="text-sm text-gray-800 font-bold">{sgLabelDisplay || '—'}</span>
              </div>
            </div>
          )}
          {/* Appointment Contact Fields — Required */}
          <div className="border-t border-gray-200 pt-3 mt-1">
            <p className="text-[10px] font-bold text-[#D1A054] uppercase tracking-wider mb-2">Who will be at the property?</p>
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Contact Name</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Name of person at property"
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
                  required
                  data-testid="appointment-contact-name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Contact Phone</label>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="Phone number for inspector SMS"
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
                  required
                  data-testid="appointment-contact-phone"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 bg-gray-100 text-gray-600 rounded text-sm">Cancel</button>
            <button type="submit" className="flex-1 px-3 py-2 bg-[#D1A054] text-[#1F2937] font-medium rounded text-sm" data-testid="save-appointment">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AssignModal = ({ order, inspectors, onClose, onSave }) => {
  const [inspector, setInspector] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inspector) {
      onSave(order.id, inspector);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-gray-200 p-4 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Assign Inspector</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <select
            value={inspector}
            onChange={(e) => setInspector(e.target.value)}
            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
            required
            data-testid="assign-inspector-select"
          >
            <option value="">Select Inspector</option>
            {inspectors.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
          </select>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 bg-gray-100 text-gray-600 rounded text-sm">Cancel</button>
            <button type="submit" className="flex-1 px-3 py-2 bg-[#D1A054] text-[#1F2937] font-medium rounded text-sm" data-testid="confirm-assign">Assign</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateOrderModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({
    order_number: '', company: '', cliente: '', address: '', city: '',
    due_date: '', flow_type: 'Regular'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(form);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-gray-200 p-4 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Create Order</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          {['order_number', 'company', 'cliente', 'address', 'city'].map(field => (
            <div key={field}>
              <label className="block text-xs text-gray-500 mb-1 capitalize">{field.replace('_', ' ')}</label>
              <input
                type="text"
                value={form[field]}
                onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
                required
                data-testid={`input-${field}`}
              />
            </div>
          ))}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Due Date</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
              data-testid="input-due-date"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Flow Type</label>
            <select
              value={form.flow_type}
              onChange={(e) => setForm({ ...form, flow_type: e.target.value })}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
              data-testid="input-flow-type"
            >
              <option value="Regular">Regular</option>
              <option value="Internal">Internal</option>
              <option value="External">External</option>
            </select>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 bg-gray-100 text-gray-600 rounded text-sm">Cancel</button>
            <button type="submit" className="flex-1 px-3 py-2 bg-[#D1A054] text-[#1F2937] font-medium rounded text-sm" data-testid="create-order-submit">Create</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Import Modal
const ImportModal = ({ onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/orders/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult({ type: 'success', data: response.data });
      onImport();
    } catch (error) {
      setResult({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Import failed' 
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-gray-200 p-5 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Import Orders from CSV</h3>
        
        <div className="mb-4">
          <p className="text-gray-500 text-xs mb-3">
            Upload a CSV file with columns: Company, Order_ID, Order_Type, Flow_Type, Address, City, State, Zip, Due_Date, Start_Date, PH_Name, PH_Phone_or_Contact, Agent_Name, Agent_Phone, SG_Scheduling_Status, Scheduling_Result, Comment, Appointment_Detail, Status, Queue, Attempts_Total, Notes
          </p>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".csv"
            className="hidden"
            data-testid="import-file-input"
          />
          
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#D1A054] transition-colors"
          >
            <Upload className="h-8 w-8 text-gray-500 mx-auto mb-2" />
            {file ? (
              <p className="text-gray-700 text-sm">{file.name}</p>
            ) : (
              <p className="text-gray-500 text-sm">Click to select CSV file</p>
            )}
          </div>
        </div>

        {result && (
          <div className={`mb-4 p-3 rounded-lg ${result.type === 'success' ? 'bg-[#A7C4A0]/15 border border-[#A7C4A0]/40' : 'bg-[#F5EDED]0/10 border border-[#C07070]/30'}`}>
            {result.type === 'success' ? (
              <div className="text-sm">
                <p className="text-[#6B8F62] font-medium mb-2">Import Completed!</p>
                <div className="text-gray-600 space-y-1">
                  <p>Created: {result.data.stats.created}</p>
                  <p>Updated: {result.data.stats.updated}</p>
                  <p>Reactivated: {result.data.stats.reactivated}</p>
                  <p>Duplicates skipped: {result.data.stats.duplicates}</p>
                  <p>Removed from source: {result.data.stats.removed}</p>
                  {result.data.stats.errors > 0 && <p className="text-[#8B4545]">Errors: {result.data.stats.errors}</p>}
                </div>
              </div>
            ) : (
              <p className="text-[#8B4545] text-sm">{result.message}</p>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <button 
            type="button" 
            onClick={onClose} 
            className="flex-1 px-3 py-2 bg-gray-100 text-gray-600 rounded text-sm"
          >
            {result?.type === 'success' ? 'Close' : 'Cancel'}
          </button>
          {!result?.type && (
            <button 
              onClick={handleImport}
              disabled={!file || importing}
              className="flex-1 px-3 py-2 bg-[#D1A054] text-[#1F2937] font-medium rounded text-sm disabled:opacity-50"
              data-testid="import-submit"
            >
              {importing ? 'Importing...' : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// LOCKED VISIBLE COLUMNS - DO NOT CHANGE
// Correct order: order_number, company, type, cliente, ph_name, address, city, state, due_date, appointment_set
// unique_key is INTERNAL ONLY - never display in lists
const baseColumns = [
  { key: 'order_number', label: 'Order #', sortable: false, render: (v, o) => (
    <div className="flex items-center gap-1.5">
      <span>{v}</span>
      {o.returned === true && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#E8D4D4] text-[#7A3B3B] whitespace-nowrap" data-testid="returned-flag">RETURNED</span>}
    </div>
  )},
  { key: 'company', label: 'Company', sortable: false },  // Safeguard or National
  { key: 'order_type', label: 'Type', sortable: false },
  { key: 'cliente', label: 'Cliente', sortable: false },  // Insurance/mortgage company
  { key: 'ph_name', label: 'PH Name', sortable: false },  // Policyholder name
  { key: 'address', label: 'Address', sortable: false },
  { key: 'city', label: 'City', sortable: false },
  { key: 'state', label: 'State', sortable: false },
  { key: 'due_date', label: 'Due Date', sortable: true, render: (v) => formatDateDisplay(v) },
  { key: 'appointment_set', label: 'Appt Set', sortable: false },
];

// Scheduling-specific columns with live scheduling data
const schedulingColumns = [
  { key: 'due_date', label: 'Due Date', sortable: true, render: (v) => formatDateDisplay(v) },
  { key: 'order_number', label: 'Order #', sortable: true },
  { key: 'company', label: 'Company', sortable: true },
  { key: 'order_type', label: 'Order Type', sortable: true },
  { key: 'flow_type', label: 'Flow', sortable: true },
  // Angela 2026-04-29 — Scheduling Status column (derived from backend).
  // NOTE: this column reads the DERIVED `scheduling_status` field injected
  // by GET /api/scheduling/orders. The persisted `status` (used by Dispatch /
  // Finance / QA / Closing) is NOT read here. "Assigned" NEVER appears.
  {
    key: 'scheduling_status',
    label: 'Scheduling Status',
    sortable: true,
    render: (v) => {
      const label = v || '—';
      const styles = {
        'New':                                   'bg-blue-50 text-blue-700 border-blue-200',
        'Waiting for Scheduling':                'bg-amber-50 text-amber-700 border-amber-200',
        'Returned / Reappeared from Safeguard':  'bg-rose-50 text-rose-700 border-rose-200',
        'Flipped':                               'bg-purple-50 text-purple-700 border-purple-200',
        'Scheduled':                             'bg-emerald-50 text-emerald-700 border-emerald-200',
        'Ready for Safeguard Review':            'bg-amber-50 text-amber-700 border-amber-200',
        'Ready to Dispatch':                     'bg-sky-50 text-sky-700 border-sky-200',
      };
      const cls = styles[label] || 'bg-gray-50 text-gray-700 border-gray-200';
      return (
        <span
          className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${cls}`}
          data-testid={`scheduling-status-badge-${label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
        >
          {label}
        </span>
      );
    },
  },
  { key: 'address', label: 'Address', sortable: true, render: (val) => val ? <span className="truncate max-w-[220px] block" title={val}>{val}</span> : <span className="text-gray-300">-</span> },
  { key: 'city', label: 'City', sortable: true },
  { key: 'state', label: 'State', sortable: true },
  { key: 'sg_attempts', label: 'Attempts', sortable: true, render: (v) => v != null ? v : '-' },
];

// Scheduling Page
const SCHED_BATCH_FUNCTIONS = ["Active Call", "Incoming Call", "Email", "SMS", "Respond Safeguard Email", "Inspector Help", "Voicemail"];

const BatchAssignModal = ({ selectedCount, operators, onClose, onAssign }) => {
  const [operator, setOperator] = useState('');
  const [func, setFunc] = useState('');
  const handleSubmit = () => {
    if (!operator || !func) return;
    onAssign(operator, func);
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="batch-assign-modal">
      <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Assign Batch</h3>
        <p className="text-gray-500 text-sm mb-4">{selectedCount} orders selected</p>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-500 text-xs uppercase mb-1">Operator</label>
            <select value={operator} onChange={e => setOperator(e.target.value)} className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-gray-800 text-sm" data-testid="batch-operator-select">
              <option value="">Select operator...</option>
              {operators.map(op => <option key={op.login_id || op.name} value={op.name}>{op.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-gray-500 text-xs uppercase mb-1">Function</label>
            <select value={func} onChange={e => setFunc(e.target.value)} className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-gray-800 text-sm" data-testid="batch-function-select">
              <option value="">Select function...</option>
              {SCHED_BATCH_FUNCTIONS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-800" data-testid="batch-cancel-btn">Cancel</button>
          <button onClick={handleSubmit} disabled={!operator || !func} className="px-4 py-2 bg-[#D1A054] hover:bg-[#B08A3E] disabled:opacity-40 text-[#1F2937] text-sm font-semibold rounded-lg" data-testid="batch-assign-btn">Assign Batch</button>
        </div>
      </div>
    </div>
  );
};

const SchedulingPage = () => {
  const QA_PIPELINE = ['EOD Closed', 'QA', 'QA Approved', 'Follow-Up', 'Ready for Billing', 'Billing', 'Finance', 'Paid', 'Completed'];
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inspectors, setInspectors] = useState([]);
  const [operators, setOperators] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('due_date');
  const [sortDir, setSortDir] = useState('desc');
  const [activeQueue, setActiveQueue] = useState('all_scheduling');
  // Angela 2026-05-03: when a Scheduling card is clicked (activeQueue changes),
  // scroll the order list section into view so the operator does not have to
  // scroll manually. Skips the first render so the page loads at the top.
  const orderListRef = useRef(null);
  const isFirstActiveQueueRef = useRef(true);
  useEffect(() => {
    if (isFirstActiveQueueRef.current) {
      isFirstActiveQueueRef.current = false;
      return;
    }
    orderListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeQueue]);
  const [showHiddenNationalCalls, setShowHiddenNationalCalls] = useState(false);
  // Angela 2026-05-10 — Inspector Field Updates queue (red queue button under
  // External Morning Calls). Polls /api/inspector-field-events/pending every
  // 30s and shows a flashing red badge until the operator opens the modal.
  const [fieldUpdatesPending, setFieldUpdatesPending] = useState([]);
  const [showFieldUpdatesModal, setShowFieldUpdatesModal] = useState(false);
  const fetchFieldUpdatesPending = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inspector-field-events/pending`);
      setFieldUpdatesPending(Array.isArray(r.data) ? r.data : []);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => {
    fetchFieldUpdatesPending();
    const id = setInterval(fetchFieldUpdatesPending, 30000);
    return () => clearInterval(id);
  }, [fetchFieldUpdatesPending]);
  const openFieldUpdatesModal = useCallback(async () => {
    // Angela 2026-05-10 v2 — Manual-handle queue: opening the modal does
    // NOT clear pending. Items remain in the queue until Scheduling clicks
    // "Mark Handled" on each row. Re-fetch on open to refresh list.
    await fetchFieldUpdatesPending();
    setShowFieldUpdatesModal(true);
  }, [fetchFieldUpdatesPending]);
  const markFieldUpdateHandled = useCallback(async (eventId) => {
    if (!eventId) return;
    try {
      await axios.post(`${API}/inspector-field-events/${eventId}/mark-handled`);
      setFieldUpdatesPending(prev => prev.filter(e => e.id !== eventId));
    } catch (e) { /* silent */ }
  }, []);
  const fieldUpdatesPendingCount = fieldUpdatesPending.length;
  // P1-1 (Angela 2026-05-17) — Manual Review pending count for top banner.
  // Fetched from dedicated endpoint so it reflects ALL orders in db (not
  // just the filtered Scheduling set held in `orders` state).
  const [manualReviewCount, setManualReviewCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/scheduling/manual-review-count`);
        if (!cancelled) setManualReviewCount(r.data?.count || 0);
      } catch (e) { /* silent */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000); // refresh every 60s
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [appointmentOrder, setAppointmentOrder] = useState(null);
  const [assignOrder, setAssignOrder] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBatchAssign, setShowBatchAssign] = useState(false);
  const [batchMessage, setBatchMessage] = useState(null);
  const [allCompanyOrders, setAllCompanyOrders] = useState([]);
  const [liveBatches, setLiveBatches] = useState([]);
  const [liveAssignments, setLiveAssignments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showKB, setShowKB] = useState(false);
  const [kbTab, setKbTab] = useState('insurance');
  const [eodStats, setEodStats] = useState({ worked_today: 0, updated: 0, missing: 0 });
  const [showForceEOD, setShowForceEOD] = useState(false);
  const [forceEODProcessing, setForceEODProcessing] = useState(false);
  const [showVoicemailModal, setShowVoicemailModal] = useState(false);
  const [bulkSmsOpen, setBulkSmsOpen] = useState(false);
  const [bulkSmsPreview, setBulkSmsPreview] = useState(null);
  // Angela 2026-05-19 — per-row selection state for both bulk SMS modals.
  // The frontend defaults to "every ready row selected"; Angela can uncheck
  // any she wants to skip. INVALID / NO PHONE / NO TIME / ALREADY SENT rows
  // start UNCHECKED (and are visually disabled). The send call POSTs an
  // explicit whitelist so the backend never touches rows we hid.
  const [bulkSmsSelected, setBulkSmsSelected] = useState(() => new Set());
  const [dayBeforeSmsSelected, setDayBeforeSmsSelected] = useState(() => new Set());
  const [bulkSmsLoading, setBulkSmsLoading] = useState(false);
  const [bulkSmsResult, setBulkSmsResult] = useState(null);
  // Angela Feb 2026 — inline phone edit inside SMS modals (Today +
  // Day-Before). Keyed by `${modalKey}:${order_number}` so both modals
  // can host an active edit independently without collisions.
  //   editingPhone     — the key currently in edit mode ("" if none)
  //   editingPhoneVal  — controlled input value
  //   editingPhoneErr  — validation error text
  //   editingPhoneBusy — "saving" while the PATCH is in flight
  const [editingPhone, setEditingPhone] = useState("");
  const [editingPhoneVal, setEditingPhoneVal] = useState("");
  const [editingPhoneErr, setEditingPhoneErr] = useState("");
  const [editingPhoneBusy, setEditingPhoneBusy] = useState(false);
  // Day-before reminder SMS (internal orders scheduled for TOMORROW)
  const [dayBeforeSmsOpen, setDayBeforeSmsOpen] = useState(false);
  const [dayBeforeSmsPreview, setDayBeforeSmsPreview] = useState(null);
  const [dayBeforeSmsLoading, setDayBeforeSmsLoading] = useState(false);
  const [dayBeforeSmsResult, setDayBeforeSmsResult] = useState(null);
  // One-time time-correction SMS batch (fix customers who got Safeguard-shifted time)
  const [correctionSmsOpen, setCorrectionSmsOpen] = useState(false);
  const [correctionSmsPreview, setCorrectionSmsPreview] = useState(null);
  const [correctionSmsLoading, setCorrectionSmsLoading] = useState(false);
  const [correctionSmsResult, setCorrectionSmsResult] = useState(null);
  const [showCallWithoutModal, setShowCallWithoutModal] = useState(false);
  const [showQuickAssignInspector, setShowQuickAssignInspector] = useState(false);
  const [dailyProductivity, setDailyProductivity] = useState({ totals: { calls_made: 0, calls_received: 0, missed_calls: 0, sms: 0, emails: 0, appointments_set: 0, safeguard_emails: 0, total_actions: 0 }, operators: [], last_updated: null });
  const [prodDrill, setProdDrill] = useState(null); // { metric, operator, label } when open
  const [prodDrillRows, setProdDrillRows] = useState([]);
  const [prodDrillLoading, setProdDrillLoading] = useState(false);
  // RC-2: minimal counters for RingCentral unified inbox cards
  const [rcInboxCounts, setRcInboxCounts] = useState({ sms: 0, voicemail: 0 });
  // Angela Feb 2026 (External Calls consistency — URGENT fix):
  // Order-ids that have ANY scheduling_log activity since NY midnight today.
  // Used as a safety-net exclusion alongside `last_call_logged_at` so
  // External Calls never lists an order the team already worked today —
  // even if the call was logged via a path that skipped the per-order stamp.
  const [handledTodayIds, setHandledTodayIds] = useState(new Set());

  // Angela 2026-05-16 — Queue Drawer state (visual redesign).
  // Each StatCard click now ALSO opens a side-drawer with the same orders
  // the table is filtering — predicates and counts UNTOUCHED. Drawer is
  // purely a presentational view of the activeQueue filtered list.
  const [queueDrawer, setQueueDrawer] = useState(null); // {key, title} | null
  // Collapse state for low-priority sections (Angela 2026-05-16 redesign).
  // All five collapsed by default; Scheduling Map stays expanded.
  const [expandedSections, setExpandedSections] = useState({
    registerCallWithout: false,
    weekends: false,
    holdCalls: false,
    liveAssignment: false,
    ordersTable: false,
  });
  const toggleSection = (k) => setExpandedSections((p) => ({ ...p, [k]: !p[k] }));

  const isAdmin = user?.role === 'Admin';

  const openKB = (tab) => { setKbTab(tab); setShowKB(true); };

  // Angela 2026-05-16 — opens the queue drawer AND keeps the existing
  // setActiveQueue table-filter behaviour (no business logic change).
  const openQueueAsDrawer = (queueKey, queueTitle) => {
    setActiveQueue(queueKey);
    setQueueDrawer({ key: queueKey, title: queueTitle });
  };

  const fetchOrders = useCallback(async () => {    try {
      const response = await axios.get(`${API}/scheduling/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Angela Feb 2026 (External Calls consistency): pull today's handled-set
  // from scheduling_log. Cheap (~ few hundred IDs). Refreshed alongside orders.
  const fetchHandledToday = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scheduling/handled-today`);
      const ids = new Set((res.data?.order_ids) || []);
      setHandledTodayIds(ids);
    } catch (error) {
      console.warn('handled-today fetch failed (non-blocking):', error);
    }
  }, []);

  const fetchAllCompanyOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders?scope=active`);
      // Live orders only (full history lives in /scheduling/all-orders). MASTER HISTORICAL VIEW — no filtering by status, active, or stage.
      // Every order must appear regardless of its lifecycle state.
      setAllCompanyOrders(response.data || []);
    } catch (error) {
      console.error('Failed to fetch company orders:', error);
    }
  }, []);

  const fetchLiveBatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scheduling/batches?status=working`);
      const pending = await axios.get(`${API}/scheduling/batches?status=pending`);
      setLiveBatches([...response.data, ...pending.data]);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  }, []);

  const fetchLiveAssignments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scheduling/live-assignments`);
      setLiveAssignments(response.data);
    } catch (error) {
      console.error('Failed to fetch live assignments:', error);
    }
  }, []);

  const fetchEodStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/summary`);
      setEodStats(res.data.eod_updates || { worked_today: 0, updated: 0, missing: 0 });
    } catch (e) { /* ignore */ }
  }, []);

  const fetchDailyProductivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scheduling/productivity`);
      setDailyProductivity(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  // Drill-down (Apr 22 2026) — fetch on open
  useEffect(() => {
    if (!prodDrill) return;
    let cancelled = false;
    (async () => {
      setProdDrillLoading(true);
      try {
        const params = { metric: prodDrill.metric };
        if (prodDrill.operator) params.operator = prodDrill.operator;
        const res = await axios.get(`${API}/scheduling/productivity/drill`, { params });
        if (!cancelled) setProdDrillRows(res.data?.rows || []);
      } catch (e) {
        if (!cancelled) setProdDrillRows([]);
      } finally {
        if (!cancelled) setProdDrillLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [prodDrill]);

  const fetchInspectors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inspectors`);
      setInspectors(response.data);
    } catch (error) {
      console.error('Failed to fetch inspectors:', error);
    }
  }, []);

  const fetchOperators = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/team/operators`);
      setOperators(response.data);
    } catch (error) {
      console.error('Failed to fetch operators:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchHandledToday();
    fetchInspectors();
    fetchOperators();
    fetchAllCompanyOrders();
    fetchLiveBatches();
    fetchLiveAssignments();
    fetchEodStats();
    fetchDailyProductivity();
    const interval = setInterval(() => { fetchLiveBatches(); fetchLiveAssignments(); fetchEodStats(); fetchDailyProductivity(); }, 15000);
    // Auto-refresh orders every 30s so queue counters (Internal Calls, SMS Queue, etc.) decrease live as work is completed
    const ordersInterval = setInterval(() => { fetchOrders(); fetchHandledToday(); fetchAllCompanyOrders(); }, 30000);
    return () => { clearInterval(interval); clearInterval(ordersInterval); };
  }, [fetchOrders, fetchHandledToday, fetchInspectors, fetchOperators, fetchAllCompanyOrders, fetchLiveBatches, fetchLiveAssignments, fetchEodStats, fetchDailyProductivity]);

  // Immediate refresh when the user returns from a detail page (e.g., after registering Call PH).
  // The detail page passes `location.state.refreshedAt = Date.now()` on navigate.
  useEffect(() => {
    if (location.state && location.state.refreshedAt) {
      fetchOrders();
      fetchHandledToday();
      fetchAllCompanyOrders();
      fetchLiveBatches();
      fetchLiveAssignments();
      fetchDailyProductivity();
    }
  }, [location.state, fetchOrders, fetchAllCompanyOrders, fetchLiveBatches, fetchLiveAssignments, fetchDailyProductivity]);

  // RC-2: pull RingCentral inbox counters periodically (read-only counts only)
  useEffect(() => {
    let cancelled = false;
    const fetchRcCounts = async () => {
      try {
        const [smsR, vmR] = await Promise.all([
          axios.get(`${API}/rc-inbox/sms?limit=1`),
          axios.get(`${API}/rc-inbox/voicemail?limit=1`),
        ]);
        if (!cancelled) setRcInboxCounts({ sms: smsR.data?.total || 0, voicemail: vmR.data?.total || 0 });
      } catch (e) { /* silent — RC inbox may not be available for all roles */ }
    };
    fetchRcCounts();
    const id = setInterval(fetchRcCounts, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  // National Call Queue — manual hide/restore toggle (Apr 2026)
  const handleToggleNationalCallQueue = async (orderId, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    try {
      await axios.post(`${API}/orders/${orderId}/national-call-queue/toggle`);
      // Refresh orders to pick up the new flag value
      fetchOrders();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to toggle queue visibility");
    }
  };

  const handleSelectToggle = (orderId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(filteredOrders.map(o => o.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleShiftSelect = (rangeIds) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      rangeIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleQuickSelect = (count) => {
    setSelectedIds(new Set(filteredOrders.slice(0, count).map(o => o.id)));
  };

  const handleBatchAssign = async (operatorName, functionType) => {
    try {
      await axios.post(`${API}/scheduling/batch-assign`, {
        operator_name: operatorName,
        function_type: functionType,
        order_ids: Array.from(selectedIds),
      });
      setBatchMessage({ type: 'success', text: `${selectedIds.size} orders assigned to ${operatorName} for ${functionType}` });
      setSelectedIds(new Set());
      setShowBatchAssign(false);
      fetchOrders();
      setTimeout(() => setBatchMessage(null), 4000);
    } catch (error) {
      setBatchMessage({ type: 'error', text: 'Failed to assign batch' });
      setTimeout(() => setBatchMessage(null), 4000);
    }
  };

  const handleBulkActivity = async (activityType) => {
    const count = selectedIds.size;
    const resultMap = { SMS: "Sent SMS", Email: "Sent Email", "Respond Safeguard Email": "Sent Email" };
    try {
      const res = await axios.post(`${API}/scheduling/bulk-activity`, {
        order_ids: Array.from(selectedIds),
        activity_type: activityType,
        result: resultMap[activityType] || "Other",
        comment: `Bulk ${activityType} for ${count} orders`,
      });
      setBatchMessage({ type: 'success', text: `${activityType} registered for ${res.data.processed} orders` });
      setSelectedIds(new Set());
      fetchOrders();
      setTimeout(() => setBatchMessage(null), 4000);
    } catch (err) {
      setBatchMessage({ type: 'error', text: `Failed to register bulk ${activityType}` });
      setTimeout(() => setBatchMessage(null), 4000);
    }
  };

  const handleForceEndOfDay = async () => {
    setForceEODProcessing(true);
    try {
      const res = await axios.post(`${API}/orders/force-remove-assignment`, {
        order_ids: Array.from(selectedIds),
        comment: "End of Day Reset",
      });
      setBatchMessage({ type: 'success', text: `Assignments removed from ${res.data.processed} orders` });
      setSelectedIds(new Set());
      setShowForceEOD(false);
      fetchOrders();
      setTimeout(() => setBatchMessage(null), 4000);
    } catch (err) {
      setBatchMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to remove assignments' });
      setTimeout(() => setBatchMessage(null), 4000);
    } finally {
      setForceEODProcessing(false);
    }
  };

  const handleQuickAssignInspector = async (inspectorLogin) => {
    let assigned = 0, errors = 0;
    for (const oid of selectedIds) {
      try {
        await axios.post(`${API}/orders/${oid}/quick-assign-inspector`, { inspector_name: inspectorLogin });
        assigned++;
      } catch (err) {
        errors++;
        console.error('Assign error:', err);
      }
    }
    const msg = errors > 0 ? `${assigned} assigned, ${errors} errors` : `${assigned} order(s) assigned to ${inspectorLogin}`;
    setBatchMessage({ type: errors > 0 ? 'error' : 'success', text: msg });
    setSelectedIds(new Set());
    setShowQuickAssignInspector(false);
    fetchOrders();
    setTimeout(() => setBatchMessage(null), 5000);
  };

  const uniqueOperators = useMemo(() => {
    const ops = new Set();
    orders.forEach(o => { if (o.assigned_operator) ops.add(o.assigned_operator); });
    return Array.from(ops).sort();
  }, [orders]);


  const todayStr = useMemo(() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }, []);

  const tomorrowStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }, []);

  const day2Str = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }, []);

  // ISO timestamp for start-of-today (UTC) — used to exclude orders whose
  // `last_call_logged_at` happened today from the National Calls pending counter.
  const todayStartIso = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);

  // Compute queue counts for badges
  const queueCounts = useMemo(() => {
    const isDueTodayOrOverdue = (o) => o.due_date && o.due_date <= todayStr;
    const isNational = (o) => (o.company || '').toLowerCase() === 'national';
    const isSafeguard = (o) => (o.company || '').toLowerCase() === 'safeguard';
    // ── Scheduling Dashboard rules — FINAL (Angela, 2026-04-27) ──
    // Source of truth: the latest SAFEGUARD – SCHEDULE.xlsx import.
    //   1. Workcode (order_type) classifies Internal vs External:
    //        Internal = order_type starts with 'I' OR exactly 'SIFINT'
    //        External = order_type starts with 'E' OR exactly 'SIFI'
    //        PHOT     = order_type/workcode contains 'PHOT'
    //        Unmapped = any other value (e.g., 'SIFIH6')
    //   2. Buckets are by due_date ONLY:
    //        Overdue=<today, Today=today, Tomorrow=tomorrow, Upcoming>=day+2
    //   3. Internal Calls = workcode-Internal + appointment_set!=yes + active.
    //        DO NOT use Next Steps / sg_next_action / last_call_logged_at /
    //        attempts / cooldown / call cycle to filter Internal.
    //   4. External Calls = workcode-External + zero attempts + appointment_set!=yes + active.
    //        Attempts is the ONLY rule applied to External (Angela rule).
    //   5. Appointment_set=yes → Dispatch flow, never in call queues.
    //   6. PHOT is its own queue.
    //   7. Need Reschedule / Reschedule Queue NOT used here.
    //   8. Phase C statuses (Manual Review, Field Reschedule Needed,
    //      Daily Close Review, Finance Review) keep their pills but DO NOT
    //      block orders from appearing in Internal/External by date+workcode.
    //   9. Hard-terminal statuses (Archived/Completed/Paid/QA*) are excluded.
    //  10. Scope to current Schedule import: active_in_latest_import !== false.
    //      (true OR null = in scope; only False = removed by latest import.)
    const HARD_TERMINAL = new Set([
      'Archived', 'Completed', 'Paid',
      'QA', 'QA Approved', 'QA Returned',
    ]);
    const isHardActive = (o) =>
      o.active_order !== 'no'
      && o.removed_from_source !== 'yes'
      && o.is_duplicate !== 'yes'
      && !HARD_TERMINAL.has(o.status || '');
    const inLatestSchedule = (o) => o.active_in_latest_import === true;
    const isActiveScheduling = (o) =>
      isSafeguard(o) && isHardActive(o) && o.appointment_set !== 'yes' && inLatestSchedule(o);
    // Live-queue filter (Angela 2026-04-28): an order leaves a Scheduling
    // call queue the instant a valid activity is registered today.  The
    // backend stamps `last_call_logged_at` on every Call/SMS/Email log
    // (see register_activity in server.py).  We compare against the
    // start-of-today ISO so the queue auto-resets each new day.
    const notHandledToday = (o) =>
      !o.last_call_logged_at || o.last_call_logged_at < todayStartIso;
    // Attempts=0 test — used for External Calls only.
    const isZeroAttempts = (o) => {
      const a = o.attempts;
      if (a === null || a === undefined) return true;
      if (typeof a === 'number') return a === 0;
      const s = String(a).trim().toLowerCase();
      if (s === '' || s === '0' || s === 'no attempts' || s === 'attempts: 0') return true;
      return false;
    };
    // Workcode classification — uses `order_type` (DB field that mirrors
    // the SAFEGUARD WorkCode column).  PHOT first, then exact matches,
    // then prefix matches.
    //
    // "Unmapped" = an order in the Schedule whose workcode follows the
    // Safeguard pattern (starts with SIFI) but is NOT one of the known
    // exact codes (SIFI / SIFINT).  Example today: SIFIH6.  This list is
    // intentionally narrow so unrelated workcodes from the Regular Orders
    // import (FI / DF / FINT / WFFIF / …) are NOT surfaced as "Unmapped"
    // — they belong to a different spreadsheet entirely and should never
    // show up in the Scheduling dashboard.
    const wcClass = (o) => {
      const ot = String(o.order_type || '').trim().toUpperCase();
      const wc = String(o.workcode || '').trim().toUpperCase();
      if (ot.includes('PHOT') || wc.includes('PHOT')) return 'PHOT';
      const v = ot || wc;
      if (!v) return 'Unknown';
      if (v === 'SIFINT') return 'Internal';
      if (v === 'SIFI' || v === 'SIFIEX') return 'External';
      if (v.startsWith('SIFI')) return 'Unmapped';  // SIFIH6, SIFIxx, etc.
      if (v.startsWith('I')) return 'Internal';
      if (v.startsWith('E')) return 'External';
      return 'OutOfSchedule';   // FI / DF / FINT / WFFIF / etc — Regular Orders
    };
    const isWcInternal = (o) => wcClass(o) === 'Internal';
    const isWcExternal = (o) => wcClass(o) === 'External';
    const isWcPhot = (o) => wcClass(o) === 'PHOT';
    const isWcUnmapped = (o) => wcClass(o) === 'Unmapped';
    // Angela 2026-08-09 — STRICT SG SCHEDULE Internal rule (supersedes the
    // 2026-05-19 "first letter I or S" heuristic which was silently pulling
    // ALL SIFI/SIFIEX/SIFIH6 rows into the Internal bucket).
    //   Internal = order_type starts with 'I'  OR  order_type == 'SIFINT'
    //   Explicit exclude: 'SIFI', 'SIFIEX', 'SIFI*' (SIFIH6, etc.)
    // Check order matters: SIFINT (exact) BEFORE the generic SIFI-prefix
    // exclusion, otherwise SIFINT falls into External.
    const isSgScheduleInternalWc = (o) => {
      const v = String(o.order_type || '').trim().toUpperCase();
      if (!v) return false;
      if (v === 'SIFINT') return true;        // exact SIFINT → Internal
      if (v.startsWith('SIFI')) return false; // SIFI / SIFIEX / SIFIH6 → NOT Internal
      return v.startsWith('I');               // any other I-prefix → Internal
    };
    const _SG_EXCLUDED_STATUSES = new Set(['scheduled', 'rescheduled']);
    const isNotSgExcludedStatus = (o) => {
      const s = String(o.status || '').trim().toLowerCase();
      return !_SG_EXCLUDED_STATUSES.has(s);
    };
    return {
      full_company: allCompanyOrders.length,
      all_scheduling: orders.length,
      // ── Internal Calls — TOTAL = Safeguard Internal + National Internal ──
      // Per Angela 2026-04-27: Total must equal explicit sum (no double-count).
      // Subtotals available via safeguard_internal_calls / national_internal_calls.
      // Live-queue rule (Angela 2026-04-28): exclude orders already handled today.
      safeguard_internal_calls: allCompanyOrders.filter(o => isActiveScheduling(o) && isWcInternal(o) && notHandledToday(o)).length,
      // Angela 2026-05-03: National Calls = National Internal only.
      // National External must appear under External Calls, not here.
      national_internal_calls: allCompanyOrders.filter(o =>
        isNational(o)
        && isHardActive(o)
        && (o.flow_type || '') === 'Internal'
        && o.appointment_set !== 'yes'
        && o.national_call_queue_hidden !== 'yes'
        && notHandledToday(o)
      ).length,
      internal_calls: (
        allCompanyOrders.filter(o => isActiveScheduling(o) && isWcInternal(o) && notHandledToday(o)).length
        + allCompanyOrders.filter(o =>
            isNational(o) && isHardActive(o)
            && (o.flow_type || '') === 'Internal'
            && o.appointment_set !== 'yes'
            && o.national_call_queue_hidden !== 'yes'
            && notHandledToday(o)
          ).length
      ),
      // ── External Calls — Safeguard External + no appointment ──
      // Rule (Angela Feb 2026 STRICT LOCK): filter by flow_type === 'External'
      // (the canonical AARIFI classification), NOT by workcode/order_type.
      // Workcode-based filter (`isWcExternal`) was under-including orders
      // whose flow_type is External but whose order_type didn't match the
      // SIFI*/E* pattern (e.g., 354286873, 354286604, 354286870, 354301941
      // — flow_type=External, order_type classified `OutOfSchedule`).
      // Supersedes Angela 2026-05-02 H-4 fix: still includes ANY attempts
      // value, still excludes `appointment_set === 'yes'`, still uses the
      // live-queue (notHandledToday) and latest-import (inLatestSchedule)
      // rules — those are explicitly kept untouched.
      // Angela 2026-05-03: also include National External (no-appt) so they
      // share the External Calls bucket instead of National Calls.
      // Angela Feb 2026 (External Calls URGENT fix): ALSO exclude orders
      // whose order_id is in the authoritative `scheduling_log` handled-today
      // set (`handledTodayIds`). Safety-net so João's registered calls drop
      // from the list even when the per-order `last_call_logged_at` stamp
      // was not written (legacy activity_type, etc.).
      // Angela Feb 2026 (REAL-ATTEMPT FIX — Scheduling-only): require
      // `real_attempt_count === 0`.  This field is computed server-side in
      // /api/scheduling/orders as max(attempts_int(o.attempts),
      //  count of call-type entries in scheduling_log for that order).
      // Backend already drops attempts>=1 External rows; the frontend check
      // is a defensive mirror so card/list never disagree.
      external_calls: allCompanyOrders.filter(o => {
        const isExt = isSafeguard(o) && isHardActive(o) && o.appointment_set !== 'yes' && inLatestSchedule(o) && (o.flow_type || '') === 'External';
        const isAarifiExterior = (o.inspector_login || '').trim().toUpperCase() === 'AARIFI-EXTERIOR'
          && isHardActive(o) && o.appointment_set !== 'yes' && inLatestSchedule(o) && (o.flow_type || '') === 'External';
        const isNatExt = isNational(o) && isHardActive(o) && o.appointment_set !== 'yes'
          && (o.flow_type || '') === 'External'
          && o.national_call_queue_hidden !== 'yes';
        if (!(isExt || isAarifiExterior || isNatExt)) return false;
        if (!notHandledToday(o)) return false;
        if (handledTodayIds && handledTodayIds.has(String(o.id || ''))) return false;
        if (Number(o.real_attempt_count || 0) >= 1) return false;
        return true;
      }).length,
      national_calls: allCompanyOrders.filter(o =>
        isNational(o)
        && isHardActive(o)
        && (o.flow_type || '') === 'Internal'
        && o.appointment_set !== 'yes'
        && o.national_call_queue_hidden !== 'yes'
        && notHandledToday(o)
      ).length,
      // External Morning Calls — LIVE QUEUE (Angela 2026-04-28).
      //   Counts only EXTERNAL orders due TODAY that are still pending the
      //   morning-call step.  Drops out instantly when an action (call/SMS/
      //   email) is logged today (last_call_logged_at >= start-of-today),
      //   when the operator marks the morning call complete, or when the
      //   day rolls (due_date != today).  Static daily reset by design.
      // HOTFIX (Angela 2026-05-18 PROD) — accept BOTH due_date AND route_date
      // matching today. Some workflows stamp only one of the two when the
      // morning-call queue is built, so being strict on due_date alone hid 8
      // legitimate orders. Belt-and-suspenders predicate.
      external_morning_calls: orders.filter(o =>
        (o.flow_type || '').toLowerCase() === 'external'
        && (o.due_date === todayStr || o.route_date === todayStr)
        && (o.external_morning_call_required || '').toLowerCase() === 'yes'
        && (o.active_order || 'yes') !== 'no'
        && !o.external_morning_call_completed_at
        && (!o.last_call_logged_at || o.last_call_logged_at < todayStartIso)
      ).length,
      // ── Safeguard Internal date buckets — workcode I*/SIFINT + due_date only.
      //    Only Today / Tomorrow / +2 days are surfaced on the Scheduling
      //    dashboard (per Angela 2026-04-27).  No "Internal Overdue" and no
      //    "Internal Upcoming" cards on this screen — Scheduling is for the
      //    Today/Tomorrow/+2 call workload only.
      //    Live-queue rule (Angela 2026-04-28): exclude handled-today.
      //
      // Angela 2026-07-30 — REVERTS the 2026-05-15 exception below.
      // Definitive rule: no order with `appointment_set === 'yes'` should
      // appear in SG Today / SG TOM / SG +2D call lists. Ever. Appt Set
      // orders never receive calls. Predicate here MUST stay in lockstep
      // with the drawer filter (search "activeQueue === 'internal_today'")
      // so the count and the drawer's list are always identical.
      //
      // Prior comment (kept for history — do not act on it):
      //    Angela 2026-05-15 v3 — Source-of-truth predicate aligned with
      //    SAFEGUARD–SCHEDULE.xlsx row counts (16 / 17 / 18 for 05/15-17).
      //    Three changes vs prior call-workload predicate:
      //      1. Workcode filter widened: Internal OR External.
      //      2. `appointment_set !== 'yes'` dropped — appt_set rows are
      //         still part of the daily schedule total.
      //      3. `notHandledToday(o)` dropped.
      //    Reason: 3 appt_set rows on 05/15 XLSX. That reason no longer
      //    applies (2026-07-30) — Angela wants appt_set excluded from
      //    all call lists.
      safeguard_internal_today: allCompanyOrders.filter(o =>
        isSafeguard(o) && isHardActive(o) && inLatestSchedule(o)
        && isSgScheduleInternalWc(o) && isNotSgExcludedStatus(o)
        && o.appointment_set !== 'yes'
        && o.due_date === todayStr
      ).length,
      safeguard_internal_tomorrow: allCompanyOrders.filter(o =>
        isSafeguard(o) && isHardActive(o) && inLatestSchedule(o)
        && isSgScheduleInternalWc(o) && isNotSgExcludedStatus(o)
        && o.appointment_set !== 'yes'
        && o.due_date === tomorrowStr
      ).length,
      safeguard_internal_2days: allCompanyOrders.filter(o =>
        isSafeguard(o) && isHardActive(o) && inLatestSchedule(o)
        && isSgScheduleInternalWc(o) && isNotSgExcludedStatus(o)
        && o.appointment_set !== 'yes'
        && o.due_date === day2Str
      ).length,
      // Legacy aliases (kept for any existing reference; not surfaced on UI).
      // Must stay in lockstep with the cards above so the list behind each
      // card matches its count.
      internal_today: allCompanyOrders.filter(o =>
        isSafeguard(o) && isHardActive(o) && inLatestSchedule(o)
        && isSgScheduleInternalWc(o) && isNotSgExcludedStatus(o)
        && o.appointment_set !== 'yes'
        && o.due_date === todayStr
      ).length,
      internal_tomorrow: allCompanyOrders.filter(o =>
        isActiveScheduling(o) && isWcInternal(o)
        && o.due_date === tomorrowStr && notHandledToday(o)
      ).length,
      internal_2days: allCompanyOrders.filter(o =>
        isActiveScheduling(o) && isWcInternal(o)
        && o.due_date === day2Str && notHandledToday(o)
      ).length,
      // ── External date buckets REMOVED (Angela 2026-04-27) ──
      // Business rule: External with zero/no attempts is released to the
      // External Calls queue IMMEDIATELY, regardless of due_date.  No
      // Overdue / Today / Tomorrow / Upcoming breakdown.
      // ── PHOT — own queue ──
      phot_queue: allCompanyOrders.filter(o => isHardActive(o) && o.appointment_set !== 'yes' && inLatestSchedule(o) && isWcPhot(o)).length,
      // ── Appointment / Dispatch flow — appointment_set=yes (active, in latest import) ──
      appointment_total: allCompanyOrders.filter(o => isHardActive(o) && inLatestSchedule(o) && o.appointment_set === 'yes').length,
      // ── Unmapped workcode (e.g., SIFIH6) — needs manual review.
      //    Always surface regardless of appointment status so the operator
      //    can classify it explicitly.  Excludes hard-terminal statuses.
      unmapped_workcode: allCompanyOrders.filter(o => isSafeguard(o) && isHardActive(o) && inLatestSchedule(o) && isWcUnmapped(o)).length,
      // Urgent: due today or overdue
      urgent_calls: orders.filter(o => (isDueTodayOrOverdue(o) || o.urgent_call === 'yes') && (o.scheduling_queue === 'Call Queue' || !o.scheduling_queue)).length,
      // Action queues
      call_queue: orders.filter(o => o.scheduling_queue === 'Call Queue' || (!o.scheduling_queue && ['Internal', 'External'].includes(o.flow_type))).length,
      email_queue: orders.filter(o => o.scheduling_queue === 'Email Queue').length,
      // Phase C pills (gated by phase_c_routed=true so legacy "Manual Review" data, if any, is ignored)
      manual_review: orders.filter(o => (o.status === 'Manual Review' || o.manual_review_required === true) && o.phase_c_routed === true).length,
      // P1-1 (Angela 2026-05-17) — Manual Review pending bucket. Catches
      // BOTH Phase C reappearance routing AND import-rejected ghost-login
      // parks (from P0-3). Drives the top banner with red pulse.
      manual_review_pending: orders.filter(o => o.manual_review_required === true || o.scheduling_queue === 'Manual Review' || o.status === 'Manual Review').length,
      admin_review: orders.filter(o => o.admin_review === 'yes').length,
      // Special queues
      priority_queue: orders.filter(o => o.special_queue === 'Priority Queue').length,
      admin_review_queue: orders.filter(o => o.scheduling_queue === 'Admin Review Queue').length,
      sg_email_queue: orders.filter(o => o.scheduling_queue === 'SG Email Queue').length,
    };
  }, [orders, allCompanyOrders, todayStr, tomorrowStr, day2Str, todayStartIso, handledTodayIds]);

  const filteredOrders = useMemo(() => {
    // Full Company Orders uses allCompanyOrders
    if (activeQueue === 'full_company') {
      let result = [...allCompanyOrders];
      if (filters.flow_type) result = result.filter(o => o.flow_type === filters.flow_type);
      if (filters.status) result = result.filter(o => o.status === filters.status);
      if (filters.assigned_inspector) result = result.filter(o => o.assigned_inspector === filters.assigned_inspector);
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        result = result.filter(o =>
          (o.cliente || '').toLowerCase().includes(term) ||
          (o.order_number || '').toLowerCase().includes(term) ||
          (o.address || '').toLowerCase().includes(term) ||
          (o.city || '').toLowerCase().includes(term)
        );
      }
      result.sort((a, b) => {
        let aVal = a[sortBy] || ''; let bVal = b[sortBy] || '';
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      return result;
    }

    // Queue source: use the master set (allCompanyOrders) for workcode-based
    // queues so the list matches the count cards exactly.  Other legacy
    // queues (call_queue, sms/email queues, status pills) still pull from
    // the call-scoped `orders` payload.
    const WORKCODE_QUEUES = new Set([
      'internal_calls','all_internal_calls','external_calls','national_calls',
      'internal_overdue','internal_today','internal_tomorrow','internal_upcoming','internal_2days',
      'phot_queue','appointment_total','unmapped_workcode',
    ]);
    let result = WORKCODE_QUEUES.has(activeQueue) ? [...allCompanyOrders] : [...orders];
    
    // Queue filters — share logic with queueCounts (workcode + due_date rules).
    const isDueTodayOrOverdue = (o) => o.due_date && o.due_date <= todayStr;
    const isNational = (o) => (o.company || '').toLowerCase() === 'national';
    const isSafeguard = (o) => (o.company || '').toLowerCase() === 'safeguard';
    const HARD_TERMINAL_LIST = new Set(['Archived','Completed','Paid','QA','QA Approved','QA Returned']);
    const isHardActiveQ = (o) =>
      o.active_order !== 'no' && o.removed_from_source !== 'yes'
      && o.is_duplicate !== 'yes' && !HARD_TERMINAL_LIST.has(o.status || '');
    const inLatestScheduleQ = (o) => o.active_in_latest_import === true;
    const isActiveSchedQ = (o) =>
      isSafeguard(o) && isHardActiveQ(o) && o.appointment_set !== 'yes' && inLatestScheduleQ(o);
    const wcClassQ = (o) => {
      const ot = String(o.order_type || '').trim().toUpperCase();
      const wc = String(o.workcode || '').trim().toUpperCase();
      if (ot.includes('PHOT') || wc.includes('PHOT')) return 'PHOT';
      const v = ot || wc;
      if (!v) return 'Unknown';
      if (v === 'SIFINT') return 'Internal';
      if (v === 'SIFI' || v === 'SIFIEX') return 'External';  // Angela 2026-08-09: SIFIEX added
      if (v.startsWith('SIFI')) return 'Unmapped';
      if (v.startsWith('I')) return 'Internal';
      if (v.startsWith('E')) return 'External';
      return 'OutOfSchedule';
    };
    const isZero = (a) => a === null || a === undefined || a === 0
      || (typeof a === 'string' && ['', '0', 'no attempts', 'attempts: 0'].includes(a.trim().toLowerCase()));
    // Angela 2026-08-09 — STRICT drawer helper — MUST mirror the dashboard
    // predicate `isSgScheduleInternalWc` exactly so each drawer's list
    // matches its card count. Order of checks matters (SIFINT before SIFI).
    const isSgScheduleInternalWcQ = (o) => {
      const v = String(o.order_type || '').trim().toUpperCase();
      if (!v) return false;
      if (v === 'SIFINT') return true;
      if (v.startsWith('SIFI')) return false;
      return v.startsWith('I');
    };
    const _SG_EXCLUDED_STATUSES_Q = new Set(['scheduled', 'rescheduled']);
    const isNotSgExcludedStatusQ = (o) => {
      const s = String(o.status || '').trim().toLowerCase();
      return !_SG_EXCLUDED_STATUSES_Q.has(s);
    };
    const isExtCall = (o) => {
      // Angela Feb 2026 STRICT LOCK — mirror the `external_calls` count above:
      // classify by flow_type === 'External', NOT by workcode/order_type.
      const isExt = isSafeguard(o) && isHardActiveQ(o) && o.appointment_set !== 'yes' && inLatestScheduleQ(o) && (o.flow_type || '') === 'External';
      const isAarifiExterior = (o.inspector_login || '').trim().toUpperCase() === 'AARIFI-EXTERIOR'
        && isHardActiveQ(o) && o.appointment_set !== 'yes' && inLatestScheduleQ(o) && (o.flow_type || '') === 'External';
      const isNatExt = isNational(o) && isHardActiveQ(o) && o.appointment_set !== 'yes'
        && (o.flow_type || '') === 'External'
        && o.national_call_queue_hidden !== 'yes';
      // Angela 2026-05-02 H-4 fix: any attempts value qualifies (rule:
      // "any text/value containing Attempts = no appointment = Call").
      // Scheduled/Rescheduled are filtered out by appointment_set !== 'yes'.
      // Angela 2026-05-03: National External also enters this bucket.
      return (isExt || isAarifiExterior || isNatExt);
    };

    // Live-queue filter (drill-down) — must match the count formula above.
    const notHandledTodayQ = (o) => !o.last_call_logged_at || o.last_call_logged_at < todayStartIso;

    if (activeQueue === 'internal_calls') {
      result = result.filter(o => isActiveSchedQ(o) && wcClassQ(o) === 'Internal' && notHandledTodayQ(o));
    } else if (activeQueue === 'all_internal_calls') {
      // Combined queue: Safeguard Internal + National Internal (no appt, active).
      result = result.filter(o => {
        const sg = isSafeguard(o) && isHardActiveQ(o) && o.appointment_set !== 'yes' && inLatestScheduleQ(o) && wcClassQ(o) === 'Internal';
        const nat = isNational(o) && isHardActiveQ(o) && o.appointment_set !== 'yes' && (o.flow_type || '') === 'Internal' && o.national_call_queue_hidden !== 'yes';
        return (sg || nat) && notHandledTodayQ(o);
      });
    } else if (activeQueue === 'external_calls') {
      // Angela Feb 2026 (External Calls URGENT fix): drill-down list MUST
      // match the card count exactly — including the `scheduling_log`
      // handled-today safety-net exclusion AND the server-computed
      // real_attempt_count (any prior call → leave Scheduling).
      result = result.filter(o =>
        isExtCall(o)
        && notHandledTodayQ(o)
        && !(handledTodayIds && handledTodayIds.has(String(o.id || '')))
        && Number(o.real_attempt_count || 0) === 0
      );
    } else if (activeQueue === 'national_calls') {
      if (showHiddenNationalCalls) {
        result = result.filter(o => isNational(o) && o.national_call_queue_hidden === 'yes');
      } else {
        result = result.filter(o =>
          isNational(o)
          && isHardActiveQ(o)
          && (o.flow_type || '') === 'Internal'
          && o.appointment_set !== 'yes'
          && o.national_call_queue_hidden !== 'yes'
          && notHandledTodayQ(o)
        );
      }
    } else if (activeQueue === 'internal_today') {
      // Angela 2026-07-30 — filter must exactly match card predicate:
      // SG row, workcode I/S, status NOT in (Scheduled, Rescheduled),
      // due today, AND `appointment_set !== 'yes'` (appt_set rows never
      // receive calls, reverts the 2026-05-15 exception).
      result = result.filter(o =>
        isSafeguard(o) && isHardActiveQ(o) && inLatestScheduleQ(o)
        && isSgScheduleInternalWcQ(o) && isNotSgExcludedStatusQ(o)
        && o.appointment_set !== 'yes'
        && o.due_date === todayStr
      );
    } else if (activeQueue === 'internal_overdue') {
      result = result.filter(o => isActiveSchedQ(o) && wcClassQ(o) === 'Internal' && o.due_date && o.due_date < todayStr);
    } else if (activeQueue === 'internal_tomorrow') {
      result = result.filter(o =>
        isSafeguard(o) && isHardActiveQ(o) && inLatestScheduleQ(o)
        && isSgScheduleInternalWcQ(o) && isNotSgExcludedStatusQ(o)
        && o.appointment_set !== 'yes'
        && o.due_date === tomorrowStr
      );
    } else if (activeQueue === 'internal_upcoming') {
      result = result.filter(o => isActiveSchedQ(o) && wcClassQ(o) === 'Internal' && o.due_date && o.due_date >= day2Str);
    } else if (activeQueue === 'internal_2days') {
      result = result.filter(o =>
        isSafeguard(o) && isHardActiveQ(o) && inLatestScheduleQ(o)
        && isSgScheduleInternalWcQ(o) && isNotSgExcludedStatusQ(o)
        && o.appointment_set !== 'yes'
        && o.due_date === day2Str
      );
    } else if (activeQueue === 'phot_queue') {
      result = result.filter(o => isHardActiveQ(o) && o.appointment_set !== 'yes' && inLatestScheduleQ(o) && wcClassQ(o) === 'PHOT');
    } else if (activeQueue === 'appointment_total') {
      result = result.filter(o => isHardActiveQ(o) && inLatestScheduleQ(o) && o.appointment_set === 'yes');
    } else if (activeQueue === 'unmapped_workcode') {
      result = result.filter(o => isSafeguard(o) && isHardActiveQ(o) && inLatestScheduleQ(o) && wcClassQ(o) === 'Unmapped');
    } else if (activeQueue === 'external_morning_calls') {
      // HOTFIX (Angela 2026-05-18 PROD) — accept BOTH due_date and route_date.
      result = result.filter(o =>
        (o.flow_type || '').toLowerCase() === 'external'
        && (o.due_date === todayStr || o.route_date === todayStr)
        && (o.external_morning_call_required || '').toLowerCase() === 'yes'
        && (o.active_order || 'yes') !== 'no'
        && !o.external_morning_call_completed_at
        && (!o.last_call_logged_at || o.last_call_logged_at < todayStartIso)
      );
    } else if (activeQueue === 'urgent_calls') {
      result = result.filter(o => isDueTodayOrOverdue(o) || o.urgent_call === 'yes');
    } else if (activeQueue === 'call_queue') {
      result = result.filter(o => o.scheduling_queue === 'Call Queue' || (!o.scheduling_queue && ['Internal', 'External'].includes(o.flow_type)));
    } else if (activeQueue === 'email_queue') {
      result = result.filter(o => o.scheduling_queue === 'Email Queue');
    } else if (activeQueue === 'manual_review') {
      result = result.filter(o => (o.status === 'Manual Review' || o.manual_review_required === true) && o.phase_c_routed === true);
    } else if (activeQueue === 'manual_review_pending') {
      result = result.filter(o => o.manual_review_required === true || o.scheduling_queue === 'Manual Review' || o.status === 'Manual Review');
    } else if (activeQueue === 'admin_review') {
      result = result.filter(o => o.admin_review === 'yes');
    } else if (activeQueue === 'priority_queue') {
      result = result.filter(o => o.special_queue === 'Priority Queue');
    } else if (activeQueue === 'admin_review_queue') {
      result = result.filter(o => o.scheduling_queue === 'Admin Review Queue');
    } else if (activeQueue === 'sg_email_queue') {
      result = result.filter(o => o.scheduling_queue === 'SG Email Queue');
    }

    // Apply filters
    if (filters.flow_type) result = result.filter(o => o.flow_type === filters.flow_type);
    if (filters.status) result = result.filter(o => o.status === filters.status);
    if (filters.assigned_inspector) result = result.filter(o => o.assigned_inspector === filters.assigned_inspector);
    if (filters.assigned_operator) result = result.filter(o => o.assigned_operator === filters.assigned_operator);

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(o =>
        (o.cliente || '').toLowerCase().includes(term) ||
        (o.order_number || '').toLowerCase().includes(term) ||
        (o.address || '').toLowerCase().includes(term) ||
        (o.city || '').toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';
      if (sortBy === 'attempts') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [orders, allCompanyOrders, filters, sortBy, sortDir, activeQueue, todayStr, tomorrowStr, day2Str, searchTerm, showHiddenNationalCalls, todayStartIso, handledTodayIds]);

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API}/orders`, data);
      fetchOrders();
    } catch (error) {
      console.error('Failed to create order:', error);
    }
  };

  const handleCall = async (orderId) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/attempt`);
      fetchOrders();
    } catch (error) {
      console.error('Failed to increment attempt:', error);
    }
  };

  const handleSetAppointment = async (orderId, date, selectedWindow, windowConfig, contactName, contactPhone) => {
    try {
      const payload = {
        appointment_sg_date: date,
        appointment_real_date: date,
        appointment_real_time: selectedWindow,
        appointment_contact_name: contactName || '',
        appointment_contact_phone: contactPhone || '',
      };
      if (windowConfig) {
        // Internal (AARIFI real) — sent to backend, validated against whitelist.
        payload.internal_start_time = windowConfig.start;
        payload.internal_end_time = windowConfig.end;
        // Safeguard (reported) — backend re-derives from the mapping table
        // and overwrites this value.  We send it for display logs only.
        if (INTERNAL_TO_SG_LABEL[selectedWindow]) {
          payload.appointment_sg_time = INTERNAL_TO_SG_LABEL[selectedWindow];
        }
      }
      await axios.patch(`${API}/orders/${orderId}/appointment`, payload);
      fetchOrders();
    } catch (error) {
      console.error('Failed to set appointment:', error);
    }
  };

  const handleAssign = async (orderId, inspectorName) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, {
        assigned_inspector: inspectorName,
        status: 'Assigned'
      });
      fetchOrders();
    } catch (error) {
      console.error('Failed to assign:', error);
    }
  };

  const handleComplete = async (orderId) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status`, { status: 'Completed' });
      fetchOrders();
    } catch (error) {
      console.error('Failed to complete:', error);
    }
  };

  const loadBulkPreview = async () => {
    setBulkSmsLoading(true);
    setBulkSmsResult(null);
    try {
      const r = await axios.get(`${API}/ringcentral/bulk-appointment-sms/preview`);
      setBulkSmsPreview(r.data);
    } catch (e) { console.error(e); }
    setBulkSmsLoading(false);
    setBulkSmsOpen(true);
  };

  // ── Internal Appointment / Baseline / Mirror features REMOVED
  //    (Angela 2026-04-29 final cleanup): handlers, state and modals deleted
  //    completely. Underlying backend endpoints are still reachable via direct
  //    API call but no UI surface remains in this app.

  // Angela 2026-05-19 — Auto-default-select all READY rows when previews load.
  // INVALID / NO PHONE / NO TIME / ALREADY SENT rows start unchecked so
  // Angela never accidentally sends to a bad number.
  useEffect(() => {
    if (!bulkSmsPreview?.preview) return;
    const ready = new Set();
    for (const p of (bulkSmsPreview.preview || [])) {
      const st = (p.status || '').toLowerCase();
      // Today modal often has no status on each row — treat the preview list
      // as "ready" by default unless explicit blocked status present.
      const blocked = ['no_phone', 'invalid_phone', 'no_time', 'already_sent'].includes(st);
      if (!blocked && p.order_number) ready.add(String(p.order_number));
    }
    setBulkSmsSelected(ready);
  }, [bulkSmsPreview]);

  useEffect(() => {
    if (!dayBeforeSmsPreview?.preview) return;
    const ready = new Set();
    for (const p of (dayBeforeSmsPreview.preview || [])) {
      if ((p.status || '').toLowerCase() === 'ready' && p.order_number) {
        ready.add(String(p.order_number));
      }
    }
    setDayBeforeSmsSelected(ready);
  }, [dayBeforeSmsPreview]);

  // Angela Feb 2026 — inline phone edit inside the SMS modals (Today +
  // Day-Before). Opens a controlled input on the same row; on save,
  // PATCHes /api/orders/{id}/ph-phone which writes `ph_phone_manual`
  // (used by resolve_ph_phone across the app). Then reloads the preview
  // so the row's `status` flips from `invalid_phone` / `no_phone` to
  // `ready`, and auto-checks that order in the send whitelist.
  const openPhoneEdit = (modalKey, orderNumber, currentPhone) => {
    setEditingPhone(`${modalKey}:${orderNumber}`);
    setEditingPhoneVal(currentPhone || "");
    setEditingPhoneErr("");
    setEditingPhoneBusy(false);
  };
  const cancelPhoneEdit = () => {
    setEditingPhone("");
    setEditingPhoneVal("");
    setEditingPhoneErr("");
    setEditingPhoneBusy(false);
  };
  const saveInlinePhone = async (modalKey, orderId, orderNumber) => {
    if (!orderId) {
      setEditingPhoneErr("Missing order id — refresh the modal and retry.");
      return;
    }
    setEditingPhoneBusy(true);
    setEditingPhoneErr("");
    try {
      await axios.patch(`${API}/orders/${orderId}/ph-phone`, {
        ph_phone: (editingPhoneVal || "").trim(),
      });
      // Reload the corresponding preview to reflect the new status.
      if (modalKey === "today") {
        const r = await axios.get(`${API}/ringcentral/bulk-appointment-sms/preview`);
        setBulkSmsPreview(r.data);
        setBulkSmsSelected(prev => {
          const next = new Set(prev);
          next.add(String(orderNumber));
          return next;
        });
      } else if (modalKey === "day-before") {
        const r = await axios.get(`${API}/ringcentral/bulk-day-before-sms/preview`);
        setDayBeforeSmsPreview(r.data);
        setDayBeforeSmsSelected(prev => {
          const next = new Set(prev);
          next.add(String(orderNumber));
          return next;
        });
      }
      cancelPhoneEdit();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to save phone.";
      setEditingPhoneErr(String(detail));
      setEditingPhoneBusy(false);
    }
  };

  const handleBulkSmsSend = async () => {
    const count = bulkSmsSelected.size;
    if (!count) return;
    if (!window.confirm(`Send appointment SMS to ${count} clients NOW?\n\nThis cannot be undone.`)) return;
    setBulkSmsLoading(true);
    try {
      const res = await axios.post(`${API}/ringcentral/bulk-appointment-sms`, {
        order_numbers: Array.from(bulkSmsSelected),
      });
      setBulkSmsResult(res.data);
      setBulkSmsPreview(null);
      setBulkSmsSelected(new Set());
    } catch (e) {
      setBulkSmsResult({ success: false, error: e.response?.data?.detail || 'Failed to send' });
    }
    setBulkSmsLoading(false);
  };

  // ── Today SMS modal — inline fix for "Missing Internal Appointment" rows
  // Local edit state keyed by order_number. Does NOT persist to global state;
  // lives only while the modal is open.
  const [missingFixState, setMissingFixState] = useState({});
  const [missingFixBusy, setMissingFixBusy] = useState({});

  const setMissingFixField = (orderNum, field, value) => {
    setMissingFixState(prev => ({
      ...prev,
      [orderNum]: { ...(prev[orderNum] || {}), [field]: value },
    }));
  };

  const handleSaveInternalAppointment = async (item) => {
    const edit = missingFixState[item.order_number] || {};
    const payload = {
      appointment_real_date: edit.appointment_real_date || '',
      internal_start_time: edit.internal_start_time || '',
      internal_end_time: edit.internal_end_time || '',
      ph_phone: (edit.ph_phone || '').trim() || null,
      note: (edit.note || '').trim() || null,
    };
    if (!payload.appointment_real_date || !payload.internal_start_time || !payload.internal_end_time) {
      window.alert('Internal date, start time and end time are all required.');
      return;
    }
    setMissingFixBusy(prev => ({ ...prev, [item.order_number]: 'save' }));
    try {
      // Resolve via existing order lookup endpoint (returns order_id).
      const lookup = await axios.get(`${API}/orders/lookup-by-number/${encodeURIComponent(item.order_number)}`);
      const orderId = lookup.data?.order_id;
      if (!orderId) throw new Error('Order id lookup failed');
      await axios.patch(`${API}/ringcentral/bulk-appointment-sms/inline-fix/${orderId}`, payload);
      // Refresh preview so it moves from missing → ready
      const r = await axios.get(`${API}/ringcentral/bulk-appointment-sms/preview`);
      setBulkSmsPreview(r.data);
      // Clear local edit state for this order
      setMissingFixState(prev => { const n = { ...prev }; delete n[item.order_number]; return n; });
    } catch (e) {
      window.alert('Save failed: ' + (e.response?.data?.detail || e.message));
    }
    setMissingFixBusy(prev => { const n = { ...prev }; delete n[item.order_number]; return n; });
  };

  const handleSendAnyway = async (item) => {
    const ok = window.confirm(
      `This order does not have an AARIFI internal appointment. Are you sure you want to send SMS using the displayed time?\n\nOrder: ${item.order_number}\nTime (Safeguard, displayed): ${item.appointment_sg_time || '(unknown)'}`
    );
    if (!ok) return;
    setMissingFixBusy(prev => ({ ...prev, [item.order_number]: 'send' }));
    try {
      const lookup = await axios.get(`${API}/orders/lookup-by-number/${encodeURIComponent(item.order_number)}`);
      const orderId = lookup.data?.order_id;
      if (!orderId) throw new Error('Order id lookup failed');
      await axios.post(`${API}/ringcentral/bulk-appointment-sms/send-one/${orderId}`, {
        override: true,
        time_override: item.appointment_sg_time || null,
      });
      const r = await axios.get(`${API}/ringcentral/bulk-appointment-sms/preview`);
      setBulkSmsPreview(r.data);
    } catch (e) {
      window.alert('Send failed: ' + (e.response?.data?.detail || e.message));
    }
    setMissingFixBusy(prev => { const n = { ...prev }; delete n[item.order_number]; return n; });
  };

  const loadDayBeforePreview = async () => {
    setDayBeforeSmsLoading(true);
    setDayBeforeSmsResult(null);
    try {
      const r = await axios.get(`${API}/ringcentral/bulk-day-before-sms/preview`);
      setDayBeforeSmsPreview(r.data);
    } catch (e) { console.error(e); }
    setDayBeforeSmsLoading(false);
    setDayBeforeSmsOpen(true);
  };

  const handleDayBeforeSmsSend = async () => {
    const count = dayBeforeSmsSelected.size;
    if (!count) {
      setDayBeforeSmsResult({ success: false, error: "No rows selected." });
      return;
    }
    if (!window.confirm(`Send reminder SMS to ${count} internal clients scheduled for TOMORROW?\n\nThis cannot be undone.`)) return;
    setDayBeforeSmsLoading(true);
    try {
      const res = await axios.post(`${API}/ringcentral/bulk-day-before-sms`, {
        order_numbers: Array.from(dayBeforeSmsSelected),
      });
      setDayBeforeSmsResult(res.data);
      setDayBeforeSmsPreview(null);
      setDayBeforeSmsSelected(new Set());
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.error || e.message || "Failed to send";
      setDayBeforeSmsResult({ success: false, error: `${detail} (HTTP ${e.response?.status || '?'})` });
    }
    setDayBeforeSmsLoading(false);
  };

  // One-time TIME CORRECTION SMS batch (fix customers who got Safeguard-shifted time)
  const loadCorrectionPreview = async () => {
    setCorrectionSmsLoading(true);
    setCorrectionSmsResult(null);
    try {
      const r = await axios.get(`${API}/ringcentral/bulk-time-correction-sms/preview`);
      setCorrectionSmsPreview(r.data);
    } catch (e) { console.error(e); }
    setCorrectionSmsLoading(false);
    setCorrectionSmsOpen(true);
  };

  const handleCorrectionSmsSend = async () => {
    if (!correctionSmsPreview || correctionSmsPreview.eligible === 0) {
      setCorrectionSmsResult({ success: false, error: "No eligible orders need a time correction." });
      return;
    }
    if (!window.confirm(`Send UPDATE OF TIME correction SMS to ${correctionSmsPreview.eligible} clients?\n\nThis is a ONE-TIME correction batch and cannot be undone.`)) return;
    setCorrectionSmsLoading(true);
    try {
      const res = await axios.post(`${API}/ringcentral/bulk-time-correction-sms`);
      setCorrectionSmsResult(res.data);
      setCorrectionSmsPreview(null);
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.error || e.message || "Failed to send";
      setCorrectionSmsResult({ success: false, error: `${detail} (HTTP ${e.response?.status || '?'})` });
    }
    setCorrectionSmsLoading(false);
  };


  return (
    <Layout>
      <div className="space-y-6 -m-4 p-4 bg-[#EEF1F6] min-h-[calc(100vh-1rem)]" data-testid="scheduling-page">
        {/* Angela 2026-05-16 — Header reorganizado em 2 rows:
            Row 1: título + chip user (esquerda)
            Row 2: Quick Action buttons (linha própria, full-width, wrap em mobile)
            Antes: tudo numa única linha com justify-between → causava overflow
            em viewports < 1400px (botões pareciam "flutuantes" no canto direito). */}
        {/* Header bar: navy nav strip with logo, title, user chip. Per design spec — bg #0F1B2D, h-14. */}
        <div
          className="flex items-center gap-3 flex-wrap rounded-[14px] px-4 py-3"
          style={{
            background: '#0F1B2D',
            boxShadow: '0 4px 12px rgba(15,27,45,0.3)',
            border: '1px solid #ffffff10',
          }}
        >
          <div className="flex items-center gap-3">
            {/* Logo orb — radial gold gradient per spec */}
            <div
              className="h-9 w-9 rounded-full shrink-0"
              style={{
                background: 'radial-gradient(circle at 35% 35%, #D4A843, #8B6318)',
                border: '1px solid #B8972E44',
                boxShadow: '0 2px 6px rgba(15,27,45,0.4)',
              }}
              aria-hidden
            />
            <div>
              <h1 className="text-base sm:text-lg font-bold text-white leading-tight">Scheduling</h1>
              <p className="text-[10px] sm:text-xs text-[#94A3B8] font-mono-dm">{filteredOrders.length} orders</p>
            </div>
          </div>
          {/* User status chip — navy-on-navy with green ping. */}
          {user && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full ml-auto"
              style={{ background: '#ffffff12', border: '1px solid #ffffff20' }}
              data-testid="user-status-chip"
            >
              <span className="relative flex h-2 w-2" aria-label="Online">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#4ADE80' }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: '#4ADE80', boxShadow: '0 0 0 2px #4ADE8044' }} />
              </span>
              <span className="text-xs font-semibold text-white truncate max-w-[140px]" data-testid="user-status-name">
                {user.name || user.email}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">
                {user.role}
              </span>
              <button
                onClick={() => { try { localStorage.clear(); } catch (e) {} window.location.href = "/login"; }}
                className="ml-1 text-[10px] font-bold uppercase tracking-wider border-l border-[#ffffff20] pl-2 hover:opacity-80"
                style={{ color: '#B5302A' }}
                data-testid="user-status-logout"
              >
                Logout
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="scheduling-quick-actions">
            {(user?.role === 'Admin' || user?.role === 'Scheduling') && (
              <OperatorActivityButtons
                onVoicemail={() => setShowVoicemailModal(true)}
                onActivity={() => setShowCallWithoutModal(true)}
              />
            )}
            <button
              onClick={loadBulkPreview}
              className="px-4 py-3 bg-[#1E6B45] hover:bg-[#19593A] text-white text-sm font-bold rounded-[13px] flex items-center gap-2 shadow-[0_3px_8px_#1E6B4555] transition-all hover:shadow-[0_4px_12px_#1E6B4577]"
              data-testid="bulk-sms-btn"
            >
              <MessageSquare className="h-4 w-4" />
              Send Today SMS
            </button>
            <button
              onClick={loadDayBeforePreview}
              className="px-4 py-3 bg-[#2D5A8A] hover:bg-[#234A75] text-white text-sm font-bold rounded-[13px] flex items-center gap-2 shadow-[0_3px_8px_#2D5A8A55] transition-all hover:shadow-[0_4px_12px_#2D5A8A77]"
              data-testid="bulk-day-before-sms-btn"
            >
              <Calendar className="h-4 w-4" />
              Send Day-Before SMS
            </button>
            {/* AUDIT BUTTONS REMOVED (Angela 2026-04-29):
                Internal Appt Review · Appt Baseline Audit · SG Mirror Audit
                were temporary stabilization tools — fully deleted (handlers,
                state, and modals removed). Operation pages no longer expose
                these features. */}
            {/* Time Correction batch — DISABLED. One-time fix already done; not used going forward.
                Normal Day-Before / Today SMS flows already send the correct internal AM/PM time. */}
            {false && (
            <button
              onClick={loadCorrectionPreview}
              className="px-3 py-1.5 bg-[#C07070] hover:bg-[#A55A5A] text-white text-sm font-medium rounded-lg flex items-center gap-1.5"
              data-testid="bulk-time-correction-sms-btn"
              title="One-time correction batch for customers who got the wrong time"
            >
              <AlertTriangle className="h-4 w-4" />
              Send Time Correction
            </button>
            )}
            <button
              onClick={() => navigate('/scheduling/all-orders')}
              className="px-4 py-3 text-white text-sm font-bold rounded-[14px] flex items-center gap-2 transition-all"
              style={{
                background: 'linear-gradient(135deg, #1A2E45 0%, #0F1B2D 100%)',
                border: '1px solid #ffffff15',
                boxShadow: '0 4px 12px rgba(15,27,45,0.3)',
              }}
              data-testid="scheduling-all-company-orders-btn"
            >
              <Database className="h-4 w-4" style={{ filter: 'drop-shadow(0 0 6px #B8972E66)' }} />
              All Company Orders
            </button>
          </div>

        {/* Reference Tools — Quick Access */}
        <div className="flex flex-wrap gap-2" data-testid="kb-quick-access">
          {[
            { id: "insurance", label: "Insurance Rules", icon: "Building2", color: "amber" },
            { id: "safeguard", label: "Safeguard Rules", icon: "Shield", color: "cyan" },
            { id: "scripts", label: "Scripts", icon: "FileText", color: "blue" },
            { id: "logins", label: "Logins & Passwords", icon: "Key", color: "emerald" },
          ].map(btn => (
            <button
              key={btn.id}
              onClick={() => openKB(btn.id)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5 hover:scale-[1.02] active:scale-[0.98] ${
                btn.color === "amber" ? "border-[#D1A054]/30 bg-[#D1A054]/10 text-[#9A7A3E] hover:bg-[#D1A054]/15" :
                btn.color === "cyan" ? "border-[#2F5D8A]/20 bg-[#2F5D8A]/8 text-[#2F5D8A] hover:bg-[#2F5D8A]/12" :
                btn.color === "blue" ? "border-[#2F5D8A]/20 bg-[#2F5D8A]/8 text-[#2F5D8A] hover:bg-[#2F5D8A]/12" :
                "border-[#A7C4A0]/30 bg-[#A7C4A0]/10 text-[#4A7A56] hover:bg-[#A7C4A0]/15"
              }`}
              data-testid={`kb-btn-${btn.id}`}
            >
              {btn.id === "insurance" && <Building2 className="h-3.5 w-3.5" />}
              {btn.id === "safeguard" && <ClipboardCheck className="h-3.5 w-3.5" />}
              {btn.id === "scripts" && <Mail className="h-3.5 w-3.5" />}
              {btn.id === "logins" && <Eye className="h-3.5 w-3.5" />}
              {btn.label}
            </button>
          ))}
        </div>

        {/* EOD Update Alert - hidden from scheduling, kept in logic */}
        {/* eodStats alert hidden per user request */}

        {batchMessage && (
          <div className={`px-4 py-3 rounded-lg text-sm font-medium ${batchMessage.type === 'success' ? 'bg-[#A7C4A0]/25 text-[#6B8F62] border border-[#A7C4A0]/40' : 'bg-[#F5EDED]0/20 text-[#8B4545] border border-[#C07070]/30'}`} data-testid="batch-message">
            {batchMessage.text}
          </div>
        )}

        {/* Full Company / All Scheduling cards hidden per user request */}

        {/* Row 2: Urgent Calls - hidden as standalone card, kept as queue filter below */}
        {/* Urgent Calls standalone card hidden per user request */}

        {/* Angela 2026-07-30 — Manual Review banner removed permanently.
            The Phase C manual_review queue still exists in the drawer
            filter list (activeQueue === 'manual_review'), but no top
            banner will alert about it anymore. */}

        {/* Row 3: Internal Calls — National + Safeguard Internal, always 2 cols. */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Internal Calls</p>
          <div className="grid grid-cols-2 gap-3">
            <StatCard title="National Calls" titleShort="NAT CALLS" subtitle="Internal — no appointment" icon={Phone} color="emerald" value={queueCounts.national_internal_calls} active={activeQueue === 'national_calls'} onClick={() => openQueueAsDrawer('national_calls', 'National Calls')} testId="sched-card-national-internal" />
            <StatCard title="Safeguard Internal" titleShort="SG INT" subtitle="Safeguard internal calls" icon={Phone} color="amber" value={queueCounts.safeguard_internal_calls} active={activeQueue === 'internal_calls'} onClick={() => openQueueAsDrawer('internal_calls', 'Safeguard Internal')} testId="sched-card-safeguard-internal" />
            {/* Angela 2026-05-16: "Total Internal Calls" card removed visually (state preserved). */}
          </div>
        </div>

        {/* Row 3.5 (Angela 2026-05-16): Safeguard Schedule moved up to sit
            directly under Internal Calls — always 3 cols horizontal. */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Safeguard Schedule — by Due Date</p>
          <div className="grid grid-cols-3 gap-3">
            <StatCard title="Safeguard Today" titleShort="SG TODAY" subtitle="due_date = today" icon={Clock} color="rose" value={queueCounts.safeguard_internal_today} active={activeQueue === 'internal_today'} onClick={() => openQueueAsDrawer('internal_today', 'Safeguard Today')} testId="sched-card-sg-today" />
            <StatCard title="Safeguard Tomorrow" titleShort="SG TOM" subtitle="due_date = tomorrow" icon={Clock} color="amber" value={queueCounts.safeguard_internal_tomorrow} active={activeQueue === 'internal_tomorrow'} onClick={() => openQueueAsDrawer('internal_tomorrow', 'Safeguard Tomorrow')} testId="sched-card-sg-tomorrow" />
            <StatCard title="Safeguard +2 Days" titleShort="SG +2D" subtitle="due_date = day+2" icon={Clock} color="navy" value={queueCounts.safeguard_internal_2days} active={activeQueue === 'internal_2days'} onClick={() => openQueueAsDrawer('internal_2days', 'Safeguard +2 Days')} testId="sched-card-sg-2days" />
          </div>
        </div>

        {/* Row 3b (Angela 2026-05-17): External Calls + External Morning Calls
            + Inspector Field Updates — all 3 horizontal in a single row.
            External Morning Calls and Inspector Field Updates keep their
            red alert styling. */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard title="External Calls" titleShort="EXT CALLS" subtitle="Any attempts (no appointment)" icon={Phone} color="white" value={queueCounts.external_calls} active={activeQueue === 'external_calls'} onClick={() => openQueueAsDrawer('external_calls', 'External Calls')} testId="sched-card-external" />

          {/* External Morning Calls — mirror queue for João's next-morning
              compliance calls on External orders that arrived after 5 PM.
              Uses Priority-style red visual treatment — these are
              morning-critical tasks. */}
          <button
            onClick={() => openQueueAsDrawer('external_morning_calls', 'External Morning Calls')}
            className={`relative p-3 rounded-[14px] border-2 transition-all text-left ${
              activeQueue === 'external_morning_calls'
                ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                : queueCounts.external_morning_calls > 0
                ? 'border-red-400 bg-red-50/60 hover:bg-red-50'
                : 'border-gray-200 bg-white hover:border-red-300'
            }`}
            data-testid="sched-card-external-morning-calls"
          >
            {queueCounts.external_morning_calls > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 text-white text-[10px] font-bold items-center justify-center">{queueCounts.external_morning_calls}</span>
              </span>
            )}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 shrink-0" />
              <span className="sm:hidden text-[9px] font-extrabold text-red-700 uppercase tracking-[0.08em]">EXT MORNING</span>
              <span className="hidden sm:inline text-[10px] font-extrabold text-red-700 uppercase tracking-[0.12em]">External Morning Calls</span>
            </div>
            <p className="text-[28px] sm:text-[36px] font-black leading-none text-red-600 mt-1 font-mono-dm">{queueCounts.external_morning_calls}</p>
          </button>

          {/* Inspector Field Updates — Angela 2026-05-10. Red queue button.
              Flashes while there are unseen Inspector → Office field events
              (Arrived / Finished / Reschedule / Denied Inspection / Wrong
              Address). Excludes FINT. Click opens a modal listing pending
              events; clicking an item navigates to the related Scheduling
              order detail. Marking-as-seen happens on modal open. */}
          <button
            onClick={openFieldUpdatesModal}
            className={`relative p-3 rounded-[14px] border-2 transition-all text-left ${
              fieldUpdatesPendingCount > 0
                ? 'border-red-400 bg-red-50/60 hover:bg-red-50 animate-pulse'
                : 'border-gray-200 bg-white hover:border-red-300'
            }`}
            data-testid="sched-card-inspector-field-updates"
          >
            {fieldUpdatesPendingCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 text-white text-[10px] font-bold items-center justify-center">{fieldUpdatesPendingCount}</span>
              </span>
            )}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Radio className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 shrink-0" />
              <span className="sm:hidden text-[9px] font-extrabold text-red-700 uppercase tracking-[0.08em]">FIELD UPD</span>
              <span className="hidden sm:inline text-[10px] font-extrabold text-red-700 uppercase tracking-[0.12em]">Inspector Field Updates</span>
            </div>
            <p className="text-[28px] sm:text-[36px] font-black leading-none text-red-600 mt-1 font-mono-dm">{fieldUpdatesPendingCount}</p>
          </button>
        </div>

        {/* Row 4b removed (Angela 2026-04-27): External does not use date
            buckets. All zero/no-attempt External orders appear immediately
            in the single "External Calls" card above. */}

        {/* Row 4c removed (Angela 2026-04-27): PHOT / Appointment Dispatch /
            Unmapped Workcode are Dispatch/Operations items, NOT Scheduling.
            Moved to DispatchPage. Internal Overdue / Internal Upcoming are
            also removed — Scheduling shows only Today / Tomorrow / +2. */}

        {/* RingCentral + Office Email — compact communication cards.
            Read-only. Separate from operational SMS/Email queues. */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Communication</p>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => navigate('/scheduling/rc-sms-inbox')}
              className="p-3 rounded-[14px] border-2 border-[#6B8F62]/30 bg-white hover:border-[#6B8F62] hover:bg-[#6B8F62]/5 transition-all text-left"
              data-testid="rc-sms-inbox-card"
              title="RingCentral SMS"
            >
              <div className="flex items-center gap-1.5 sm:gap-2">
                <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#6B8F62] shrink-0" />
                <span className="sm:hidden text-[9px] font-extrabold text-[#3F5C3A] uppercase tracking-[0.08em]">SMS RC</span>
                <span className="hidden sm:inline text-[10px] font-extrabold text-[#3F5C3A] uppercase tracking-[0.12em]">SMS RingCentral</span>
              </div>
              <p className="text-[28px] sm:text-[36px] font-black leading-none text-[#6B8F62] mt-1 font-mono-dm" data-testid="rc-sms-inbox-count">{rcInboxCounts.sms}</p>
            </button>
            <button
              onClick={() => navigate('/scheduling/rc-voicemail-inbox')}
              className="p-3 rounded-[14px] border-2 border-[#9A7A3E]/30 bg-white hover:border-[#9A7A3E] hover:bg-[#9A7A3E]/5 transition-all text-left"
              data-testid="rc-voicemail-inbox-card"
              title="RingCentral Voicemail"
            >
              <div className="flex items-center gap-1.5 sm:gap-2">
                <VoicemailIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#9A7A3E] shrink-0" />
                <span className="sm:hidden text-[9px] font-extrabold text-[#7F6632] uppercase tracking-[0.08em]">VOICEMAIL</span>
                <span className="hidden sm:inline text-[10px] font-extrabold text-[#7F6632] uppercase tracking-[0.12em]">Voicemail RingCentral</span>
              </div>
              <p className="text-[28px] sm:text-[36px] font-black leading-none text-[#9A7A3E] mt-1 font-mono-dm" data-testid="rc-voicemail-inbox-count">{rcInboxCounts.voicemail}</p>
            </button>
            <button
              onClick={() => navigate('/scheduling/email-inbox')}
              className="p-3 rounded-[14px] border-2 border-blue-300 bg-white hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              data-testid="office-email-inbox-card"
              title="Office / Scheduling Email — coming soon (IMAP pending)"
            >
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 shrink-0" />
                <span className="sm:hidden text-[9px] font-extrabold text-blue-700 uppercase tracking-[0.08em]">OFFICE EMAIL</span>
                <span className="hidden sm:inline text-[10px] font-extrabold text-blue-700 uppercase tracking-[0.12em]">Office / Scheduling Email</span>
              </div>
              <p className="text-[28px] sm:text-[36px] font-black leading-none text-blue-600 mt-1 font-mono-dm" data-testid="office-email-inbox-count">—</p>
            </button>
          </div>
        </div>

        {/* Phase C — Reappearance routing pills (only show when count > 0).
            Read-only filter pills. Hidden when feature flag is OFF / no orders routed. */}
        {(queueCounts.manual_review > 0) && (
          <div data-testid="sched-phase-c-row">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Reappearance Routing (Phase C)</p>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => openQueueAsDrawer('manual_review', 'Manual Review (Phase C)')}
                className={`p-3 rounded-lg border-2 transition-all text-left ${
                  activeQueue === 'manual_review'
                    ? 'border-[#8B4545] bg-[#F5EDED] ring-2 ring-[#C07070]/30'
                    : queueCounts.manual_review > 0
                    ? 'border-[#C07070]/60 bg-[#F5EDED]/60 hover:bg-[#F5EDED]'
                    : 'border-gray-200 bg-white hover:border-[#C07070]/40'
                }`}
                data-testid="sched-card-manual-review"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[#8B4545]" />
                  <span className="text-xs font-semibold text-[#8B4545] uppercase">Manual Review</span>
                </div>
                <p className="text-2xl font-bold text-[#8B4545] mt-1">{queueCounts.manual_review}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Phase C · Reappeared, manual review</p>
              </button>
            </div>
          </div>
        )}

        {/* Operational Queues — Priority + Admin Review (Angela 2026-05-17 visual final) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <button
            onClick={() => openQueueAsDrawer('priority_queue', 'Priority')}
            className={`relative p-3 sm:p-4 rounded-[14px] border shadow-[0_1px_4px_rgba(0,0,0,0.06)] transition-all text-left bg-[#FCECEA] ${
              activeQueue === 'priority_queue'
                ? 'border-[#B5302A] ring-2 ring-[#B5302A33]'
                : 'border-[#B5302A33] hover:border-[#B5302A]'
            }`}
            data-testid="sched-card-priority-queue"
          >
            {queueCounts.priority_queue > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 text-white text-[10px] font-bold items-center justify-center">{queueCounts.priority_queue}</span>
              </span>
            )}
            <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
              <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#B5302A] shrink-0" />
              <span className="text-[9px] font-extrabold text-[#64748B] uppercase tracking-[0.06em]">Priority</span>
            </div>
            <p className={`text-[28px] sm:text-[36px] font-black leading-none font-mono-dm ${queueCounts.priority_queue > 0 ? 'text-[#B5302A]' : 'text-[#94A3B8]'}`}>{queueCounts.priority_queue}</p>
          </button>
          <button
            onClick={() => openQueueAsDrawer('admin_review_queue', 'Admin Review')}
            className={`p-3 sm:p-4 rounded-[14px] border shadow-[0_1px_4px_rgba(0,0,0,0.06)] transition-all text-left bg-[#FEF3E8] ${
              activeQueue === 'admin_review_queue'
                ? 'border-[#C4600A] ring-2 ring-[#C4600A33]'
                : 'border-[#C4600A33] hover:border-[#C4600A]'
            }`}
            data-testid="sched-card-admin-review-queue"
          >
            <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
              <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#C4600A] shrink-0" />
              <span className="text-[9px] font-extrabold text-[#64748B] uppercase tracking-[0.06em]">Admin Review</span>
            </div>
            <p className={`text-[28px] sm:text-[36px] font-black leading-none font-mono-dm ${queueCounts.admin_review_queue > 0 ? 'text-[#C4600A]' : 'text-[#94A3B8]'}`}>{queueCounts.admin_review_queue}</p>
          </button>
        </div>

        {/* ═══ SECTION 1: Saturdays & Sundays — collapsed by default (Angela 2026-05-16) ═══ */}
        <details
          open={expandedSections.weekends}
          onToggle={(e) => setExpandedSections((p) => ({ ...p, weekends: e.target.open }))}
          className="bg-white border border-gray-200 rounded-xl"
          data-testid="weekends-section"
        >
          <summary className="cursor-pointer px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider hover:bg-gray-50 select-none">
            Saturdays & Sundays
          </summary>
          <div className="px-4 pb-4">
            <WeekendCallPhotos />
          </div>
        </details>

        {/* ═══ SECTION 1b: Hold Calls — collapsed by default ═══ */}
        <details
          open={expandedSections.holdCalls}
          onToggle={(e) => setExpandedSections((p) => ({ ...p, holdCalls: e.target.open }))}
          className="bg-white border border-gray-200 rounded-xl"
          data-testid="hold-calls-collapsible"
        >
          <summary className="cursor-pointer px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider hover:bg-gray-50 select-none">
            Hold Calls (Hold Review + 48h Wait)
          </summary>
          <div className="px-4 pb-4">
            <HoldCallsSection />
          </div>
        </details>

        {/* ═══ SECTION 2: Map — expanded by default ═══ */}
        <OperationalMap />

        {/* ═══ SECTION 3: Today's Activity — navy panel per design spec ═══ */}
        <div
          data-testid="daily-productivity-counters"
          className="rounded-[16px] p-4"
          style={{
            background: '#0F1B2D',
            boxShadow: '0 4px 16px rgba(15,27,45,0.25)',
          }}
        >
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#94A3B8]">Today's Activity</h2>
            {dailyProductivity.last_updated && (
              <span className="text-[10px] text-[#64748B] font-mono-dm" data-testid="prod-last-updated">
                Last updated: {new Date(dailyProductivity.last_updated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {[
              { label: 'Calls Made',       metric: 'calls_made',       value: dailyProductivity.totals.calls_made,           color: '#60A5FA' },
              { label: 'Calls Received',   metric: 'calls_received',   value: dailyProductivity.totals.calls_received,       color: '#4ADE80' },
              { label: 'Missed Calls',     metric: 'missed_calls',     value: dailyProductivity.totals.missed_calls || 0,    color: '#F87171' },
              { label: 'SMS Sent',         metric: 'sms',              value: dailyProductivity.totals.sms,                  color: '#FCD34D' },
              { label: 'Emails Sent',      metric: 'emails',           value: dailyProductivity.totals.emails,               color: '#FB923C' },
              { label: 'Appointments Set', metric: 'appointments_set', value: dailyProductivity.totals.appointments_set,     color: '#4ADE80' },
              { label: 'Safeguard Emails', metric: 'safeguard_emails', value: dailyProductivity.totals.safeguard_emails || 0, color: '#94A3B8' },
            ].map(({ label, metric, value, color }) => (
              <button
                key={label}
                onClick={() => setProdDrill({ metric, operator: '', label })}
                className="rounded-[12px] px-3 py-2.5 text-center transition-all hover:scale-[1.02]"
                style={{ background: '#ffffff08', border: '1px solid #ffffff10' }}
                data-testid={`prod-counter-${label.toLowerCase().replace(/\s+/g, '-')}`}
                title="Click for detail"
              >
                <p className="text-[22px] font-black leading-none font-mono-dm" style={{ color }}>{value}</p>
                <p className="text-[9px] font-bold uppercase tracking-[0.06em] mt-1 text-[#94A3B8]">{label}</p>
              </button>
            ))}
          </div>

          {/* Breakdown by operator (Apr 22 2026) */}
          {dailyProductivity.operators && dailyProductivity.operators.length > 0 && (
            <div className="mt-3 rounded-[12px] overflow-hidden" style={{ background: '#ffffff08', border: '1px solid #ffffff10' }} data-testid="prod-operator-breakdown">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.08em] text-[#94A3B8]" style={{ borderBottom: '1px solid #ffffff10' }}>
                Breakdown by operator · today
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="text-[10px] uppercase text-[#64748B]">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Operator</th>
                      <th className="px-2 py-1.5 text-center">Made</th>
                      <th className="px-2 py-1.5 text-center">Recv</th>
                      <th className="px-2 py-1.5 text-center">Missed</th>
                      <th className="px-2 py-1.5 text-center">SMS</th>
                      <th className="px-2 py-1.5 text-center">Email</th>
                      <th className="px-2 py-1.5 text-center">Appt</th>
                      <th className="px-2 py-1.5 text-center">Safeguard</th>
                      <th className="px-2 py-1.5 text-center font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyProductivity.operators.map(op => (
                      <tr key={op.operator_name} style={{ borderTop: '1px solid #ffffff10' }} className="hover:bg-[#ffffff08]" data-testid={`prod-row-${op.operator_name.toLowerCase().replace(/\s+/g, '-')}`}>
                        <td className="px-3 py-1.5 font-semibold text-white">{op.operator_name}</td>
                        <td className="px-2 py-1.5 text-center font-mono-dm" style={{ color: op.calls_made ? '#60A5FA' : '#475569' }}>{op.calls_made}</td>
                        <td className="px-2 py-1.5 text-center font-mono-dm" style={{ color: op.calls_received ? '#4ADE80' : '#475569' }}>{op.calls_received}</td>
                        <td className="px-2 py-1.5 text-center font-mono-dm" style={{ color: op.missed_calls ? '#F87171' : '#475569' }}>{op.missed_calls || 0}</td>
                        <td className="px-2 py-1.5 text-center font-mono-dm" style={{ color: op.sms ? '#FCD34D' : '#475569' }}>{op.sms}</td>
                        <td className="px-2 py-1.5 text-center font-mono-dm" style={{ color: op.emails ? '#FB923C' : '#475569' }}>{op.emails}</td>
                        <td className="px-2 py-1.5 text-center font-mono-dm" style={{ color: op.appointments_set ? '#4ADE80' : '#475569' }}>{op.appointments_set}</td>
                        <td className="px-2 py-1.5 text-center font-mono-dm" style={{ color: op.safeguard_emails ? '#94A3B8' : '#475569' }}>{op.safeguard_emails || 0}</td>
                        <td className="px-2 py-1.5 text-center font-bold text-white font-mono-dm">{op.total_actions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Productivity drill-down modal (Apr 22 2026) */}
        {prodDrill && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setProdDrill(null)} data-testid="prod-drill-modal">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-[#1F2937]">
                    {prodDrill.label}
                    {prodDrill.operator ? <span className="ml-2 text-xs font-normal text-gray-500">· {prodDrill.operator}</span> : <span className="ml-2 text-xs font-normal text-gray-500">· all operators</span>}
                  </h3>
                  <p className="text-[11px] text-gray-500 mt-0.5">{prodDrillLoading ? 'Loading…' : `${prodDrillRows.length} records`}</p>
                </div>
                <button onClick={() => setProdDrill(null)} className="p-1 hover:bg-gray-100 rounded" data-testid="prod-drill-close"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex-1 overflow-auto">
                {!prodDrillLoading && prodDrillRows.length === 0 && (
                  <p className="p-6 text-center text-sm text-gray-400">No records match this metric for today.</p>
                )}
                {prodDrillRows.length > 0 && (
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Operator</th>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prodDrillRows.map((r, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-semibold text-[#1F2937] whitespace-nowrap">{r.operator || '—'}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{String(r.start_time || r.action_date || r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                          <td className="px-3 py-2 text-gray-700">
                            {r.direction && <span className="mr-2 text-[10px] uppercase text-gray-500">{r.direction}</span>}
                            {r.from_number && <span className="mr-2">{r.from_name || ''} {r.from_number} → {r.to_number || ''}</span>}
                            {r.order_number && <span className="mr-2 font-mono text-[#2F5D8A]">#{r.order_number}</span>}
                            {r.activity_type && <span className="mr-2 text-gray-600">{r.activity_type}</span>}
                            {r.address && <span className="mr-2">{r.address}</span>}
                            {r.file_name && <span className="mr-2 text-[#8A5A2F]">{r.file_name}</span>}
                            {r.result && <span className="text-gray-500 italic">{r.result}</span>}
                            {r.notes && <span className="text-gray-500">{r.notes}</span>}
                            {r.duration && <span className="ml-2 text-[10px] text-gray-400">({r.duration}s)</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
        {/* ═══ SECTION 4: Live Assignment — collapsed by default (Angela 2026-05-16) ═══ */}
        <details
          open={expandedSections.liveAssignment}
          onToggle={(e) => setExpandedSections((p) => ({ ...p, liveAssignment: e.target.open }))}
          className="bg-white border border-gray-200 rounded-xl"
          data-testid="live-assignment-section"
        >
          <summary className="cursor-pointer px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider hover:bg-gray-50 select-none">
            Live Assignment
          </summary>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {["Active Call", "Incoming Call", "Email", "SMS", "Responded Safeguard", "Inspector Help", "Voicemail"].map((fn) => {
              const liveOp = liveAssignments.find(a => a.function_type === fn || (fn === "Responded Safeguard" && a.function_type === "Respond Safeguard Email"));
              const opName = liveOp ? liveOp.operator_name : null;
              const hasOp = !!opName;
              const fnMatch = fn === "Responded Safeguard" ? "Respond Safeguard Email" : fn;
              const assignedCount = orders.filter(o => o.assigned_function === fnMatch && o.assigned_operator === opName).length;
              return (
                <div key={fn} className={`rounded-lg border px-3 py-2.5 ${hasOp ? 'border-[#D1A054]/30 bg-[#D1A054]/5' : 'border-gray-200 bg-white/80'}`} data-testid={`live-assign-${fn.replace(/\s+/g, '-').toLowerCase()}`}>
                  {hasOp ? (
                    <>
                      <p className="text-xs font-bold text-gray-800 truncate" title={opName}>{opName}</p>
                      <p className="text-[10px] uppercase font-medium text-[#B08A3E] mt-0.5">{fn}</p>
                      <p className="text-lg font-bold text-[#9A7A3E] mt-1" data-testid={`live-count-${fn.replace(/\s+/g, '-').toLowerCase()}`}>{assignedCount} <span className="text-[10px] font-normal text-gray-500">orders</span></p>
                    </>
                  ) : (
                    <>
                      <p className="text-[10px] uppercase font-medium text-gray-300 mb-0.5">{fn}</p>
                      <p className="text-xs text-gray-300 italic">Unassigned</p>
                    </>
                  )}
                </div>
              );
            })}
            </div>
          </div>
        </details>

        {/* ═══ SECTION 5: All Orders — collapsed by default (Angela 2026-05-16) ═══ */}
        <details
          open={expandedSections.ordersTable}
          onToggle={(e) => setExpandedSections((p) => ({ ...p, ordersTable: e.target.open }))}
          className="bg-white border border-gray-200 rounded-xl"
          data-testid="orders-table-section"
        >
          <summary className="cursor-pointer px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider hover:bg-gray-50 select-none">
            All Department Orders ({filteredOrders.length})
          </summary>
          <div className="px-4 pb-4 space-y-3">
        <Filters filters={filters} setFilters={setFilters} inspectors={[]} operators={uniqueOperators} showStatus={false} showInspector={false} showOperator={true} />

        <div className="relative max-w-md" data-testid="scheduling-search-wrapper">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by client, order number, address, city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-8 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:border-[#D1A054] focus:outline-none"
            data-testid="scheduling-search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-800" data-testid="scheduling-search-clear">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Selection Toolbar */}
        {selectedIds.size > 0 ? (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-[#D1A054]/10 border border-[#D1A054]/30 rounded-lg" data-testid="selection-toolbar">
            <span className="text-[#B08A3E] text-sm font-medium">{selectedIds.size} orders selected</span>
            <button
              onClick={() => handleBulkActivity("SMS")}
              className="px-3 py-1.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5"
              data-testid="bulk-sms-btn"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Bulk SMS
            </button>
            <button
              onClick={() => handleBulkActivity("Email")}
              className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5"
              data-testid="bulk-email-btn"
            >
              <Mail className="h-3.5 w-3.5" />
              Bulk Email
            </button>
            <button
              onClick={async () => {
                if (!window.confirm(`Send ${selectedIds.size} order(s) to Priority Queue?`)) return;
                let sent = 0;
                for (const oid of selectedIds) {
                  try {
                    await axios.post(`${API}/orders/${oid}/send-to-queue`, { queue: 'Priority Queue', reason: 'Manual priority via Scheduling' });
                    sent++;
                  } catch (err) { console.error('Priority queue error:', err); }
                }
                setBatchMessage({ type: 'success', text: `${sent} order(s) sent to Priority Queue` });
                setSelectedIds(new Set());
                fetchOrders();
              }}
              className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg flex items-center gap-1.5"
              data-testid="bulk-priority-btn"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              Priority Queue
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowForceEOD(true)}
                className="px-3 py-1.5 bg-[#C07070] hover:bg-[#A85A5A] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5"
                data-testid="force-eod-btn"
              >
                <Power className="h-3.5 w-3.5" />
                Force End of Day
              </button>
            )}
            <button
              onClick={async () => {
                if (!window.confirm(`Send ${selectedIds.size} order(s) to Dispatch?`)) return;
                let sent = 0, skipped = 0;
                for (const oid of selectedIds) {
                  try {
                    await axios.post(`${API}/orders/${oid}/send-to-dispatch`);
                    sent++;
                  } catch (err) {
                    if (err.response?.status === 400) skipped++;
                    else console.error('Send to dispatch error:', err);
                  }
                }
                const msg = skipped > 0 ? `${sent} sent to Dispatch, ${skipped} already in Dispatch` : `${sent} order(s) sent to Dispatch`;
                setBatchMessage({ type: 'success', text: msg });
                setSelectedIds(new Set());
                fetchOrders();
              }}
              className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#254A6F] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5"
              data-testid="send-to-dispatch-btn"
            >
              <ArrowRight className="h-3.5 w-3.5" />
              Send to Dispatch
            </button>
            <button
              onClick={() => setShowQuickAssignInspector(true)}
              className="px-3 py-1.5 bg-[#D1A054] hover:bg-[#B08A3E] text-[#1F2937] text-sm font-semibold rounded-lg flex items-center gap-1.5"
              data-testid="assign-inspector-btn"
            >
              <User className="h-3.5 w-3.5" />
              Assign Inspector
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 text-gray-500 hover:text-gray-800 text-sm"
              data-testid="clear-selection-btn"
            >
              Clear
            </button>
          </div>
        ) : filteredOrders.length > 0 && (
          <div className="flex items-center gap-2" data-testid="quick-select-bar">
            <span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Quick Select:</span>
            {[20, 50, 100].map(n => (
              <button
                key={n}
                onClick={() => handleQuickSelect(n)}
                disabled={filteredOrders.length === 0}
                className="px-2.5 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded border border-gray-300 transition-colors"
                data-testid={`quick-select-${n}`}
              >
                Top {n}
              </button>
            ))}
            <span className="text-gray-300 text-xs ml-1">({filteredOrders.length} visible)</span>
          </div>
        )}

        {activeQueue === 'national_calls' && (
          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="national-call-queue-toolbar">
            <p className="text-xs text-emerald-800">
              <span className="font-semibold">National Calls queue:</span> use <span className="font-semibold">Remove from Queue</span> to hide an order from this view only. It stays active everywhere else (status, assignment, history and All Orders are untouched).
            </p>
            <button
              onClick={() => setShowHiddenNationalCalls(v => !v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded border transition-colors ${
                showHiddenNationalCalls
                  ? 'bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700'
                  : 'bg-white text-emerald-700 border-emerald-400 hover:bg-emerald-100'
              }`}
              data-testid="toggle-hidden-national-calls"
            >
              {showHiddenNationalCalls ? 'Back to Active Queue' : 'Show Hidden Orders'}
            </button>
          </div>
        )}

        <div ref={orderListRef} className="bg-white rounded-lg border border-gray-200">
          <OrderTable
            orders={filteredOrders}
            columns={activeQueue === 'national_calls' ? [
              ...schedulingColumns,
              {
                key: '__national_call_queue_hide',
                label: 'Queue',
                sortable: false,
                render: (_v, row) => {
                  const hidden = row.national_call_queue_hidden === 'yes';
                  return (
                    <button
                      onClick={(e) => handleToggleNationalCallQueue(row.id, e)}
                      className={`px-2.5 py-1 rounded border text-[11px] font-semibold transition-colors whitespace-nowrap ${
                        hidden
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                          : 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100'
                      }`}
                      data-testid={`national-call-queue-toggle-${row.id}`}
                      title={hidden ? 'Restore this order to the National Calls queue' : 'Remove this order from the National Calls queue (does not change status or assignment)'}
                    >
                      {hidden ? 'Restore to Queue' : 'Remove from Queue'}
                    </button>
                  );
                },
              },
            ] : schedulingColumns}
            loading={loading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            onRowClick={(order) => navigate(`/scheduling/order/${order.id}`)}
            selectable
            selectedIds={selectedIds}
            onSelectToggle={handleSelectToggle}
            onSelectAll={handleSelectAll}
            onShiftSelect={handleShiftSelect}
          />
        </div>
          </div>
        </details>

        {showCreate && <CreateOrderModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}
        {showImport && <ImportModal onClose={() => setShowImport(false)} onImport={fetchOrders} />}
        {appointmentOrder && <AppointmentModal order={appointmentOrder} onClose={() => setAppointmentOrder(null)} onSave={handleSetAppointment} />}
        {assignOrder && <AssignModal order={assignOrder} inspectors={inspectors} onClose={() => setAssignOrder(null)} onSave={handleAssign} />}
        <SchedulingKBPanel isOpen={showKB} onClose={() => setShowKB(false)} initialTab={kbTab} />

        {/* Angela 2026-05-16 — Queue Drawer (side sheet for counter clicks). */}
        <SchedulingQueueDrawer
          open={!!queueDrawer}
          queueTitle={queueDrawer?.title}
          queueOrders={filteredOrders}
          onClose={() => setQueueDrawer(null)}
          onPickOrder={(o) => {
            setQueueDrawer(null);
            navigate(`/scheduling/order/${o.id}`);
          }}
        />

        {/* Inspector Field Updates modal — Angela 2026-05-10 */}
        {showFieldUpdatesModal && (
          <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowFieldUpdatesModal(false)} data-testid="inspector-field-updates-modal">
            <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-5xl shadow-2xl mt-12" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Radio className="h-5 w-5 text-red-500" />
                  <h3 className="text-base font-bold text-gray-800">Inspector Field Updates</h3>
                </div>
                <button onClick={() => setShowFieldUpdatesModal(false)} className="text-gray-400 hover:text-gray-600" data-testid="inspector-field-updates-close-btn">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4">Pending inspector → office communications. Click an order link to open the related order. Items stay in the queue until you click <span className="font-semibold text-emerald-700">Handled</span>.</p>
              {fieldUpdatesPending.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center" data-testid="inspector-field-updates-empty">No pending inspector field updates.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="inspector-field-updates-modal-table">
                    <thead>
                      <tr className="border-b border-gray-200 text-gray-500">
                        <th className="text-left py-2 pr-3 font-medium">Date / Time</th>
                        <th className="text-left py-2 pr-3 font-medium">Order</th>
                        <th className="text-left py-2 pr-3 font-medium">Flow</th>
                        <th className="text-left py-2 pr-3 font-medium">Inspector</th>
                        <th className="text-left py-2 pr-3 font-medium">Activity</th>
                        <th className="text-left py-2 pr-3 font-medium">Comment / Observation</th>
                        <th className="text-left py-2 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {fieldUpdatesPending.map((ev, i) => (
                        <tr key={ev.id || i} className="text-gray-700 hover:bg-red-50" data-testid={`inspector-field-update-modal-row-${i}`}>
                          <td className="py-2 pr-3 whitespace-nowrap">{ev.created_at ? new Date(ev.created_at).toLocaleString() : '-'}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">
                            <button
                              onClick={() => {
                                setShowFieldUpdatesModal(false);
                                navigate(`/scheduling/order/${ev.order_id}`);
                              }}
                              className="text-left hover:underline"
                              data-testid={`inspector-field-update-open-order-${i}`}
                            >
                              <span className="font-semibold text-[#2F5D8A]">{ev.order_number || '-'}</span>
                              <span className="block text-[10px] text-gray-400">{ev.address || ''}{ev.city ? `, ${ev.city}` : ''}</span>
                            </button>
                          </td>
                          <td className="py-2 pr-3 whitespace-nowrap">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ev.flow_type === 'External' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>{ev.flow_type || '-'}</span>
                          </td>
                          <td className="py-2 pr-3 whitespace-nowrap">{ev.inspector_name || '-'}</td>
                          <td className="py-2 pr-3 whitespace-nowrap font-semibold">{ev.activity_type || '-'}</td>
                          <td className="py-2 pr-3 max-w-[200px] truncate" title={ev.comment}>{ev.comment || '-'}</td>
                          <td className="py-2 whitespace-nowrap">
                            <button
                              onClick={() => markFieldUpdateHandled(ev.id)}
                              className="px-2.5 py-1 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded whitespace-nowrap"
                              data-testid={`inspector-field-update-mark-handled-${i}`}
                              title="Manually remove this update from the queue"
                            >
                              Handled
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Quick Assign Inspector Modal (Scheduling) */}
        {showQuickAssignInspector && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowQuickAssignInspector(false)} data-testid="quick-assign-inspector-modal">
            <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-[#D1A054]/12 rounded-lg"><User className="h-5 w-5 text-[#B08A3E]" /></div>
                <div>
                  <h3 className="text-[#1F2937] text-lg font-semibold">Assign Inspector</h3>
                  <p className="text-gray-500 text-xs">{selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''} selected</p>
                </div>
              </div>
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {inspectors.map(insp => (
                  <button key={insp.safeguard_login || insp.name}
                    onClick={() => handleQuickAssignInspector(insp.safeguard_login || insp.name)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#D1A054]/10 transition-colors flex items-center gap-3 border border-transparent hover:border-[#D1A054]/25"
                    data-testid={`assign-inspector-option-${(insp.safeguard_login || insp.name).toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#2F5D8A]/15 flex items-center justify-center text-[#2F5D8A] text-xs font-bold">
                      {(insp.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1F2937]">{insp.name}</p>
                      <p className="text-[10px] text-gray-400">{insp.safeguard_login}</p>
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => setShowQuickAssignInspector(false)}
                className="w-full mt-4 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 font-medium"
                data-testid="quick-assign-cancel">Cancel</button>
            </div>
          </div>
        )}

        {/* Force End of Day Confirmation Modal */}
        {showForceEOD && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="force-eod-modal">
            <div className="bg-white rounded-xl border border-[#C07070]/30 p-6 w-full max-w-md shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-[#C07070]/12 rounded-lg">
                  <Power className="h-5 w-5 text-[#8B4545]" />
                </div>
                <div>
                  <h3 className="text-[#1F2937] text-lg font-semibold">Force End of Day</h3>
                  <p className="text-gray-500 text-xs">Remove all assignments from {selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="bg-amber-50/80 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                Are you sure you want to remove assignments from selected orders? This will clear operator and inspector assignments. Call logs, comments, attempts, and appointment data will be preserved.
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowForceEOD(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg"
                  data-testid="force-eod-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleForceEndOfDay}
                  disabled={forceEODProcessing}
                  className="flex-1 px-4 py-2 bg-[#C07070] hover:bg-[#A85A5A] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="force-eod-confirm"
                >
                  <Power className="h-4 w-4" />
                  {forceEODProcessing ? "Processing..." : "Confirm Remove"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Voicemail & Call Without Order Modals */}
        {showVoicemailModal && <RegisterActivityModal onClose={() => setShowVoicemailModal(false)} onSaved={() => setShowVoicemailModal(false)} operators={operators} isVoicemail />}
        {showCallWithoutModal && <RegisterActivityModal onClose={() => setShowCallWithoutModal(false)} onSaved={() => setShowCallWithoutModal(false)} operators={operators} />}

        {/* Bulk SMS Modal */}
        {bulkSmsOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setBulkSmsOpen(false); setBulkSmsPreview(null); setBulkSmsResult(null); }}>
            <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bulk-sms-modal">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Send Today Appointment SMS</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Bulk SMS to all clients with appointments today</p>
                </div>
                <button onClick={() => { setBulkSmsOpen(false); setBulkSmsPreview(null); setBulkSmsResult(null); }} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5 text-gray-400" /></button>
              </div>

              {bulkSmsLoading && !bulkSmsResult && (
                <div className="flex items-center justify-center py-10"><div className="animate-spin h-6 w-6 border-b-2 border-[#2F5D8A] rounded-full" /></div>
              )}

              {bulkSmsPreview && !bulkSmsResult && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div className="bg-[#6B8F62]/10 rounded-lg p-3 text-center" data-testid="bulksms-stat-ready">
                      <p className="text-2xl font-bold text-[#4A7A56]">{bulkSmsPreview.eligible}</p>
                      <p className="text-[10px] text-gray-500 font-medium">Ready to Send</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="bulksms-stat-already">
                      <p className="text-2xl font-bold text-gray-400">{bulkSmsPreview.already_sent}</p>
                      <p className="text-[10px] text-gray-500 font-medium">Already Sent</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center" data-testid="bulksms-stat-nophone">
                      <p className="text-2xl font-bold text-amber-500">{bulkSmsPreview.no_phone}</p>
                      <p className="text-[10px] text-gray-500 font-medium">No Phone</p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-center" data-testid="bulksms-stat-notime">
                      <p className="text-2xl font-bold text-yellow-700">{bulkSmsPreview.no_time || 0}</p>
                      <p className="text-[10px] text-yellow-800 font-semibold">⚠ NO INTERNAL TIME</p>
                    </div>
                    {/* MISSING INTERNAL APPT card REMOVED (Angela 2026-04-29):
                        SG-created appointments now auto-mirror SG → internal in
                        the preview endpoint. Truly-invalid records go to
                        backend audit only — no warning bucket in the SMS UI. */}
                    {(bulkSmsPreview.auto_mirrored_count || 0) > 0 && (
                      <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-center" data-testid="bulksms-stat-auto-mirrored">
                        <p className="text-2xl font-bold text-emerald-700">{bulkSmsPreview.auto_mirrored_count}</p>
                        <p className="text-[10px] text-emerald-800 font-semibold">✓ SG → INTERNAL AUTO-MIRRORED</p>
                      </div>
                    )}
                  </div>

                  {/* Missing Internal Appointment banner REMOVED (Angela 2026-04-29):
                      Bucket no longer surfaces in SMS UI. */}
                  {false && (bulkSmsPreview.missing_internal || 0) > 0 && (
                    <details className="bg-orange-50 border border-orange-300 rounded-lg p-3" data-testid="bulksms-missing-internal-details" open>
                      <summary className="text-xs font-semibold text-orange-800 cursor-pointer">
                        ⚠ {bulkSmsPreview.missing_internal} order(s) — Missing AARIFI internal appointment. Edit fields below to populate the internal appointment, OR click "Send anyway" with confirmation to send using the displayed Safeguard time.
                      </summary>
                      {(bulkSmsPreview.missing_internal_list || []).length > 0 && (
                        <div className="mt-3 space-y-3">
                          {(bulkSmsPreview.missing_internal_list || []).map((s, i) => {
                            const edit = missingFixState[s.order_number] || {};
                            const busy = missingFixBusy[s.order_number];
                            return (
                              <div key={i} className="bg-white border border-orange-200 rounded-lg p-3" data-testid={`bulksms-missing-internal-item-${i}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-xs font-semibold text-gray-800">{s.order_number}</div>
                                  <div className="text-[10px] text-gray-400">
                                    Safeguard ref — date: <code className="bg-orange-50 px-1 rounded">{s.appointment_sg_date || '—'}</code>
                                    {' '}time: <code className="bg-orange-50 px-1 rounded">{s.appointment_sg_time ? toAmPm24(s.appointment_sg_time) : '—'}</code>
                                    {' '}phone: <code className="bg-orange-50 px-1 rounded">{s.ph_phone || '—'}</code>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                                  <div>
                                    <label className="block text-[10px] text-gray-500 mb-0.5">Internal date</label>
                                    <input
                                      type="date"
                                      value={edit.appointment_real_date || ''}
                                      onChange={e => setMissingFixField(s.order_number, 'appointment_real_date', e.target.value)}
                                      className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                                      data-testid={`bulksms-fix-${s.order_number}-date`}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] text-gray-500 mb-0.5">Start (HH:MM)</label>
                                    <input
                                      type="time"
                                      value={edit.internal_start_time || ''}
                                      onChange={e => setMissingFixField(s.order_number, 'internal_start_time', e.target.value)}
                                      className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                                      data-testid={`bulksms-fix-${s.order_number}-start`}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] text-gray-500 mb-0.5">End (HH:MM)</label>
                                    <input
                                      type="time"
                                      value={edit.internal_end_time || ''}
                                      onChange={e => setMissingFixField(s.order_number, 'internal_end_time', e.target.value)}
                                      className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                                      data-testid={`bulksms-fix-${s.order_number}-end`}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] text-gray-500 mb-0.5">Phone (optional)</label>
                                    <input
                                      type="text"
                                      placeholder={s.ph_phone || '—'}
                                      value={edit.ph_phone || ''}
                                      onChange={e => setMissingFixField(s.order_number, 'ph_phone', e.target.value)}
                                      className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                                      data-testid={`bulksms-fix-${s.order_number}-phone`}
                                    />
                                  </div>
                                </div>
                                <div className="mb-2">
                                  <label className="block text-[10px] text-gray-500 mb-0.5">Note / comment (optional)</label>
                                  <input
                                    type="text"
                                    value={edit.note || ''}
                                    onChange={e => setMissingFixField(s.order_number, 'note', e.target.value)}
                                    className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                                    data-testid={`bulksms-fix-${s.order_number}-note`}
                                  />
                                </div>
                                <div className="text-[10px] text-gray-400 mb-2">
                                  Allowed AARIFI windows: 8:00–9:00, 8:00–12:00, 9:00–13:00, 9:30–13:30, 10:00–14:00, 11:00–15:00.
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleSaveInternalAppointment(s)}
                                    disabled={!!busy}
                                    className="flex-1 px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#264C73] text-white text-xs font-semibold rounded disabled:opacity-40"
                                    data-testid={`bulksms-fix-${s.order_number}-save-btn`}
                                  >
                                    {busy === 'save' ? 'Saving…' : 'Save internal appointment'}
                                  </button>
                                  <button
                                    onClick={() => handleSendAnyway(s)}
                                    disabled={!!busy}
                                    className="flex-1 px-3 py-1.5 bg-[#C07070] hover:bg-[#A85A5A] text-white text-xs font-semibold rounded disabled:opacity-40"
                                    data-testid={`bulksms-fix-${s.order_number}-sendanyway-btn`}
                                  >
                                    {busy === 'send' ? 'Sending…' : 'Send anyway'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </details>
                  )}

                  {(bulkSmsPreview.no_time || 0) > 0 && (
                    <details className="bg-yellow-50 border border-yellow-300 rounded-lg p-3" data-testid="bulksms-skipped-notime-details">
                      <summary className="text-xs font-semibold text-yellow-800 cursor-pointer">
                        ⚠ {bulkSmsPreview.no_time} order(s) SKIPPED — Internal time missing.  Operator must set internal_start_time + internal_end_time on each order.
                      </summary>
                      {(bulkSmsPreview.skipped_no_time_list || []).length > 0 && (
                        <ul className="mt-2 ml-4 text-[11px] text-yellow-900 space-y-0.5 list-disc">
                          {(bulkSmsPreview.skipped_no_time_list || []).map((s, i) => (
                            <li key={i}>
                              <span className="font-semibold">{s.order_number}</span>
                              {s.appointment_sg_time && <span> — sg_time: <code className="bg-yellow-100 px-1 rounded">{toAmPm24(s.appointment_sg_time)}</code></span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </details>
                  )}

                  {bulkSmsPreview.preview?.length > 0 && (
                    <div className="border border-gray-100 rounded-lg overflow-hidden">
                      {(() => {
                        const eligibleNums = bulkSmsPreview.preview
                          .filter(p => !['no_phone','invalid_phone','no_time','already_sent'].includes((p.status || '').toLowerCase()))
                          .map(p => String(p.order_number));
                        const allSelected = eligibleNums.length > 0 && eligibleNums.every(n => bulkSmsSelected.has(n));
                        const toggleAll = () => {
                          setBulkSmsSelected(prev => {
                            const next = new Set(prev);
                            if (allSelected) eligibleNums.forEach(n => next.delete(n));
                            else eligibleNums.forEach(n => next.add(n));
                            return next;
                          });
                        };
                        return (
                          <div className="flex items-center justify-between px-3 py-2 bg-[#6B8F62]/5 border-b border-gray-100">
                            <label className="flex items-center gap-2 text-xs font-semibold text-[#4A7A56] cursor-pointer" data-testid="today-sms-select-all">
                              <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-3.5 w-3.5 accent-[#6B8F62]" />
                              {allSelected ? 'Deselect all' : 'Select all'}
                            </label>
                            <span className="text-[10px] text-gray-500" data-testid="today-sms-selected-count">
                              {bulkSmsSelected.size} of {eligibleNums.length} selected
                            </span>
                          </div>
                        );
                      })()}
                      <table className="w-full text-xs">
                        <thead><tr className="bg-gray-50 border-b border-gray-100">
                          <th className="w-8 px-2 py-2"></th>
                          <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Order</th>
                          <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Time</th>
                          <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Phone</th>
                          <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Status</th>
                        </tr></thead>
                        <tbody>
                          {bulkSmsPreview.preview.map((p, i) => {
                            const num = String(p.order_number || '');
                            const st = (p.status || 'ready').toLowerCase();
                            const blocked = ['no_phone','invalid_phone','no_time','already_sent'].includes(st);
                            const checked = bulkSmsSelected.has(num);
                            const badge = (
                              st === "ready" ? { cls: "bg-[#6B8F62]/15 text-[#4A7A56]", label: "Ready" } :
                              st === "no_phone" ? { cls: "bg-amber-100 text-amber-700", label: "No Phone" } :
                              st === "invalid_phone" ? { cls: "bg-[#C07070]/15 text-[#C07070]", label: "Invalid" } :
                              st === "no_time" ? { cls: "bg-yellow-100 text-yellow-800", label: "No Time" } :
                              st === "already_sent" ? { cls: "bg-gray-100 text-gray-500", label: "Sent" } :
                              { cls: "bg-gray-100 text-gray-500", label: st || "—" }
                            );
                            const editable = st === 'invalid_phone' || st === 'no_phone';
                            const editKey = `today:${num}`;
                            const isEditing = editingPhone === editKey;
                            return (
                              <tr key={i} className={`border-b border-gray-50 ${blocked ? 'bg-gray-50/60' : ''}`} data-testid={`today-sms-row-${num}`}>
                                <td className="px-2 py-1.5 text-center">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={blocked || !num}
                                    onChange={() => {
                                      setBulkSmsSelected(prev => {
                                        const next = new Set(prev);
                                        if (next.has(num)) next.delete(num); else next.add(num);
                                        return next;
                                      });
                                    }}
                                    className="h-3.5 w-3.5 accent-[#6B8F62]"
                                    data-testid={`today-sms-checkbox-${num}`}
                                  />
                                </td>
                                <td className="px-3 py-1.5 font-medium text-[#2F5D8A]">{p.order_number}</td>
                                <td className="px-3 py-1.5 text-gray-600">{p.time}</td>
                                <td className="px-3 py-1.5 text-gray-500">
                                  {isEditing ? (
                                    <div className="flex flex-col gap-1" data-testid={`today-sms-phone-edit-${num}`}>
                                      <div className="flex items-center gap-1">
                                        <input
                                          type="tel"
                                          autoFocus
                                          value={editingPhoneVal}
                                          onChange={e => setEditingPhoneVal(e.target.value)}
                                          onKeyDown={e => {
                                            if (e.key === 'Enter') saveInlinePhone('today', p.order_id, num);
                                            if (e.key === 'Escape') cancelPhoneEdit();
                                          }}
                                          placeholder="(555) 123-4567"
                                          disabled={editingPhoneBusy}
                                          className="flex-1 px-2 py-1 text-xs border border-[#2F5D8A]/40 rounded focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                                          data-testid={`today-sms-phone-input-${num}`}
                                        />
                                        <button
                                          onClick={() => saveInlinePhone('today', p.order_id, num)}
                                          disabled={editingPhoneBusy}
                                          className="px-2 py-1 text-[10px] font-semibold text-white bg-[#4A7A56] hover:bg-[#3E6547] rounded disabled:opacity-40"
                                          data-testid={`today-sms-phone-save-${num}`}
                                        >
                                          {editingPhoneBusy ? '...' : 'Save'}
                                        </button>
                                        <button
                                          onClick={cancelPhoneEdit}
                                          disabled={editingPhoneBusy}
                                          className="px-2 py-1 text-[10px] text-gray-500 hover:text-gray-800"
                                          data-testid={`today-sms-phone-cancel-${num}`}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                      {editingPhoneErr && (
                                        <span className="text-[10px] text-[#C07070] font-medium" data-testid={`today-sms-phone-error-${num}`}>{editingPhoneErr}</span>
                                      )}
                                    </div>
                                  ) : editable && p.order_id ? (
                                    <button
                                      onClick={() => openPhoneEdit('today', num, p.phone)}
                                      className="text-[#C07070] underline decoration-dotted underline-offset-2 hover:text-[#8B4545] font-medium"
                                      data-testid={`today-sms-phone-btn-${num}`}
                                      title="Click to correct phone number"
                                    >
                                      {p.phone || 'add phone'}
                                    </button>
                                  ) : (
                                    <span className={blocked ? 'text-gray-400' : ''}>{p.phone || '—'}</span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase ${badge.cls}`}>{badge.label}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <button onClick={handleBulkSmsSend} disabled={bulkSmsLoading || bulkSmsSelected.size === 0}
                    className="w-full py-3 bg-[#6B8F62] hover:bg-[#5A7B52] text-white font-bold rounded-lg transition-colors disabled:opacity-40 flex items-center justify-center gap-2 text-sm"
                    data-testid="bulk-sms-send-btn">
                    <MessageSquare className="h-4 w-4" />
                    {bulkSmsLoading ? 'Sending...' : `Send SMS to ${bulkSmsSelected.size} Clients`}
                  </button>
                </div>
              )}

              {bulkSmsResult && (
                <div className="space-y-3">
                  {bulkSmsResult.success ? (
                    <>
                      <div className="bg-[#6B8F62]/10 border border-[#A7C4A0]/30 rounded-lg p-4 text-center">
                        <CheckCircle className="h-8 w-8 mx-auto mb-2 text-[#4A7A56]" />
                        <p className="text-lg font-bold text-[#4A7A56]">{bulkSmsResult.sent} SMS Sent</p>
                        <p className="text-xs text-gray-400 mt-1">{bulkSmsResult.total_eligible} total eligible orders</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold text-gray-500">{bulkSmsResult.skipped_no_phone || 0}</p><p className="text-[9px] text-gray-400">No Phone</p></div>
                        <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold text-gray-500">{bulkSmsResult.skipped_invalid_phone || 0}</p><p className="text-[9px] text-gray-400">Invalid Phone</p></div>
                        <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold text-gray-500">{bulkSmsResult.skipped_no_time || 0}</p><p className="text-[9px] text-gray-400">No Time</p></div>
                        <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold text-gray-500">{bulkSmsResult.skipped_already_sent || 0}</p><p className="text-[9px] text-gray-400">Already Sent</p></div>
                        <div className="bg-[#C07070]/10 rounded-lg p-2"><p className="font-bold text-[#C07070]">{bulkSmsResult.failed || 0}</p><p className="text-[9px] text-[#C07070]">API Failed</p></div>
                      </div>
                      {bulkSmsResult.results && bulkSmsResult.results.length > 0 && (
                        <div className="max-h-[150px] overflow-y-auto bg-gray-50 rounded-lg border border-gray-200 text-[10px]">
                          <table className="w-full"><tbody>
                            {bulkSmsResult.results.slice(0, 30).map((r, i) => (
                              <tr key={i} className="border-b border-gray-100"><td className="px-2 py-1 font-medium">{r.order}</td><td className="px-2 py-1 text-gray-400">{r.phone || ""}</td><td className={`px-2 py-1 font-bold ${r.status === "sent" ? "text-[#6B8F62]" : r.status === "failed" ? "text-[#C07070]" : "text-gray-400"}`}>{r.status}</td><td className="px-2 py-1 text-gray-300 truncate max-w-[100px]">{r.error || ""}</td></tr>
                            ))}
                          </tbody></table>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-[#C07070]/10 border border-[#C07070]/30 rounded-lg p-4 text-center">
                      <p className="text-sm font-bold text-[#C07070]">{bulkSmsResult.error || 'Failed to send'}</p>
                    </div>
                  )}
                  <button onClick={() => { setBulkSmsOpen(false); setBulkSmsPreview(null); setBulkSmsResult(null); }}
                    className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium rounded-lg text-sm">Close</button>
                </div>
              )}
            </div>
          </div>
        )}


        {/* Day-Before SMS Modal (tomorrow's internal appointments) */}
        {dayBeforeSmsOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setDayBeforeSmsOpen(false); setDayBeforeSmsPreview(null); setDayBeforeSmsResult(null); }}>
            <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bulk-day-before-sms-modal">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Send Day-Before Reminder SMS</h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Internal appointments scheduled for TOMORROW
                    {dayBeforeSmsPreview?.target_date ? ` (${dayBeforeSmsPreview.target_date})` : ""}
                  </p>
                </div>
                <button onClick={() => { setDayBeforeSmsOpen(false); setDayBeforeSmsPreview(null); setDayBeforeSmsResult(null); }} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5 text-gray-400" /></button>
              </div>

              {dayBeforeSmsLoading && !dayBeforeSmsResult && (
                <div className="flex items-center justify-center py-10"><div className="animate-spin h-6 w-6 border-b-2 border-[#9A7A3E] rounded-full" /></div>
              )}

              {dayBeforeSmsPreview && !dayBeforeSmsResult && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-[#9A7A3E]/10 to-[#9A7A3E]/5 border border-[#9A7A3E]/30 rounded-lg p-4 text-center">
                    <p className="text-[10px] text-[#7F6632] font-semibold uppercase tracking-wider">Tomorrow Appointments</p>
                    <p className="text-4xl font-bold text-[#7F6632] leading-tight" data-testid="day-before-total-tomorrow">{dayBeforeSmsPreview.total_tomorrow ?? 0}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">Internal · {dayBeforeSmsPreview.target_date}</p>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <div className="bg-[#6B8F62]/10 rounded-lg p-2.5 text-center" data-testid="day-before-ready">
                      <p className="text-xl font-bold text-[#4A7A56]">{dayBeforeSmsPreview.ready_to_send ?? dayBeforeSmsPreview.eligible ?? 0}</p>
                      <p className="text-[10px] text-gray-600 font-medium">Ready to Send</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2.5 text-center" data-testid="day-before-no-phone">
                      <p className="text-xl font-bold text-amber-600">{dayBeforeSmsPreview.no_phone ?? 0}</p>
                      <p className="text-[10px] text-amber-700 font-medium">No Phone</p>
                    </div>
                    <div className="bg-[#C07070]/10 rounded-lg p-2.5 text-center" data-testid="day-before-invalid-phone">
                      <p className="text-xl font-bold text-[#C07070]">{dayBeforeSmsPreview.invalid_phone ?? 0}</p>
                      <p className="text-[10px] text-[#C07070] font-medium">Invalid Phone</p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-2.5 text-center" data-testid="day-before-no-time">
                      <p className="text-xl font-bold text-yellow-700">{dayBeforeSmsPreview.no_time ?? 0}</p>
                      <p className="text-[10px] text-yellow-800 font-semibold">⚠ NO INTERNAL TIME</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2.5 text-center" data-testid="day-before-already-sent">
                      <p className="text-xl font-bold text-gray-400">{dayBeforeSmsPreview.already_sent ?? 0}</p>
                      <p className="text-[10px] text-gray-500 font-medium">Already Sent</p>
                    </div>
                  </div>

                  {(dayBeforeSmsPreview.no_phone > 0 || dayBeforeSmsPreview.invalid_phone > 0) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-[11px] text-amber-800 flex items-start gap-1.5">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        {(dayBeforeSmsPreview.no_phone || 0) + (dayBeforeSmsPreview.invalid_phone || 0)} record(s) need phone review.
                        They are <b>kept in the queue</b> (not hidden) so João can correct them manually before resending.
                      </span>
                    </div>
                  )}

                  {dayBeforeSmsPreview.preview?.length > 0 && (
                    <div className="border border-gray-100 rounded-lg overflow-hidden">
                      {(() => {
                        const eligibleNums = dayBeforeSmsPreview.preview
                          .filter(p => (p.status || '').toLowerCase() === 'ready')
                          .map(p => String(p.order_number));
                        const allSelected = eligibleNums.length > 0 && eligibleNums.every(n => dayBeforeSmsSelected.has(n));
                        const toggleAll = () => {
                          setDayBeforeSmsSelected(prev => {
                            const next = new Set(prev);
                            if (allSelected) eligibleNums.forEach(n => next.delete(n));
                            else eligibleNums.forEach(n => next.add(n));
                            return next;
                          });
                        };
                        return (
                          <div className="flex items-center justify-between px-3 py-2 bg-[#9A7A3E]/10 border-b border-gray-100">
                            <label className="flex items-center gap-2 text-xs font-semibold text-[#7F6632] cursor-pointer" data-testid="day-before-sms-select-all">
                              <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-3.5 w-3.5 accent-[#9A7A3E]" />
                              {allSelected ? 'Deselect all' : 'Select all'}
                            </label>
                            <span className="text-[10px] text-gray-500" data-testid="day-before-sms-selected-count">
                              {dayBeforeSmsSelected.size} of {eligibleNums.length} selected
                            </span>
                          </div>
                        );
                      })()}
                      <table className="w-full text-xs">
                        <thead><tr className="bg-gray-50 border-b border-gray-100">
                          <th className="w-8 px-2 py-2"></th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase">Order</th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase">Time</th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase">Phone</th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase">Status</th>
                        </tr></thead>
                        <tbody>
                          {dayBeforeSmsPreview.preview.map((p, i) => {
                            const num = String(p.order_number || '');
                            const isReady = (p.status || '').toLowerCase() === 'ready';
                            const checked = dayBeforeSmsSelected.has(num);
                            const badge = (
                              p.status === "ready" ? { cls: "bg-[#6B8F62]/15 text-[#4A7A56]", label: "Ready" } :
                              p.status === "no_phone" ? { cls: "bg-amber-100 text-amber-700", label: "No Phone" } :
                              p.status === "invalid_phone" ? { cls: "bg-[#C07070]/15 text-[#C07070]", label: "Invalid" } :
                              p.status === "no_time" ? { cls: "bg-yellow-100 text-yellow-800", label: "No Time" } :
                              p.status === "already_sent" ? { cls: "bg-gray-100 text-gray-500", label: "Sent" } :
                              { cls: "bg-gray-100 text-gray-500", label: p.status || "—" }
                            );
                            const rowBg =
                              p.status === "no_time" ? "bg-yellow-50/40" :
                              (p.status === "no_phone" || p.status === "invalid_phone") ? "bg-amber-50/30" : "";
                            return (
                              <tr key={i} className={`border-b border-gray-50 ${rowBg} ${!isReady ? 'opacity-60' : ''}`} data-testid={`day-before-row-${p.order_number}`}>
                                <td className="px-2 py-1.5 text-center">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={!isReady || !num}
                                    onChange={() => {
                                      setDayBeforeSmsSelected(prev => {
                                        const next = new Set(prev);
                                        if (next.has(num)) next.delete(num); else next.add(num);
                                        return next;
                                      });
                                    }}
                                    className="h-3.5 w-3.5 accent-[#9A7A3E]"
                                    data-testid={`day-before-checkbox-${num}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5 font-medium text-[#2F5D8A]">{p.order_number}</td>
                                <td className="px-2 py-1.5 text-gray-600">{p.time}</td>
                                <td className="px-2 py-1.5 text-gray-500">
                                  {(() => {
                                    const editable = p.status === 'invalid_phone' || p.status === 'no_phone';
                                    const editKey = `day-before:${num}`;
                                    const isEditing = editingPhone === editKey;
                                    if (isEditing) {
                                      return (
                                        <div className="flex flex-col gap-1" data-testid={`day-before-phone-edit-${num}`}>
                                          <div className="flex items-center gap-1">
                                            <input
                                              type="tel"
                                              autoFocus
                                              value={editingPhoneVal}
                                              onChange={e => setEditingPhoneVal(e.target.value)}
                                              onKeyDown={e => {
                                                if (e.key === 'Enter') saveInlinePhone('day-before', p.order_id, num);
                                                if (e.key === 'Escape') cancelPhoneEdit();
                                              }}
                                              placeholder="(555) 123-4567"
                                              disabled={editingPhoneBusy}
                                              className="flex-1 px-2 py-1 text-xs border border-[#9A7A3E]/40 rounded focus:outline-none focus:ring-1 focus:ring-[#9A7A3E]"
                                              data-testid={`day-before-phone-input-${num}`}
                                            />
                                            <button
                                              onClick={() => saveInlinePhone('day-before', p.order_id, num)}
                                              disabled={editingPhoneBusy}
                                              className="px-2 py-1 text-[10px] font-semibold text-white bg-[#7F6632] hover:bg-[#6A5427] rounded disabled:opacity-40"
                                              data-testid={`day-before-phone-save-${num}`}
                                            >
                                              {editingPhoneBusy ? '...' : 'Save'}
                                            </button>
                                            <button
                                              onClick={cancelPhoneEdit}
                                              disabled={editingPhoneBusy}
                                              className="px-2 py-1 text-[10px] text-gray-500 hover:text-gray-800"
                                              data-testid={`day-before-phone-cancel-${num}`}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                          {editingPhoneErr && (
                                            <span className="text-[10px] text-[#C07070] font-medium" data-testid={`day-before-phone-error-${num}`}>{editingPhoneErr}</span>
                                          )}
                                        </div>
                                      );
                                    }
                                    if (editable && p.order_id) {
                                      return (
                                        <button
                                          onClick={() => openPhoneEdit('day-before', num, p.phone)}
                                          className="text-[#C07070] underline decoration-dotted underline-offset-2 hover:text-[#8B4545] font-medium"
                                          data-testid={`day-before-phone-btn-${num}`}
                                          title="Click to correct phone number"
                                        >
                                          {p.phone || 'add phone'}
                                        </button>
                                      );
                                    }
                                    return p.phone || <span className="italic text-amber-600">missing</span>;
                                  })()}
                                </td>
                                <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase ${badge.cls}`}>{badge.label}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <button onClick={handleDayBeforeSmsSend} disabled={dayBeforeSmsLoading || dayBeforeSmsSelected.size === 0}
                    className="w-full py-3 bg-[#9A7A3E] hover:bg-[#7F6632] text-white font-bold rounded-lg transition-colors disabled:opacity-40 flex items-center justify-center gap-2 text-sm"
                    data-testid="bulk-day-before-sms-send-btn">
                    <Calendar className="h-4 w-4" />
                    {dayBeforeSmsLoading ? 'Sending...' : `Send Reminder SMS to ${dayBeforeSmsSelected.size} Clients`}
                  </button>
                </div>
              )}

              {dayBeforeSmsResult && (
                <div className="space-y-3">
                  {dayBeforeSmsResult.success ? (
                    <>
                      {dayBeforeSmsResult.sent > 0 ? (
                        <div className="bg-[#9A7A3E]/10 border border-[#9A7A3E]/30 rounded-lg p-4 text-center">
                          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-[#7F6632]" />
                          <p className="text-lg font-bold text-[#7F6632]">{dayBeforeSmsResult.sent} Reminder SMS Sent</p>
                          <p className="text-xs text-gray-400 mt-1">Target date: {dayBeforeSmsResult.target_date} · {dayBeforeSmsResult.total_eligible} total internal orders for tomorrow</p>
                        </div>
                      ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                          <AlertTriangle className="h-7 w-7 mx-auto mb-2 text-amber-600" />
                          <p className="text-sm font-bold text-amber-800">No new reminders sent</p>
                          <p className="text-[11px] text-amber-700 mt-1">
                            {dayBeforeSmsResult.skipped_already_sent > 0 && `${dayBeforeSmsResult.skipped_already_sent} already sent today · `}
                            {(dayBeforeSmsResult.skipped_no_phone + dayBeforeSmsResult.skipped_invalid_phone) > 0 && `${dayBeforeSmsResult.skipped_no_phone + dayBeforeSmsResult.skipped_invalid_phone} skipped (invalid/missing phone)`}
                            {dayBeforeSmsResult.total_eligible === 0 && "No eligible appointments found for tomorrow."}
                          </p>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold text-gray-500">{dayBeforeSmsResult.total_eligible || 0}</p><p className="text-[9px] text-gray-400">Orders Found</p></div>
                        <div className="bg-[#6B8F62]/10 rounded-lg p-2"><p className="font-bold text-[#4A7A56]">{dayBeforeSmsResult.sent || 0}</p><p className="text-[9px] text-gray-500">Sent OK</p></div>
                        <div className="bg-[#C07070]/10 rounded-lg p-2"><p className="font-bold text-[#C07070]">{dayBeforeSmsResult.failed || 0}</p><p className="text-[9px] text-[#C07070]">Provider Failed</p></div>
                        <div className="bg-amber-50 rounded-lg p-2"><p className="font-bold text-amber-700">{dayBeforeSmsResult.skipped_no_phone || 0}</p><p className="text-[9px] text-amber-700">No Phone</p></div>
                        <div className="bg-amber-50 rounded-lg p-2"><p className="font-bold text-amber-700">{dayBeforeSmsResult.skipped_invalid_phone || 0}</p><p className="text-[9px] text-amber-700">Invalid Phone</p></div>
                        <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold text-gray-500">{dayBeforeSmsResult.skipped_already_sent || 0}</p><p className="text-[9px] text-gray-400">Already Sent</p></div>
                      </div>
                      {dayBeforeSmsResult.results && dayBeforeSmsResult.results.length > 0 && (
                        <div className="max-h-[160px] overflow-y-auto bg-gray-50 rounded-lg border border-gray-200 text-[10px]">
                          <table className="w-full"><tbody>
                            {dayBeforeSmsResult.results.slice(0, 30).map((r, i) => (
                              <tr key={i} className="border-b border-gray-100">
                                <td className="px-2 py-1 font-medium">{r.order}</td>
                                <td className="px-2 py-1 text-gray-400">{r.phone || ""}</td>
                                <td className={`px-2 py-1 font-bold ${r.status === "sent" ? "text-[#6B8F62]" : r.status === "failed" ? "text-[#C07070]" : "text-gray-400"}`}>{r.status}</td>
                                <td className="px-2 py-1 text-gray-300 truncate max-w-[140px]">{r.error || ""}</td>
                              </tr>
                            ))}
                          </tbody></table>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-[#C07070]/10 border border-[#C07070]/30 rounded-lg p-4 text-center">
                      <AlertCircle className="h-7 w-7 mx-auto mb-2 text-[#C07070]" />
                      <p className="text-sm font-bold text-[#C07070]">{dayBeforeSmsResult.error || 'Failed to send'}</p>
                    </div>
                  )}
                  <button onClick={() => { setDayBeforeSmsOpen(false); setDayBeforeSmsPreview(null); setDayBeforeSmsResult(null); }}
                    className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium rounded-lg text-sm">Close</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Time Correction SMS Modal (one-time batch — UPDATE OF TIME header) */}
        {correctionSmsOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setCorrectionSmsOpen(false); setCorrectionSmsPreview(null); setCorrectionSmsResult(null); }}>
            <div className="bg-white rounded-xl border-2 border-[#C07070] p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bulk-time-correction-sms-modal">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-lg font-bold text-[#C07070] flex items-center gap-1.5">
                    <AlertTriangle className="h-5 w-5" /> Send Time Correction SMS
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">One-time batch · Fixes customers who got the wrong (Safeguard-shifted) time</p>
                </div>
                <button onClick={() => { setCorrectionSmsOpen(false); setCorrectionSmsPreview(null); setCorrectionSmsResult(null); }} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5 text-gray-400" /></button>
              </div>

              <div className="bg-[#C07070]/10 border border-[#C07070]/30 rounded p-2.5 mb-4 text-[11px] text-[#8F4545]">
                <b>Preview of message:</b>
                <pre className="whitespace-pre-wrap font-mono text-[10px] mt-1">{`UPDATE OF TIME

Hello!

Your property inspection appointment has been updated.

Date: [internal date]
Correct Time: [internal time window]

Address: [property address]
Client: [client name]
Order No: [order number]

Please disregard the previous message with the incorrect time.`}</pre>
              </div>

              {correctionSmsLoading && !correctionSmsResult && (
                <div className="flex items-center justify-center py-10"><div className="animate-spin h-6 w-6 border-b-2 border-[#C07070] rounded-full" /></div>
              )}

              {correctionSmsPreview && !correctionSmsResult && (
                <div className="space-y-3">
                  <div className="bg-[#C07070]/10 border border-[#C07070]/30 rounded-lg p-2.5 text-[11px] text-[#8F4545] flex items-start gap-1.5">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>Só envia para clientes com <b>horário Safeguard à tarde (PM)</b> — são os que receberam horário errado no SMS anterior. Clientes com janela Safeguard de manhã são ignorados (já receberam horário certo).</span>
                  </div>
                  <div className="grid grid-cols-5 gap-1.5 text-center text-xs">
                    <div className="bg-[#C07070]/10 rounded-lg p-2"><p className="font-bold text-[#8F4545] text-xl">{correctionSmsPreview.eligible}</p><p className="text-[9px] text-gray-500">Ready (PM)</p></div>
                    <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold text-gray-400 text-xl">{correctionSmsPreview.skipped_not_sg_afternoon || 0}</p><p className="text-[9px] text-gray-400">SG Morning</p></div>
                    <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold text-gray-400 text-xl">{correctionSmsPreview.already_corrected}</p><p className="text-[9px] text-gray-400">Already Done</p></div>
                    <div className="bg-amber-50 rounded-lg p-2"><p className="font-bold text-amber-600 text-xl">{correctionSmsPreview.no_phone}</p><p className="text-[9px] text-gray-500">No Phone</p></div>
                    <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold text-gray-400 text-xl">{correctionSmsPreview.no_time}</p><p className="text-[9px] text-gray-400">No Time</p></div>
                  </div>
                  {correctionSmsPreview.preview?.length > 0 && (
                    <div className="max-h-[220px] overflow-y-auto border border-gray-100 rounded-lg">
                      <table className="w-full text-[10px]">
                        <thead className="bg-gray-50 sticky top-0"><tr>
                          <th className="text-left px-2 py-1 font-semibold text-gray-500">Order</th>
                          <th className="text-left px-2 py-1 font-semibold text-gray-500">SG (wrong)</th>
                          <th className="text-left px-2 py-1 font-semibold text-gray-500">Correct Time</th>
                          <th className="text-left px-2 py-1 font-semibold text-gray-500">Phone</th>
                        </tr></thead>
                        <tbody>
                          {correctionSmsPreview.preview.map((p, i) => (
                            <tr key={i} className="border-b border-gray-50">
                              <td className="px-2 py-1 font-medium text-[#2F5D8A]">{p.order_number}</td>
                              <td className="px-2 py-1 text-[#C07070] line-through">{p.sg_time || "—"}</td>
                              <td className="px-2 py-1 font-bold text-[#2F7D32]">{p.time}</td>
                              <td className="px-2 py-1 text-gray-500">{p.phone}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <button onClick={handleCorrectionSmsSend} disabled={correctionSmsLoading || correctionSmsPreview.eligible === 0}
                    className="w-full py-3 bg-[#C07070] hover:bg-[#A55A5A] text-white font-bold rounded-lg transition-colors disabled:opacity-40 flex items-center justify-center gap-2 text-sm"
                    data-testid="bulk-time-correction-sms-send-btn">
                    <AlertTriangle className="h-4 w-4" />
                    {correctionSmsLoading ? 'Sending correction...' : `Send UPDATE OF TIME to ${correctionSmsPreview.eligible} clients`}
                  </button>
                </div>
              )}

              {correctionSmsResult && (
                <div className="space-y-3">
                  {correctionSmsResult.success ? (
                    <div className="bg-[#C07070]/10 border border-[#C07070]/30 rounded-lg p-4 text-center">
                      <CheckCircle className="h-8 w-8 mx-auto mb-2 text-[#8F4545]" />
                      <p className="text-lg font-bold text-[#8F4545]">{correctionSmsResult.sent} Correction SMS Sent</p>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs mt-3">
                        <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold">{correctionSmsResult.skipped_already_corrected || 0}</p><p className="text-[9px] text-gray-500">Already Done</p></div>
                        <div className="bg-gray-50 rounded-lg p-2"><p className="font-bold">{correctionSmsResult.skipped_no_phone + correctionSmsResult.skipped_invalid_phone || 0}</p><p className="text-[9px] text-gray-500">Bad Phone</p></div>
                        <div className="bg-red-50 rounded-lg p-2"><p className="font-bold text-red-600">{correctionSmsResult.failed || 0}</p><p className="text-[9px] text-red-600">Failed</p></div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-300 rounded-lg p-4 text-center">
                      <AlertCircle className="h-7 w-7 mx-auto mb-2 text-red-600" />
                      <p className="text-sm font-bold text-red-700">{correctionSmsResult.error || 'Failed'}</p>
                    </div>
                  )}
                  <button onClick={() => { setCorrectionSmsOpen(false); setCorrectionSmsPreview(null); setCorrectionSmsResult(null); }}
                    className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium rounded-lg text-sm">Close</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};


// Dispatch Payment Mirror — Read-only view of Finance payment data
const DispatchPaymentMirror = ({ type, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedWeek, setExpandedWeek] = useState(-1);
  const fmtLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const weeks = React.useMemo(() => {
    const today = new Date();
    const daysSinceTue = (today.getDay() + 5) % 7;
    const periods = [];
    for (let i = 0; i < 4; i++) {
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceTue - (i * 7));
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
      periods.push({ start: fmtLocalDate(start), end: fmtLocalDate(end) });
    }
    return periods;
  }, []);
  const [weekData, setWeekData] = useState({});

  const endpoint = type === 'operational' ? 'finance/operational-payment' : 'finance/transmission-payment';
  const title = type === 'operational' ? 'Operational Payment' : 'Transmission Payment';

  const loadWeek = async (idx) => {
    try {
      const res = await axios.get(`${API}/${endpoint}?week_start=${weeks[idx].start}`);
      setWeekData(prev => ({ ...prev, [idx]: res.data }));
    } catch {}
  };

  useEffect(() => { setLoading(false); }, []);

  const fmtRange = (s, e) => {
    if (!s || !e) return "";
    const sd = new Date(s + "T00:00:00");
    const ed = new Date(e + "T00:00:00");
    const opts = { month: "short", day: "numeric" };
    return `${sd.toLocaleDateString("en-US", opts)} – ${ed.toLocaleDateString("en-US", opts)}, ${ed.getFullYear()}`;
  };

  return (
    <div className="space-y-4" data-testid={`dispatch-mirror-${type}`}>
      <div className="flex items-center gap-3">
        <div className="bg-[#B08A3E]/10 border border-[#B08A3E]/25 rounded-lg px-4 py-2 flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 text-[#B08A3E]" />
          <span className="text-xs font-bold text-[#B08A3E] uppercase">Read Only</span>
        </div>
        <p className="text-xs text-gray-400">{title} — mirror from Finance. No editing allowed.</p>
      </div>
      <div className="space-y-3">
        {weeks.map((w, i) => {
          const expanded = expandedWeek === i;
          const wd = weekData[i];
          return (
            <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`mirror-week-${i}`}>
              <button onClick={() => { setExpandedWeek(expanded ? -1 : i); if (!weekData[i]) loadWeek(i); }} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left">
                <p className="text-sm font-semibold text-[#1F2937]">{fmtRange(w.start, w.end)}</p>
                {wd && <span className="text-xs text-gray-500">{type === 'operational' ? `${wd.total_orders || 0} orders — $${(wd.grand_total || 0).toFixed(2)}` : `${wd.total_orders || wd.orders?.length || 0} orders`}</span>}
                {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </button>
              {expanded && wd && (
                <div className="border-t border-gray-200 px-5 py-4">
                  {type === 'operational' ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div className="bg-gray-50 rounded p-3"><span className="text-gray-400 block">Total Orders</span><span className="text-lg font-bold text-[#1F2937]">{wd.total_orders || 0}</span></div>
                        <div className="bg-gray-50 rounded p-3"><span className="text-gray-400 block">Rate/Order</span><span className="text-lg font-bold text-[#B08A3E]">${wd.rate_per_order || 0}</span></div>
                        <div className="bg-gray-50 rounded p-3"><span className="text-gray-400 block">Total</span><span className="text-lg font-bold text-[#6B8F62]">${(wd.grand_total || 0).toFixed(2)}</span></div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">
                      {wd.orders?.length > 0 ? (
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-gray-200 text-[10px] uppercase text-gray-400"><th className="px-3 py-2 text-left">Inspector</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-right">Orders</th><th className="px-3 py-2 text-right">Amount</th></tr></thead>
                          <tbody>{(wd.grouped || wd.orders || []).map((g, idx) => (
                            <tr key={idx} className="border-b border-gray-100"><td className="px-3 py-2 text-gray-800 font-medium">{g.inspector || g.name}</td><td className="px-3 py-2 text-gray-500">{g.date || '—'}</td><td className="px-3 py-2 text-right">{g.count || g.total_orders || 0}</td><td className="px-3 py-2 text-right font-bold text-[#B08A3E]">${(g.amount || g.total_final || 0).toFixed(2)}</td></tr>
                          ))}</tbody>
                        </table>
                      ) : <p className="text-center py-4 text-gray-400">No data for this week.</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


// Dispatch Page
const DispatchPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inspectors, setInspectors] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('due_date');
  const [sortDir, setSortDir] = useState('desc');
  const [activeTab, setActiveTab] = useState('all');
  const [message, setMessage] = useState(null);
  const [dispatchView, setDispatchView] = useState('orders'); // orders | transmission | inspector-orders | operational-payment | transmission-payment
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBatchAssign, setShowBatchAssign] = useState(false);
  const [inspectorStatuses, setInspectorStatuses] = useState({});
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [showManualFollowUp, setShowManualFollowUp] = useState(false);
  const [showManualOrder, setShowManualOrder] = useState(false);
  const [manualOrderForm, setManualOrderForm] = useState({
    order_number: '', company: 'Safeguard', client: '', order_type: '', address: '',
    city: '', state: '', zip_code: '', assigned_inspector: '', route_date: new Date().toISOString().slice(0, 10), notes: ''
  });
  const [manualOrderSaving, setManualOrderSaving] = useState(false);
  const [mfuForm, setMfuForm] = useState({ address: '', city: '', state: '', zip_code: '', assigned_inspector: '', notes: '', original_order_number: '' });
  const [mfuSaving, setMfuSaving] = useState(false);
  const [holdOrders, setHoldOrders] = useState([]);
  const [holdFilter, setHoldFilter] = useState({ inspector: '', search: '' });
  const [showBatchHold, setShowBatchHold] = useState(false);
  const [batchHoldForm, setBatchHoldForm] = useState({ hold_reason: '', hold_until_date: '', hold_notes: '', office_paid: false });
  const [batchHoldSaving, setBatchHoldSaving] = useState(false);
  const [showForceEOD, setShowForceEOD] = useState(false);
  const [forceEODProcessing, setForceEODProcessing] = useState(false);
  const [allActiveOrders, setAllActiveOrders] = useState([]);
  // Single source of truth for the Dispatch inspector-card "In Route" count.
  // Driven by GET /api/dispatch/inspector-active-counts (V4) — applies the
  // IDENTICAL filter as the per-inspector orders endpoint and excludes Hold
  // so the outside card matches the inside Active list exactly.
  const [inspectorActiveCounts, setInspectorActiveCounts] = useState({});
  // Per-inspector route validation snapshot for the tiny ✓/✗ card indicator.
  // Aggregate read from GET /api/dispatch/inspector-route-status.
  const [inspectorRouteStatus, setInspectorRouteStatus] = useState({});
  const [unassignedReviewOrders, setUnassignedReviewOrders] = useState([]);
  const [dailyMetrics, setDailyMetrics] = useState(null);
  const [showOpsSummary, setShowOpsSummary] = useState(false);
  const [metricsDate, setMetricsDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [metricsView, setMetricsView] = useState('today'); // today | week
  // P1-5 (Angela 2026-05-17) — All Orders modal state. Modal in-place
  // (no route change). Search across all orders, reassign inspector,
  // move to queue (Priority / Admin Review / Manual Review / Returns).
  const [showAllOrdersModal, setShowAllOrdersModal] = useState(false);

  const isAdmin = user?.role === 'Admin';
  const isNationalTransmission = user?.role === 'National Transmission';
  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dispatch/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllActiveOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dispatch/orders?view=all`);
      setAllActiveOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch all active orders:', error);
    }
  }, []);

  const fetchInspectors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inspectors`);
      // Angela 2026-04-28: hide Inactive inspectors from Dispatch cards/lists
      // and selectors.  Records still exist in Team profile for history.
      const list = (response.data || []).filter(i => i?.onboarding_status !== 'Inactive');
      setInspectors(list);
    } catch (error) {
      console.error('Failed to fetch inspectors:', error);
    }
  }, []);

  const fetchInspectorStatuses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dispatch/inspector-status`);
      setInspectorStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch inspector statuses:', error);
    }
  }, []);

  const fetchHoldOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dispatch/hold-orders`);
      setHoldOrders(res.data);
    } catch { setHoldOrders([]); }
  }, []);

  const fetchUnassignedReview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dispatch/unassigned-review`);
      setUnassignedReviewOrders(res.data);
    } catch { setUnassignedReviewOrders([]); }
  }, []);

  const fetchDailyMetrics = useCallback(async (date) => {
    try {
      const res = await axios.get(`${API}/dispatch/daily-metrics?date=${date || metricsDate}`);
      setDailyMetrics(res.data);
    } catch { setDailyMetrics(null); }
  }, [metricsDate]);

  // Fetches the per-inspector ACTIVE count for TODAY. Uses the V4 aggregate
  // (`/api/dispatch/inspector-active-counts`) which applies the IDENTICAL
  // filter as the per-inspector orders endpoint AND explicitly excludes Hold
  // orders — guaranteeing the outside Dispatch card "In Route" number matches
  // the inside inspector-page "Active" count exactly.
  const fetchInspectorActiveCounts = useCallback(async () => {
    try {
      // Angela 2026-05-10 v3.6 — FIELD FIX: switched from V5 (route-counts,
      // includes Hold) to V4 (active-counts, excludes Hold). Aligns the
      // Office Dispatch card with the inspector active view and matches the
      // documented Apr 21 2026 single-source-of-truth design.
      const res = await axios.get(`${API}/dispatch/inspector-active-counts`);
      setInspectorActiveCounts(res.data?.counts || {});
    } catch { setInspectorActiveCounts({}); }
  }, []);

  // Ensure the outside card number never drifts from the inside inspector
  // page number. Any time fetchOrders runs (after assign, unassign, route
  // validate, bulk transfer, daily-close, hold, etc.), also refresh the
  // per-inspector active counts so the card re-syncs with the page.
  useEffect(() => {
    fetchInspectorActiveCounts();
  }, [orders, fetchInspectorActiveCounts]);

  // Per-inspector ✓/✗ badge on Inspector Availability cards.  Reads from the
  // latest spreadsheet route-assignment upload (Angela 2026-04-28 strict
  // rule: ✅ only when uploaded count == DB count).  Read-only.
  const fetchInspectorRouteStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dispatch/route-upload-status`);
      setInspectorRouteStatus(res.data?.statuses || {});
    } catch { setInspectorRouteStatus({}); }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchAllActiveOrders();
    fetchInspectors();
    fetchInspectorStatuses();
    fetchHoldOrders();
    fetchUnassignedReview();
    fetchDailyMetrics();
    fetchInspectorActiveCounts();
    fetchInspectorRouteStatus();
  }, [fetchOrders, fetchAllActiveOrders, fetchInspectors, fetchInspectorStatuses, fetchHoldOrders, fetchUnassignedReview, fetchDailyMetrics, fetchInspectorActiveCounts, fetchInspectorRouteStatus]);

  // Deep-link support (Apr 21 2026): after the Validate Route → Open Order →
  // Assign flow, the Order page redirects back to /dispatch?inspector=...&validate=1.
  // This restores the inspector view and opens Validate Route again so the
  // office stays in the Missing-list correction flow.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const insp = params.get('inspector');
    if (insp) {
      setSelectedInspector(insp);
      setDispatchView('inspector-orders');
      // Clean the URL so reloading doesn't re-trigger the jump.
      const clean = location.pathname + (params.get('validate') === '1' ? '' : '');
      window.history.replaceState({}, '', clean);
    }
    // Intentionally only runs once on mount / when location.search changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else { setSortBy(key); setSortDir('desc'); }
  };

  const todayStr = useMemo(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const isInternal = (o) => o.flow_type === 'Internal';
  const isDueTodayOrOverdue = (o) => o.due_date && o.due_date <= todayStr;

  const dispatchTabs = [
    { id: 'all', label: 'All Dispatch' },
    { id: 'all_active', label: 'All Active Orders' },
    { id: 'regular', label: 'Regular Orders' },
    { id: 'appointment_set', label: 'Appointment Set' },
    { id: 'external_dispatched', label: 'External Dispatched' },
    { id: 'phot_queue', label: 'PHOT Queue' },
    { id: 'unmapped_workcode', label: 'Unmapped Workcode' },
    { id: 'follow_up', label: 'Follow-Up' },
  ];

  const tabCounts = useMemo(() => {
    const regular = orders.filter(o => o.flow_type === 'Regular' && o.appointment_set !== 'yes').length;
    const appointment_set = orders.filter(o => o.appointment_set === 'yes').length;
    const external_dispatched = orders.filter(o => o.flow_type === 'External' && o.external_scheduling_complete === 'yes').length;
    // ═══ Open Follow-Up queue (Apr 21 2026 — STRICT RULE) ═══
    // The Follow-Up queue shows ONLY truly open, actionable follow-ups.
    // Everything that's already been handled (Paid, QA, QA Approved,
    // Completed, EOD Closed, Transmitted, Archived) is NOT a follow-up —
    // it's historical/handled. PHOT is its own workflow and never enters
    // the Follow-Up queue.
    const FOLLOWUP_TERMINAL = [
      'Paid', 'QA', 'QA Approved', 'Completed',
      'EOD Closed', 'Transmitted', 'Completed Transmitted', 'Archived',
    ];
    const isFollowUpOrder = (o) => {
      if (!o) return false;
      if (o.follow_up_resolved === true) return false;
      if (o.flow_type === 'PHOT') return false;
      if (FOLLOWUP_TERMINAL.includes(o.status)) return false;
      if (o.active_order === 'no') return false;
      // Strict rule (aligned with backend Angela 2026-05-03):
      // Follow-Up tab/card shows ONLY orders sent through Daily Close
      // Follow-Up (dc_follow_up === true). Legacy flags (follow_up,
      // manual_follow_up, flow_type='Follow-Up', status='Follow Up'/
      // 'Follow-Up', finance_reopened) are no longer auto-included.
      return o.dc_follow_up === true;
    };
    const follow_up = orders.filter(isFollowUpOrder).length;
    // PHOT and Unmapped Workcode — moved here from Scheduling (Angela 2026-04-27).
    // PHOT = order_type/workcode contains PHOT.
    // Unmapped = workcode starts with SIFI but is neither SIFI nor SIFINT (e.g., SIFIH6).
    const isPhotOrder = (o) => {
      const ot = String(o.order_type || '').toUpperCase();
      const wc = String(o.workcode || '').toUpperCase();
      return ot.includes('PHOT') || wc.includes('PHOT');
    };
    const isUnmappedOrder = (o) => {
      if (isPhotOrder(o)) return false;
      const ot = String(o.order_type || '').trim().toUpperCase();
      if (!ot) return false;
      if (ot === 'SIFINT' || ot === 'SIFI') return false;
      return ot.startsWith('SIFI');
    };
    const phot_queue = orders.filter(o =>
      isPhotOrder(o)
      && o.active_order !== 'no' && o.removed_from_source !== 'yes'
      && !['Archived','Paid','Completed'].includes(o.status || '')
    ).length;
    const unmapped_workcode = orders.filter(o =>
      (o.company || '').toLowerCase() === 'safeguard'
      && isUnmappedOrder(o)
      && o.active_order !== 'no' && o.removed_from_source !== 'yes'
      && !['Archived','Paid','Completed'].includes(o.status || '')
    ).length;
    return {
      all: regular + appointment_set + external_dispatched,
      all_active: allActiveOrders.length,
      regular,
      appointment_set,
      external_dispatched,
      follow_up,
      unassigned_review: unassignedReviewOrders.length,
      phot_queue,
      unmapped_workcode,
    };
  }, [orders, allActiveOrders, unassignedReviewOrders]);

  const opsCounts = useMemo(() => {
    if (dailyMetrics) {
      return {
        assigned: dailyMetrics.active?.assigned || 0,
        in_field_inspectors: dailyMetrics.active?.in_field_inspectors || 0,
        completed: dailyMetrics.today?.completed || 0,
        returned: dailyMetrics.today?.returned || 0,
        no_show: dailyMetrics.today?.no_show || 0,
        follow_up: (dailyMetrics.today?.follow_up_inspector || 0) + (dailyMetrics.today?.follow_up_safeguard || 0),
        total_closed: dailyMetrics.today?.total_closed || 0,
        unassigned_review: dailyMetrics.active?.unassigned_review || 0,
      };
    }
    // Fallback to static counts
    const assigned = orders.filter(o => o.assigned_inspector && o.assigned_inspector.trim()).length;
    const inFieldInspectors = Object.values(inspectorStatuses).filter(s => s === 'In Route').length;
    const completed = orders.filter(o => o.status === 'Inspection Completed').length;
    const returned = orders.filter(o => o.status === 'Returned').length;
    return { assigned, in_field_inspectors: inFieldInspectors, completed, returned, no_show: 0, follow_up: 0, total_closed: 0, unassigned_review: unassignedReviewOrders.length };
  }, [orders, inspectorStatuses, dailyMetrics, unassignedReviewOrders]);

  // Build inspector list: ONLY registered inspectors from team registry
  const allInspectors = useMemo(() => {
    return [...inspectors];
  }, [inspectors]);

  const filteredOrders = useMemo(() => {
    // ═══════════════════════════════════════════════════════════════════
    // Dispatch Search Parity with Scheduling → All Orders (Apr 22 2026).
    // When a search term is present, operate against the BROADER pool
    // (allActiveOrders — same as /api/dispatch/orders?view=all) instead
    // of the tab-restricted operational slice. Same search fields the
    // /api/orders/all-company endpoint uses: order_number, address,
    // city, cliente. Guarantees "the order exists in the system" ==
    // "the search finds it", mirroring Scheduling All Orders behavior.
    // ═══════════════════════════════════════════════════════════════════
    const searchTerm = (filters.search || '').trim().toLowerCase();
    const matchesSearch = (o) => {
      if (!searchTerm) return true;
      return (
        (o.order_number || '').toLowerCase().includes(searchTerm) ||
        (o.address || '').toLowerCase().includes(searchTerm) ||
        (o.city || '').toLowerCase().includes(searchTerm) ||
        (o.cliente || '').toLowerCase().includes(searchTerm)
      );
    };

    // Search overrides the tab filter. This mirrors the Scheduling
    // All-Orders behavior where typing a query returns every match
    // regardless of which sub-filter was previously selected.
    if (searchTerm) {
      let result = [...(allActiveOrders && allActiveOrders.length ? allActiveOrders : orders)];
      if (filters.assigned_inspector) result = result.filter(o => o.assigned_inspector === filters.assigned_inspector);
      result = result.filter(matchesSearch);
      result.sort((a, b) => {
        let aVal = a[sortBy] || ''; let bVal = b[sortBy] || '';
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      return result;
    }

    // "All Active Orders" tab uses allActiveOrders data source
    if (activeTab === 'all_active') {
      let result = [...allActiveOrders];
      if (filters.assigned_inspector) result = result.filter(o => o.assigned_inspector === filters.assigned_inspector);
      result.sort((a, b) => {
        let aVal = a[sortBy] || ''; let bVal = b[sortBy] || '';
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      return result;
    }

    let result = [...orders];
    if (activeTab === 'all') {
      result = result.filter(o => o.flow_type === 'Regular' || o.appointment_set === 'yes' || (o.flow_type === 'External' && o.external_scheduling_complete === 'yes'));
    } else if (activeTab === 'regular') {
      result = result.filter(o => o.flow_type === 'Regular' && o.appointment_set !== 'yes');
    } else if (activeTab === 'appointment_set') {
      result = result.filter(o => o.appointment_set === 'yes');
    } else if (activeTab === 'external_dispatched') {
      result = result.filter(o => o.flow_type === 'External' && o.external_scheduling_complete === 'yes');
    } else if (activeTab === 'follow_up') {
      // STRICT RULE (Angela 2026-05-03 — Dispatch P0 bundle):
      // Follow-Up tab mirrors isFollowUpOrder: dc_follow_up === true only.
      // Legacy flags (follow_up, manual_follow_up, flow_type='Follow-Up',
      // status='Follow Up'/'Follow-Up', finance_reopened) are no longer
      // matched here. PHOT / resolved / inactive still excluded.
      const FOLLOWUP_TERMINAL = [
        'Paid', 'QA', 'QA Approved', 'Completed',
        'EOD Closed', 'Transmitted', 'Completed Transmitted', 'Archived',
      ];
      result = result.filter(o => {
        if (!o) return false;
        if (o.follow_up_resolved === true) return false;
        if (o.flow_type === 'PHOT') return false;
        if (FOLLOWUP_TERMINAL.includes(o.status)) return false;
        if (o.active_order === 'no') return false;
        return o.dc_follow_up === true;
      });
    } else if (activeTab === 'phot_queue') {
      // PHOT — moved here from Scheduling (Angela 2026-04-27).
      result = result.filter(o => {
        const ot = String(o.order_type || '').toUpperCase();
        const wc = String(o.workcode || '').toUpperCase();
        if (!(ot.includes('PHOT') || wc.includes('PHOT'))) return false;
        if (o.active_order === 'no' || o.removed_from_source === 'yes') return false;
        if (['Archived','Paid','Completed'].includes(o.status || '')) return false;
        return true;
      });
    } else if (activeTab === 'unmapped_workcode') {
      // Unmapped Workcode (e.g., SIFIH6) — moved here from Scheduling.
      result = result.filter(o => {
        if ((o.company || '').toLowerCase() !== 'safeguard') return false;
        const ot = String(o.order_type || '').trim().toUpperCase();
        if (!ot) return false;
        const wc = String(o.workcode || '').toUpperCase();
        if (ot.includes('PHOT') || wc.includes('PHOT')) return false;
        if (ot === 'SIFINT' || ot === 'SIFI') return false;
        if (!ot.startsWith('SIFI')) return false;
        if (o.active_order === 'no' || o.removed_from_source === 'yes') return false;
        if (['Archived','Paid','Completed'].includes(o.status || '')) return false;
        return true;
      });
    } else if (activeTab === 'unassigned_review') {
      // Use the dedicated unassigned review data
      result = [...unassignedReviewOrders];
    }

    if (filters.assigned_inspector) result = result.filter(o => o.assigned_inspector === filters.assigned_inspector);
    result.sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [orders, allActiveOrders, unassignedReviewOrders, activeTab, filters, sortBy, sortDir, todayStr]);

  const dispatchColumns = [
    { key: 'due_date', label: 'Due Date', sortable: true, render: (v) => formatDateDisplay(v) },
    { key: 'order_number', label: 'Order #', sortable: true, render: (v, o) => (
      <div className="flex items-center gap-1.5">
        <span>{v}</span>
        {o.returned === true && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#E8D4D4] text-[#7A3B3B] whitespace-nowrap" data-testid="returned-flag">RETURNED</span>}
      </div>
    )},
    { key: 'company', label: 'Company', sortable: true },
    { key: 'order_type', label: 'Type', sortable: true, render: (v, o) => (
      <div className="flex items-center gap-1.5">
        <span>{v || '-'}</span>
        {o.manual_follow_up && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#DCE4ED] text-[#2F5D8A] whitespace-nowrap">MFU</span>}
      </div>
    )},
    { key: 'flow_type', label: 'Flow', sortable: true },
    { key: 'cliente', label: 'Client', sortable: true },
    { key: 'address', label: 'Address', sortable: true },
    { key: 'city', label: 'City', sortable: true },
    { key: 'state', label: 'State', sortable: true },
    { key: 'appointment_set', label: 'Appt', sortable: true, render: (v) => v === 'yes' ? <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#EFF5ED] text-[#4A7A56] whitespace-nowrap">APPT</span> : null },
    { key: 'assigned_inspector', label: 'Inspector', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (v, o) => {
        const isFlagged = (o.external_morning_call_required || '').toLowerCase() === 'yes' && !o.external_morning_call_completed_at;
        const isDone = !!o.external_morning_call_completed_at;
        return (
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <span>{v || '-'}</span>
            {isFlagged && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#FFF7ED] text-[#B45309] border border-[#FDBA74]" title="Flagged for Morning Call"><Phone className="h-2.5 w-2.5" />MC</span>}
            {isDone && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#EFF5ED] text-[#4A7A56]" title="Morning Call done"><CheckCircle className="h-2.5 w-2.5" />MC</span>}
          </span>
        );
      },
    },
  ];

  const handleSelectToggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedIds(new Set(filteredOrders.map(o => o.id)));
    else setSelectedIds(new Set());
  };

  const handleShiftSelect = (rangeIds) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      rangeIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleQuickSelect = (count) => {
    setSelectedIds(new Set(filteredOrders.slice(0, count).map(o => o.id)));
  };

  const toggleNextDayExternalCall = async (order) => {
    const currentlyFlagged = (order.external_morning_call_required || '').toLowerCase() === 'yes' && !order.external_morning_call_completed_at;
    try {
      await axios.patch(`${API}/orders/${order.id}/external-morning-call`, {
        required: !currentlyFlagged,
        reason: currentlyFlagged ? '' : 'Flagged from Dispatch — next-morning call required',
      });
      // Optimistic update — reflect change in local state immediately.
      setOrders(prev => prev.map(o => o.id === order.id ? {
        ...o,
        external_morning_call_required: !currentlyFlagged ? 'yes' : 'no',
        external_morning_call_completed_at: !currentlyFlagged ? '' : o.external_morning_call_completed_at,
      } : o));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update Morning Call flag');
    }
  };

  const handleBatchAssignInspector = async (inspectorName) => {
    try {
      const res = await axios.post(`${API}/dispatch/batch-assign-inspector`, {
        inspector_name: inspectorName,
        order_ids: Array.from(selectedIds),
      });
      setMessage({ type: 'success', text: `${res.data.assigned} orders assigned to ${inspectorName}` });
      setSelectedIds(new Set());
      setShowBatchAssign(false);
      fetchOrders();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to assign orders' });
    }
  };

  const handleToggleInspectorStatus = async (name) => {
    const current = inspectorStatuses[name] || "Not Available";
    const newStatus = current === "In Route" ? "Not Available" : "In Route";
    try {
      await axios.put(`${API}/dispatch/inspector-status/${encodeURIComponent(name)}`, { status: newStatus });
      setInspectorStatuses(prev => ({ ...prev, [name]: newStatus }));
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleCreateManualFollowUp = async () => {
    if (!mfuForm.address.trim() || !mfuForm.city.trim()) {
      setMessage({ type: 'error', text: 'Address and City are required' });
      return;
    }
    setMfuSaving(true);
    try {
      await axios.post(`${API}/orders/manual-followup`, {
        ...mfuForm,
        company: 'Safeguard',
      });
      setMessage({ type: 'success', text: 'Manual Follow-Up order created successfully' });
      setShowManualFollowUp(false);
      setMfuForm({ address: '', city: '', state: '', zip_code: '', assigned_inspector: '', notes: '', original_order_number: '' });
      fetchOrders();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create manual follow-up' });
    } finally { setMfuSaving(false); }
  };

  const handleCreateManualOrder = async () => {
    if (!manualOrderForm.order_number.trim()) { setMessage({ type: 'error', text: 'Order ID is required' }); return; }
    setManualOrderSaving(true);
    try {
      const res = await axios.post(`${API}/orders/manual-entry`, manualOrderForm);
      const msg = res.data.added_to_closing
        ? `Manual order #${res.data.order_number} created and added to Daily Closing`
        : `Manual order #${res.data.order_number} created`;
      setMessage({ type: 'success', text: msg });
      setShowManualOrder(false);
      setManualOrderForm({ order_number: '', company: 'Safeguard', client: '', order_type: '', address: '', city: '', state: '', zip_code: '', assigned_inspector: '', route_date: new Date().toISOString().slice(0, 10), notes: '' });
      fetchOrders();
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create manual order' }); }
    setManualOrderSaving(false);
  };


  const handleReleaseHold = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/release-hold`);
      setMessage({ type: 'success', text: 'Order released from hold' });
      fetchHoldOrders();
      fetchOrders();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to release hold' });
    }
  };

  const handleBatchHold = async () => {
    if (!batchHoldForm.hold_reason || !batchHoldForm.hold_until_date) {
      setMessage({ type: 'error', text: 'Hold reason and Hold Until Date are required' });
      return;
    }
    setBatchHoldSaving(true);
    setMessage(null);
    let success = 0;
    const ids = Array.from(selectedIds);
    for (const id of ids) {
      try {
        await axios.post(`${API}/orders/${id}/hold`, batchHoldForm);
        success++;
      } catch { /* skip failed */ }
    }
    setBatchHoldSaving(false);
    setShowBatchHold(false);
    setBatchHoldForm({ hold_reason: '', hold_until_date: '', hold_notes: '', office_paid: false });
    setSelectedIds(new Set());
    setMessage({ type: 'success', text: `${success} of ${ids.length} order(s) placed on hold` });
    fetchOrders();
    fetchHoldOrders();
  };

  const handleForceEndOfDay = async () => {
    setForceEODProcessing(true);
    try {
      const res = await axios.post(`${API}/orders/force-remove-assignment`, {
        order_ids: Array.from(selectedIds),
        comment: "End of Day Reset",
      });
      setMessage({ type: 'success', text: `Assignments removed from ${res.data.processed} orders` });
      setSelectedIds(new Set());
      setShowForceEOD(false);
      fetchOrders();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to remove assignments' });
    } finally {
      setForceEODProcessing(false);
    }
  };

  const filteredHoldOrders = useMemo(() => {
    let result = [...holdOrders];
    if (holdFilter.inspector) result = result.filter(o => o.assigned_inspector === holdFilter.inspector);
    if (holdFilter.search) {
      const term = holdFilter.search.toLowerCase();
      result = result.filter(o =>
        (o.order_number || '').toLowerCase().includes(term) ||
        (o.address || '').toLowerCase().includes(term) ||
        (o.city || '').toLowerCase().includes(term) ||
        (o.hold_reason || '').toLowerCase().includes(term)
      );
    }
    return result;
  }, [holdOrders, holdFilter]);

  const holdByInspector = useMemo(() => {
    const grouped = {};
    filteredHoldOrders.forEach(o => {
      const name = o.assigned_inspector || 'Unassigned';
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push(o);
    });
    return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredHoldOrders]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="dispatch-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{isNationalTransmission ? 'National Dispatch' : 'Dispatch'}</h1>
            <p className="text-gray-500 text-sm">{dispatchView === 'orders' ? `${filteredOrders.length} orders` : dispatchView === 'inspector-orders' ? `Inspector: ${selectedInspector}` : dispatchView === 'operational-payment' ? 'Operational Payment (read-only)' : dispatchView === 'transmission-payment' ? 'Transmission Payment (read-only)' : 'Transmission tracking'}</p>
          </div>
          <div className="flex gap-1.5">
            {dispatchView !== 'inspector-orders' && (
              <>
                <button
                  onClick={() => setDispatchView('orders')}
                  className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-all ${dispatchView === 'orders' ? 'bg-[#D1A054] text-[#1F2937]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  data-testid="dispatch-view-orders"
                >
                  <Truck className="h-3.5 w-3.5" />
                  Orders
                </button>
              </>
            )}
            {dispatchView !== 'inspector-orders' && !isNationalTransmission && (
              <button
                onClick={() => setShowAllOrdersModal(true)}
                className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-all bg-[#2F5D8A] text-white hover:bg-[#264A6E]"
                data-testid="dispatch-all-company-orders-btn"
              >
                <Database className="h-3.5 w-3.5" />
                All Company Orders
              </button>
            )}
            {dispatchView !== 'inspector-orders' && !isNationalTransmission && (
              <a
                href="https://inspector-academy.preview.emergentagent.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-all bg-[#2F5D8A] text-white hover:bg-[#264A6E]"
                title="Access inspector training and guidelines"
                data-testid="dispatch-training-btn"
              >
                <GraduationCap className="h-3.5 w-3.5" />
                Training
              </a>
            )}
          </div>
        </div>

        {dispatchView === 'inspector-orders' && selectedInspector ? (
          <InspectorOrdersPage
            inspectorName={selectedInspector}
            onBack={() => { setDispatchView('orders'); setSelectedInspector(null); }}
            userEmail={user?.email}
            autoOpenValidateRoute={new URLSearchParams(location.search).get('validate') === '1'}
          />
        ) : (
          <>
            {message && (
              <div className={`p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]' : 'bg-[#F5EDED]0/10 border border-[#C07070]/30 text-[#8B4545]'}`}>
                {message.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                <span className="text-sm">{message.text}</span>
                <button onClick={() => setMessage(null)} className="ml-auto"><X className="h-4 w-4" /></button>
              </div>
            )}

            {/* CRITICAL FIX #2 — Returned Orders queue (Angela 2026-05-16).
                Sits at the very top of /dispatch, above the focus cards.
                Hidden when count==0 only for the counter banner; the section
                itself is always rendered with an empty-state message. */}
            {!isNationalTransmission && <DispatchReturnsSection />}

            {/* TOP CARDS — 3 big focus cards (per user spec Apr 21, 2026).
                Other tabs (All Active / Regular / Appointment / External)
                moved to compact filter pills above the table. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dispatch-cards">
              <button
                onClick={() => setActiveTab('all')}
                className={`group text-left rounded-xl border-2 p-4 transition-all shadow-sm hover:shadow-md ${activeTab === 'all' ? 'border-[#2F5D8A] bg-[#2F5D8A]/5' : 'border-gray-200 bg-white hover:border-[#2F5D8A]/40'}`}
                data-testid="dispatch-card-all"
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg bg-[#2F5D8A]/10 text-[#2F5D8A] flex items-center justify-center"><Truck className="h-5 w-5" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase font-bold text-[#2F5D8A]/70 tracking-wider">All Orders Available</p>
                    <p className="text-[11px] text-gray-500">ready to assign</p>
                  </div>
                  <p className="text-3xl font-bold text-[#1F2937]">{tabCounts.all}</p>
                </div>
              </button>
              <button
                onClick={() => navigate('/scheduling/all-orders')}
                className="group text-left rounded-xl border-2 border-gray-200 hover:border-[#6B5B8D]/50 bg-white p-4 transition-all shadow-sm hover:shadow-md"
                data-testid="dispatch-card-archive"
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg bg-[#6B5B8D]/10 text-[#6B5B8D] flex items-center justify-center"><List className="h-5 w-5" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase font-bold text-[#6B5B8D]/80 tracking-wider">Company Full Archive</p>
                    <p className="text-[11px] text-gray-500">all orders · all departments</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-[#6B5B8D]" />
                </div>
              </button>
              <button
                onClick={() => setActiveTab('follow_up')}
                className={`group text-left rounded-xl border-2 p-4 transition-all shadow-sm hover:shadow-md ${activeTab === 'follow_up' ? 'border-[#B08A3E] bg-[#B08A3E]/8' : 'border-gray-200 bg-white hover:border-[#B08A3E]/50'}`}
                data-testid="dispatch-card-follow-up"
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg bg-[#B08A3E]/12 text-[#B08A3E] flex items-center justify-center"><AlertCircle className="h-5 w-5" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase font-bold text-[#B08A3E] tracking-wider">Follow-Up</p>
                    <p className="text-[11px] text-gray-500">needs action</p>
                  </div>
                  <p className="text-3xl font-bold text-[#1F2937]">{tabCounts.follow_up}</p>
                </div>
              </button>
            </div>

            {/* ═════ REORDER (Apr 21, 2026): Inspector Availability now directly
                below top cards. Ops Summary collapsible. Manual buttons pulled
                out of the filter bar into a dedicated action row. ═════ */}

            {/* Sprint B-3 (Angela 2026-05-18): Follow-up queue with photo review */}
            <DispatchFollowupQueueWidget />

            {/* Inspector Status Panel */}
            <div data-testid="inspector-status-panel" className="bg-[#F5F2EC] rounded-xl border border-[#E5DFD3] p-3.5">
              <div className="flex items-center justify-between mb-2.5 px-0.5">
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Inspector Availability</h2>
                <button
                  onClick={() => setShowOpsSummary(v => !v)}
                  className="text-[10px] uppercase font-bold text-gray-400 hover:text-[#2F5D8A] tracking-wider flex items-center gap-1"
                  data-testid="toggle-ops-summary"
                >
                  {showOpsSummary ? 'Hide Ops Summary' : 'Show Ops Summary'}
                  <ChevronRight className={`h-3 w-3 transition-transform ${showOpsSummary ? 'rotate-90' : ''}`} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[...allInspectors]
                  // Sort: In Route first, then Off (stable by name within each bucket).
                  // Purely visual — no logic, no count, no click behavior changes.
                  .sort((a, b) => {
                    const aRoute = (inspectorStatuses[a.name] || "Not Available") === "In Route";
                    const bRoute = (inspectorStatuses[b.name] || "Not Available") === "In Route";
                    if (aRoute !== bRoute) return aRoute ? -1 : 1;
                    return (a.name || "").localeCompare(b.name || "");
                  })
                  .map(insp => {
                  const status = inspectorStatuses[insp.name] || "Not Available";
                  const isRoute = status === "In Route";
                  // ═══ SINGLE SOURCE OF TRUTH (Apr 21 2026) ═══
                  // The outside Dispatch card "In Route" number MUST match the
                  // inside inspector-page "X active orders" header exactly.
                  // Both now read from the same backend aggregate
                  // (GET /api/dispatch/inspector-active-counts), which uses
                  // the IDENTICAL filter as GET /api/dispatch/inspector-orders/{name}.
                  // Do NOT reintroduce a frontend-side filter here — it drifts.
                  const inRouteCount = inspectorActiveCounts?.[insp.name] || 0;
                  const byInsp = dailyMetrics?.by_inspector?.[insp.name] || {};
                  // LIVE RULE (Apr 21 2026, per user spec): while inspector is
                  // still "In Route" (route not yet closed for the day), do NOT
                  // surface daily-close data on the Dispatch card — those are
                  // premature and produce false 100% when e.g. one order was
                  // daily-closed mid-day. Only show final completion numbers
                  // AFTER the inspector toggles off-route (route closed).
                  // Fixes Cesar showing "Completed Today = 1 / 100%" while
                  // still working the field with 21+ active stops.
                  const completedToday = isRoute ? 0 : (byInsp.completed || 0);
                  const totalToday = isRoute ? 0 : (byInsp.total || 0);
                  const pct = totalToday > 0 ? Math.round((completedToday / totalToday) * 100) : 0;
                  const pctColor = pct >= 75 ? 'text-[#6B8F62]' : pct >= 40 ? 'text-[#B08A3E]' : 'text-gray-400';
                  return (
                    <div
                      key={insp.id}
                      // ═══ Full-card click opens the inspector page (Apr 21 2026).
                      // The "View" button was removed per user spec — the entire
                      // tile is now the primary click target. Inner controls
                      // (status dot + pill) still stopPropagation to handle
                      // their own toggles.
                      role="button"
                      tabIndex={0}
                      onClick={() => { setSelectedInspector(insp.name); setDispatchView('inspector-orders'); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedInspector(insp.name);
                          setDispatchView('inspector-orders');
                        }
                      }}
                      className={`rounded-lg border px-4 py-3.5 transition-all cursor-pointer select-none hover:shadow-md hover:ring-1 hover:ring-[#2F5D8A]/30 focus:outline-none focus:ring-2 focus:ring-[#2F5D8A]/40 ${
                        isRoute
                          ? 'bg-white border-[#A7C4A0]/60 shadow-sm'
                          // Off inspectors: subtle muted-red tone for quick visual distinction.
                          : 'bg-[#F7E9E9]/50 border-[#C07070]/30'
                      }`}
                      data-testid={`inspector-row-${insp.name}`}
                    >
                      {/* Header row: status dot + name + status pill */}
                      <div className="flex items-center gap-2.5 mb-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleInspectorStatus(insp.name); }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onTouchEnd={(e) => e.stopPropagation()}
                          className={`h-3 w-3 rounded-full shrink-0 transition-all hover:ring-2 hover:ring-[#2F5D8A]/20 ${
                            isRoute ? 'bg-[#6B8F62]' : 'bg-[#C07070]/60'
                          }`}
                          title={`Toggle status: ${status}`}
                          data-testid={`inspector-status-toggle-${insp.name}`}
                        />
                        <p className={`flex-1 text-sm font-bold truncate flex items-center gap-1.5 ${isRoute ? 'text-[#1F2937]' : 'text-[#6B5353]'}`}>
                          {insp.name}
                          {/* Tiny Validate Route ✓/✗ indicator (Apr 21 2026).
                              ✓ OK  → matched & balanced (missing=0, extra=0, PDF present)
                              ✗ Check → anything else (missing, extra, no PDF, not run).
                              Data source: GET /api/dispatch/inspector-route-status. */}
                          {(() => {
                            const rs = inspectorRouteStatus?.[insp.name];
                            if (!rs) return null;
                            if (rs.ok) {
                              return (
                                <span
                                  className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#4A7A56]"
                                  title={`Validate Route: ${rs.matched} matched, 0 missing, 0 extra`}
                                  data-testid={`route-ok-${insp.name}`}
                                >
                                  <CheckCircle className="h-3 w-3" /> OK
                                </span>
                              );
                            }
                            const reason = !rs.has_pdf
                              ? "No route PDF attached"
                              : `${rs.missing || 0} missing · ${rs.extra || 0} extra`;
                            return (
                              <span
                                className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#8B4545]"
                                title={`Validate Route needs review — ${reason}`}
                                data-testid={`route-check-${insp.name}`}
                              >
                                <X className="h-3 w-3" /> Check
                              </span>
                            );
                          })()}
                        </p>
                        {/* Clickable status toggle — cycles between In Route / Off.
                            Uses the same handler as the status dot. Purely
                            display + live-status write, no route/metrics change.
                            Mobile fix (Angela 2026-05-10 v3.5): the parent card
                            is also clickable (opens inspector orders). On iOS
                            Safari, touchend/pointerdown can fire on the parent
                            BEFORE click resolves, so we stop propagation on all
                            three events. Also enlarged the tap target to meet
                            iOS 44px guideline so the touch lands on the pill,
                            not the surrounding card. */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleInspectorStatus(insp.name); }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onTouchEnd={(e) => e.stopPropagation()}
                          className={`text-[10px] uppercase font-bold tracking-wider px-3 py-1.5 min-h-[32px] rounded-full flex items-center gap-1 transition-colors touch-manipulation ${
                            isRoute
                              ? 'bg-[#6B8F62]/12 text-[#6B8F62] hover:bg-[#6B8F62]/20 active:bg-[#6B8F62]/30'
                              : 'bg-[#C07070]/15 text-[#8B4545] hover:bg-[#C07070]/25 active:bg-[#C07070]/35'
                          }`}
                          title={`Click to switch — currently ${isRoute ? 'In Route' : 'Off'}`}
                          data-testid={`inspector-status-pill-${insp.name}`}
                        >
                          {isRoute ? 'In Route' : 'Off'}
                          <ChevronDown className="h-2.5 w-2.5 opacity-70" />
                        </button>
                      </div>
                      {/* Metrics row: 3 columns, clearly separated */}
                      <div className={`grid grid-cols-3 gap-3 pt-2.5 border-t ${isRoute ? 'border-gray-100' : 'border-[#C07070]/15'}`}>
                        <div className="flex flex-col items-start">
                          <span className="text-[9px] uppercase font-bold text-gray-400 tracking-wider">In Route</span>
                          <span className={`text-xl font-bold mt-0.5 ${isRoute ? 'text-[#2F5D8A]' : 'text-[#8B4545]/70'}`}>{inRouteCount}</span>
                        </div>
                        <div className="flex flex-col items-start">
                          <span className="text-[9px] uppercase font-bold text-gray-400 tracking-wider">Completed Today</span>
                          <span className="text-xl font-bold text-[#6B8F62] mt-0.5">{completedToday}</span>
                        </div>
                        <div className="flex flex-col items-start">
                          <span className="text-[9px] uppercase font-bold text-gray-400 tracking-wider">% Complete</span>
                          <span className={`text-xl font-bold mt-0.5 ${pctColor}`}>{pct}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {allInspectors.length === 0 && <p className="text-xs text-gray-300 italic">No inspectors found</p>}
              </div>
            </div>

            {/* Manual Action Row — pulled out of the filter bar for visibility */}
            {!isNationalTransmission && (
              <div className="flex flex-wrap items-center gap-2" data-testid="dispatch-manual-actions">
                <button
                  onClick={() => setShowManualFollowUp(true)}
                  className="px-4 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors shadow-sm"
                  data-testid="create-manual-followup-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> Manual Follow-Up
                </button>
                <button
                  onClick={() => setShowManualOrder(true)}
                  className="px-4 py-2 bg-[#B08A3E] hover:bg-[#9A7735] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors shadow-sm"
                  data-testid="create-manual-order-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> Manual Order
                </button>
                {tabCounts.unassigned_review > 0 && (
                  <button
                    onClick={() => setActiveTab('unassigned_review')}
                    className={`ml-auto px-3 py-2 rounded-lg text-[11px] font-bold flex items-center gap-1.5 transition-all ${activeTab === 'unassigned_review' ? 'bg-[#C0392B] text-white' : 'bg-[#C0392B]/10 text-[#C0392B] hover:bg-[#C0392B]/20'}`}
                    data-testid="dispatch-card-unassigned"
                  >
                    <UserX className="h-3.5 w-3.5" /> Unassigned Review ({tabCounts.unassigned_review})
                  </button>
                )}
              </div>
            )}

            {/* Operational Summary — collapsed by default (toggle above) */}
            {showOpsSummary && (
            <div data-testid="dispatch-ops-summary">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Operational Summary</h2>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                    <button onClick={() => { setMetricsView('today'); }} className={`px-3 py-1 rounded text-[10px] font-semibold transition-all ${metricsView === 'today' ? 'bg-white text-[#1F2937] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="metrics-tab-today">Today</button>
                    <button onClick={() => { setMetricsView('week'); }} className={`px-3 py-1 rounded text-[10px] font-semibold transition-all ${metricsView === 'week' ? 'bg-white text-[#1F2937] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="metrics-tab-week">This Week</button>
                  </div>
                  <input type="date" value={metricsDate} onChange={e => { setMetricsDate(e.target.value); fetchDailyMetrics(e.target.value); }}
                    className="px-2 py-1 border border-gray-200 rounded text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                    data-testid="metrics-date-picker"
                  />
                </div>
              </div>

              {metricsView === 'today' ? (
                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                  <div className="bg-white rounded-lg border border-[#6B8F62]/30 px-4 py-3" data-testid="ops-completed">
                    <p className="text-[10px] uppercase text-[#6B8F62] font-semibold tracking-wider">Completed</p>
                    <p className="text-2xl font-bold text-[#6B8F62] mt-0.5">{opsCounts.completed}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#C07070]/30 px-4 py-3" data-testid="ops-returned">
                    <p className="text-[10px] uppercase text-[#8B4545] font-semibold tracking-wider">Returned</p>
                    <p className="text-2xl font-bold text-[#8B4545] mt-0.5">{opsCounts.returned}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#DC2626]/25 px-4 py-3" data-testid="ops-noshow">
                    <p className="text-[10px] uppercase text-[#DC2626] font-semibold tracking-wider">No Show</p>
                    <p className="text-2xl font-bold text-[#DC2626] mt-0.5">{opsCounts.no_show}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#B08A3E]/30 px-4 py-3" data-testid="ops-followup">
                    <p className="text-[10px] uppercase text-[#B08A3E] font-semibold tracking-wider">Follow Up</p>
                    <p className="text-2xl font-bold text-[#B08A3E] mt-0.5">{opsCounts.follow_up}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#2F5D8A]/25 px-4 py-3" data-testid="ops-assigned">
                    <p className="text-[10px] uppercase text-[#2F5D8A] font-semibold tracking-wider">Assigned Active</p>
                    <p className="text-2xl font-bold text-[#2F5D8A] mt-0.5">{opsCounts.assigned}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#D1A054]/30 px-4 py-3" data-testid="ops-in-field">
                    <p className="text-[10px] uppercase text-[#D1A054] font-semibold tracking-wider">In Field</p>
                    <p className="text-2xl font-bold text-[#B08A3E] mt-0.5">{opsCounts.in_field_inspectors}</p>
                    <p className="text-[10px] text-gray-400">inspectors</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 px-4 py-3" data-testid="ops-total-closed">
                    <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">Total Closed</p>
                    <p className="text-2xl font-bold text-[#1F2937] mt-0.5">{opsCounts.total_closed}</p>
                  </div>
                </div>
              ) : (
                /* Weekly History View */
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-100">
                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                        <th className="text-center px-3 py-2 text-[10px] font-semibold text-[#6B8F62] uppercase tracking-wider">Completed</th>
                        <th className="text-center px-3 py-2 text-[10px] font-semibold text-[#8B4545] uppercase tracking-wider">Returned</th>
                        <th className="text-center px-3 py-2 text-[10px] font-semibold text-[#DC2626] uppercase tracking-wider">No Show</th>
                        <th className="text-center px-3 py-2 text-[10px] font-semibold text-[#B08A3E] uppercase tracking-wider">Follow Up</th>
                        <th className="text-center px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyMetrics?.weekly && Object.entries(dailyMetrics.weekly).map(([date, data]) => {
                        const isToday = date === metricsDate;
                        const dayName = new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' });
                        return (
                          <tr key={date} className={`border-b border-gray-50 ${isToday ? 'bg-[#2F5D8A]/[0.04]' : 'hover:bg-gray-50/50'}`}>
                            <td className="px-3 py-2 text-xs">
                              <span className="font-medium text-[#1F2937]">{dayName}</span>
                              <span className="text-gray-400 ml-1.5">{date}</span>
                              {isToday && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-[#2F5D8A]/10 text-[#2F5D8A] text-[9px] font-bold">TODAY</span>}
                            </td>
                            <td className="text-center px-3 py-2 text-xs font-semibold text-[#6B8F62]">{data.completed || '-'}</td>
                            <td className="text-center px-3 py-2 text-xs font-semibold text-[#8B4545]">{data.returned || '-'}</td>
                            <td className="text-center px-3 py-2 text-xs font-semibold text-[#DC2626]">{data.no_show || '-'}</td>
                            <td className="text-center px-3 py-2 text-xs font-semibold text-[#B08A3E]">{data.follow_up || '-'}</td>
                            <td className="text-center px-3 py-2 text-xs font-bold text-[#1F2937]">{data.total || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            )}

            {/* Search & Filter */}
            <div className="flex flex-wrap items-center gap-2" data-testid="dispatch-filters">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search order #, address, city, client..."
                  value={filters.search || ''}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-9 pr-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-800 placeholder-gray-400 focus:border-[#D1A054] focus:outline-none"
                  data-testid="dispatch-search-input"
                />
              </div>
              <select
                value={filters.assigned_inspector || ''}
                onChange={(e) => setFilters({ ...filters, assigned_inspector: e.target.value })}
                className="px-2 py-1.5 bg-gray-100 border border-gray-300 rounded text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
                data-testid="dispatch-filter-inspector"
              >
                <option value="">All Inspectors</option>
                {allInspectors.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
              </select>
              {(filters.search || filters.assigned_inspector) && (
                <button
                  onClick={() => setFilters({})}
                  className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-800"
                  data-testid="dispatch-clear-filters"
                >
                  Clear
                </button>
              )}
              {/* Compact secondary filter pills (moved from top cards Apr 21, 2026) */}
              <div className="ml-auto flex flex-wrap items-center gap-1" data-testid="dispatch-secondary-tabs">
                {[
                  { id: 'all_active', label: 'All Active', v: tabCounts.all_active },
                  { id: 'regular', label: 'Regular', v: tabCounts.regular },
                  { id: 'appointment_set', label: 'Appointment Dispatch', v: tabCounts.appointment_set },
                  { id: 'external_dispatched', label: 'External', v: tabCounts.external_dispatched },
                  { id: 'phot_queue', label: 'PHOT Queue', v: tabCounts.phot_queue },
                  { id: 'unmapped_workcode', label: 'Unmapped Workcode', v: tabCounts.unmapped_workcode },
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${activeTab === t.id ? 'bg-[#2F5D8A] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    data-testid={`dispatch-tab-${t.id}`}
                  >
                    {t.label} <span className="opacity-70">{t.v}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Selection Toolbar */}
            {selectedIds.size > 0 ? (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-[#D1A054]/10 border border-[#D1A054]/30 rounded-lg" data-testid="dispatch-selection-toolbar">
                <span className="text-[#B08A3E] text-sm font-medium">{selectedIds.size} orders selected</span>
                <button
                  onClick={() => setShowBatchAssign(true)}
                  className="px-3 py-1.5 bg-[#D1A054] hover:bg-[#B08A3E] text-[#1F2937] text-sm font-semibold rounded-lg flex items-center gap-1.5"
                  data-testid="dispatch-assign-btn"
                >
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  Assign to Inspector
                </button>
                {/* Next-Day External Call — bulk mirror flag for selected External orders.
                    Shows only when at least 1 selected order is External. */}
                {Array.from(selectedIds).some(id => {
                  const o = filteredOrders.find(x => x.id === id);
                  return o && (o.flow_type || '') === 'External';
                }) && (() => {
                  const selectedExternal = Array.from(selectedIds)
                    .map(id => filteredOrders.find(x => x.id === id))
                    .filter(o => o && (o.flow_type || '') === 'External');
                  const pendingCount = selectedExternal.filter(o => (o.external_morning_call_required || '').toLowerCase() !== 'yes').length;
                  const flaggedCount = selectedExternal.filter(o => (o.external_morning_call_required || '').toLowerCase() === 'yes' && !o.external_morning_call_completed_at).length;
                  return (
                    <button
                      onClick={async () => {
                        try {
                          // If all selected External are already flagged → toggle OFF
                          const flagAll = pendingCount > 0;
                          await Promise.all(selectedExternal.map(o => axios.patch(`${API}/orders/${o.id}/external-morning-call`, {
                            required: flagAll,
                            reason: flagAll ? 'Bulk Dispatch — next-morning call required' : '',
                          }).catch(e => console.error('flag fail', o.order_number, e))));
                          setOrders(prev => prev.map(o => {
                            if (!selectedExternal.find(s => s.id === o.id)) return o;
                            return {
                              ...o,
                              external_morning_call_required: flagAll ? 'yes' : 'no',
                              external_morning_call_completed_at: flagAll ? '' : o.external_morning_call_completed_at,
                            };
                          }));
                        } catch (e) { alert('Failed to update Morning Call flags'); }
                      }}
                      className="px-3 py-1.5 bg-[#B45309] hover:bg-[#92400E] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5"
                      data-testid="dispatch-bulk-next-day-external-call-btn"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {pendingCount > 0 ? `Next-Day External Call (${pendingCount})` : `✔ Added to Morning Calls (${flaggedCount})`}
                    </button>
                  );
                })()}
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-3 py-1.5 text-gray-500 hover:text-gray-800 text-sm"
                  data-testid="dispatch-clear-selection"
                >
                  Clear
                </button>
              </div>
            ) : filteredOrders.length > 0 && (
              <div className="flex items-center gap-2" data-testid="dispatch-quick-select-bar">
                <span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Quick Select:</span>
                {[20, 50, 100].map(n => (
                  <button
                    key={n}
                    onClick={() => handleQuickSelect(n)}
                    className="px-2.5 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded border border-gray-300 transition-colors"
                    data-testid={`dispatch-quick-select-${n}`}
                  >
                    Top {n}
                  </button>
                ))}
                <span className="text-gray-300 text-xs ml-1">({filteredOrders.length} visible)</span>
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200">
              <OrderTable
                orders={filteredOrders}
                columns={dispatchColumns}
                loading={loading}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                onRowClick={(order) => navigate(`/order/${order.id}`)}
                selectable
                selectedIds={selectedIds}
                onSelectToggle={handleSelectToggle}
                onSelectAll={handleSelectAll}
                onShiftSelect={handleShiftSelect}
              />
            </div>

            {/* Batch Assign Inspector Modal */}
            {showBatchAssign && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowBatchAssign(false)}>
                <div className="bg-white rounded-lg border border-gray-200 p-5 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="dispatch-batch-assign-modal">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Assign to Inspector</h3>
                  <p className="text-gray-500 text-sm mb-4">{selectedIds.size} orders selected</p>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {inspectors.map(insp => {
                      const status = inspectorStatuses[insp.name] || "Not Available";
                      const isRoute = status === "In Route";
                      return (
                        <button
                          key={insp.id}
                          onClick={() => handleBatchAssignInspector(insp.name)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-left transition-all hover:border-[#D1A054]/50 ${
                            isRoute ? 'border-[#A7C4A0]/40 bg-[#A7C4A0]/8' : 'border-gray-200 bg-white/80'
                          }`}
                          data-testid={`batch-assign-inspector-${insp.name}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${isRoute ? 'bg-[#A7C4A0]' : 'bg-gray-200'}`} />
                            <span className="text-sm font-medium text-gray-700">{insp.name}</span>
                          </div>
                          <span className={`text-[10px] uppercase font-bold ${isRoute ? 'text-[#6B8F62]' : 'text-gray-300'}`}>{status}</span>
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => setShowBatchAssign(false)} className="w-full mt-4 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 font-medium" data-testid="dispatch-batch-cancel">Cancel</button>
                </div>
              </div>
            )}

            {/* Manual Follow-Up Creation Modal */}
            {showManualFollowUp && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowManualFollowUp(false)}>
                <div className="bg-white rounded-lg border border-gray-200 p-5 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="manual-followup-modal">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Create Manual Follow-Up</h3>
                      <p className="text-gray-500 text-xs mt-0.5">PHOT order - Manual Entry</p>
                    </div>
                    <span className="px-2 py-1 rounded-md text-[10px] font-bold bg-[#DCE4ED] text-[#2F5D8A]">PHOT</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Address *</label>
                      <input type="text" value={mfuForm.address} onChange={e => setMfuForm(p => ({...p, address: e.target.value}))} placeholder="123 Main St" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="mfu-address" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">City *</label>
                        <input type="text" value={mfuForm.city} onChange={e => setMfuForm(p => ({...p, city: e.target.value}))} placeholder="Worcester" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="mfu-city" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">State</label>
                        <input type="text" value={mfuForm.state} onChange={e => setMfuForm(p => ({...p, state: e.target.value}))} placeholder="MA" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="mfu-state" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Zip</label>
                        <input type="text" value={mfuForm.zip_code} onChange={e => setMfuForm(p => ({...p, zip_code: e.target.value}))} placeholder="01609" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="mfu-zip" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Inspector</label>
                        <select value={mfuForm.assigned_inspector} onChange={e => setMfuForm(p => ({...p, assigned_inspector: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="mfu-inspector">
                          <option value="">Select Inspector</option>
                          {allInspectors.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Original Order #</label>
                        <input type="text" value={mfuForm.original_order_number} onChange={e => setMfuForm(p => ({...p, original_order_number: e.target.value}))} placeholder="353636209" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="mfu-original-order" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Notes</label>
                      <textarea value={mfuForm.notes} onChange={e => setMfuForm(p => ({...p, notes: e.target.value}))} placeholder="Manual Follow-Up - Missing from Safeguard" rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none resize-none" data-testid="mfu-notes" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setShowManualFollowUp(false)} className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 font-medium" data-testid="mfu-cancel-btn">Cancel</button>
                    <button onClick={handleCreateManualFollowUp} disabled={mfuSaving} className="flex-1 px-3 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="mfu-submit-btn">
                      {mfuSaving ? 'Creating...' : 'Create Follow-Up'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Manual Order Modal */}
            {showManualOrder && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="manual-order-modal">
                <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-[#1F2937]">Manual Order</h3>
                    <button onClick={() => setShowManualOrder(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
                  </div>
                  <div className="px-5 py-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Order ID *</label>
                        <input type="text" value={manualOrderForm.order_number} onChange={e => setManualOrderForm(p => ({...p, order_number: e.target.value}))} placeholder="353636209" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-order-number" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Company</label>
                        <select value={manualOrderForm.company} onChange={e => setManualOrderForm(p => ({...p, company: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-company">
                          <option value="Safeguard">Safeguard</option>
                          <option value="National">National</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Client</label>
                        <input type="text" value={manualOrderForm.client} onChange={e => setManualOrderForm(p => ({...p, client: e.target.value}))} placeholder="Client name..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-client" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Inspection Type</label>
                        <input type="text" value={manualOrderForm.order_type} onChange={e => setManualOrderForm(p => ({...p, order_type: e.target.value}))} placeholder="PHOT, PMGR..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-order-type" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Address</label>
                      <input type="text" value={manualOrderForm.address} onChange={e => setManualOrderForm(p => ({...p, address: e.target.value}))} placeholder="123 Main St" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-address" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">City</label>
                        <input type="text" value={manualOrderForm.city} onChange={e => setManualOrderForm(p => ({...p, city: e.target.value}))} placeholder="City" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-city" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">State</label>
                        <input type="text" value={manualOrderForm.state} onChange={e => setManualOrderForm(p => ({...p, state: e.target.value}))} placeholder="MA" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-state" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">ZIP</label>
                        <input type="text" value={manualOrderForm.zip_code} onChange={e => setManualOrderForm(p => ({...p, zip_code: e.target.value}))} placeholder="01234" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-zip" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Inspector</label>
                        <select value={manualOrderForm.assigned_inspector} onChange={e => setManualOrderForm(p => ({...p, assigned_inspector: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-inspector">
                          <option value="">Select Inspector...</option>
                          {inspectors.map(i => <option key={i.id || i.name} value={i.name}>{i.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Route Date</label>
                        <input type="date" value={manualOrderForm.route_date} onChange={e => setManualOrderForm(p => ({...p, route_date: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none" data-testid="mo-route-date" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Notes</label>
                      <textarea value={manualOrderForm.notes} onChange={e => setManualOrderForm(p => ({...p, notes: e.target.value}))} placeholder="Manual Entry - reason for adding..." rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#B08A3E] focus:outline-none resize-none" data-testid="mo-notes" />
                    </div>
                    <div className="bg-[#B08A3E]/5 border border-[#B08A3E]/20 rounded-lg p-2.5 text-xs text-[#7A6535]">
                      <p className="font-medium">This order will be:</p>
                      <ul className="mt-1 space-y-0.5 pl-3">
                        <li>Created with status "Manual Entry"</li>
                        {manualOrderForm.assigned_inspector && <li>Auto-added to Daily Closing for {manualOrderForm.assigned_inspector}</li>}
                        <li>Visible in Daily Closing, Inspector Closing View, and Reports</li>
                        <li>Clearly marked as "Manual"</li>
                      </ul>
                    </div>
                  </div>
                  <div className="px-5 py-3 border-t border-gray-200 flex gap-2">
                    <button onClick={() => setShowManualOrder(false)} className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 font-medium" data-testid="mo-cancel-btn">Cancel</button>
                    <button onClick={handleCreateManualOrder} disabled={manualOrderSaving} className="flex-1 px-3 py-2 bg-[#B08A3E] hover:bg-[#9A7735] text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="mo-submit-btn">
                      {manualOrderSaving ? 'Creating...' : 'Create Manual Order'}
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/* Batch Hold Modal */}
            {showBatchHold && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowBatchHold(false)}>
                <div className="bg-white rounded-lg border border-gray-200 p-5 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="batch-hold-modal">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Send to Hold</h3>
                      <p className="text-gray-500 text-xs mt-0.5">Transmission Hold — {selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''} selected</p>
                    </div>
                    <Archive className="h-5 w-5 text-[#7A6535]" />
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Hold Until Date *</label>
                      <input type="date" value={batchHoldForm.hold_until_date} onChange={e => setBatchHoldForm(p => ({...p, hold_until_date: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="batch-hold-until-date" />
                      <p className="text-[9px] text-gray-400 mt-1">Date when the order becomes available for transmission</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Hold Reason *</label>
                      <select value={batchHoldForm.hold_reason} onChange={e => setBatchHoldForm(p => ({...p, hold_reason: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="batch-hold-reason">
                        <option value="">Select reason...</option>
                        <option value="Transmission window not open">Transmission window not open</option>
                        <option value="Waiting for transmission date">Waiting for transmission date</option>
                        <option value="Window stop">Window stop</option>
                        <option value="Client/date restriction">Client/date restriction</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Notes</label>
                      <textarea value={batchHoldForm.hold_notes} onChange={e => setBatchHoldForm(p => ({...p, hold_notes: e.target.value}))}
                        placeholder="Optional notes..." rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none resize-none" data-testid="batch-hold-notes" />
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="batch_office_paid" checked={batchHoldForm.office_paid}
                        onChange={e => setBatchHoldForm(p => ({...p, office_paid: e.target.checked}))}
                        className="h-4 w-4 rounded border-gray-300 text-[#2F5D8A] focus:ring-[#2F5D8A]" data-testid="batch-hold-office-paid" />
                      <label htmlFor="batch_office_paid" className="text-xs text-gray-600">Office Paid Hold</label>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setShowBatchHold(false)} className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 font-medium" data-testid="batch-hold-cancel">Cancel</button>
                    <button onClick={handleBatchHold} disabled={batchHoldSaving}
                      className="flex-1 px-3 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="batch-hold-submit">
                      {batchHoldSaving ? 'Saving...' : 'Place on Hold'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Force End of Day Confirmation Modal */}
        {showForceEOD && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="dispatch-force-eod-modal">
            <div className="bg-white rounded-xl border border-[#C07070]/30 p-6 w-full max-w-md shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-[#C07070]/12 rounded-lg">
                  <Power className="h-5 w-5 text-[#8B4545]" />
                </div>
                <div>
                  <h3 className="text-[#1F2937] text-lg font-semibold">Force End of Day</h3>
                  <p className="text-gray-500 text-xs">Remove all assignments from {selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="bg-amber-50/80 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                Are you sure you want to remove assignments from selected orders? This will clear operator and inspector assignments. Call logs, comments, attempts, and appointment data will be preserved.
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowForceEOD(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg"
                  data-testid="dispatch-force-eod-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleForceEndOfDay}
                  disabled={forceEODProcessing}
                  className="flex-1 px-4 py-2 bg-[#C07070] hover:bg-[#A85A5A] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="dispatch-force-eod-confirm"
                >
                  <Power className="h-4 w-4" />
                  {forceEODProcessing ? "Processing..." : "Confirm Remove"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* P1-5 (Angela 2026-05-17) — All Company Orders modal (in-place).
            Search + filter + manual assign + move-to-queue. No route change. */}
        {showAllOrdersModal && (
          <DispatchAllOrdersModal
            inspectors={inspectors}
            currentUser={user}
            isAdmin={isAdmin}
            onClose={() => setShowAllOrdersModal(false)}
            onMutated={() => { fetchOrders(); fetchAllActiveOrders(); }}
          />
        )}
      </div>
    </Layout>
  );
};

/* ── P1-5 (Angela 2026-05-17) — Dispatch All Orders Modal ──
   Modal in-place (no route change). Operates on /api/orders/all-company.
   Allows search by number/address/inspector/status, plus per-row actions:
   - Reassign to whitelist inspector
   - Move to Priority / Admin Review / Manual Review / clear queue
   Writes via existing endpoints (no new mutation routes added). */
const DispatchAllOrdersModal = ({ inspectors, currentUser, isAdmin, onClose, onMutated }) => {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({ status: '', company: '', inspector: '', active: 'true' });
  const [data, setData] = useState({ orders: [], total: 0, page: 1, pages: 0, filters: { companies: [], inspectors: [], statuses: [] } });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actingOrderId, setActingOrderId] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, search };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/orders/all-company`, { params });
      setData(res.data || { orders: [], total: 0, page: 1, pages: 0, filters: { companies: [], inspectors: [], statuses: [] } });
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Failed to load orders: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setLoading(false);
    }
  }, [page, search, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReassign = async (orderId, currentInspector) => {
    const opts = inspectors.map(i => i.name || i).filter(Boolean);
    const choice = window.prompt(
      `Reassign inspector for this order.\nWhitelist:\n  ${opts.join('\n  ')}\n\nType the FULL name (or blank to clear):`,
      currentInspector || ''
    );
    if (choice === null) return;
    const cleaned = choice.trim();
    setActingOrderId(orderId);
    try {
      await axios.put(`${API}/orders/${orderId}`, { assigned_inspector: cleaned || null });
      setActionMsg({ type: 'success', text: 'Inspector reassigned' });
      await fetchData();
      onMutated && onMutated();
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Reassign failed: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setActingOrderId(null);
    }
  };

  const handleMoveQueue = async (orderId, queue) => {
    setActingOrderId(orderId);
    try {
      if (!queue) {
        await axios.post(`${API}/orders/${orderId}/remove-from-queue`);
      } else {
        const reason = window.prompt(`Reason for moving to ${queue}:`) || `Moved via Dispatch All Orders by ${currentUser?.email || ''}`;
        await axios.post(`${API}/orders/${orderId}/send-to-queue`, { queue, reason, notes: '' });
      }
      setActionMsg({ type: 'success', text: queue ? `Moved to ${queue}` : 'Removed from queue' });
      await fetchData();
      onMutated && onMutated();
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Queue change failed: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setActingOrderId(null);
    }
  };

  const orders = data.orders || [];
  const totalPages = data.pages || 1;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="dispatch-all-orders-modal">
      <div className="bg-white rounded-[14px] w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-800">All Company Orders — Dispatch</h2>
            <p className="text-xs text-gray-500">Search, reassign and move queues. {data.total} total orders.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="all-orders-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-3 border-b border-gray-200 flex gap-2 flex-wrap items-center">
          <input
            type="text"
            placeholder="Search order #, address, inspector, status..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 min-w-[240px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#2F5D8A] focus:outline-none"
            data-testid="all-orders-search-input"
          />
          <select
            value={filters.status}
            onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            data-testid="all-orders-filter-status"
          >
            <option value="">All Statuses</option>
            {(data.filters?.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={filters.inspector}
            onChange={(e) => { setFilters(f => ({ ...f, inspector: e.target.value })); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            data-testid="all-orders-filter-inspector"
          >
            <option value="">All Inspectors</option>
            {(data.filters?.inspectors || []).map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <select
            value={filters.company}
            onChange={(e) => { setFilters(f => ({ ...f, company: e.target.value })); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            data-testid="all-orders-filter-company"
          >
            <option value="">All Companies</option>
            {(data.filters?.companies || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {actionMsg && (
          <div className={`px-4 py-2 text-xs ${actionMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {actionMsg.text}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {loading ? (
            <p className="p-6 text-sm text-gray-500">Loading...</p>
          ) : orders.length === 0 ? (
            <p className="p-6 text-sm text-gray-500" data-testid="all-orders-empty">No orders match the filters.</p>
          ) : (
            <table className="min-w-full text-sm" data-testid="all-orders-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Order</th>
                  <th className="px-3 py-2 text-left">Company</th>
                  <th className="px-3 py-2 text-left">Address</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Queue</th>
                  <th className="px-3 py-2 text-left">Inspector</th>
                  <th className="px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id || o._id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`all-orders-row-${o.order_number}`}>
                    <td className="px-3 py-2 font-mono text-xs font-bold">{o.order_number}</td>
                    <td className="px-3 py-2 text-xs">{o.company}</td>
                    <td className="px-3 py-2 text-xs">{[o.address, o.city, o.state].filter(Boolean).join(', ')}</td>
                    <td className="px-3 py-2 text-xs">{o.status}</td>
                    <td className="px-3 py-2 text-xs">{o.scheduling_queue || '—'}</td>
                    <td className="px-3 py-2 text-xs">{o.assigned_inspector || <span className="text-gray-400">unassigned</span>}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <button
                        onClick={() => handleReassign(o.id || o._id, o.assigned_inspector)}
                        disabled={actingOrderId === (o.id || o._id)}
                        className="px-2 py-1 text-[10px] font-bold rounded bg-[#2F5D8A] text-white hover:opacity-90 disabled:opacity-50 mr-1"
                        data-testid={`all-orders-reassign-${o.order_number}`}
                      >
                        Assign
                      </button>
                      <select
                        value={o.scheduling_queue || ''}
                        onChange={(e) => handleMoveQueue(o.id || o._id, e.target.value)}
                        disabled={actingOrderId === (o.id || o._id)}
                        className="px-1 py-1 text-[10px] rounded border border-gray-300 bg-white"
                        data-testid={`all-orders-queue-${o.order_number}`}
                      >
                        <option value="">— No queue —</option>
                        <option value="Priority Queue">Priority</option>
                        <option value="Admin Review Queue">Admin Review</option>
                        <option value="Manual Review">Manual Review</option>
                        <option value="SG Email Queue">SG Email</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-3 border-t border-gray-200 flex items-center justify-between text-xs">
          <span className="text-gray-500">{data.total} orders · page {data.page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40"
              data-testid="all-orders-prev-page"
            >
              Prev
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40"
              data-testid="all-orders-next-page"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Inspector Page - List of inspectors + Profile view
const InspectorPage = () => {
  const { user } = useAuth();
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', full_name: '', email: '', phone: '' });
  const [addMsg, setAddMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchInspectors = async () => {
    try {
      const res = await axios.get(`${API}/inspectors`);
      setInspectors(res.data);
      if (user?.role === 'Inspector') {
        const own = res.data.find(i => i.email?.toLowerCase() === user.email?.toLowerCase());
        if (own) setSelectedInspector(own);
      }
    } catch (e) {
      console.error('Failed to fetch inspectors:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInspectors(); }, [user]);

  const handleAddInspector = async () => {
    if (!addForm.name.trim()) { setAddMsg({ type: 'error', text: 'Inspector name is required' }); return; }
    setSaving(true); setAddMsg(null);
    try {
      await axios.post(`${API}/inspectors`, addForm);
      setAddMsg({ type: 'success', text: `Inspector "${addForm.name}" created` });
      setAddForm({ name: '', full_name: '', email: '', phone: '' });
      setTimeout(() => { setShowAddForm(false); setAddMsg(null); }, 1500);
      fetchInspectors();
    } catch (err) { setAddMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to create inspector' }); }
    finally { setSaving(false); }
  };

  const filtered = searchTerm
    ? inspectors.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : inspectors;

  if (selectedInspector) {
    return (
      <Layout>
        <InspectorProfilePage
          inspector={selectedInspector}
          onBack={user?.role === 'Inspector' ? null : () => setSelectedInspector(null)}
        />
      </Layout>
    );
  }

  // Inspector role users should not see the full inspector list
  if (user?.role === 'Inspector') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#D1A054]" />
          ) : (
            'Inspector profile not found for your account. Contact admin.'
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="inspector-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Inspectors</h1>
            <p className="text-gray-500 text-sm">{inspectors.length} inspectors registered</p>
          </div>
          <button onClick={() => { setShowAddForm(true); setAddMsg(null); }}
            className="px-4 py-2 bg-[#6B8F62] hover:bg-[#5A7D52] text-white text-sm font-semibold rounded-lg flex items-center gap-2"
            data-testid="add-inspector-btn">
            <Plus className="h-4 w-4" /> Add Inspector
          </button>
        </div>

        {/* Add Inspector Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddForm(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="add-inspector-modal">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-gray-800">Register New Inspector</h2>
                <button onClick={() => setShowAddForm(false)} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4 text-gray-500" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 font-medium">Inspector Name *</label>
                  <input value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D1A054]" placeholder="MA-INSPECTORNAME"
                    data-testid="add-insp-name" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Full Name</label>
                  <input value={addForm.full_name} onChange={e => setAddForm(p => ({ ...p, full_name: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D1A054]" placeholder="John Doe"
                    data-testid="add-insp-fullname" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Email</label>
                  <input value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} type="email"
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D1A054]" placeholder="inspector@aarifi.com"
                    data-testid="add-insp-email" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Phone</label>
                  <input value={addForm.phone} onChange={e => setAddForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D1A054]" placeholder="(555) 123-4567"
                    data-testid="add-insp-phone" />
                </div>
              </div>
              {addMsg && (
                <div className={`p-2 rounded text-xs ${addMsg.type === 'success' ? 'bg-[#A7C4A0]/15 text-[#6B8F62]' : 'bg-[#C07070]/12 text-[#8B4545]'}`}>{addMsg.text}</div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={handleAddInspector} disabled={saving}
                  className="px-4 py-2 bg-[#6B8F62] hover:bg-[#5A7D52] text-white text-sm font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2"
                  data-testid="save-inspector-btn">
                  {saving ? 'Saving...' : 'Register Inspector'}
                </button>
                <button onClick={() => setShowAddForm(false)} className="px-4 py-2 text-gray-500 text-sm">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-md" data-testid="inspector-search-wrapper">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search inspector..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-8 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:border-[#D1A054] focus:outline-none"
            data-testid="inspector-search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-800">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#D1A054]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="inspector-list">
            {filtered.map(insp => (
              <button
                key={insp.id}
                onClick={() => setSelectedInspector(insp)}
                className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-[#D1A054]/50 transition-all text-left group"
                data-testid={`inspector-item-${insp.id}`}
              >
                <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-[#D1A054]/20 transition-colors">
                  <User className="h-4 w-4 text-gray-500 group-hover:text-[#B08A3E]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 group-hover:text-[#B08A3E] transition-colors">{insp.full_name || insp.name}</p>
                  <div className="flex items-center gap-1.5">
                    <p className="text-[10px] text-gray-400 truncate">{insp.full_name ? insp.name : ''} {insp.email}</p>
                    {insp.onboarding_status && insp.onboarding_status !== "Active" && (
                      <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                        insp.onboarding_status === "Waiting for Documents" ? "bg-amber-50 text-amber-600" :
                        insp.onboarding_status === "Documents Received" ? "bg-blue-50 text-blue-600" :
                        insp.onboarding_status === "Contract Sent" ? "bg-purple-50 text-purple-600" :
                        insp.onboarding_status === "Contract Signed" ? "bg-indigo-50 text-indigo-600" :
                        insp.onboarding_status === "Inactive" ? "bg-red-50 text-red-500" :
                        "bg-gray-50 text-gray-500"
                      }`} data-testid={`onboarding-badge-${insp.id}`}>{insp.onboarding_status}</span>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-300 ml-auto group-hover:text-[#B08A3E]" />
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="col-span-full text-center text-gray-400 text-sm py-8">No inspectors found</p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

// QA Page — Manual flow: EOD Closed → Manual Send → QA Review → Finance
const QAPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('due_date');
  const [sortDir, setSortDir] = useState('asc');
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingSelectedIds, setPendingSelectedIds] = useState(new Set());
  const [message, setMessage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [pendingSearch, setPendingSearch] = useState('');
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenSource, setReopenSource] = useState('qa');
  const [showReturnInspectorModal, setShowReturnInspectorModal] = useState(false);
  const [returnInspectorSource, setReturnInspectorSource] = useState('qa');
  const [returnInspectorName, setReturnInspectorName] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [inspectorsList, setInspectorsList] = useState([]);
  // Filter states
  const [filterCompany, setFilterCompany] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterInspector, setFilterInspector] = useState('');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // Grouping
  const [groupBy, setGroupBy] = useState('none');

  // Derive unique filter options from both order sets
  const filterOptions = useMemo(() => {
    const allOrds = [...orders, ...pendingOrders];
    const companies = [...new Set(allOrds.map(o => o.company).filter(Boolean))].sort();
    const types = [...new Set(allOrds.map(o => o.order_type).filter(Boolean))].sort();
    const inspectors = [...new Set(allOrds.map(o => o.assigned_inspector).filter(Boolean))].sort();
    return { companies, types, inspectors };
  }, [orders, pendingOrders]);

  // Date helpers for due date highlighting
  const todayStr = useMemo(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }, []);
  const weekEndStr = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }, []);

  const getDueDateStyle = useCallback((dueDate) => {
    if (!dueDate) return '';
    const dd = dueDate.length === 10 && dueDate.includes('/') ? `${dueDate.slice(6)}-${dueDate.slice(0,2)}-${dueDate.slice(3,5)}` : dueDate;
    if (dd < todayStr) return 'bg-red-50 border-l-4 border-l-red-400';
    if (dd === todayStr) return 'bg-orange-50 border-l-4 border-l-orange-400';
    return '';
  }, [todayStr]);

  const getDueDateBadge = useCallback((dueDate) => {
    if (!dueDate) return null;
    const dd = dueDate.length === 10 && dueDate.includes('/') ? `${dueDate.slice(6)}-${dueDate.slice(0,2)}-${dueDate.slice(3,5)}` : dueDate;
    if (dd < todayStr) return <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700 border border-red-200">OVERDUE</span>;
    if (dd === todayStr) return <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700 border border-orange-200">TODAY</span>;
    return null;
  }, [todayStr]);

  // Apply common filters to an order list
  const applyFilters = useCallback((list) => {
    let result = [...list];
    if (filterCompany) result = result.filter(o => o.company === filterCompany);
    if (filterType) result = result.filter(o => o.order_type === filterType);
    if (filterInspector) result = result.filter(o => o.assigned_inspector === filterInspector);
    if (filterDateRange === 'today') {
      result = result.filter(o => { const dd = o.due_date || ''; return dd === todayStr || (dd.includes('/') && `${dd.slice(6)}-${dd.slice(0,2)}-${dd.slice(3,5)}` === todayStr); });
    } else if (filterDateRange === 'week') {
      result = result.filter(o => { const dd = o.due_date || ''; const norm = dd.includes('/') ? `${dd.slice(6)}-${dd.slice(0,2)}-${dd.slice(3,5)}` : dd; return norm >= todayStr && norm <= weekEndStr; });
    } else if (filterDateRange === 'older24h') {
      result = result.filter(o => (o.hours_since_approved || 0) >= 24);
    } else if (filterDateRange === 'custom' && (filterDateFrom || filterDateTo)) {
      result = result.filter(o => { const dd = o.due_date || ''; const norm = dd.includes('/') ? `${dd.slice(6)}-${dd.slice(0,2)}-${dd.slice(3,5)}` : dd; if (filterDateFrom && norm < filterDateFrom) return false; if (filterDateTo && norm > filterDateTo) return false; return true; });
    }
    return result;
  }, [filterCompany, filterType, filterInspector, filterDateRange, filterDateFrom, filterDateTo, todayStr, weekEndStr]);

  const formatAddress = useCallback((order) => {
    const parts = [order.address, order.city, order.state && order.zip_code ? `${order.state} ${order.zip_code}` : (order.state || order.zip_code || '')].filter(Boolean);
    return parts.join(', ');
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const [qaRes, pendingRes] = await Promise.all([
        axios.get(`${API}/qa/orders`),
        axios.get(`${API}/qa/pending`),
      ]);
      setOrders(qaRes.data);
      setPendingOrders(pendingRes.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Fetch inspectors for return-to-inspector modal
  useEffect(() => {
    axios.get(`${API}/inspectors`).then(res => {
      // Include inspectors that are active or don't have active field set (default to active)
      setInspectorsList(res.data.filter(i => i.active === 'yes' || i.active === undefined || i.active === null));
    }).catch(() => {});
  }, []);

  const handleReturnToInspector = async () => {
    if (!returnInspectorName) return;
    const sourceIds = returnInspectorSource === 'qa' ? selectedIds : pendingSelectedIds;
    if (sourceIds.size === 0) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/qa/return-to-inspector`, {
        order_ids: Array.from(sourceIds),
        inspector_name: returnInspectorName,
        return_reason: returnReason,
      });
      setMessage({ type: 'success', text: `${res.data.returned} order(s) returned to ${res.data.inspector_name}` });
      if (returnInspectorSource === 'qa') setSelectedIds(new Set());
      else setPendingSelectedIds(new Set());
      setShowReturnInspectorModal(false);
      setReturnInspectorName('');
      setReturnReason('');
      fetchOrders();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to return orders to inspector' });
    } finally { setProcessing(false); }
  };

  const handleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  const tabCounts = useMemo(() => ({
    all: orders.length,
    waiting: orders.filter(o => o.status === 'QA' && o.qa_status !== 'Approved' && o.qa_status !== 'Follow-Up' && o.status !== 'Follow-Up').length,
    followup: orders.filter(o => o.qa_status === 'Follow-Up' || o.status === 'Follow-Up').length,
    approved: orders.filter(o => o.status === 'QA Approved' || o.qa_status === 'Approved').length,
  }), [orders]);

  const filteredOrders = useMemo(() => {
    let result = [...orders];
    if (activeTab === 'waiting') result = result.filter(o => o.status === 'QA' && o.qa_status !== 'Approved' && o.qa_status !== 'Follow-Up' && o.status !== 'Follow-Up');
    else if (activeTab === 'followup') result = result.filter(o => o.qa_status === 'Follow-Up' || o.status === 'Follow-Up');
    else if (activeTab === 'approved') result = result.filter(o => o.status === 'QA Approved' || o.qa_status === 'Approved');
    result = applyFilters(result);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(o =>
        (o.order_number || '').toLowerCase().includes(term) ||
        (o.cliente || '').toLowerCase().includes(term) ||
        (o.address || '').toLowerCase().includes(term) ||
        (o.city || '').toLowerCase().includes(term) ||
        (o.state || '').toLowerCase().includes(term) ||
        (o.assigned_inspector || '').toLowerCase().includes(term) ||
        (o.zip_code || '').toLowerCase().includes(term)
      );
    }
    result.sort((a, b) => { let aV = a[sortBy] || '', bV = b[sortBy] || ''; return sortDir === 'asc' ? (aV < bV ? -1 : aV > bV ? 1 : 0) : (aV > bV ? -1 : aV < bV ? 1 : 0); });
    return result;
  }, [orders, activeTab, searchTerm, sortBy, sortDir, applyFilters]);

  const filteredPending = useMemo(() => {
    let result = applyFilters(pendingOrders);
    if (pendingSearch) {
      const term = pendingSearch.toLowerCase();
      result = result.filter(o =>
        (o.order_number || '').toLowerCase().includes(term) ||
        (o.cliente || '').toLowerCase().includes(term) ||
        (o.address || '').toLowerCase().includes(term) ||
        (o.city || '').toLowerCase().includes(term) ||
        (o.state || '').toLowerCase().includes(term) ||
        (o.assigned_inspector || '').toLowerCase().includes(term) ||
        (o.zip_code || '').toLowerCase().includes(term)
      );
    }
    return result;
  }, [pendingOrders, pendingSearch, applyFilters]);

  // Group orders helper
  const groupOrders = useCallback((orderList) => {
    if (groupBy === 'none') return null;
    const key = groupBy === 'city' ? 'city' : 'assigned_inspector';
    const groups = {};
    orderList.forEach(o => {
      const gk = o[key] || 'Unassigned';
      if (!groups[gk]) groups[gk] = [];
      groups[gk].push(o);
    });
    return Object.entries(groups).sort(([a],[b]) => a.localeCompare(b));
  }, [groupBy]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (filterCompany) c++;
    if (filterType) c++;
    if (filterInspector) c++;
    if (filterDateRange !== 'all') c++;
    return c;
  }, [filterCompany, filterType, filterInspector, filterDateRange]);

  const clearAllFilters = () => {
    setFilterCompany(''); setFilterType(''); setFilterInspector('');
    setFilterDateRange('all'); setFilterDateFrom(''); setFilterDateTo('');
    setSearchTerm('');
  };

  // QA table selection
  const handleSelectToggle = (id) => { setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const handleSelectAll = (checked) => { if (checked) setSelectedIds(new Set(filteredOrders.map(o => o.id))); else setSelectedIds(new Set()); };
  const allSelected = filteredOrders.length > 0 && filteredOrders.every(o => selectedIds.has(o.id));

  // Pending table selection
  const handlePendingToggle = (id) => { setPendingSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const handlePendingSelectAll = (checked) => { if (checked) setPendingSelectedIds(new Set(filteredPending.map(o => o.id))); else setPendingSelectedIds(new Set()); };
  const handlePendingSelect20 = () => { setPendingSelectedIds(new Set(filteredPending.slice(0, 20).map(o => o.id))); };
  const allPendingSelected = filteredPending.length > 0 && filteredPending.every(o => pendingSelectedIds.has(o.id));

  // Send EOD Closed → QA (manual)
  const handleSendToQA = async () => {
    if (pendingSelectedIds.size === 0) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/qa/send-batch`, { order_ids: Array.from(pendingSelectedIds) });
      setMessage({ type: 'success', text: `${res.data.sent_to_qa} order(s) sent to QA successfully.` });
      setPendingSelectedIds(new Set());
      fetchOrders();
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to send orders to QA' });
    } finally { setProcessing(false); }
  };

  // Send QA → Finance
  const handleSendToFinance = async () => {
    if (selectedIds.size === 0) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/qa/send-to-finance`, { order_ids: Array.from(selectedIds) });
      const { moved, skipped_status } = res.data;
      let msg = `${moved} order(s) sent to Billing.`;
      if (skipped_status > 0) msg += ` ${skipped_status} skipped (not QA Approved).`;
      setMessage({ type: moved > 0 ? 'success' : 'warning', text: msg });
      setSelectedIds(new Set());
      fetchOrders();
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to send orders to Billing' });
    } finally { setProcessing(false); }
  };

  // Return QA → Dispatch as Follow-Up
  const handleReturnToDispatch = async () => {
    if (selectedIds.size === 0) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/qa/return-to-dispatch`, { order_ids: Array.from(selectedIds) });
      setMessage({ type: 'success', text: `${res.data.returned} order(s) returned to Dispatch as Follow-Up.` });
      setSelectedIds(new Set());
      fetchOrders();
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to return orders' });
    } finally { setProcessing(false); }
  };

  const handleUpdateQAStatus = async (orderId, newStatus) => {
    try {
      await axios.post(`${API}/qa/update-status/${orderId}`, { qa_status: newStatus });
      fetchOrders();
    } catch (e) { console.error('Failed to update QA status:', e); }
  };

  const qaStatusBadge = (order) => {
    const qs = order.status === 'QA Approved' ? 'QA Approved' : (order.qa_status || (order.status === 'Follow-Up' ? 'Follow-Up' : 'Waiting Review'));
    const styles = {
      'Waiting Review': 'bg-[#E0DEE8] text-[#5A4F7A] border-[#C5BFD6]',
      'Follow-Up': 'bg-[#EDE5D6] text-[#7A6535] border-[#D6C9A8]',
      'Approved': 'bg-[#D8E8D4] text-[#4A7A56] border-[#B8D4B0]',
      'QA Approved': 'bg-[#D8E8D4] text-[#4A7A56] border-[#B8D4B0]',
    };
    return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${styles[qs] || styles['Waiting Review']}`}>{qs}</span>;
  };

  const SortHeader = ({ label, sortKey }) => (
    <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400 whitespace-nowrap cursor-pointer hover:text-gray-600" onClick={() => handleSort(sortKey)}>
      <div className="flex items-center gap-1">{label}{sortBy === sortKey && (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}</div>
    </th>
  );

  // Render a QA table row (shared between grouped and non-grouped views)
  const renderQaRow = (order, isSelected, type) => {
    const daysInQa = order.days_in_qa || 0;
    const holdOk = daysInQa >= 3;
    const currentQaStatus = order.status === 'QA Approved' ? 'Approved' : (order.qa_status || (order.status === 'Follow-Up' ? 'Follow-Up' : 'Waiting Review'));
    const dueDateRowClass = getDueDateStyle(order.due_date);
    const hoursApproved = order.hours_since_approved || 0;
    const isOlderThan24h = hoursApproved >= 24;
    if (type === 'pending') {
      return (
        <tr key={order.id} className={`border-b border-gray-100 transition-colors ${isSelected ? 'bg-[#2F5D8A]/5' : dueDateRowClass || 'hover:bg-gray-50'}`} data-testid={`pending-row-${order.id}`}>
          <td className="px-3 py-2"><input type="checkbox" checked={isSelected} onChange={() => handlePendingToggle(order.id)} className="rounded border-gray-300" /></td>
          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDateDisplay(order.due_date)}{getDueDateBadge(order.due_date)}</td>
          <td className="px-3 py-2 text-gray-800 font-medium whitespace-nowrap cursor-pointer hover:text-[#2F5D8A]" onClick={() => navigate(`/order/${order.id}`)}>{order.order_number}</td>
          <td className="px-3 py-2 text-gray-600 text-xs max-w-[260px] truncate" title={formatAddress(order)}>{formatAddress(order)}</td>
          <td className="px-3 py-2 text-gray-600">{order.company}</td>
          <td className="px-3 py-2 text-gray-500">{order.order_type}</td>
          <td className="px-3 py-2 text-gray-500">{order.assigned_inspector || '-'}</td>
          <td className="px-3 py-2 text-gray-500">{order.daily_closed_by || '-'}</td>
          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{order.daily_closed_date || '-'}</td>
        </tr>
      );
    }
    return (
      <tr key={order.id} className={`border-b border-gray-200 transition-colors ${isSelected ? 'bg-[#2F5D8A]/5' : dueDateRowClass || 'hover:bg-gray-50'}`} data-testid={`qa-order-row-${order.id}`}>
        <td className="px-3 py-2"><input type="checkbox" checked={isSelected} onChange={() => handleSelectToggle(order.id)} className="rounded border-gray-300" /></td>
        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDateDisplay(order.due_date)}{getDueDateBadge(order.due_date)}</td>
        <td className="px-3 py-2 text-gray-800 font-medium whitespace-nowrap cursor-pointer hover:text-[#2F5D8A]" onClick={() => navigate(`/order/${order.id}`)}>{order.order_number}</td>
        <td className="px-3 py-2 text-gray-600 text-xs max-w-[260px] truncate" title={formatAddress(order)}>{formatAddress(order)}</td>
        <td className="px-3 py-2 text-gray-600">{order.company}</td>
        <td className="px-3 py-2 text-gray-500">{order.order_type}</td>
        <td className="px-3 py-2 text-gray-500">{order.assigned_inspector}</td>
        <td className="px-3 py-2">{qaStatusBadge(order)}</td>
        <td className="px-3 py-2">
          {order.status === 'QA Approved' ? (
            <span className={`text-xs font-bold ${isOlderThan24h ? 'text-[#6B8F62]' : 'text-[#9A7A3E]'}`} title={`QA Approved ${hoursApproved.toFixed(0)}h ago`}>
              {hoursApproved >= 24 ? `${Math.floor(hoursApproved/24)}d` : `${hoursApproved.toFixed(0)}h`}
              {isOlderThan24h && <span className="ml-1 text-[9px] text-[#6B8F62]">ready</span>}
            </span>
          ) : (
            <span className={`text-xs font-bold ${holdOk ? 'text-[#6B8F62]' : 'text-[#9A7A3E]'}`} title={holdOk ? 'Eligible for billing' : `${3 - daysInQa} day(s) remaining`}>{daysInQa}d</span>
          )}
        </td>
        <td className="px-3 py-2">
          {order.status === 'QA Approved' ? (
            <button onClick={() => { setSelectedIds(new Set([order.id])); handleSendToFinance(); }}
              className="px-2 py-0.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-[10px] font-semibold rounded"
              data-testid={`qa-send-billing-${order.id}`}>
              Send to Billing
            </button>
          ) : (
            <select value={currentQaStatus} onChange={(e) => handleUpdateQAStatus(order.id, e.target.value)}
              className="bg-gray-50 border border-gray-300 rounded px-2 py-1 text-[11px] text-gray-800 focus:border-[#2F5D8A] focus:outline-none"
              data-testid={`qa-status-select-${order.id}`}>
              <option value="Waiting Review">Waiting Review</option>
              <option value="Follow-Up">Follow-Up</option>
              <option value="Approved">Approved</option>
            </select>
          )}
        </td>
      </tr>
    );
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="qa-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Quality Control</h1>
            <p className="text-gray-500 text-sm">{orders.length} orders in QA review &middot; {pendingOrders.length} pending send</p>
          </div>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <button onClick={clearAllFilters} className="px-2.5 py-1.5 text-xs text-red-600 hover:text-red-800 font-medium" data-testid="qa-clear-all-filters">
                Clear All Filters ({activeFilterCount})
              </button>
            )}
          </div>
        </div>

        {/* ═══ GLOBAL FILTER BAR ═══ */}
        <div className="bg-white rounded-lg border border-[#D1D5DB] p-3" data-testid="qa-filter-bar">
          <div className="flex flex-wrap items-center gap-3">
            {/* Smart Search */}
            <div className="relative flex-1 min-w-[240px] max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" placeholder="Search order #, address, city, inspector..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-8 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:border-[#2F5D8A] focus:outline-none"
                data-testid="qa-search-input" />
              {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-800"><X className="h-3.5 w-3.5" /></button>}
            </div>

            {/* Toggle Filters */}
            <button onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-colors ${showFilters || activeFilterCount > 0 ? 'bg-[#2F5D8A] text-white border-[#2F5D8A]' : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100'}`}
              data-testid="qa-toggle-filters-btn">
              <Filter className="h-3.5 w-3.5" />
              Filters{activeFilterCount > 0 && <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-[10px]">{activeFilterCount}</span>}
            </button>

            {/* Grouping */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="font-medium">Group:</span>
              <button onClick={() => setGroupBy(groupBy === 'city' ? 'none' : 'city')}
                className={`px-2 py-1 rounded border text-[11px] font-medium transition-colors ${groupBy === 'city' ? 'bg-[#2F5D8A] text-white border-[#2F5D8A]' : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                data-testid="qa-group-city-btn">
                City
              </button>
              <button onClick={() => setGroupBy(groupBy === 'inspector' ? 'none' : 'inspector')}
                className={`px-2 py-1 rounded border text-[11px] font-medium transition-colors ${groupBy === 'inspector' ? 'bg-[#2F5D8A] text-white border-[#2F5D8A]' : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                data-testid="qa-group-inspector-btn">
                Inspector
              </button>
            </div>

            {/* Older than 24h filter (for QA Approved) */}
            {activeTab === 'approved' && (
              <button onClick={() => { setFilterDateRange(filterDateRange === 'older24h' ? 'all' : 'older24h'); }}
                className={`px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold flex items-center gap-1 transition-colors ${filterDateRange === 'older24h' ? 'bg-[#6B8F62] text-white border-[#6B8F62]' : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                data-testid="qa-older-24h-btn">
                <Clock className="h-3 w-3" />
                Older than 24h
              </button>
            )}
          </div>

          {/* Expanded Dropdown Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-end gap-3" data-testid="qa-dropdown-filters">
              <div className="min-w-[140px]">
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Company</label>
                <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-300 rounded-lg text-xs text-gray-800 focus:border-[#2F5D8A] focus:outline-none"
                  data-testid="qa-filter-company">
                  <option value="">All Companies</option>
                  {filterOptions.companies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="min-w-[120px]">
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Type</label>
                <select value={filterType} onChange={e => setFilterType(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-300 rounded-lg text-xs text-gray-800 focus:border-[#2F5D8A] focus:outline-none"
                  data-testid="qa-filter-type">
                  <option value="">All Types</option>
                  {filterOptions.types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="min-w-[160px]">
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Inspector</label>
                <select value={filterInspector} onChange={e => setFilterInspector(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-300 rounded-lg text-xs text-gray-800 focus:border-[#2F5D8A] focus:outline-none"
                  data-testid="qa-filter-inspector">
                  <option value="">All Inspectors</option>
                  {filterOptions.inspectors.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="min-w-[150px]">
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Due Date</label>
                <select value={filterDateRange} onChange={e => setFilterDateRange(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-300 rounded-lg text-xs text-gray-800 focus:border-[#2F5D8A] focus:outline-none"
                  data-testid="qa-filter-date-range">
                  <option value="all">All Dates</option>
                  <option value="today">Due Today</option>
                  <option value="week">This Week</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              {filterDateRange === 'custom' && (
                <>
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">From</label>
                    <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                      className="px-2.5 py-1.5 bg-gray-50 border border-gray-300 rounded-lg text-xs text-gray-800 focus:border-[#2F5D8A] focus:outline-none"
                      data-testid="qa-filter-date-from" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">To</label>
                    <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                      className="px-2.5 py-1.5 bg-gray-50 border border-gray-300 rounded-lg text-xs text-gray-800 focus:border-[#2F5D8A] focus:outline-none"
                      data-testid="qa-filter-date-to" />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Message */}
        {message && (
          <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
            message.type === 'success' ? 'bg-[#EDF3EC] border border-[#C5D9C1] text-[#4A7A56]' :
            message.type === 'warning' ? 'bg-[#F5F0E6] border border-[#DDD3BF] text-[#9A7A3E]' :
            'bg-[#F3ECEC] border border-[#DFC4C4] text-[#8B4545]'
          }`}>
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} className="ml-auto"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}

        {/* ═══ SECTION 1: Pending QA (EOD Closed orders) ═══ */}
        {pendingOrders.length > 0 && (
          <div className="bg-white rounded-lg border border-[#D1D5DB] overflow-hidden" data-testid="pending-qa-section">
            <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#2F5D8A]" />
                <h2 className="text-sm font-bold text-[#1F2937]">Pending QA Send</h2>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#DCE4ED] text-[#2F5D8A]">{filteredPending.length}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input type="text" placeholder="Search pending..." value={pendingSearch} onChange={(e) => setPendingSearch(e.target.value)}
                    className="pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-300 rounded-lg text-xs text-gray-800 w-48 focus:border-[#2F5D8A] focus:outline-none"
                    data-testid="pending-qa-search" />
                </div>
                <button onClick={handlePendingSelect20} className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-[#4B5563] text-xs font-medium rounded-lg" data-testid="pending-select-20">
                  Select 20
                </button>
                <button onClick={() => handlePendingSelectAll(true)} className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-[#4B5563] text-xs font-medium rounded-lg" data-testid="pending-select-all">
                  Select All
                </button>
                {pendingSelectedIds.size > 0 && (
                  <>
                    <span className="text-xs text-[#2F5D8A] font-semibold">{pendingSelectedIds.size} selected</span>
                    <button onClick={handleSendToQA} disabled={processing}
                      className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="send-to-qa-btn">
                      <CheckCircle className="h-3.5 w-3.5" />
                      {processing ? 'Sending...' : 'Send to QA'}
                    </button>
                    <button onClick={() => { setReopenSource('pending'); setShowReopenModal(true); }} disabled={processing}
                      className="px-3 py-1.5 bg-[#D1A054] hover:bg-[#B08A3E] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="pending-reopen-btn">
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reopen
                    </button>
                    <button onClick={() => { setReturnInspectorSource('pending'); setShowReturnInspectorModal(true); }} disabled={processing}
                      className="px-3 py-1.5 bg-[#6B5B8D] hover:bg-[#5A4F7A] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="pending-return-inspector-btn">
                      <User className="h-3.5 w-3.5" />
                      Return to Inspector
                    </button>
                    <button onClick={() => setPendingSelectedIds(new Set())} className="text-xs text-gray-500 hover:text-gray-800">Clear</button>
                  </>
                )}
              </div>
            </div>
            <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="pending-qa-table">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2"><input type="checkbox" checked={allPendingSelected} onChange={(e) => handlePendingSelectAll(e.target.checked)} className="rounded border-gray-300" data-testid="pending-select-all-checkbox" /></th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Due Date</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Order #</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Address</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Company</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Type</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Inspector</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Closed By</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Closed Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPending.map(order => renderQaRow(order, pendingSelectedIds.has(order.id), 'pending'))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ SECTION 2: QA Review (status = QA or Follow-Up) ═══ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="qa-cards">
          <StatCard title="All in QA" icon={ClipboardCheck} color="slate" value={tabCounts.all} active={activeTab === 'all'} onClick={() => setActiveTab('all')} testId="qa-card-all" />
          <StatCard title="Waiting Review" icon={Clock} color="blue" value={tabCounts.waiting} active={activeTab === 'waiting'} onClick={() => setActiveTab('waiting')} testId="qa-card-waiting" />
          <StatCard title="Follow-Up" icon={AlertCircle} color="amber" value={tabCounts.followup} active={activeTab === 'followup'} onClick={() => setActiveTab('followup')} testId="qa-card-followup" />
          <StatCard title="QA Approved (Waiting)" icon={CheckCircle} color="emerald" value={tabCounts.approved} active={activeTab === 'approved'} onClick={() => setActiveTab('approved')} testId="qa-card-approved" />
        </div>

        {/* QA Selection Toolbar */}
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-[#2F5D8A]/8 border border-[#2F5D8A]/20 rounded-lg" data-testid="qa-toolbar">
            <span className="text-[#2F5D8A] text-sm font-medium">{selectedIds.size} selected</span>
            <button onClick={handleSendToFinance} disabled={processing}
              className="px-3 py-1.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              data-testid="qa-send-to-billing-btn">
              <DollarSign className="h-3.5 w-3.5" />{processing ? 'Processing...' : 'Send to Billing'}
            </button>
            <button onClick={handleReturnToDispatch} disabled={processing}
              className="px-3 py-1.5 bg-[#C07070] hover:bg-[#A85A5A] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              data-testid="qa-return-to-dispatch-btn">
              <ArrowLeft className="h-3.5 w-3.5" />{processing ? 'Processing...' : 'Return to Dispatch'}
            </button>
            <button onClick={() => { setReopenSource('qa'); setShowReopenModal(true); }} disabled={processing}
              className="px-3 py-1.5 bg-[#D1A054] hover:bg-[#B08A3E] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              data-testid="qa-reopen-btn">
              <RotateCcw className="h-3.5 w-3.5" />{processing ? 'Processing...' : 'Reopen Order'}
            </button>
            <button onClick={() => { setReturnInspectorSource('qa'); setShowReturnInspectorModal(true); }} disabled={processing}
              className="px-3 py-1.5 bg-[#6B5B8D] hover:bg-[#5A4F7A] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              data-testid="qa-return-inspector-btn">
              <User className="h-3.5 w-3.5" />{processing ? 'Processing...' : 'Return to Inspector'}
            </button>
            <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1.5 text-gray-500 hover:text-gray-800 text-sm">Clear</button>
          </div>
        )}

        {/* QA Orders Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5D8A]" /></div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No orders in QA review{(searchTerm || activeFilterCount > 0) && ' matching your filters'}</div>
          ) : groupBy !== 'none' && groupOrders(filteredOrders) ? (
            /* Grouped view */
            <div className="divide-y divide-gray-200">
              {groupOrders(filteredOrders).map(([groupName, groupItems]) => (
                <div key={groupName}>
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    {groupBy === 'city' ? <MapPin className="h-3.5 w-3.5 text-[#2F5D8A]" /> : <User className="h-3.5 w-3.5 text-[#2F5D8A]" />}
                    <span className="text-xs font-bold text-[#1F2937]">{groupName}</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#DCE4ED] text-[#2F5D8A]">{groupItems.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="px-3 py-2 text-left w-8"><input type="checkbox" onChange={(e) => { const ids = groupItems.map(o => o.id); setSelectedIds(prev => { const n = new Set(prev); ids.forEach(id => e.target.checked ? n.add(id) : n.delete(id)); return n; }); }} className="rounded border-gray-300" /></th>
                          <SortHeader label="Due Date" sortKey="due_date" />
                          <SortHeader label="Order #" sortKey="order_number" />
                          <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Address</th>
                          <SortHeader label="Company" sortKey="company" />
                          <SortHeader label="Type" sortKey="order_type" />
                          <SortHeader label="Inspector" sortKey="assigned_inspector" />
                          <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">QA Status</th>
                          <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Days</th>
                          <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupItems.map(order => renderQaRow(order, selectedIds.has(order.id), 'qa'))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Flat view */
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="qa-orders-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2.5 text-left"><input type="checkbox" checked={allSelected} onChange={(e) => handleSelectAll(e.target.checked)} className="rounded border-gray-300" data-testid="qa-select-all" /></th>
                    <SortHeader label="Due Date" sortKey="due_date" />
                    <SortHeader label="Order #" sortKey="order_number" />
                    <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Address</th>
                    <SortHeader label="Company" sortKey="company" />
                    <SortHeader label="Type" sortKey="order_type" />
                    <SortHeader label="Inspector" sortKey="assigned_inspector" />
                    <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">QA Status</th>
                    <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Days</th>
                    <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map(order => renderQaRow(order, selectedIds.has(order.id), 'qa'))}
                </tbody>
              </table>
            </div>
          )}
          {/* Result count */}
          {!loading && filteredOrders.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-[11px] text-gray-500 flex items-center justify-between">
              <span>Showing {filteredOrders.length} of {orders.length} orders</span>
              {(searchTerm || activeFilterCount > 0) && <span className="text-[#2F5D8A] font-medium">{activeFilterCount} filter(s) active</span>}
            </div>
          )}
        </div>

        {/* Reopen Modal */}
        {showReopenModal && (
          <ReopenModal
            orderIds={Array.from(reopenSource === 'qa' ? selectedIds : pendingSelectedIds)}
            orderNumbers={
              (reopenSource === 'qa' ? filteredOrders : filteredPending)
                .filter(o => (reopenSource === 'qa' ? selectedIds : pendingSelectedIds).has(o.id))
                .map(o => o.order_number)
            }
            onClose={() => setShowReopenModal(false)}
            onSuccess={(msg) => {
              setMessage({ type: 'success', text: msg });
              if (reopenSource === 'qa') setSelectedIds(new Set());
              else setPendingSelectedIds(new Set());
              fetchOrders();
            }}
          />
        )}

        {/* Return to Inspector Modal */}
        {showReturnInspectorModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowReturnInspectorModal(false)}>
            <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="return-inspector-modal">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-[#6B5B8D]/10 rounded-lg">
                  <User className="h-5 w-5 text-[#6B5B8D]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1F2937]">Return to Inspector</h3>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {(returnInspectorSource === 'qa' ? selectedIds : pendingSelectedIds).size} order(s) selected
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5">Assign to Inspector *</label>
                  <select
                    value={returnInspectorName}
                    onChange={e => setReturnInspectorName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#6B5B8D] focus:outline-none bg-white"
                    data-testid="return-inspector-select"
                  >
                    <option value="">Select inspector...</option>
                    {inspectorsList.map(i => (
                      <option key={i.id || i.name} value={i.name}>{i.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5">Reason</label>
                  <textarea
                    value={returnReason}
                    onChange={e => setReturnReason(e.target.value)}
                    placeholder="Why are these orders being returned..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#6B5B8D] focus:outline-none resize-none"
                    data-testid="return-inspector-reason"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => { setShowReturnInspectorModal(false); setReturnInspectorName(''); setReturnReason(''); }}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg"
                  data-testid="return-inspector-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReturnToInspector}
                  disabled={processing || !returnInspectorName}
                  className="flex-1 px-4 py-2.5 bg-[#6B5B8D] hover:bg-[#5A4F7A] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="return-inspector-confirm"
                >
                  <User className="h-4 w-4" />
                  {processing ? 'Returning...' : 'Confirm Return'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Clock-in gate — verifies office Operator clocked in today; cached in sessionStorage
const useClockInStatus = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState({ loading: true, clockedIn: false });
  const isOperator = user?.role === "Operator";
  useEffect(() => {
    if (!user || !isOperator) { setStatus({ loading: false, clockedIn: true }); return; }
    const cached = sessionStorage.getItem("office_clock_gate_ok");
    if (cached === "yes") { setStatus({ loading: false, clockedIn: true }); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/office/my-status`);
        const ci = r.data?.time_log?.clock_in;
        const co = r.data?.time_log?.clock_out;
        const ok = !!ci && !co;
        if (ok) sessionStorage.setItem("office_clock_gate_ok", "yes");
        setStatus({ loading: false, clockedIn: ok });
      } catch {
        // If endpoint fails (e.g., non-office user), don't block — fail-open
        setStatus({ loading: false, clockedIn: true });
      }
    })();
  }, [user, isOperator]);
  return status;
};

// Protected route extension: if Operator not clocked in, force them to /office first.
// Exception: transmission-related screens (Inspectors Upload + Order Detail) are
// ALWAYS accessible because transmission work is NOT paid office time. Vitória
// only needs to clock in when she's doing office (scheduling/dispatch) tasks.
const TRANSMISSION_PATHS = new Set(["/national-inspectors-upload", "/aly-portal", "/aly-national-view", "/aly-national-calendar"]);
const isTransmissionPath = (pathname) => {
  if (!pathname) return false;
  if (TRANSMISSION_PATHS.has(pathname)) return true;
  // Order detail pages are read+photo-upload for transmission workflow
  if (pathname.startsWith("/order/")) return true;
  // Aly's transmission subroutes
  if (pathname.startsWith("/aly-")) return true;
  return false;
};

const RequireClockInOrOffice = ({ children }) => {
  const { user } = useAuth();
  const { loading, clockedIn } = useClockInStatus();
  const location = useLocation();
  // only applies to Operator role; other roles bypass entirely
  if (user?.role !== "Operator") return children;
  // Transmission screens never require clock-in (separate from paid office work)
  if (isTransmissionPath(location.pathname)) return children;
  if (loading) return <div className="p-10 text-center text-xs text-gray-400">Checking clock-in status...</div>;
  if (!clockedIn && location.pathname !== "/office") {
    return <Navigate to="/office" replace state={{ reason: "clock_in_required" }} />;
  }
  return children;
};

// Home redirect - route to the first allowed screen per role
const HomeRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const role = user.role;
  // Operators: FIRST screen after login is always the Office Workforce page (forces Clock In)
  if (role === 'Operator') return <Navigate to="/office" replace />;
  if (role === 'Admin' || role === 'Finance') return <Navigate to="/dashboard" replace />;
  if (role === 'Scheduling') return <Navigate to="/scheduling" replace />;
  if (role === 'Dispatch') return <Navigate to="/dispatch" replace />;
  if (role === 'National Transmission') return <Navigate to="/aly-calendar" replace />;
  if (role === 'Inspector') return <Navigate to="/my-orders" replace />;
  if (role === 'Operations') return <Navigate to="/scheduling" replace />;
  return <Navigate to="/dashboard" replace />;
};

// Upload Orders Page — 4 Distinct Entry Points
const UPLOAD_TYPES = [
  {
    key: 'safeguard_regular',
    title: 'SAFEGUARD \u2013 REGULAR ORDERS',
    description: 'Ordens regulares do Safeguard. Inspector rule aplicada.',
    color: '#2F5D8A',
    bgColor: '#EBF1F7',
    borderColor: '#2F5D8A',
  },
  {
    key: 'safeguard_schedule',
    title: 'SAFEGUARD \u2013 SCHEDULE',
    description: 'Agendamentos do Safeguard. Classificação por workcode.',
    color: '#6B8F62',
    bgColor: '#EFF5ED',
    borderColor: '#6B8F62',
  },
  {
    key: 'national_orders',
    title: 'NATIONAL \u2013 ORDERS',
    description: 'Ordens da National. Sem apontamento = Ligação do dia.',
    color: '#9A7A3E',
    bgColor: '#F8F3EA',
    borderColor: '#9A7A3E',
  },
  {
    key: 'national_appointments',
    title: 'NATIONAL \u2013 APPOINTMENTS SET',
    description: 'Apontamentos da National. Marca quais ordens já têm agendamento.',
    color: '#7A5C9A',
    bgColor: '#F3EEF8',
    borderColor: '#7A5C9A',
  },
];

const UploadSection = ({ config }) => {
  const [file, setFile] = useState(null);
  const [uploadDate, setUploadDate] = useState(() => aarifiTodayStr());
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file || !uploadDate) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Operational date the operator says this file belongs to (NY-local,
      // YYYY-MM-DD). Surfaced on the upload card so every Order file is
      // tied to a specific business day. Backend ignores unknown form
      // fields, so this is forward-compatible until backend wires it up.
      formData.append('upload_date', uploadDate);
      const endpoint = config.key === 'national_appointments'
        ? `${API}/orders/import-national-appointments`
        : `${API}/orders/import?upload_type=${config.key}`;
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // Report-style xlsx parsing can take a few seconds
      });
      setResult({
        type: 'success',
        message: response.data.message || 'Upload concluído com sucesso',
        stats: response.data.stats || response.data,
        routing: response.data.routing,
        isAppointments: config.key === 'national_appointments',
        raw: response.data,
      });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      // Surface a structured error with the backend's detail so the operator
      // knows exactly why the file was rejected (missing Order IDs, no
      // APPOINTMENT blocks, parse error, auth error, etc.).
      const backendDetail = error.response?.data?.detail;
      const hint = config.key === 'national_appointments'
        ? 'Esperado: export do National em xlsx contendo blocos "APPOINTMENT: ... Order ID: ...". CSV também é aceito.'
        : 'Formato esperado: Excel (.xlsx) ou CSV com as colunas padrão da planilha.';
      setResult({
        type: 'error',
        message: backendDetail || 'Falha no upload. Verifique o formato do arquivo.',
        hint,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls'))) {
      setFile(droppedFile);
      setResult(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border-2 p-5" style={{ borderColor: config.borderColor + '40' }} data-testid={`upload-section-${config.key}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-2 h-8 rounded-full" style={{ backgroundColor: config.color }} />
        <div>
          <h2 className="text-base font-bold text-gray-800" data-testid={`upload-title-${config.key}`}>{config.title}</h2>
          <p className="text-gray-500 text-xs">{config.description}</p>
        </div>
      </div>

      <div className="mb-3" data-testid={`upload-date-row-${config.key}`}>
        <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wider block mb-1.5">
          Upload Date <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          value={uploadDate}
          onChange={(e) => setUploadDate(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5D8A]/30"
          style={{ borderColor: uploadDate ? config.color + '40' : '#FCA5A5' }}
          data-testid={`upload-date-input-${config.key}`}
        />
        <p className="text-[10px] text-gray-400 mt-1">Operational date this file belongs to (NY-local).</p>
      </div>

      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4"
        style={{ borderColor: file ? config.color : '#D1D5DB' }}
        data-testid={`upload-drop-zone-${config.key}`}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv,.xlsx,.xls"
          className="hidden"
          data-testid={`upload-file-input-${config.key}`}
        />
        <Upload className="h-8 w-8 mx-auto mb-2" style={{ color: file ? config.color : '#9CA3AF' }} />
        {file ? (
          <div>
            <p className="text-gray-800 font-medium text-sm">{file.name}</p>
            <p className="text-gray-500 text-xs mt-1">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
        ) : (
          <div>
            <p className="text-gray-600 font-medium text-sm">Clique ou arraste o arquivo</p>
            <p className="text-gray-400 text-xs mt-1">Excel (.xlsx) ou CSV</p>
          </div>
        )}
      </div>

      {result && (
        <div className={`rounded-lg p-3 mb-4 ${result.type === 'success' ? 'bg-[#A7C4A0]/15 border border-[#A7C4A0]/40' : 'bg-[#F5EDED] border border-[#C07070]/30'}`}>
          {result.type === 'success' ? (
            result.isAppointments ? (
              <div data-testid="upload-appts-result">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-[#6B8F62]" />
                  <p className="text-[#6B8F62] font-medium text-sm">{result.message}</p>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="bg-white rounded p-2 text-center">
                    <p className="text-gray-500">No Arquivo</p>
                    <p className="text-lg font-bold text-gray-800">{result.raw?.total_in_file ?? 0}</p>
                  </div>
                  <div className="bg-white rounded p-2 text-center">
                    <p className="text-gray-500">Aplicados</p>
                    <p className="text-lg font-bold text-[#6B8F62]">{result.raw?.matched ?? 0}</p>
                  </div>
                  <div className="bg-white rounded p-2 text-center">
                    <p className="text-gray-500">Já Marcados</p>
                    <p className="text-lg font-bold text-gray-700">{result.raw?.already_set ?? 0}</p>
                  </div>
                  <div className="bg-white rounded p-2 text-center">
                    <p className="text-gray-500">Não Encontrados</p>
                    <p className="text-lg font-bold text-[#B08A3E]">{result.raw?.not_found ?? 0}</p>
                  </div>
                </div>
              </div>
            ) : (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-[#6B8F62]" />
                <p className="text-[#6B8F62] font-medium text-sm">{result.message}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white rounded p-2 text-center">
                  <p className="text-gray-500">Criadas</p>
                  <p className="text-lg font-bold text-gray-800">{result.stats?.created ?? 0}</p>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <p className="text-gray-500">Atualizadas</p>
                  <p className="text-lg font-bold text-gray-800">{result.stats?.updated ?? 0}</p>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <p className="text-gray-500">Erros</p>
                  <p className="text-lg font-bold text-gray-800">{result.stats?.errors ?? 0}</p>
                </div>
                {(result.stats?.reactivated ?? 0) > 0 && (
                  <div className="bg-white rounded p-2 text-center">
                    <p className="text-gray-500">Reativadas</p>
                    <p className="text-lg font-bold text-[#B08A3E]">{result.stats.reactivated}</p>
                  </div>
                )}
                {(result.stats?.duplicates ?? 0) > 0 && (
                  <div className="bg-white rounded p-2 text-center">
                    <p className="text-gray-500">Duplicatas</p>
                    <p className="text-lg font-bold text-[#9A7A3E]">{result.stats.duplicates}</p>
                  </div>
                )}
              </div>
              {result.routing && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-gray-600 font-medium text-xs mb-2">Roteamento:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex justify-between bg-white rounded px-2 py-1.5">
                      <span className="text-gray-500">Com Inspector</span>
                      <span className="font-bold text-[#2F5D8A]">{result.routing.assigned_to_inspector}</span>
                    </div>
                    <div className="flex justify-between bg-white rounded px-2 py-1.5">
                      <span className="text-gray-500">Sem Inspector</span>
                      <span className="font-bold text-gray-700">{result.routing.unassigned}</span>
                    </div>
                    {result.routing.on_hold > 0 && (
                      <div className="flex justify-between bg-white rounded px-2 py-1.5">
                        <span className="text-gray-500">Em Hold</span>
                        <span className="font-bold text-[#9A7A3E]">{result.routing.on_hold}</span>
                      </div>
                    )}
                    {result.routing.appointment_set > 0 && (
                      <div className="flex justify-between bg-white rounded px-2 py-1.5">
                        <span className="text-gray-500">Appointment Set</span>
                        <span className="font-bold text-[#6B8F62]">{result.routing.appointment_set}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            )
          ) : (
            <div>
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-[#8B4545] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-[#8B4545] text-sm font-semibold">{result.message}</p>
                  {result.hint && <p className="text-[#8B4545]/80 text-xs mt-1">{result.hint}</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || !uploadDate || uploading}
        className="w-full py-2.5 text-white font-semibold rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm"
        style={{ backgroundColor: (file && uploadDate) ? config.color : '#9CA3AF' }}
        data-testid={`upload-submit-btn-${config.key}`}
      >
        {uploading ? 'Enviando...' : `Upload ${config.title}`}
      </button>
    </div>
  );
};

const CheckPaymentUpload = () => null;

const FINANCE_UPLOAD_TYPES = [
  {
    key: 'orders_manual_finance',
    title: 'ORDERS MANUAL FINANCE',
    description: 'Manual finance orders spreadsheet. Populates daily closing by route date.',
    color: '#2F5D8A',
    bgColor: '#EBF1F7',
    borderColor: '#2F5D8A',
    endpoint: 'upload/smart',
  },
  {
    key: 'payments_cheque',
    title: 'PAYMENTS CHEQUE',
    description: 'Payment check spreadsheet. Matches orders and registers received amounts.',
    color: '#6B8F62',
    bgColor: '#EFF5ED',
    borderColor: '#6B8F62',
    endpoint: 'upload/smart',
  },
  {
    key: 'order_transmission',
    title: 'ORDER TRANSMISSION',
    description: 'Transmission spreadsheet for Vitoria / transmission department.',
    color: '#B08A3E',
    bgColor: '#F8F3EA',
    borderColor: '#B08A3E',
    endpoint: 'upload/transmission',
  },
  {
    key: 'reconciliation',
    title: 'RECONCILIANTION',
    description: 'Safeguard reconciliation file for audit. Used by Finance to validate paid periods.',
    color: '#6B5B8D',
    bgColor: '#F3F0F8',
    borderColor: '#6B5B8D',
    endpoint: 'upload/reconciliation',
  },
];

const FinanceUploadSection = ({ config }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) { setFile(selectedFile); setResult(null); }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (config.key === 'order_transmission') {
        fd.append('operator', 'Vitoria');
      }
      const res = await axios.post(`${API}/${config.endpoint}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult({ type: 'success', data: res.data });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.detail || 'Upload failed' });
    } finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.csv') || f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))) {
      setFile(f); setResult(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border-2 p-5" style={{ borderColor: config.borderColor + '40' }} data-testid={`finance-upload-${config.key}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-2 h-8 rounded-full" style={{ backgroundColor: config.color }} />
        <div>
          <h2 className="text-base font-bold text-gray-800" data-testid={`finance-upload-title-${config.key}`}>{config.title}</h2>
          <p className="text-gray-500 text-xs">{config.description}</p>
        </div>
      </div>

      <div onClick={() => fileInputRef.current?.click()} onDrop={handleDrop} onDragOver={e => e.preventDefault()}
        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4"
        style={{ borderColor: file ? config.color : '#D1D5DB' }}
        data-testid={`finance-drop-zone-${config.key}`}
      >
        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv,.xlsx,.xls" className="hidden" data-testid={`finance-file-input-${config.key}`} />
        <Upload className="h-8 w-8 mx-auto mb-2" style={{ color: file ? config.color : '#9CA3AF' }} />
        {file ? (
          <div>
            <p className="text-sm font-semibold" style={{ color: config.color }}>{file.name}</p>
            <p className="text-gray-400 text-xs mt-1">{(file.size / 1024).toFixed(0)} KB</p>
          </div>
        ) : (
          <div>
            <p className="text-gray-500 text-sm">Clique ou arraste o arquivo</p>
            <p className="text-gray-400 text-xs">Excel (.xlsx) ou CSV</p>
          </div>
        )}
      </div>

      <button onClick={handleUpload} disabled={!file || uploading}
        className="w-full py-2.5 text-white font-semibold rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm"
        style={{ backgroundColor: file ? config.color : '#9CA3AF' }}
        data-testid={`finance-upload-btn-${config.key}`}
      >
        {uploading ? 'Processando...' : `Upload ${config.title}`}
      </button>

      {result && (
        <div className={`mt-3 p-3 rounded-lg text-xs ${result.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}
          data-testid={`finance-result-${config.key}`}>
          {result.type === 'success' ? (
            <div className="space-y-1">
              <p className="font-semibold">{result.data.file_type || 'Upload Complete'}</p>
              <p>Rows: {result.data.total_rows}</p>
              {result.data.inserted !== undefined && <p>Inserted: <span className="font-bold">{result.data.inserted}</span></p>}
              {result.data.matched !== undefined && <p>Matched: <span className="font-bold">{result.data.matched}</span></p>}
              {result.data.skipped_duplicate > 0 && <p>Duplicates ignored: <span className="font-bold">{result.data.skipped_duplicate}</span></p>}
              {result.data.already_received > 0 && <p>Already received: {result.data.already_received}</p>}
              {result.data.not_found > 0 && <p>Not found: {result.data.not_found}</p>}
              {result.data.total_received_amount > 0 && <p>Total received: ${result.data.total_received_amount}</p>}
              {result.data.skipped_details && result.data.skipped_details.length > 0 && (
                <div className="mt-2">
                  <button onClick={() => setShowIgnored(!showIgnored)}
                    className="text-[10px] font-semibold underline text-gray-500 hover:text-gray-700" data-testid={`view-ignored-${config.key}`}>
                    {showIgnored ? 'Hide' : 'View'} Ignored Rows ({result.data.skipped_details.length})
                  </button>
                  {showIgnored && (
                    <div className="mt-1 max-h-40 overflow-y-auto bg-white rounded border border-gray-200 divide-y divide-gray-100" data-testid={`ignored-list-${config.key}`}>
                      {result.data.skipped_details.map((d, i) => (
                        <div key={i} className="px-2 py-1 text-[10px] flex items-center justify-between">
                          <span className="text-gray-700 font-medium">{d.order_number}</span>
                          {d.route_date && <span className="text-gray-400">{d.route_date}</span>}
                          {d.inspector && <span className="text-gray-400">{d.inspector}</span>}
                          <span className="text-gray-500">{d.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : <p>{result.message}</p>}
        </div>
      )}
    </div>
  );
};

// ═══ Helper: AARIFI/NY-local today (Step 4 — Angela 2026-05-09) ═══
// Backend route identity uses _aarifi_today_str() (America/New_York). The
// browser's `new Date().toISOString().slice(0,10)` returns the UTC day,
// which diverges from NY between 20:00 ET and 23:59 ET. This helper returns
// the same NY-local YYYY-MM-DD the backend uses so the UI default aligns
// with the operator's actual business day.
const aarifiTodayStr = () => {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return fmt.format(new Date());
};

// ═══ GPX Route Upload Section ═══
const RouteAssignmentUploadSection = () => {
  const [file, setFile] = useState(null);
  const [routeDate, setRouteDate] = useState(() => aarifiTodayStr()); // Step 4 — required, NY-local default
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    if (!file) { setError('Select a file first'); return; }
    if (!routeDate) { setError('Pick the Route Date for this upload'); return; }
    setUploading(true); setError(''); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('route_date', routeDate); // Step 4 — operator-selected route_date is required
      const res = await axios.post(`${API}/dispatch/route-assignment-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border-2 border-[#2F5D8A]/20 p-5 mb-6 shadow-sm" data-testid="route-assignment-upload">
      <div className="flex items-center gap-2 mb-2">
        <ClipboardCheck className="h-5 w-5 text-[#2F5D8A]" />
        <h2 className="text-base font-bold text-[#1F2937]">Route Assignment Upload</h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Upload Angela's printed route spreadsheet. The system reads each row,
        finds the order by Order Number, and assigns it to the matching active
        Dispatch inspector. Direct mirror — no validation, no lookup, no audit.
      </p>
      <p className="text-[11px] text-gray-400 mb-4">
        Required columns (header names tolerant): <strong>Order</strong> (or Order Number / Order_ID) and <strong>Inspector</strong> (or Inspector Login / Inspector Code, e.g. <code>MA-RODRIGOHAMER</code>).
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
        <div className="sm:col-span-3">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">
            Route Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={routeDate}
            onChange={(e) => { setRouteDate(e.target.value); setResult(null); setError(''); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-[#2F5D8A]"
            data-testid="route-assignment-date-input"
            required
          />
          <p className="text-[10px] text-gray-400 mt-1">Operator pick · default = NY today · choose tomorrow to prep next day's route.</p>
        </div>
        <div className="sm:col-span-6">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">Spreadsheet (.xlsx)</label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(''); }}
            className="text-sm w-full"
            data-testid="route-assignment-file-input"
          />
        </div>
        <div className="sm:col-span-3">
          <button
            onClick={handleUpload}
            disabled={!file || !routeDate || uploading}
            className="w-full px-4 py-2 bg-[#2F5D8A] hover:bg-[#264A6E] disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-1.5"
            data-testid="route-assignment-upload-btn"
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading ? 'Uploading…' : 'Upload & Assign'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="route-assignment-error">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-3" data-testid="route-assignment-result">
          {result.is_reupload && (
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="route-reupload-banner">
              <p className="text-xs font-semibold text-indigo-800">Reupload detected</p>
              <p className="text-[11px] text-indigo-700 mt-0.5">
                This corrected sheet replaced {result.replaced_batch_ids?.length || 0} previous batch{(result.replaced_batch_ids?.length || 0) === 1 ? '' : 'es'}
                {result.replaced_batch_ids?.length > 0 && (
                  <> (<code className="text-indigo-900">{result.replaced_batch_ids.map(b => String(b).slice(0, 8)).join(', ')}</code>)</>
                )}
                . Closed/QA/Paid/Archived/Completed orders were preserved.
              </p>
            </div>
          )}
          {result.operator_selected_route_date && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2"
                 data-testid="route-result-route-date">
              <span className="text-[10px] uppercase font-bold text-blue-700 tracking-wider">Route Date</span>
              <span className="text-sm font-bold text-blue-900">{result.operator_selected_route_date}</span>
              {result.batch_id && (
                <span className="ml-auto text-[10px] text-blue-700/70">Batch <code>{String(result.batch_id).slice(0, 8)}</code></span>
              )}
            </div>
          )}
          <div className={`grid gap-3 ${result.is_reupload ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-4'}`}>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Assigned</p>
              <p className="text-2xl font-bold text-emerald-900" data-testid="route-result-assigned">{result.assigned_count}</p>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-[10px] uppercase font-bold text-amber-700 tracking-wider">Reassigned</p>
              <p className="text-2xl font-bold text-amber-900" data-testid="route-result-reassigned">{result.reassigned_count}</p>
            </div>
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-[10px] uppercase font-bold text-gray-600 tracking-wider">Already Assigned</p>
              <p className="text-2xl font-bold text-gray-800" data-testid="route-result-already">{result.skipped_already_assigned}</p>
            </div>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-[10px] uppercase font-bold text-red-700 tracking-wider">Errors</p>
              <p className="text-2xl font-bold text-red-900" data-testid="route-result-errors">{result.errors_count}</p>
            </div>
            {result.is_reupload && (
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="route-result-superseded">
                <p className="text-[10px] uppercase font-bold text-indigo-700 tracking-wider">Superseded</p>
                <p className="text-2xl font-bold text-indigo-900" data-testid="route-result-superseded-count">{result.superseded_count || 0}</p>
              </div>
            )}
          </div>

          {result.superseded_count > 0 && (
            <div className="border border-indigo-200 rounded-lg overflow-hidden" data-testid="route-result-superseded-list">
              <p className="px-3 py-1.5 bg-indigo-50 text-xs font-bold text-indigo-800 uppercase tracking-wider">
                Dropped from active route ({result.superseded_count})
              </p>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Order #</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Previous Inspector</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Previous Batch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.superseded_orders || []).map((s, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-2 py-1.5 font-mono">{s.order_number || '—'}</td>
                        <td className="px-2 py-1.5">{s.previous_inspector || '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-gray-500">{s.previous_batch_id ? String(s.previous_batch_id).slice(0, 8) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.errors_count > 0 && (
            <div className="border border-red-200 rounded-lg overflow-hidden">
              <p className="px-3 py-1.5 bg-red-50 text-xs font-bold text-red-800 uppercase tracking-wider">Error rows ({result.errors_count})</p>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-xs" data-testid="route-result-errors-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Row</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Order #</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Inspector</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-2 py-1.5">{e.row}</td>
                        <td className="px-2 py-1.5 font-mono">{e.order_number || '—'}</td>
                        <td className="px-2 py-1.5">{e.inspector || '—'}</td>
                        <td className="px-2 py-1.5 text-red-700">{e.reason.replace(/_/g, ' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.assigned_orders && result.assigned_orders.length > 0 && (
            <details className="border border-emerald-200 rounded-lg" data-testid="route-result-assigned-details">
              <summary className="px-3 py-1.5 bg-emerald-50 text-xs font-bold text-emerald-800 uppercase tracking-wider cursor-pointer">
                Assigned rows ({result.assigned_orders.length})
              </summary>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Row</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Order #</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Inspector</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.assigned_orders.map((o, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-2 py-1.5">{o.row}</td>
                        <td className="px-2 py-1.5 font-mono">{o.order_number}</td>
                        <td className="px-2 py-1.5">{o.inspector}</td>
                        <td className="px-2 py-1.5 text-gray-600">
                          {o.action === 'reassigned' ? `reassigned (was: ${o.previous_inspector || '—'})` : o.action}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* PER-INSPECTOR RECONCILIATION — uploaded vs placed vs currently assigned in Dispatch */}
          {result.per_inspector_reconciliation && result.per_inspector_reconciliation.length > 0 && (
            <div className="border-2 border-[#2F5D8A]/20 rounded-lg" data-testid="route-result-reconciliation">
              <p className="px-3 py-1.5 bg-[#2F5D8A]/5 text-xs font-bold text-[#1F2937] uppercase tracking-wider flex items-center justify-between">
                <span>Per-Inspector Reconciliation</span>
                <span className="text-[10px] font-normal text-gray-500">Uploaded vs Placed vs Currently in Dispatch</span>
              </p>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Inspector</th>
                      <th className="px-2 py-1.5 text-right text-gray-500 font-semibold">Uploaded</th>
                      <th className="px-2 py-1.5 text-right text-gray-500 font-semibold">Placed</th>
                      <th className="px-2 py-1.5 text-right text-gray-500 font-semibold">Missing</th>
                      <th className="px-2 py-1.5 text-right text-gray-500 font-semibold">In Dispatch (total)</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Flag</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Missing rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.per_inspector_reconciliation.map((r, i) => {
                      const flagClass = r.flag === 'match' ? 'text-emerald-700 bg-emerald-50'
                        : r.flag === 'unresolved_inspector' ? 'text-red-700 bg-red-50'
                        : 'text-amber-700 bg-amber-50';
                      const inspectorLabel = r.inspector_resolved
                        || (r.inspector_input_samples?.[0] ? `(unresolved: ${r.inspector_input_samples[0]})` : r.key);
                      return (
                        <tr key={i} className="border-t border-gray-100" data-testid={`route-recon-row-${i}`}>
                          <td className="px-2 py-1.5 font-semibold">{inspectorLabel}</td>
                          <td className="px-2 py-1.5 text-right">{r.uploaded_count}</td>
                          <td className="px-2 py-1.5 text-right">{r.placed_count}</td>
                          <td className={`px-2 py-1.5 text-right ${r.missing_count > 0 ? 'text-red-700 font-bold' : 'text-gray-500'}`}>{r.missing_count}</td>
                          <td className="px-2 py-1.5 text-right text-gray-600">{r.currently_assigned_in_dispatch}</td>
                          <td className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider ${flagClass}`}>{r.flag.replace(/_/g, ' ')}</td>
                          <td className="px-2 py-1.5 text-[11px] text-gray-700">
                            {r.missing_rows && r.missing_rows.length > 0 ? (
                              <details>
                                <summary className="cursor-pointer text-red-700">{r.missing_count} row{r.missing_count !== 1 ? 's' : ''}</summary>
                                <ul className="mt-1 space-y-0.5 list-none">
                                  {r.missing_rows.map((m, j) => (
                                    <li key={j} className="font-mono">
                                      row {m.row} · #{m.order || '—'} · <span className="text-red-700">{m.reason.replace(/_/g, ' ')}</span>
                                    </li>
                                  ))}
                                </ul>
                              </details>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GpxRouteUploadSection = () => {
  const [inspectors, setInspectors] = useState([]);
  const [selectedInspector, setSelectedInspector] = useState("");
  const [routeDate, setRouteDate] = useState(() => aarifiTodayStr()); // Step 4 — NY-local default (Angela 2026-05-09)
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [todayRoutes, setTodayRoutes] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [inspRes, routesRes] = await Promise.all([
        axios.get(`${API}/inspectors`),
        axios.get(`${API}/routes/all-today`),
      ]);
      setInspectors(inspRes.data || []);
      setTodayRoutes(routesRes.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async () => {
    if (!file || !selectedInspector || !routeDate) return;
    setUploading(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("route_date", routeDate);
    fd.append("inspector_name", selectedInspector);
    try {
      const res = await axios.post(`${API}/routes/upload-gpx`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ success: true, data: res.data });
      setFile(null);
      fetchData();
    } catch (e) {
      setResult({ success: false, error: e.response?.data?.detail || "Upload failed" });
    }
    setUploading(false);
  };

  return (
    <div className="space-y-5" data-testid="gpx-upload-section">
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="text-sm font-bold text-gray-700 mb-4">Upload Inspector Route (GPX)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">Inspector</label>
            <select value={selectedInspector} onChange={e => setSelectedInspector(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-[#2F5D8A]"
              data-testid="gpx-inspector-select">
              <option value="">Select inspector...</option>
              {inspectors.map(i => (
                <option key={i.id || i.name} value={i.name}>{i.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">Route Date</label>
            <input type="date" value={routeDate} onChange={e => setRouteDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-[#2F5D8A]"
              data-testid="gpx-date" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">GPX File</label>
            <label className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2F5D8A]/40 transition-colors">
              <Upload className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-sm text-gray-500 truncate">{file ? file.name : 'Choose .gpx file'}</span>
              <input type="file" accept=".gpx,.xml" className="hidden" onChange={e => setFile(e.target.files[0])} data-testid="gpx-file-input" />
            </label>
          </div>
        </div>
        <button onClick={handleUpload} disabled={!file || !selectedInspector || !routeDate || uploading}
          className="px-5 py-2.5 bg-[#2F5D8A] hover:bg-[#264A6E] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-40 flex items-center gap-2"
          data-testid="gpx-upload-btn">
          <MapPin className="h-4 w-4" />
          {uploading ? 'Uploading...' : 'Upload Route'}
        </button>
        {result && (
          <div className={`mt-3 p-3 rounded-lg text-xs ${result.success ? 'bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#4A7A56]' : 'bg-[#C07070]/10 border border-[#C07070]/30 text-[#8B4545]'}`}>
            {result.success ? `Route uploaded for ${result.data.inspector} on ${result.data.route_date}` : result.error}
          </div>
        )}
      </div>

      {/* Today's Routes */}
      {todayRoutes.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="today-routes">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Today's Routes ({todayRoutes.length})</h3>
          <div className="space-y-2">
            {todayRoutes.map((r, i) => (
              <div key={r.id || i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-[#2F5D8A]" />
                  <div>
                    <p className="text-sm font-semibold text-gray-700">{r.inspector}</p>
                    <p className="text-[10px] text-gray-400">{r.filename} — uploaded by {r.uploaded_by}</p>
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-[#6B8F62]/10 text-[#4A7A56]">Active</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══ National Attempts Section ═══
const NationalAttemptsSection = () => {
  const [queues, setQueues] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQueue, setActiveQueue] = useState("not_logged");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, hRes] = await Promise.all([
        axios.get(`${API}/national-attempts/queues`),
        axios.get(`${API}/national-attempts/history`),
      ]);
      setQueues(qRes.data);
      setHistory(hRes.data.history || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const queueConfigs = [
    { key: "not_logged", label: "Not Logged in National", dataKey: "not_logged_national", color: "bg-[#C07070]", textColor: "text-[#C07070]", borderColor: "border-[#C07070]" },
    { key: "no_attempt", label: "No Attempt Today", dataKey: "no_attempt_today", color: "bg-[#D1A054]", textColor: "text-[#D1A054]", borderColor: "border-[#D1A054]" },
    { key: "missing", label: "Missing Confirmation", dataKey: "missing_confirmation", color: "bg-gray-500", textColor: "text-gray-500", borderColor: "border-gray-400" },
  ];

  const activeQueueData = queues ? queues[queueConfigs.find(q => q.key === activeQueue)?.dataKey] : null;

  return (
    <div className="space-y-5" data-testid="national-attempts-section">
      {/* Info banner */}
      <div className="bg-[#2F5D8A]/5 border border-[#2F5D8A]/15 rounded-lg p-4">
        <p className="text-xs text-[#2F5D8A] font-medium">
          Attempts data is processed automatically from the National Appointments upload.
          Include columns: "Attempts Made Today", "Attempt Date", "Logged in National" in the same file.
        </p>
      </div>

      {/* Queue cards */}
      <div className="grid grid-cols-3 gap-3" data-testid="national-queue-cards">
        {queueConfigs.map(q => {
          const count = queues?.[q.dataKey]?.count || 0;
          return (
            <button key={q.key} onClick={() => setActiveQueue(q.key)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${activeQueue === q.key ? `${q.borderColor} bg-white shadow-sm` : 'border-gray-200 bg-white hover:border-gray-300'}`}
              data-testid={`queue-card-${q.key}`}>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{q.label}</p>
              <p className={`text-3xl font-bold mt-1 ${count > 0 ? q.textColor : 'text-gray-300'}`}>{count}</p>
            </button>
          );
        })}
      </div>

      {/* Active queue table */}
      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5D8A]" /></div>
      ) : activeQueueData && activeQueueData.orders.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="national-queue-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2.5">Order #</th>
                <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2.5">Address</th>
                <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2.5">Company</th>
                <th className="text-center text-[10px] font-semibold text-gray-400 uppercase px-3 py-2.5">Attempts</th>
                <th className="text-center text-[10px] font-semibold text-gray-400 uppercase px-3 py-2.5">Logged National</th>
                <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2.5">Operator</th>
                <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {activeQueueData.orders.map((o, idx) => (
                <tr key={o.order_number || idx} className="border-b border-gray-50 hover:bg-gray-50/40" data-testid={`nat-row-${idx}`}>
                  <td className="px-3 py-2 font-semibold text-[#2F5D8A]">{o.order_number}</td>
                  <td className="px-3 py-2 text-gray-600">{o.address}, {o.city} {o.state}</td>
                  <td className="px-3 py-2 text-gray-500">{o.company}</td>
                  <td className="px-3 py-2 text-center font-bold text-gray-700">{o.attempts_manual_day ?? '-'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                      o.attempt_logged_national === 'Yes' ? 'bg-[#A7C4A0]/20 text-[#4A7A56]' :
                      o.attempt_logged_national === 'No' ? 'bg-[#C07070]/15 text-[#C07070]' :
                      'bg-gray-100 text-gray-400'
                    }`}>{o.attempt_logged_national || '-'}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-500">{o.assigned_operator || o.assigned_inspector || '-'}</td>
                  <td className="px-3 py-2"><span className="text-[9px] font-medium text-gray-400">{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-10 bg-white rounded-lg border border-gray-200" data-testid="national-empty">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-[#A7C4A0]" />
          <p className="text-sm font-medium text-gray-400">No orders in this queue</p>
          <p className="text-[10px] text-gray-300 mt-1">Upload a file to populate the queues</p>
        </div>
      )}

      {/* Upload history */}
      {history.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="upload-history">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Upload History</h3>
          <div className="space-y-1.5">
            {history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 text-[10px] text-gray-500 py-1 border-b border-gray-50 last:border-0">
                <span className="text-gray-400 w-32 shrink-0">{new Date(h.uploaded_at).toLocaleString()}</span>
                <span className="font-medium text-gray-600">{h.filename}</span>
                <span className="ml-auto text-gray-400">{h.orders_updated} updated / {h.rows_processed} rows</span>
                <span className="text-gray-400">by {h.uploaded_by}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Zone Dates upload (Admin). Simple XLSX upload for future
// appointment date spreadsheets. Uses /api/admin/appt-dates/upload-xlsx
// with dry-run preview + apply.
const ZoneDatesUploadSection = () => {
  const [uploadYear, setUploadYear] = React.useState(new Date().getFullYear());
  const [uploadFile, setUploadFile] = React.useState(null);
  const [uploadPreview, setUploadPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState(null);

  const handlePreview = async () => {
    if (!uploadFile) return;
    setBusy(true); setUploadPreview(null); setMessage(null);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("year", String(uploadYear));
      const res = await axios.post(`${API}/admin/appt-dates/upload-xlsx?dry_run=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadPreview(res.data);
      setMessage({ type: "success", text: `Preview — ${res.data.totals.dates_to_add} to add, ${res.data.totals.dates_to_remove} to remove, ${res.data.totals.dates_unchanged} unchanged.` });
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Preview failed" });
    } finally { setBusy(false); }
  };

  const handleApply = async () => {
    if (!uploadFile || !uploadPreview) return;
    if (!window.confirm(`Apply changes for year ${uploadPreview.year}, months ${uploadPreview.months.join(", ")}? Only these months will be replaced — other months stay untouched.`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("year", String(uploadYear));
      const res = await axios.post(`${API}/admin/appt-dates/upload-xlsx?dry_run=false`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadPreview(res.data);
      setMessage({ type: "success", text: `Applied — inserted ${res.data.applied.dates_inserted}, deleted ${res.data.applied.dates_deleted}, created ${res.data.applied.created_zones} zone(s), appended ${res.data.applied.cities_appended} city assignment(s).` });
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Apply failed" });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="zone-dates-upload-section">
      {message && (
        <div className={`p-2.5 rounded-lg flex items-center justify-between text-sm ${message.type === "success" ? "bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]" : "bg-[#C07070]/12 border border-[#C07070]/30 text-[#8B4545]"}`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {/* Upload XLSX */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="zone-dates-upload-block">
        <p className="text-sm font-semibold text-[#1F2937] mb-1">Upload zone dates spreadsheet</p>
        <p className="text-[11px] text-gray-500 mb-3">
          Use this to add <strong>new zone dates</strong> when a new spreadsheet becomes available. Sheet <strong>&quot;in&quot;</strong>
          with columns <strong>State, Zone, City</strong> and one or more month columns (e.g. <em>January, February, ...</em>).
          Cells contain date tokens like &quot;01/06, 01/21&quot;. Only the months present in the sheet are replaced for the
          selected year — other months stay untouched. <strong className="text-[#8B4545]">Double-check the Year field matches the spreadsheet.</strong>
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Year</label>
            <input
              type="number"
              min={2020}
              max={2100}
              value={uploadYear}
              onChange={e => setUploadYear(Number(e.target.value) || uploadYear)}
              className="w-24 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none"
              data-testid="zd-year-input"
            />
          </div>
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Spreadsheet (.xlsx)</label>
            <input
              type="file"
              accept=".xlsx"
              onChange={e => { setUploadFile(e.target.files?.[0] || null); setUploadPreview(null); }}
              className="block w-full text-xs text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-[#2F5D8A]/10 file:text-[#2F5D8A] hover:file:bg-[#2F5D8A]/20"
              data-testid="zd-file-input"
            />
          </div>
          <button
            onClick={handlePreview}
            disabled={!uploadFile || busy}
            className="px-3 py-2 bg-white border border-[#2F5D8A]/30 text-[#2F5D8A] text-sm font-medium rounded-lg hover:bg-[#2F5D8A]/5 disabled:opacity-40"
            data-testid="zd-preview-btn"
          >
            {busy && !uploadPreview?.applied ? "Loading..." : "Preview"}
          </button>
          <button
            onClick={handleApply}
            disabled={!uploadPreview || busy || uploadPreview?.applied}
            className="px-3 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-medium rounded-lg disabled:opacity-40"
            data-testid="zd-apply-btn"
          >
            {uploadPreview?.applied ? "Applied" : (busy && uploadPreview ? "Applying..." : "Apply")}
          </button>
        </div>

        {uploadPreview && (
          <div className="mt-3 border border-gray-100 rounded-lg p-3 bg-gray-50/60" data-testid="zd-preview-result">
            <div className="flex flex-wrap items-center gap-3 text-xs mb-2">
              <span className="font-semibold text-gray-700">Year {uploadPreview.year}</span>
              <span className="text-gray-500">Months: {uploadPreview.months.join(", ")}</span>
              <span className="text-gray-500">Zones: {uploadPreview.totals.zones_in_upload}</span>
              <span className="text-[#6B8F62]">+ {uploadPreview.totals.dates_to_add} add</span>
              <span className="text-[#8B4545]">- {uploadPreview.totals.dates_to_remove} remove</span>
              <span className="text-gray-500">= {uploadPreview.totals.dates_unchanged} unchanged</span>
              {uploadPreview.new_zones_to_create.length > 0 && (
                <span className="text-[#B08A3E]">new zones: {uploadPreview.new_zones_to_create.join(", ")}</span>
              )}
              {uploadPreview.applied && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#A7C4A0]/20 text-[#6B8F62]">APPLIED</span>
              )}
            </div>
            {uploadPreview.unmapped_rows?.length > 0 && (
              <div className="text-[11px] text-[#8B4545] mb-2">
                {uploadPreview.unmapped_rows.length} row(s) could not be mapped to a canonical zone; check State/Zone columns.
              </div>
            )}
            <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 rounded border border-gray-100 bg-white">
              {uploadPreview.per_zone.map(pz => (
                <div key={pz.zone_id} className="px-2.5 py-1.5 flex items-center gap-3 text-[11px]" data-testid={`zd-diff-${pz.zone_id}`}>
                  <span className="font-medium text-[#1F2937] flex-1 truncate">{pz.zone_id}</span>
                  <span className="text-gray-400">exist {pz.existing_total} → new {pz.new_total}</span>
                  {pz.add.length > 0 && <span className="text-[#6B8F62]">+{pz.add.length}</span>}
                  {pz.remove.length > 0 && <span className="text-[#8B4545]">-{pz.remove.length}</span>}
                  {pz.add.length === 0 && pz.remove.length === 0 && (
                    <span className="text-gray-400">no change</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const UploadOrdersPage = () => {
  const [activeTab, setActiveTab] = useState("uploads");
  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-800" data-testid="upload-page-title">Upload</h1>
          <p className="text-gray-500 text-sm">Upload spreadsheets for orders and financial processing.</p>
        </div>

        {/* Tabs — Angela 2026-05-11: Upload page is the single upload hub.
            2026-07-30: Added Zone Dates tab for uploading new appointment
            date spreadsheets + auditing Jul-Dec 2026 sync. */}
        <div className="flex flex-col sm:flex-row gap-1 bg-white p-1 rounded-lg border border-gray-200 mb-6" data-testid="upload-tabs">
          <button onClick={() => setActiveTab("uploads")}
            className={`flex-1 px-4 py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-all ${activeTab === "uploads" ? "bg-[#2F5D8A] text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
            data-testid="tab-uploads">
            <Upload className="h-4 w-4" />
            <span className="flex flex-col items-start leading-tight">
              <span>Order Upload</span>
              <span className={`text-[10px] font-normal ${activeTab === "uploads" ? "text-white/80" : "text-gray-400"}`}>Upload orders for processing</span>
            </span>
          </button>
          <button onClick={() => setActiveTab("morning")}
            className={`flex-1 px-4 py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-all ${activeTab === "morning" ? "bg-[#2F5D8A] text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
            data-testid="tab-morning-upload">
            <CalendarCheck className="h-4 w-4" />
            <span className="flex flex-col items-start leading-tight">
              <span>Morning Dispatch Upload</span>
              <span className={`text-[10px] font-normal ${activeTab === "morning" ? "text-white/80" : "text-gray-400"}`}>Daily assignments to daily_assignments</span>
            </span>
          </button>
          <button onClick={() => setActiveTab("zone_dates")}
            className={`flex-1 px-4 py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-all ${activeTab === "zone_dates" ? "bg-[#2F5D8A] text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
            data-testid="tab-zone-dates">
            <CalendarCheck className="h-4 w-4" />
            <span className="flex flex-col items-start leading-tight">
              <span>Zone Dates</span>
              <span className={`text-[10px] font-normal ${activeTab === "zone_dates" ? "text-white/80" : "text-gray-400"}`}>Upload new appointment date spreadsheets</span>
            </span>
          </button>
        </div>

        {activeTab === "morning" ? (
          <MorningUploadPage />
        ) : activeTab === "zone_dates" ? (
          <ZoneDatesUploadSection />
        ) : (
          <>
            {/* Finance Section */}
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Finance</p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8" data-testid="finance-upload-section">
              {FINANCE_UPLOAD_TYPES.map(config => (
                <FinanceUploadSection key={config.key} config={config} />
              ))}
            </div>

            {/* Raw Order Imports */}
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Raw Order Imports</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {UPLOAD_TYPES.map((config) => (
                <UploadSection key={config.key} config={config} />
              ))}
            </div>

            <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-gray-500 text-xs">
                <strong className="text-gray-600">Data Protection:</strong> All uploads are append-only. Existing records are NEVER overwritten or deleted.
                Duplicates are detected automatically and silently skipped.
              </p>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

// Internal appointment time mapping: Real Time → Safeguard Reported Time (end of window)
// Legacy display mapping kept intact for orders saved before 2026-07-30.
const REAL_TIME_TO_SG_TIME = {
  '8:00-9:00': '09:00 – 13:00',
  '8:00-12:00': '12:00 – 16:00',
  '9:00-1:00': '13:00 – 17:00',
  '10:00-2:00': '14:00 – 18:00',
  '11:00-3:00': '15:00 – 19:00',
};
// 2026-07-30 (Angela): only these 3 internal windows are selectable now.
const REAL_TIME_OPTIONS = ['8:00-12:00', '9:00-1:00', '11:00-3:00'];

// External Morning Call — compliance mirror flag for External orders that
// arrived after 5 PM. Flag → shows in Scheduling > External Morning Calls card.
// Logging the call only records productivity — does NOT alter order workflow.
const ExternalMorningCallSection = ({ order, orderId, onChange }) => {
  const required = (order.external_morning_call_required || '').toLowerCase() === 'yes';
  const completed = !!order.external_morning_call_completed_at;
  const [flagging, setFlagging] = React.useState(false);
  const [showLog, setShowLog] = React.useState(false);
  const [logResult, setLogResult] = React.useState('');
  const [logNotes, setLogNotes] = React.useState('');
  const [err, setErr] = React.useState('');

  const flag = async (value) => {
    setFlagging(true); setErr('');
    try {
      await axios.patch(`${API}/orders/${orderId}/external-morning-call`, { required: value });
      onChange?.({
        external_morning_call_required: value ? 'yes' : 'no',
        ...(value ? { external_morning_call_completed_at: '', external_morning_call_completed_by: '', external_morning_call_result: '', external_morning_call_notes: '' } : {}),
      });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to update flag');
    } finally { setFlagging(false); }
  };

  const logCall = async () => {
    if (!logResult.trim()) { setErr('Select a result'); return; }
    setFlagging(true); setErr('');
    try {
      await axios.post(`${API}/orders/${orderId}/external-morning-call/log`, { result: logResult, notes: logNotes.trim() });
      const now = new Date().toISOString();
      onChange?.({
        external_morning_call_completed_at: now,
        external_morning_call_result: logResult,
        external_morning_call_notes: logNotes.trim(),
      });
      setShowLog(false); setLogResult(''); setLogNotes('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to log call');
    } finally { setFlagging(false); }
  };

  if (!required && !completed) {
    return (
      <div className="mb-6" data-testid="external-morning-call-section">
        <button
          onClick={() => flag(true)}
          disabled={flagging}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-[#FFF7ED] text-[#B45309] border border-[#FDBA74]/60 rounded-lg text-xs font-semibold transition-colors"
          data-testid="flag-morning-call-btn"
        >
          <Phone className="h-3.5 w-3.5" />
          Send to External Morning Calls Queue
        </button>
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid="external-morning-call-section">
      {required && !completed && (
        <div className="bg-[#FFF7ED] border border-[#FDBA74]/60 rounded-lg px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid="morning-call-pending">
          <div className="flex items-center gap-2 min-w-0">
            <Phone className="h-3.5 w-3.5 text-[#B45309] shrink-0" />
            <div className="text-xs min-w-0">
              <span className="font-bold text-[#9A3412]">External Morning Call pending.</span>
              {order.external_morning_call_required_reason && <span className="ml-1 text-[#9A3412]/80">{order.external_morning_call_required_reason}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowLog(true)} disabled={flagging}
              className="px-3 py-1 bg-[#B45309] hover:bg-[#92400E] text-white text-xs font-bold rounded"
              data-testid="log-morning-call-btn">
              Log Call
            </button>
            <button onClick={() => flag(false)} disabled={flagging}
              className="px-2 py-1 text-[10px] font-semibold text-[#9A3412] hover:bg-[#FDBA74]/30 rounded"
              data-testid="unflag-morning-call-btn">
              Remove flag
            </button>
          </div>
        </div>
      )}
      {completed && (
        <div className="bg-[#EFF5ED] border border-[#A7C4A0]/60 rounded-lg px-3 py-2 flex items-center gap-2 text-xs" data-testid="morning-call-completed">
          <CheckCircle className="h-3.5 w-3.5 text-[#4A7A56] shrink-0" />
          <span className="font-bold text-[#4A7A56]">Morning Call logged.</span>
          <span className="text-[#4A7A56]/80">{order.external_morning_call_result}</span>
          {order.external_morning_call_completed_by && <span className="text-gray-500">by {order.external_morning_call_completed_by}</span>}
          {order.external_morning_call_completed_at && <span className="text-gray-400">· {new Date(order.external_morning_call_completed_at).toLocaleString()}</span>}
        </div>
      )}

      {showLog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowLog(false)}>
          <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="log-morning-call-modal">
            <h3 className="text-base font-bold text-gray-800 mb-1">Log Morning Call</h3>
            <p className="text-gray-500 text-xs mb-4">Order #{order.order_number}</p>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Result *</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {['Spoke with PH', 'Left voicemail', 'No answer', 'Wrong number'].map(opt => (
                <button key={opt} onClick={() => setLogResult(opt)}
                  className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${logResult === opt ? 'bg-[#B45309] text-white border-[#B45309]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                  data-testid={`result-${opt.replace(/\s/g,'-').toLowerCase()}`}>
                  {opt}
                </button>
              ))}
            </div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Notes (optional)</label>
            <textarea value={logNotes} onChange={e => setLogNotes(e.target.value)}
              placeholder="Optional notes about the call..."
              className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-[#B45309] mb-3"
              rows={3}
              data-testid="morning-call-notes-input"
            />
            {err && <p className="text-xs text-[#C07070] mb-2">{err}</p>}
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => { setShowLog(false); setErr(''); }}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded">Cancel</button>
              <button onClick={logCall} disabled={flagging || !logResult}
                className="px-3 py-1.5 bg-[#B45309] hover:bg-[#92400E] disabled:bg-gray-300 text-white text-xs font-bold rounded"
                data-testid="submit-morning-call-log-btn">
                {flagging ? 'Saving...' : 'Save Call Log'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Order Detail Page - EDITABLE with conditional fields by flow_type
const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fromParam = new URLSearchParams(location.search).get("from");
  const fromMap = fromParam === "map";
  const fromInspection = fromParam === "inspection";
  // ═══ Validate Route → "Open Order" deep-link (Apr 21 2026) ══════════════
  // When the office clicks a Missing address from the Validate Route panel,
  // we open this page with ?assign_to=MA-CESARCOSTA so the inspector can be
  // attached with one click. The banner below surfaces the action.
  const assignToParam = new URLSearchParams(location.search).get("assign_to") || "";
  const [assigningInspector, setAssigningInspector] = useState(false);
  const [order, setOrder] = useState(null);
  const [editedOrder, setEditedOrder] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [transmitting, setTransmitting] = useState(false);
  const [showTransmitModal, setShowTransmitModal] = useState(false);
  const [transmitOperator, setTransmitOperator] = useState('');
  const [transmitPaidBy, setTransmitPaidBy] = useState('');
  const [officeReason, setOfficeReason] = useState('');
  const [officeReasonDetail, setOfficeReasonDetail] = useState('');
  const [closing, setClosing] = useState(false);
  const [returnAnswer, setReturnAnswer] = useState(null); // null, 'yes', 'no'
  const [returnReason, setReturnReason] = useState('');
  const [returnDetail, setReturnDetail] = useState('');
  const [returning, setReturning] = useState(false);
  const [returnDestination, setReturnDestination] = useState('');
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdForm, setHoldForm] = useState({ hold_reason: '', hold_until_date: '', hold_notes: '', office_paid: false });
  const [holdingSaving, setHoldingSaving] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [removingAssignment, setRemovingAssignment] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(null);
  const [queueReason, setQueueReason] = useState('');
  const [queueNotes, setQueueNotes] = useState('');
  const [sendingToQueue, setSendingToQueue] = useState(false);
  const [callingClient, setCallingClient] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsText, setSmsText] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [inspManualPhone, setInspManualPhone] = useState('');
  const [showInspPhoneInput, setShowInspPhoneInput] = useState(false);
  const [inspActionLoading, setInspActionLoading] = useState('');
  const [inspActionMsg, setInspActionMsg] = useState(null);
  // Angela Feb 2026 — On My Way SMS + Arrived + Completed inspector buttons
  const [omwCooldown, setOmwCooldown] = useState({ can_send: true, minutes_ago: null, minutes_remaining: 0 });
  const [showOmwConfirm, setShowOmwConfirm] = useState(false);
  // Angela 2026-05-10 — Reschedule / Cancel Issue (Internal-only field comm)
  const [showRccModal, setShowRccModal] = useState(false);
  const [rccComment, setRccComment] = useState('');
  // Angela 2026-05-10 — Call Client compact popover (mockup spec)
  const [showCallPopover, setShowCallPopover] = useState(false);
  const [callPhoneChoice, setCallPhoneChoice] = useState('listed'); // 'listed' | 'manual'
  // Angela 2026-05-10 — External-only field comms
  const [showCallAgencyPopover, setShowCallAgencyPopover] = useState(false);
  const [agencyPhoneChoice, setAgencyPhoneChoice] = useState('listed'); // 'listed' | 'manual'
  const [agencyManualPhone, setAgencyManualPhone] = useState('');
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [denyComment, setDenyComment] = useState('');
  const [showWrongAddrModal, setShowWrongAddrModal] = useState(false);
  const [wrongAddrComment, setWrongAddrComment] = useState('');
  // Angela 2026-05-10 v2 — No Show / Flipped to Exterior Only (Internal-only)
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [noShowComment, setNoShowComment] = useState('');
  const [showFlippedModal, setShowFlippedModal] = useState(false);
  const [flippedComment, setFlippedComment] = useState('');
  // Angela 2026-05-19 — Unified "Report Problem" modal (Inspector → Office)
  // Visible on ALL 4 order types (Regular / External / Exterior Only / Internal).
  // Enforces 2 mandatory photos on critical event types before submit.
  const [showReportProblem, setShowReportProblem] = useState(false);
  const [reportProblemPreset, setReportProblemPreset] = useState('');

  // PH Phone inline editor (manual override)
  const [editingPhPhone, setEditingPhPhone] = useState(false);
  const [phPhoneDraft, setPhPhoneDraft] = useState('');
  const [phPhoneSaving, setPhPhoneSaving] = useState(false);
  const [phPhoneError, setPhPhoneError] = useState('');

  const savePhPhone = async () => {
    setPhPhoneError(''); setPhPhoneSaving(true);
    try {
      const res = await axios.patch(`${API}/orders/${orderId}/ph-phone`, { ph_phone: phPhoneDraft.trim() });
      setOrder(prev => ({ ...prev, ph_phone_manual: res.data.ph_phone_manual }));
      setEditingPhPhone(false);
    } catch (e) {
      setPhPhoneError(e.response?.data?.detail || 'Failed to update PH phone');
    } finally { setPhPhoneSaving(false); }
  };
  const clearPhPhoneOverride = async () => {
    setPhPhoneError(''); setPhPhoneSaving(true);
    try {
      const res = await axios.patch(`${API}/orders/${orderId}/ph-phone`, { ph_phone: '' });
      setOrder(prev => ({ ...prev, ph_phone_manual: res.data.ph_phone_manual }));
      setEditingPhPhone(false);
    } catch (e) {
      setPhPhoneError(e.response?.data?.detail || 'Failed to clear override');
    } finally { setPhPhoneSaving(false); }
  };

  const isAdmin = user?.role === 'Admin';
  const isFinance = user?.role === 'Finance';
  const canSendToPriority = isAdmin || user?.role === 'Scheduling';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const orderRes = await axios.get(`${API}/orders/${orderId}`);
        setOrder(orderRes.data);
        setEditedOrder(orderRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orderId]);

  useEffect(() => {
    if (showHistory && orderId) {
      setHistoryLoading(true);
      axios.get(`${API}/orders/${orderId}/full-history`).then(res => {
        setOrderHistory(res.data.history || []);
      }).catch(() => setOrderHistory([])).finally(() => setHistoryLoading(false));
    }
  }, [showHistory, orderId]);

  // Angela Feb 2026 — refresh On My Way cooldown on order load (inspector UX).
  useEffect(() => {
    if (!orderId || user?.role !== 'Inspector') return;
    axios.get(`${API}/inspector/omw-cooldown`, { params: { order_id: orderId } })
      .then(res => setOmwCooldown(res.data || { can_send: true }))
      .catch(() => { /* ignore; default allows send */ });
  }, [orderId, user?.role]);

  const getClientPhone = () => {
    // STRICT PH-phone resolution: manual override wins, then original import.
    // Never fall back to appointment_contact_phone / agent_phone — those often
    // hold the agent's number, not the person at the property.
    const raw = (order?.ph_phone_manual || order?.ph_phone || '').toString();
    // Take only the first phone number if multiple are present
    const first = raw.split(/[\s,;\/]+/).find(p => p.replace(/[^0-9]/g, '').length >= 7) || '';
    const clean = first.replace(/[^0-9+]/g, '');
    if (clean.length < 7) return null;
    return clean.startsWith('+') ? clean : clean.startsWith('1') && clean.length >= 11 ? `+${clean}` : `+1${clean}`;
  };

  const handleCallClient = async () => {
    const phone = getClientPhone();
    if (!phone) { setMessage({ type: 'error', text: 'No valid phone number found for this order.' }); return; }
    if (!window.confirm(`Call client at ${phone}?\n\nRingCentral will ring YOUR phone first, then connect you to the client.\nCaller ID: Main Office Number`)) return;
    setCallingClient(true);
    try {
      const res = await axios.post(`${API}/ringcentral/ring-out`, { to_number: phone, order_id: orderId });
      setMessage({ type: 'success', text: `Call initiated! Check your phone. Status: ${res.data.status?.callStatus || 'InProgress'}` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to initiate call' });
    }
    setCallingClient(false);
  };

  const handleSendSms = async () => {
    const phone = getClientPhone();
    if (!phone) { setMessage({ type: 'error', text: 'No valid phone number found for this order.' }); return; }
    if (!smsText.trim()) { setMessage({ type: 'error', text: 'SMS text cannot be empty.' }); return; }
    setSendingSms(true);
    try {
      await axios.post(`${API}/ringcentral/order-sms`, { to_number: phone, text: smsText.trim(), order_id: orderId });
      setMessage({ type: 'success', text: `SMS sent to ${phone}` });
      setShowSmsModal(false);
      setSmsText('');
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to send SMS' });
    }
    setSendingSms(false);
  };

  const handleOpenTransmitModal = () => {
    setTransmitOperator('');
    setTransmitPaidBy('');
    setOfficeReason('');
    setOfficeReasonDetail('');
    setShowTransmitModal(true);
  };

  const handleConfirmTransmit = async () => {
    if (!transmitOperator || !transmitPaidBy) return;
    if (transmitPaidBy === 'Office' && !officeReason) return;
    if (transmitPaidBy === 'Office' && officeReason === 'Other' && !officeReasonDetail.trim()) return;

    setTransmitting(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/transmit`, {
        transmitted_by_operator: transmitOperator,
        paid_by: transmitPaidBy,
        office_reason: transmitPaidBy === 'Office' ? officeReason : null,
        office_reason_detail: transmitPaidBy === 'Office' && officeReason === 'Other' ? officeReasonDetail : null,
      });
      setOrder(res.data);
      setEditedOrder(res.data);
      const paidLabel = transmitPaidBy === 'Office' ? ' (Office Paid)' : ' (Inspector Paid)';
      setMessage({ type: 'success', text: `Order transmitted by ${transmitOperator}${paidLabel}` });
      setShowTransmitModal(false);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to transmit' });
    } finally {
      setTransmitting(false);
    }
  };

  const handleUndoTransmit = async () => {
    setTransmitting(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/undo-transmit`);
      setOrder(res.data);
      setEditedOrder(res.data);
      setMessage({ type: 'success', text: 'Transmission reverted' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to undo transmission' });
    } finally {
      setTransmitting(false);
    }
  };

  const handleCloseDay = async () => {
    setClosing(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/close-day`);
      setOrder(res.data);
      setEditedOrder(res.data);
      setMessage({ type: 'success', text: 'Order closed for the day and sent to QA' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to close order' });
    } finally {
      setClosing(false);
    }
  };

  const handleReturnToDispatch = async () => {
    if (!returnReason || !returnDestination) return;
    setReturning(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/return-to-dispatch`, {
        reason: returnReason,
        reason_detail: returnReason === 'Other' ? returnDetail : '',
        destination: returnDestination,
      });
      setOrder(res.data);
      setEditedOrder(res.data);
      const destLabel = returnDestination === 'scheduling' ? 'Scheduling' : 'Dispatch';
      setMessage({ type: 'success', text: `Order returned to ${destLabel}` });
      setReturnAnswer(null);
      setReturnReason('');
      setReturnDetail('');
      setReturnDestination('');
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to return order' });
    } finally {
      setReturning(false);
    }
  };

  const handlePlaceOnHold = async () => {
    if (!holdForm.hold_reason || !holdForm.hold_until_date) {
      setMessage({ type: 'error', text: 'Hold reason and Hold Until Date are required' });
      return;
    }
    setHoldingSaving(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/hold`, holdForm);
      setOrder(res.data);
      setEditedOrder(res.data);
      setMessage({ type: 'success', text: 'Order placed on hold' });
      setShowHoldModal(false);
      setHoldForm({ hold_reason: '', hold_until_date: '', hold_notes: '', office_paid: false });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to place on hold' });
    } finally { setHoldingSaving(false); }
  };

  const handleReleaseFromHold = async () => {
    setMessage(null);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/release-hold`);
      setOrder(res.data);
      setEditedOrder(res.data);
      setMessage({ type: 'success', text: 'Order released from hold' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to release hold' });
    }
  };

  const handleRemoveAssignment = async () => {
    if (!window.confirm('Are you sure you want to remove the assignment from this order?')) return;
    setRemovingAssignment(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/remove-assignment`);
      setOrder(res.data);
      setEditedOrder(res.data);
      setMessage({ type: 'success', text: 'Assignment removed successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to remove assignment' });
    } finally {
      setRemovingAssignment(false);
    }
  };

  const handleChange = (field, value) => {
    setEditedOrder(prev => {
      const updated = { ...prev, [field]: value };
      const flowType = updated.flow_type;

      // Auto-mapping for Internal orders
      if (flowType === 'Internal') {
        if (field === 'appointment_real_date' && value) {
          updated.appointment_sg_date = value;
          updated.appointment_set = 'yes';
        }
        if (field === 'appointment_real_time') {
          const sgTime = REAL_TIME_TO_SG_TIME[value];
          if (sgTime) {
            updated.safeguard_reported_time = sgTime.split(' – ')[0];
            updated.appointment_sg_time = value;
            updated.appointment_set = 'yes';
            // Extract start/end from WINDOW_CONFIG
            const windows = { '8:00-9:00': ['08:00','09:00'], '8:00-12:00': ['08:00','12:00'], '9:00-1:00': ['09:00','13:00'], '10:00-2:00': ['10:00','14:00'], '11:00-3:00': ['11:00','15:00'] };
            const w = windows[value];
            if (w) {
              updated.internal_start_time = w[0];
              updated.internal_end_time = w[1];
              // 4-hour Safeguard window
              const [h, m] = w[1].split(':').map(Number);
              updated.safeguard_window_start = w[1];
              updated.safeguard_window_end = `${String((h + 4) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
            }
          }
        }
      }

      // For any flow: if both SG date and time are set, mark appointment as set
      if (field === 'appointment_sg_date' || field === 'appointment_sg_time') {
        if (updated.appointment_sg_date && updated.appointment_sg_time) {
          updated.appointment_set = 'yes';
        }
      }

      return updated;
    });
  };

  const handleSendToQueue = async () => {
    if (!showQueueModal || !queueReason.trim()) return;
    setSendingToQueue(true);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/send-to-queue`, {
        queue: showQueueModal, reason: queueReason.trim(), notes: queueNotes.trim()
      });
      setOrder(res.data);
      setEditedOrder(res.data);
      setShowQueueModal(null);
      setQueueReason('');
      setQueueNotes('');
      if (showHistory) setShowHistory(false);
      setTimeout(() => setShowHistory(true), 100);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send to queue');
    } finally { setSendingToQueue(false); }
  };

  const handleRemoveFromQueue = async () => {
    try {
      const res = await axios.post(`${API}/orders/${orderId}/remove-from-queue`);
      setOrder(res.data);
      setEditedOrder(res.data);
      if (showHistory) setShowHistory(false);
      setTimeout(() => setShowHistory(true), 100);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove from queue');
    }
  };


  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Only send simple editable fields, not the entire order object
      const safeFields = {};
      const allowedKeys = [
        'ph_phone', 'comments', 'appointment_real_date', 'appointment_real_time',
        'appointment_sg_date', 'appointment_sg_time', 'appointment_set', 'notes',
        'internal_start_time', 'internal_end_time', 'safeguard_reported_time',
        'safeguard_window_start', 'safeguard_window_end',
        'scheduling_result', 'sg_scheduling_status', 'safeguard_comment',
        'status', 'assigned_inspector', 'queue', 'department',
        'transmitted', 'transmitted_by', 'transmitted_at',
        'daily_closed', 'daily_closed_by', 'daily_closed_date',
        'appointment_detail', 'next_steps', 'data_source_ready',
        'attempts', 'attempts_number', 'ph_count', 'agent_count',
        'transmission_paid_by', 'finance_status', 'qa_result', 'qa_status',
        'action_comment', 'urgent_call',
      ];
      for (const key of allowedKeys) {
        if (key in editedOrder) {
          safeFields[key] = editedOrder[key];
        }
      }
      const res = await axios.put(`${API}/orders/${orderId}`, safeFields);
      setOrder(res.data);
      setEditedOrder(res.data);
      setMessage({ type: 'success', text: 'Order saved successfully' });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errMsg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Failed to save';
      setMessage({ type: 'error', text: errMsg });
    } finally {
      setSaving(false);
    }
  };

  // Validate Route → "Assign to {inspector}" quick action. Uses the existing
  // /api/orders/{id}/quick-assign-inspector endpoint and, on success, navigates
  // straight back to the inspector page so the office sees the route fix land.
  const handleQuickAssignInspector = async (inspector) => {
    if (!inspector) return;
    setAssigningInspector(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/quick-assign-inspector`, {
        inspector_name: inspector,
      });
      setOrder(res.data);
      setEditedOrder(res.data);
      setMessage({ type: 'success', text: `Assigned to ${inspector}` });
      // Return to the inspector's Dispatch view with Validate Route auto-opened
      // so the office stays in the Missing-list correction flow. DispatchPage
      // reads ?inspector=... and ?validate=1 on mount.
      setTimeout(() => {
        navigate(`/dispatch?inspector=${encodeURIComponent(inspector)}&validate=1`);
      }, 700);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errMsg = typeof detail === 'string' ? detail : 'Failed to assign inspector';
      setMessage({ type: 'error', text: errMsg });
    } finally {
      setAssigningInspector(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D1A054]"></div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Order not found</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-[#D1A054] hover:text-[#B08A3E]">Go back</button>
        </div>
      </Layout>
    );
  }

  const flowType = editedOrder.flow_type || order.flow_type || 'Regular';
  const isInspector = user?.role === 'Inspector';
  const isRestrictedUser = user?.email?.toLowerCase() === 'appointment.safeguard@aarifi.com';
  const TRANSMISSION_ALLOWED_EMAILS = ['angela@aarifi.com', 'aline@aarifi.com', 'office@aarifi.com'];
  const canTransmit = TRANSMISSION_ALLOWED_EMAILS.includes(user?.email?.toLowerCase());
  // ROLE-BASED UI CLEANUP (Angela 2026-04-29):
  //   Scheduling role MUST NOT see the following actions on /order/:orderId:
  //     Transmission · End of Day Close · Remove Assignment · Send to Hold ·
  //     Priority · Admin Review · SG Email · Inspector Completion Check.
  //   These belong to Dispatch / Finance / Closing workflow — never Scheduling.
  //   Admin / Finance / Dispatch / Operations keep all their legitimate buttons.
  const isScheduling = user?.role === 'Scheduling';

  // Read-only fields (identifiers) — always shown
  const readOnlyFields = [
    { key: 'order_number', label: 'Order Number' },
    { key: 'company', label: 'Company' },
    { key: 'cliente', label: 'Cliente' },
    { key: 'ph_name', label: 'PH Name' },
    { key: 'ph_phone', label: 'PH Phone' },
    { key: 'order_type', label: 'Work Code' },
    { key: 'flow_type', label: 'Flow Type' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'zip_code', label: 'Zip' },
    { key: 'due_date', label: 'Due Date', render: (v) => formatDateDisplay(v) },
    { key: 'start_date', label: 'Start Date', render: (v) => formatDateDisplay(v) },
    { key: 'assigned_inspector', label: 'Assigned Inspector' },
    { key: 'inspector_login', label: 'SG Source Login' },
    { key: 'status', label: 'Status' },
    { key: 'scheduling_queue', label: 'Queue' },
    { key: 'agt_name', label: 'Agent Name' },
    { key: 'agt_phone', label: 'Agent Phone' },
    { key: 'appointment_set', label: 'Appointment Set' },
    { key: 'appointment_sg_date', label: 'SG Appt Date' },
    { key: 'appointment_sg_time', label: 'SG Appt Time' },
    { key: 'sg_attempts', label: 'SG Attempts' },
    { key: 'sg_status', label: 'SG Status' },
    { key: 'next_steps', label: 'Next Steps' },
    { key: 'transmitted', label: 'Transmitted' },
    { key: 'daily_closed', label: 'Daily Closed' },
    { key: 'return_type', label: 'Return Type' },
    { key: 'upload_type', label: 'Upload Source' },
    { key: 'created_at', label: 'Created At', render: (v) => v ? new Date(v).toLocaleString() : '-' },
  ];

  // For inspectors, show ONLY these 14 core fields (remove admin/internal-only fields)
  const INSPECTOR_ALLOWED_FIELDS = new Set([
    'order_number', 'company', 'cliente', 'ph_name', 'ph_phone',
    'order_type', 'flow_type', 'address', 'city', 'state', 'zip_code',
    'due_date', 'assigned_inspector', 'status',
  ]);
  const visibleReadOnlyFields = isInspector
    ? readOnlyFields.filter(f => INSPECTOR_ALLOWED_FIELDS.has(f.key))
    : readOnlyFields;

  // Build editable fields based on flow_type
  const editableFields = [];

  if (flowType === 'Internal') {
    editableFields.push(
      { key: 'ph_phone', label: 'PH Phone', type: 'text' },
      { key: 'comments', label: 'Comments', type: 'textarea' },
      { key: 'action_comment', label: 'Action Comment', type: 'textarea' },
      { key: 'appointment_real_date', label: 'Internal Date', type: 'date' },
      { key: 'appointment_real_time', label: 'Internal Window', type: 'real_time_select' },
      { key: 'appointment_sg_date', label: 'SG Date', type: 'date' },
      { key: 'safeguard_window_display', label: 'SG Window (4h)', type: 'text', readOnly: true },
      { key: 'appointment_set', label: 'Appointment Set', type: 'select', options: ['no', 'yes'] },
    );
  } else if (flowType === 'External') {
    editableFields.push(
      { key: 'ph_phone', label: 'PH Phone', type: 'text' },
      { key: 'comments', label: 'Comments', type: 'textarea' },
      { key: 'action_comment', label: 'Action Comment', type: 'textarea' },
    );
  } else {
    // Regular
    editableFields.push(
      { key: 'comments', label: 'Comments', type: 'textarea' },
      { key: 'action_comment', label: 'Action Comment', type: 'textarea' },
    );
  }

  // Render a single editable field
  const renderField = (field) => {
    // Virtual computed field for SG Window display
    let fieldValue = editedOrder[field.key] || '';
    if (field.key === 'safeguard_window_display') {
      const swStart = editedOrder.safeguard_window_start;
      const swEnd = editedOrder.safeguard_window_end;
      fieldValue = (swStart && swEnd) ? `${swStart} – ${swEnd}` : '';
    }
    if (field.type === 'textarea') {
      return (
        <textarea
          value={editedOrder[field.key] || ''}
          onChange={(e) => handleChange(field.key, e.target.value)}
          className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054] min-h-[80px]"
          data-testid={`edit-${field.key}`}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <select
          value={editedOrder[field.key] || ''}
          onChange={(e) => handleChange(field.key, e.target.value)}
          className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
          data-testid={`edit-${field.key}`}
        >
          <option value="">Select...</option>
          {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    if (field.type === 'real_time_select') {
      return (
        <select
          value={editedOrder[field.key] || ''}
          onChange={(e) => handleChange(field.key, e.target.value)}
          className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
          data-testid={`edit-${field.key}`}
        >
          <option value="">Select time range...</option>
          {REAL_TIME_OPTIONS.map(opt => (
            <option key={opt} value={opt}>{opt} → SG: {REAL_TIME_TO_SG_TIME[opt]}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={field.type}
        value={fieldValue}
        onChange={(e) => handleChange(field.key, e.target.value)}
        className={`w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054] ${field.readOnly ? 'bg-[#2F5D8A]/5 font-semibold text-[#2F5D8A]' : 'bg-gray-100'}`}
        readOnly={field.readOnly}
        data-testid={`edit-${field.key}`}
      />
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center gap-4">
          <button 
            onClick={() => fromMap ? navigate("/my-orders", { state: { openMap: true } }) : fromInspection ? navigate("/inspection", { state: { openMap: true } }) : navigate(-1)} 
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-800">{isInspector ? 'Order' : 'Edit Order'} {order.order_number}</h1>
            <p className="text-gray-500 text-sm">{order.company} - {order.cliente} ({flowType})</p>
          </div>
          {!isInspector && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-[#D1A054] hover:bg-[#B08A3E] text-[#1F2937] font-semibold rounded-lg disabled:opacity-50"
              data-testid="save-order-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
          )}
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]' : 'bg-[#F5EDED]0/10 border border-[#C07070]/30 text-[#8B4545]'}`}>
            {message.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* ═══ Validate Route → Quick Assign banner (Apr 21 2026) ═══
            Appears only when navigated here via `?assign_to=<inspector>`
            (Missing-order flow from InspectorOrdersPage). Single-click
            attaches this order to the target inspector, then returns to
            the inspector page so Validate Route can re-run. */}
        {!isInspector && assignToParam && order.assigned_inspector !== assignToParam && (
          <div
            className="mb-4 p-4 rounded-lg border-2 border-[#2F5D8A] bg-[#2F5D8A]/5 flex items-center justify-between gap-3"
            data-testid="quick-assign-banner"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#1F2937]">Route fix — attach this order to an inspector</p>
              <p className="text-xs text-gray-600 mt-0.5">
                Opened from Validate Route. Currently&nbsp;
                {order.assigned_inspector
                  ? <span className="font-semibold text-[#B45309]">assigned to {order.assigned_inspector}</span>
                  : <span className="font-semibold text-[#6B8F62]">unassigned</span>}
                .
              </p>
            </div>
            <button
              onClick={() => handleQuickAssignInspector(assignToParam)}
              disabled={assigningInspector}
              className="shrink-0 px-4 py-2.5 bg-[#2F5D8A] hover:bg-[#264A6E] text-white text-sm font-bold rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors shadow-sm"
              data-testid="quick-assign-btn"
            >
              {assigningInspector
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Assigning…</>
                : <><UserPlus className="h-4 w-4" /> Assign to {assignToParam}</>
              }
            </button>
          </div>
        )}
        {!isInspector && assignToParam && order.assigned_inspector === assignToParam && (
          <div
            className="mb-4 p-3 rounded-lg border border-[#A7C4A0]/60 bg-[#A7C4A0]/15 text-[#4A7A56] flex items-center gap-2"
            data-testid="quick-assign-already-set"
          >
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm">Already assigned to <b>{assignToParam}</b>. Go back to Validate Route and re-run.</span>
          </div>
        )}

        {/* Transmission & Closing Status Bar — hidden for inspectors (admin/internal-only fields) */}
        {!isInspector && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6" data-testid="order-status-bar">
          <div className="flex flex-wrap items-center gap-3">
            {/* Status */}
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-xs uppercase">Status:</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#BAC7D8] text-[#1A3695]">{order.status || 'New'}</span>
            </div>

            {/* Phase C routing chip — only shown if order was routed by Phase C */}
            {order.phase_c_routed === true && (
              <div
                className="flex items-center gap-2"
                data-testid="phase-c-routing-chip"
                title={[
                  `Routed by Phase C → ${order.department_route || '—'} / ${order.status || '—'}`,
                  order.phase_c_route_reason ? `Reason: ${order.phase_c_route_reason}` : null,
                  order.total_reappearances != null ? `Reappearances: ${order.total_reappearances}` : null,
                ].filter(Boolean).join('\n')}
              >
                <span className="text-gray-400 text-xs uppercase">Phase C:</span>
                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#EDE5D6] text-[#7A6535] border border-[#D1A054]/30 flex items-center gap-1">
                  <Compass className="h-3 w-3" /> Routed → {order.department_route || 'Scheduling'}
                </span>
                {order.phase_c_route_reason && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200 max-w-[260px] truncate">
                    {order.phase_c_route_reason}
                  </span>
                )}
                {order.total_reappearances != null && order.total_reappearances > 0 && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#C07070]/15 text-[#8B4545]">
                    {order.total_reappearances}× reappeared
                  </span>
                )}
              </div>
            )}

            {/* Transmission Indicator */}

            {/* Daily Closed */}
            <div className="flex items-center gap-2" data-testid="daily-closed-status">
              <span className="text-gray-400 text-xs uppercase">Day Closed:</span>
              {order.daily_closed === 'yes' ? (
                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#A7C4A0]/25 text-[#6B8F62] border border-[#A7C4A0]/40 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Closed
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 text-gray-500 border border-gray-300 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Pending
                </span>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Training Guide moved (Angela 2026-05-10 v2): now renders just
            above Order Information for Internal/External and BELOW the FINT
            Property Notes panel for FINT (see block ~9620). */}

        {/* Angela 2026-05-19 — Unified "Report Problem" button for inspectors.
            Visible on ALL 4 order types (Regular / External / Exterior Only /
            Internal). Hidden on FINT (vacant-house workflow). Opens a modal
            that creates an inspector_field_event and enforces 2 mandatory
            photos for the 5 critical event types before submission. */}
        {isInspector && (order.order_type || '').toUpperCase() !== 'FINT' && (
          <div className="bg-white rounded-lg border border-[#C07070]/40 p-3 mb-4 flex items-center justify-between gap-3" data-testid="inspector-report-problem-card">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-[#8B4545]">
                Problem with this order?
              </p>
              <p className="text-[11px] text-gray-500 leading-snug">
                Report to the office — denied, wrong address, no show, reschedule, or flipped to exterior. Critical issues need 2 photos.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setReportProblemPreset(''); setShowReportProblem(true); }}
              className="px-3 py-2 bg-[#C07070] hover:bg-[#8B4545] text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shrink-0"
              data-testid="open-report-problem-btn"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Report Problem
            </button>
          </div>
        )}

        {/* Inspector Quick Actions toolbar (Call · SMS · On My Way · Arrived ·
            Finished · Reschedule). Renders for Internal · External · Regular/
            Schedule. Angela 2026-05-10 (final lock) — HIDDEN on FINT orders:
            FINT is a vacant-house workflow that uses the FINT Property Notes
            panel only, no client-contact toolbar. */}
        {isInspector && (order.order_type || '').toUpperCase() !== 'FINT' && (() => {
          // STRICT PH phone rule: manual override first, then original PH phone.
          // Never use appointment_contact_phone (agent/scheduling contact).
          const rawPhone = (order.ph_phone_manual || order.ph_phone || '').toString();
          const firstPhone = rawPhone.split(/[\s,;\/]+/).find(p => p.replace(/[^0-9]/g, '').length >= 7) || '';
          const cleanPhone = firstPhone.replace(/[^0-9+]/g, '');
          const hasPhone = cleanPhone.length >= 7;
          const formatPhone = (p) => p.startsWith('+') ? p : p.startsWith('1') && p.length >= 11 ? `+${p}` : `+1${p}`;
          const isInternal = ((order.flow_type || '').toLowerCase() === 'internal');
          // Angela 2026-05-10 — External classification (mirror of Internal rule).
          const isExternal = ((order.flow_type || '').toLowerCase() === 'external');
          const agtPhoneRaw = (order.agt_phone || '').toString();
          const agtPhoneClean = agtPhoneRaw.replace(/[^0-9+]/g, '');
          const hasAgtPhone = agtPhoneClean.length >= 7;

          const inspectorName = order.assigned_inspector || 'your assigned inspector';
          // Display name for SMS On My Way template: prefer the authenticated
          // user's full_name (e.g. "Cesar Costa"). Fall back to deriving from
          // the MA- code only if full_name is unavailable.
          const prettifyMaCode = (code) => {
            if (!code) return 'your assigned inspector';
            const bare = code.replace(/^MA-/, '');
            // Try splitting "CESARCOSTA" → "Cesar Costa" using vowel-seams.
            const spaced = bare.replace(/([A-Z])([A-Z]+?)(?=[A-Z][A-Z]+)/g, '$1$2 ');
            return spaced
              .split(/\s+/)
              .map(w => w ? w[0] + w.slice(1).toLowerCase() : '')
              .join(' ')
              .trim() || code;
          };
          const inspectorDisplayName = (user?.full_name && user.full_name.trim())
            || (user?.name && !user.name.startsWith('MA-') ? user.name.trim() : '')
            || prettifyMaCode(inspectorName);
          // Angela Feb 2026 (STRICT WINDOW LOCK): SMS must show an AARIFI
          // internal WINDOW ("between X and Y"), never a single time. Prefer
          // internal_start_time + internal_end_time; fall back only to a
          // range string in appointment_real_time or appointment_window.
          // If no window can be built, block the SMS button and show reason.
          const _extractRange = (raw) => {
            if (!raw) return null;
            const m = String(raw).match(/([0-2]?\d:\d{2}\s*(?:AM|PM|am|pm)?)\s*[-\u2013to]+\s*([0-2]?\d:\d{2}\s*(?:AM|PM|am|pm)?)/);
            return m ? [m[1].trim(), m[2].trim()] : null;
          };
          let apptWindow = null;
          if (order.internal_start_time && order.internal_end_time) {
            apptWindow = [order.internal_start_time, order.internal_end_time];
          } else {
            apptWindow = _extractRange(order.appointment_real_time) || _extractRange(order.appointment_window);
          }
          const hasApptWindow = !!apptWindow;
          const apptWindowPretty = hasApptWindow
            ? `${toAmPm24(apptWindow[0])} and ${toAmPm24(apptWindow[1])}`
            : '';
          const SMS_INTERNAL = hasApptWindow
            ? `Hello, this is a message from AARIFI Inspection Services, a Safeguard Properties affiliate. This message is to confirm that your inspection appointment is scheduled for today between ${apptWindowPretty}. Your assigned inspector, ${inspectorName}, will contact you approximately 30 to 60 minutes prior to arrival. Please ensure the entire property is accessible at the time of the inspection. If the property has multiple units or tenants, all units must be available, or access (keys) must be provided. The inspection may include all rooms, as well as basement, furnace, electrical panel, and water tank, as applicable.`
            : '';
          const SMS_EXTERIOR = "Hello, this is AARIFI Inspection Services. This message is to inform you that our inspector is at your property to complete the exterior insurance inspection. If you have any questions regarding this inspection, please feel free to come outside or contact us.";
          // Angela Feb 2026 — On My Way SMS (new, separate from Today Appointment Confirmation).
          const SMS_ON_MY_WAY = `Good morning, my name is ${inspectorDisplayName}, and I am your inspector for today. I would like to inform you that I am currently en route and should be arriving within the next 30 to 60 minutes.\n\nIf you have any questions, please feel free to reply to this message or contact our office at 401-415-6278.\n\nThank you,\nAARIFI Inspection Services\nSafeguard Properties Affiliate`;

          const getPhone = () => {
            if (hasPhone) return formatPhone(cleanPhone);
            const mp = inspManualPhone.replace(/[^0-9+]/g, '');
            return mp.length >= 7 ? formatPhone(mp) : null;
          };
          // Mockup popover override: explicitly resolve phone by user's radio choice.
          const getPhoneForCall = () => {
            if (callPhoneChoice === 'manual') {
              const mp = inspManualPhone.replace(/[^0-9+]/g, '');
              return mp.length >= 7 ? formatPhone(mp) : null;
            }
            return hasPhone ? formatPhone(cleanPhone) : null;
          };

          const doCall = async () => {
            const phone = getPhoneForCall() || getPhone();
            if (!phone) { setShowInspPhoneInput(true); return; }
            setInspActionLoading('call');
            try {
              await axios.post(`${API}/ringcentral/ring-out`, { to_number: phone, order_id: orderId });
              setInspActionMsg({ type: 'success', text: 'Call initiated! Your phone will ring first.' });
              await axios.post(`${API}/inspector-actions/log`, { order_id: orderId, action_type: 'Call Client (RC)', phone_number: phone }).catch(() => {});
            } catch (e) {
              const msg = e.response?.data?.detail || 'Call failed';
              const friendly = msg.includes('403') || msg.includes('401') || msg.includes('auth') ? 'RingCentral connection issue. Try again in a moment.' : msg;
              setInspActionMsg({ type: 'error', text: friendly });
            }
            setInspActionLoading('');
          };

          const doSMS = async (template) => {
            const phone = getPhone();
            if (!phone) { setShowInspPhoneInput(true); return; }
            setInspActionLoading('sms');
            try {
              await axios.post(`${API}/ringcentral/order-sms`, { to_number: phone, text: template, order_id: orderId });
              setInspActionMsg({ type: 'success', text: 'SMS sent successfully!' });
              await axios.post(`${API}/inspector-actions/log`, { order_id: orderId, action_type: isInternal ? 'SMS - Internal' : 'SMS - Exterior', phone_number: phone, message_text: template.substring(0, 50) }).catch(() => {});
            } catch (e) {
              const msg = e.response?.data?.detail || 'SMS failed';
              const friendly = msg.includes('403') || msg.includes('401') || msg.includes('auth') ? 'RingCentral connection issue. Try again in a moment.' : msg;
              setInspActionMsg({ type: 'error', text: friendly });
            }
            setInspActionLoading('');
          };

          // Angela Feb 2026 — On My Way SMS cooldown refresh + send flow
          const refreshOmwCooldown = async () => {
            try {
              const res = await axios.get(`${API}/inspector/omw-cooldown`, { params: { order_id: orderId } });
              setOmwCooldown(res.data || { can_send: true });
            } catch (_) { /* ignore; default allows send */ }
          };
          const doOnMyWaySend = async () => {
            const phone = getPhone();
            if (!phone) { setShowOmwConfirm(false); setShowInspPhoneInput(true); return; }
            setInspActionLoading('omw');
            try {
              await axios.post(`${API}/ringcentral/order-sms`, {
                to_number: phone, text: SMS_ON_MY_WAY, order_id: orderId,
                action_type: 'SMS - On My Way',
              });
              await axios.post(`${API}/inspector-actions/log`, {
                order_id: orderId, action_type: 'SMS - On My Way',
                phone_number: phone, message_text: SMS_ON_MY_WAY.substring(0, 50),
              }).catch(() => {});
              setInspActionMsg({ type: 'success', text: 'On My Way SMS sent successfully!' });
              setShowOmwConfirm(false);
              refreshOmwCooldown();
            } catch (e) {
              const status = e.response?.status;
              const msg = e.response?.data?.detail || 'On My Way SMS failed';
              if (status === 429) {
                setInspActionMsg({ type: 'error', text: msg });
                setShowOmwConfirm(false);
                refreshOmwCooldown();
              } else {
                const friendly = msg.includes('403') || msg.includes('401') || msg.includes('auth')
                  ? 'RingCentral connection issue. Try again in a moment.' : msg;
                setInspActionMsg({ type: 'error', text: friendly });
              }
            }
            setInspActionLoading('');
          };
          // Angela 2026-05-10 — FINAL LOCK: Arrived is communication-only.
          // Writes ONLY to inspector_field_events. The order document is
          // intentionally NOT mutated (no arrived_at, no inspector_status).
          // The order stays active in Dispatch / inspector list / Daily Close.
          const doArrived = async () => {
            setInspActionLoading('arrived');
            try {
              await axios.post(`${API}/inspector-field-events`, {
                order_id: orderId, event_type: 'arrived', comment: '',
              });
              setInspActionMsg({ type: 'success', text: 'Arrival sent to Office.' });
            } catch (e) {
              setInspActionMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to notify Office' });
            }
            setInspActionLoading('');
          };
          // Angela 2026-05-10 — FINAL LOCK: Finished is communication-only.
          // Writes ONLY to inspector_field_events. The order document is
          // intentionally NOT mutated (no inspection_completed_at, no status
          // change). The order stays active in Dispatch / inspector list /
          // Daily Close — Office handles workflow downstream.
          const doCompleted = async () => {
            setInspActionLoading('completed');
            try {
              await axios.post(`${API}/inspector-field-events`, {
                order_id: orderId, event_type: 'finished', comment: '',
              });
              setInspActionMsg({ type: 'success', text: 'Finished sent to Office.' });
            } catch (e) {
              setInspActionMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to notify Office' });
            }
            setInspActionLoading('');
          };
          // Angela 2026-05-10 — Reschedule / Cancel Issue (Internal-only).
          // Pure communication: writes ONLY to inspector_field_events.
          // Inspector does NOT reschedule or cancel. Office handles it.
          const doRescheduleCancelIssue = async () => {
            const c = (rccComment || '').trim();
            if (!c) {
              setInspActionMsg({ type: 'error', text: 'Please describe the reason before sending.' });
              return;
            }
            setInspActionLoading('rcc');
            try {
              await axios.post(`${API}/inspector-field-events`, {
                order_id: orderId,
                event_type: 'reschedule_cancel_issue',
                comment: c,
              });
              setInspActionMsg({ type: 'success', text: 'Office notified. Order is unchanged — Office will handle.' });
              setShowRccModal(false);
              setRccComment('');
            } catch (e) {
              setInspActionMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to send to Office' });
            }
            setInspActionLoading('');
          };

          // Angela 2026-05-10 v2 — No Show / Flipped to Exterior Only.
          // Pure notifications written to inspector_field_events. Office
          // handles any operational follow-up; order state, dispatch
          // counts, daily-close, finance, and qa are NOT mutated.
          const doNoShow = async () => {
            const c = (noShowComment || '').trim();
            if (!c) { setInspActionMsg({ type: 'error', text: 'Please describe the No Show situation.' }); return; }
            setInspActionLoading('no_show');
            try {
              await axios.post(`${API}/inspector-field-events`, {
                order_id: orderId, event_type: 'no_show', comment: c,
              });
              setInspActionMsg({ type: 'success', text: 'Office notified — No Show. Order is unchanged.' });
              setShowNoShowModal(false); setNoShowComment('');
            } catch (e) {
              setInspActionMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to send to Office' });
            }
            setInspActionLoading('');
          };
          const doFlippedToExteriorOnly = async () => {
            const c = (flippedComment || '').trim();
            if (!c) { setInspActionMsg({ type: 'error', text: 'Please describe why this was flipped to exterior only.' }); return; }
            setInspActionLoading('flipped_to_exterior_only');
            try {
              await axios.post(`${API}/inspector-field-events`, {
                order_id: orderId, event_type: 'flipped_to_exterior_only', comment: c,
              });
              setInspActionMsg({ type: 'success', text: 'Office notified — flipped to exterior only. Order is unchanged.' });
              setShowFlippedModal(false); setFlippedComment('');
            } catch (e) {
              setInspActionMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to send to Office' });
            }
            setInspActionLoading('');
          };

          // Angela 2026-05-10 — Internal/Regular handlers above this line.
          // Below this line: External-only handlers (Call PH, SMS PH, Call
          // Agency, Deny Inspection, Wrong Address). Pure communications —
          // none of them mutate order.status, dispatch counts, daily-close
          // eligibility, finance, or qa. They write ONLY to inspector_field_events.
          const doCallPH = async () => {
            // Reuses the Internal "Call Client" path via existing /ringcentral/ring-out.
            await doCall();
          };
          const doSMSPH = async () => {
            await doSMS(SMS_EXTERIOR);
          };
          const getAgencyPhoneForCall = () => {
            if (agencyPhoneChoice === 'manual') {
              const mp = agencyManualPhone.replace(/[^0-9+]/g, '');
              return mp.length >= 7 ? formatPhone(mp) : null;
            }
            return hasAgtPhone ? formatPhone(agtPhoneClean) : null;
          };
          const doCallAgency = async () => {
            const phone = getAgencyPhoneForCall();
            if (!phone) {
              setInspActionMsg({ type: 'error', text: 'No agency phone available — enter a number to call.' });
              return;
            }
            setInspActionLoading('call_agency');
            setInspActionMsg(null);
            try {
              await axios.post(`${API}/ringcentral/ring-out`, {
                to_number: phone, from_number: '+14014156278', order_id: orderId,
              });
              setInspActionMsg({ type: 'success', text: 'Calling agency via RingCentral…' });
              axios.post(`${API}/inspector-actions/log`, {
                order_id: orderId, action_type: 'Call Agency (RC)', phone_number: phone,
              }).catch(() => {});
            } catch (e) {
              setInspActionMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to start call' });
            }
            setInspActionLoading('');
          };
          const doDenyInspection = async () => {
            const c = (denyComment || '').trim();
            if (!c) {
              setInspActionMsg({ type: 'error', text: 'Please describe why the inspection was denied.' });
              return;
            }
            setInspActionLoading('deny');
            try {
              await axios.post(`${API}/inspector-field-events`, {
                order_id: orderId, event_type: 'deny_inspection', comment: c,
              });
              setInspActionMsg({ type: 'success', text: 'Office notified — inspection denied. Order is unchanged.' });
              setShowDenyModal(false);
              setDenyComment('');
            } catch (e) {
              setInspActionMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to send to Office' });
            }
            setInspActionLoading('');
          };
          const doWrongAddress = async () => {
            const c = (wrongAddrComment || '').trim();
            if (!c) {
              setInspActionMsg({ type: 'error', text: 'Please describe the address issue.' });
              return;
            }
            setInspActionLoading('wrong_addr');
            try {
              await axios.post(`${API}/inspector-field-events`, {
                order_id: orderId, event_type: 'wrong_address', comment: c,
              });
              setInspActionMsg({ type: 'success', text: 'Office notified — wrong address. Order is unchanged.' });
              setShowWrongAddrModal(false);
              setWrongAddrComment('');
            } catch (e) {
              setInspActionMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to send to Office' });
            }
            setInspActionLoading('');
          };

          // Angela 2026-05-10 (final lock) — STRICT SCOPE: render the
          // Contact-Client / External card only for Internal OR External
          // orders. Regular orders (FI and any other Regular order_type) are
          // intentionally untouched: no toolbar, no mural, no UI changes.
          if (!isInternal && !isExternal) return null;

          return (
            <div className="bg-[#162d4a] rounded-lg p-4 mb-6" data-testid="inspector-quick-actions">
              <h2 className="text-xs font-bold text-[#D1A054] uppercase tracking-wider mb-2">{isExternal && !isInternal ? 'External Order' : 'Contact Client'}</h2>
              {hasPhone && (
                <p className="text-[10px] text-gray-400 mb-3">Phone: <span className="text-white font-semibold">{rawPhone}</span>
                  {(isInternal || isExternal) && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[8px] font-bold bg-white/10 text-gray-300">{isInternal ? 'INTERNAL' : 'EXTERIOR'}</span>
                  )}
                </p>
              )}
              {!hasPhone && !showInspPhoneInput && (
                <p className="text-[10px] text-amber-300 mb-3">No phone number on this order — tap a button to enter manually</p>
              )}

              {showInspPhoneInput && !hasPhone && (
                <div className="mb-3 flex gap-2">
                  <input type="tel" value={inspManualPhone} onChange={e => setInspManualPhone(e.target.value)} placeholder="Enter phone number..."
                    className="flex-1 px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:border-[#D1A054]"
                    data-testid="manual-phone-input" autoFocus />
                  <button onClick={() => setShowInspPhoneInput(false)} className="px-3 py-2 text-xs text-gray-400 hover:text-white">Cancel</button>
                </div>
              )}

              {inspActionMsg && (
                <div className={`mb-3 px-3 py-2 rounded-lg text-xs font-medium ${inspActionMsg.type === 'success' ? 'bg-[#6B8F62]/20 text-[#A7C4A0]' : 'bg-[#C07070]/20 text-[#E8A0A0]'}`} data-testid="action-message">
                  {inspActionMsg.text}
                </div>
              )}

              {/* Compact horizontal action bar (mockup spec — Angela 2026-05-10).
                  All 6 actions: Call Client (with split-chevron popover) · SMS ·
                  On My Way · Arrived · Finished · Reschedule.
                  Renders for Internal only. External shows its own 5-button
                  legacy bar below (Call PH · SMS PH · Call Agency · Denied
                  Inspection · Wrong Address). FINT/Regular do not render this
                  card at all (early-return above). */}
              {isInternal && (
              <div className="flex flex-wrap gap-2 relative" data-testid="inspector-action-bar">
                {/* Call Client — split button: main click opens popover; chevron also opens it */}
                <div className="relative inline-flex">
                  <button
                    onClick={() => { setInspActionMsg(null); setCallPhoneChoice(hasPhone ? 'listed' : 'manual'); setShowCallPopover(v => !v); }}
                    disabled={inspActionLoading === 'call'}
                    className="flex items-center gap-2 pl-3 pr-2 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-l-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-50"
                    data-testid="inspector-rc-call-btn">
                    <Phone className="h-4 w-4 text-[#2F5D8A]" />
                    {inspActionLoading === 'call' ? 'Calling…' : 'Call Client'}
                  </button>
                  <button
                    onClick={() => { setInspActionMsg(null); setCallPhoneChoice(hasPhone ? 'listed' : 'manual'); setShowCallPopover(v => !v); }}
                    disabled={inspActionLoading === 'call'}
                    className="flex items-center px-1.5 py-2 bg-white hover:bg-gray-50 border border-l-0 border-gray-200 rounded-r-lg text-[#1F2937] disabled:opacity-50"
                    aria-label="Open call options"
                    data-testid="inspector-rc-call-chevron">
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </button>

                  {/* Call Client popover — radio (use listed / enter different) + Call button */}
                  {showCallPopover && (
                    <div className="absolute z-40 top-[110%] left-0 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-3" data-testid="call-popover">
                      <p className="text-sm font-semibold text-[#1F2937]">Call Client</p>
                      <p className="text-[11px] text-gray-500 mb-3">Use listed phone or enter a different number.</p>

                      <label className={`flex items-start gap-2 mb-2 cursor-pointer ${!hasPhone ? 'opacity-50' : ''}`}>
                        <input
                          type="radio" name="call-phone-choice" value="listed"
                          disabled={!hasPhone}
                          checked={callPhoneChoice === 'listed'}
                          onChange={() => setCallPhoneChoice('listed')}
                          className="mt-0.5 accent-[#2F5D8A]"
                          data-testid="call-phone-listed"
                        />
                        <span>
                          <span className="block text-xs font-semibold text-[#1F2937]">Use listed phone</span>
                          <span className="block text-[11px] text-gray-500 font-mono">{hasPhone ? rawPhone : 'no phone on order'}</span>
                        </span>
                      </label>

                      <label className="flex items-start gap-2 mb-1 cursor-pointer">
                        <input
                          type="radio" name="call-phone-choice" value="manual"
                          checked={callPhoneChoice === 'manual'}
                          onChange={() => setCallPhoneChoice('manual')}
                          className="mt-0.5 accent-[#2F5D8A]"
                          data-testid="call-phone-manual"
                        />
                        <span className="text-xs font-semibold text-[#1F2937]">Enter different number</span>
                      </label>
                      {callPhoneChoice === 'manual' && (
                        <input
                          type="tel"
                          value={inspManualPhone}
                          onChange={e => setInspManualPhone(e.target.value)}
                          placeholder="Enter phone number"
                          className="w-full mb-2 px-2 py-1.5 bg-white border border-gray-200 rounded text-xs text-[#1F2937] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                          data-testid="call-phone-manual-input"
                          autoFocus
                        />
                      )}

                      <button
                        onClick={async () => {
                          setShowCallPopover(false);
                          await doCall();
                        }}
                        disabled={inspActionLoading === 'call' || (callPhoneChoice === 'listed' && !hasPhone) || (callPhoneChoice === 'manual' && inspManualPhone.replace(/[^0-9+]/g, '').length < 7)}
                        className="w-full mt-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-bold rounded-lg disabled:opacity-50"
                        data-testid="call-popover-call-btn">
                        <Phone className="h-3.5 w-3.5" /> Call
                      </button>
                    </div>
                  )}
                </div>

                {/* SMS — Today Appointment Confirmation (Internal/Regular flow). */}
                <button
                  onClick={() => doSMS(SMS_INTERNAL)}
                  disabled={inspActionLoading === 'sms' || !hasApptWindow}
                  title={!hasApptWindow ? 'No AARIFI internal appointment window set — set the window before sending.' : 'SMS — Today Appointment Confirmation'}
                  className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-50"
                  data-testid="inspector-rc-sms-btn">
                  <MessageSquare className="h-4 w-4 text-[#2F5D8A]" />
                  {inspActionLoading === 'sms' ? 'Sending…' : 'SMS'}
                </button>

                {/* On My Way (60-min cooldown preserved) */}
                <button
                  onClick={() => { setInspActionMsg(null); refreshOmwCooldown(); setShowOmwConfirm(true); }}
                  disabled={inspActionLoading === 'omw' || omwCooldown.can_send === false}
                  title={omwCooldown.can_send === false ? `Already sent ${omwCooldown.minutes_ago}m ago — wait ${omwCooldown.minutes_remaining}m` : 'Notify client you are on the way'}
                  className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-60"
                  data-testid="inspector-rc-omw-btn">
                  <Truck className="h-4 w-4 text-[#2F5D8A]" />
                  {inspActionLoading === 'omw' ? 'Sending…' : 'On My Way'}
                </button>

                {/* Arrived */}
                <button
                  onClick={doArrived}
                  disabled={inspActionLoading === 'arrived'}
                  className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-60"
                  data-testid="inspector-arrived-btn">
                  <MapPin className="h-4 w-4 text-[#2F5D8A]" />
                  {inspActionLoading === 'arrived' ? 'Saving…' : 'Arrived'}
                </button>

                {/* Finished */}
                <button
                  onClick={doCompleted}
                  disabled={inspActionLoading === 'completed'}
                  className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-60"
                  data-testid="inspector-completed-btn">
                  <CheckCircle className="h-4 w-4 text-[#2F5D8A]" />
                  {inspActionLoading === 'completed' ? 'Saving…' : 'Finished'}
                </button>

                {/* Reschedule (label per mockup) — opens existing reason modal */}
                <button
                  onClick={() => { setInspActionMsg(null); setReportProblemPreset('reschedule_cancel_issue'); setShowReportProblem(true); }}
                  disabled={inspActionLoading === 'rcc'}
                  className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-60"
                  data-testid="inspector-rcc-btn">
                  <Calendar className="h-4 w-4 text-[#2F5D8A]" />
                  Reschedule
                </button>

                {/* No Show — Angela 2026-05-10 v2. Inspector arrived but
                    cannot complete inside (client absent / no access). Pure
                    notification to Office; does NOT change order status. */}
                <button
                  onClick={() => { setInspActionMsg(null); setReportProblemPreset('no_show'); setShowReportProblem(true); }}
                  disabled={inspActionLoading === 'no_show'}
                  className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-60"
                  data-testid="inspector-no-show-btn">
                  <UserX className="h-4 w-4 text-[#8B4545]" />
                  No Show
                </button>

                {/* Flipped to Exterior Only — Angela 2026-05-10 v2. Inspector
                    visited; interior unavailable; insurance allows exterior
                    completion. Pure notification to Office. */}
                <button
                  onClick={() => { setInspActionMsg(null); setReportProblemPreset('flipped_to_exterior_only'); setShowReportProblem(true); }}
                  disabled={inspActionLoading === 'flipped_to_exterior_only'}
                  className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-60"
                  data-testid="inspector-flipped-btn">
                  <Repeat className="h-4 w-4 text-[#7A5BA1]" />
                  Flipped to Exterior Only
                </button>
              </div>
              )}{/* /isInternal — 8-button toolbar */}

              {/* Angela Feb 2026 — On My Way confirmation modal */}
              {showOmwConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="omw-confirm-modal">
                  <div className="bg-[#162d4a] border border-[#D1A054]/40 rounded-xl p-5 max-w-md w-full shadow-2xl">
                    <h3 className="text-[#D1A054] font-bold text-base mb-3 flex items-center gap-2">
                      <Truck className="h-4 w-4" /> Send "On My Way" SMS?
                    </h3>
                    {hasPhone ? (
                      <p className="text-[11px] text-gray-300 mb-3">Phone: <span className="text-white font-semibold">{rawPhone}</span></p>
                    ) : (
                      <p className="text-[11px] text-amber-300 mb-3">No phone saved — the SMS cannot be sent. Enter a phone first via the Call / SMS button.</p>
                    )}
                    <div className="bg-black/30 rounded-lg p-3 mb-3 max-h-48 overflow-y-auto">
                      <p className="text-[10px] text-gray-300 whitespace-pre-line leading-relaxed">{SMS_ON_MY_WAY}</p>
                    </div>
                    {omwCooldown.can_send === false && (
                      <p className="text-[11px] text-[#E8A0A0] mb-2">
                        Already sent {omwCooldown.minutes_ago}m ago — wait {omwCooldown.minutes_remaining}m before sending again.
                      </p>
                    )}
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setShowOmwConfirm(false)}
                        className="px-3 py-2 text-xs text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg"
                        data-testid="omw-confirm-cancel-btn">
                        Cancel
                      </button>
                      <button
                        onClick={doOnMyWaySend}
                        disabled={!hasPhone || inspActionLoading === 'omw' || omwCooldown.can_send === false}
                        className="px-4 py-2 text-xs font-bold text-white bg-[#D1A054] hover:bg-[#B88A3E] rounded-lg disabled:opacity-50"
                        data-testid="omw-confirm-send-btn">
                        {inspActionLoading === 'omw' ? 'Sending...' : 'Confirm & Send'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-[9px] text-gray-500 mt-2 text-center">Via RingCentral — {isExternal ? 'Exterior path' : 'Office Number'}</p>

              {/* Angela 2026-05-10 — Reschedule / Cancel Issue confirmation modal */}
              {showRccModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="rcc-modal">
                  <div className="bg-[#162d4a] border border-[#B08A3E]/40 rounded-xl p-5 max-w-md w-full shadow-2xl">
                    <h3 className="text-[#D1A054] font-bold text-base mb-2 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" /> Reschedule / Cancel Issue
                    </h3>
                    <p className="text-[11px] text-gray-300 mb-3">
                      This sends a message to Office. The inspector does <strong>NOT</strong> reschedule
                      or cancel the order. Office/Admin will handle it.
                    </p>
                    <p className="text-[10px] text-gray-400 mb-2">Reason / detail (required):</p>
                    <textarea
                      value={rccComment}
                      onChange={e => setRccComment(e.target.value)}
                      rows={4}
                      placeholder="e.g. Owner asked to reschedule for next Wed AM. Refused access. Wrong appt. No access — gate locked. etc."
                      className="w-full px-3 py-2 bg-[#0d1f33] border border-white/20 rounded-lg text-sm !text-white placeholder:!text-gray-400 focus:outline-none focus:border-[#B08A3E]"
                      data-testid="rcc-comment-input"
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end mt-3">
                      <button
                        onClick={() => { setShowRccModal(false); setRccComment(''); }}
                        className="px-3 py-2 text-xs text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg"
                        data-testid="rcc-cancel-btn">
                        Cancel
                      </button>
                      <button
                        onClick={doRescheduleCancelIssue}
                        disabled={!rccComment.trim() || inspActionLoading === 'rcc'}
                        className="px-4 py-2 text-xs font-bold text-white bg-[#B08A3E] hover:bg-[#9A7735] rounded-lg disabled:opacity-50"
                        data-testid="rcc-send-btn">
                        {inspActionLoading === 'rcc' ? 'Sending…' : 'Notify Office'}
                      </button>
                    </div>
                    <p className="text-[9px] text-gray-500 mt-3 text-center">
                      Order status, dispatch counts, and Daily Close will not change.
                    </p>
                  </div>
                </div>
              )}

              {/* No Show modal — Angela 2026-05-10 v2 (Internal-only) */}
              {showNoShowModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="noshow-modal">
                  <div className="bg-[#162d4a] border border-[#8B4545]/40 rounded-xl p-5 max-w-md w-full shadow-2xl">
                    <h3 className="text-[#E8A0A0] font-bold text-base mb-2 flex items-center gap-2">
                      <UserX className="h-4 w-4" /> No Show
                    </h3>
                    <p className="text-[11px] text-gray-200 mb-3">
                      Use this when the client is not on site and the inspection cannot be completed inside.
                      This sends a notification to Office. Order status will <strong>NOT</strong> change.
                    </p>
                    <p className="text-[10px] text-gray-300 mb-2">Reason / detail (required):</p>
                    <textarea
                      value={noShowComment}
                      onChange={e => setNoShowComment(e.target.value)}
                      rows={4}
                      placeholder="e.g. Knocked twice, no answer. Called listed number — no answer. Waited 10 min."
                      className="w-full px-3 py-2 bg-[#0d1f33] border border-white/20 rounded-lg text-sm !text-white placeholder:!text-gray-400 focus:outline-none focus:border-[#8B4545]"
                      data-testid="noshow-comment-input"
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end mt-3">
                      <button onClick={() => { setShowNoShowModal(false); setNoShowComment(''); }} className="px-3 py-2 text-xs text-gray-200 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg" data-testid="noshow-cancel-btn">Cancel</button>
                      <button onClick={doNoShow} disabled={!noShowComment.trim() || inspActionLoading === 'no_show'} className="px-4 py-2 text-xs font-bold text-white bg-[#8B4545] hover:bg-[#723838] rounded-lg disabled:opacity-50" data-testid="noshow-send-btn">
                        {inspActionLoading === 'no_show' ? 'Sending…' : 'Notify Office'}
                      </button>
                    </div>
                    <p className="text-[9px] text-gray-400 mt-3 text-center">Order status, dispatch counts, and Daily Close will not change.</p>
                  </div>
                </div>
              )}

              {/* Flipped to Exterior Only modal — Angela 2026-05-10 v2 (Internal-only) */}
              {showFlippedModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="flipped-modal">
                  <div className="bg-[#162d4a] border border-[#7A5BA1]/40 rounded-xl p-5 max-w-md w-full shadow-2xl">
                    <h3 className="text-[#C9B5E0] font-bold text-base mb-2 flex items-center gap-2">
                      <Repeat className="h-4 w-4" /> Flipped to Exterior Only
                    </h3>
                    <p className="text-[11px] text-gray-200 mb-3">
                      Use this when the interior was unavailable and insurance allows exterior-only completion.
                      This sends a notification to Office. Order status will <strong>NOT</strong> change.
                    </p>
                    <p className="text-[10px] text-gray-300 mb-2">Reason / detail (required):</p>
                    <textarea
                      value={flippedComment}
                      onChange={e => setFlippedComment(e.target.value)}
                      rows={4}
                      placeholder="e.g. Owner not home, insurance approves exterior only. Tenant refused interior; insurance OK with exterior."
                      className="w-full px-3 py-2 bg-[#0d1f33] border border-white/20 rounded-lg text-sm !text-white placeholder:!text-gray-400 focus:outline-none focus:border-[#7A5BA1]"
                      data-testid="flipped-comment-input"
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end mt-3">
                      <button onClick={() => { setShowFlippedModal(false); setFlippedComment(''); }} className="px-3 py-2 text-xs text-gray-200 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg" data-testid="flipped-cancel-btn">Cancel</button>
                      <button onClick={doFlippedToExteriorOnly} disabled={!flippedComment.trim() || inspActionLoading === 'flipped_to_exterior_only'} className="px-4 py-2 text-xs font-bold text-white bg-[#7A5BA1] hover:bg-[#634885] rounded-lg disabled:opacity-50" data-testid="flipped-send-btn">
                        {inspActionLoading === 'flipped_to_exterior_only' ? 'Sending…' : 'Notify Office'}
                      </button>
                    </div>
                    <p className="text-[9px] text-gray-400 mt-3 text-center">Order status, dispatch counts, and Daily Close will not change.</p>
                  </div>
                </div>
              )}

              {/* ───────────────────────────────────────────────────────────
                  Angela 2026-05-10 (final lock) — EXTERNAL-only action bar.
                  Renders ONLY when isExternal. Pure communications:
                  none of these change order.status, dispatch counts, finance,
                  qa, daily-close eligibility, or assignment. Writes go to
                  inspector_field_events / inspector-actions/log only.
                  Buttons: Call PH · SMS PH · Call Agency · Denied Inspection · Wrong Address
                  ─────────────────────────────────────────────────────────── */}
              {isExternal && (
                <div data-testid="inspector-action-bar-external">
                  <div className="flex flex-wrap gap-2 relative">
                    {/* Call PH (split button + popover) */}
                    <div className="relative inline-flex">
                      <button
                        onClick={() => { setInspActionMsg(null); setCallPhoneChoice(hasPhone ? 'listed' : 'manual'); setShowCallPopover(v => !v); }}
                        disabled={inspActionLoading === 'call'}
                        className="flex items-center gap-2 pl-3 pr-2 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-l-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-50"
                        data-testid="ext-call-ph-btn">
                        <Phone className="h-4 w-4 text-[#2F5D8A]" />
                        {inspActionLoading === 'call' ? 'Calling…' : 'Call PH'}
                      </button>
                      <button
                        onClick={() => { setInspActionMsg(null); setCallPhoneChoice(hasPhone ? 'listed' : 'manual'); setShowCallPopover(v => !v); }}
                        disabled={inspActionLoading === 'call'}
                        className="flex items-center px-1.5 py-2 bg-white hover:bg-gray-50 border border-l-0 border-gray-200 rounded-r-lg text-[#1F2937] disabled:opacity-50"
                        aria-label="Open call options"
                        data-testid="ext-call-ph-chevron">
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      </button>
                      {showCallPopover && (
                        <div className="absolute z-40 top-[110%] left-0 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-3" data-testid="ext-call-ph-popover">
                          <p className="text-sm font-semibold text-[#1F2937]">Call PH</p>
                          <p className="text-[11px] text-gray-500 mb-3">Use listed PH phone or enter a different number.</p>
                          <label className={`flex items-start gap-2 mb-2 cursor-pointer ${!hasPhone ? 'opacity-50' : ''}`}>
                            <input type="radio" name="ext-call-ph-choice" disabled={!hasPhone} checked={callPhoneChoice === 'listed'} onChange={() => setCallPhoneChoice('listed')} className="mt-0.5 accent-[#2F5D8A]" data-testid="ext-call-ph-listed" />
                            <span>
                              <span className="block text-xs font-semibold text-[#1F2937]">Use listed phone</span>
                              <span className="block text-[11px] text-gray-500 font-mono">{hasPhone ? rawPhone : 'no phone on order'}</span>
                            </span>
                          </label>
                          <label className="flex items-start gap-2 mb-1 cursor-pointer">
                            <input type="radio" name="ext-call-ph-choice" checked={callPhoneChoice === 'manual'} onChange={() => setCallPhoneChoice('manual')} className="mt-0.5 accent-[#2F5D8A]" data-testid="ext-call-ph-manual" />
                            <span className="text-xs font-semibold text-[#1F2937]">Enter different number</span>
                          </label>
                          {callPhoneChoice === 'manual' && (
                            <input type="tel" value={inspManualPhone} onChange={e => setInspManualPhone(e.target.value)} placeholder="Enter phone number" className="w-full mb-2 px-2 py-1.5 bg-white border border-gray-200 rounded text-xs text-[#1F2937] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]" data-testid="ext-call-ph-manual-input" autoFocus />
                          )}
                          <button
                            onClick={async () => { setShowCallPopover(false); await doCallPH(); }}
                            disabled={inspActionLoading === 'call' || (callPhoneChoice === 'listed' && !hasPhone) || (callPhoneChoice === 'manual' && inspManualPhone.replace(/[^0-9+]/g, '').length < 7)}
                            className="w-full mt-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-bold rounded-lg disabled:opacity-50"
                            data-testid="ext-call-ph-call-btn">
                            <Phone className="h-3.5 w-3.5" /> Call
                          </button>
                        </div>
                      )}
                    </div>

                    {/* SMS PH */}
                    <button
                      onClick={doSMSPH}
                      disabled={inspActionLoading === 'sms' || !hasPhone}
                      title={!hasPhone ? 'No PH phone on order' : 'SMS PH (Exterior template)'}
                      className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-50"
                      data-testid="ext-sms-ph-btn">
                      <MessageSquare className="h-4 w-4 text-[#2F5D8A]" />
                      {inspActionLoading === 'sms' ? 'Sending…' : 'SMS PH'}
                    </button>

                    {/* Call Agency (split button + popover with agt_phone) */}
                    <div className="relative inline-flex">
                      <button
                        onClick={() => { setInspActionMsg(null); setAgencyPhoneChoice(hasAgtPhone ? 'listed' : 'manual'); setShowCallAgencyPopover(v => !v); }}
                        disabled={inspActionLoading === 'call_agency'}
                        className="flex items-center gap-2 pl-3 pr-2 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-l-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-50"
                        data-testid="ext-call-agency-btn">
                        <Phone className="h-4 w-4 text-[#6B8F62]" />
                        {inspActionLoading === 'call_agency' ? 'Calling…' : 'Call Agency'}
                      </button>
                      <button
                        onClick={() => { setInspActionMsg(null); setAgencyPhoneChoice(hasAgtPhone ? 'listed' : 'manual'); setShowCallAgencyPopover(v => !v); }}
                        disabled={inspActionLoading === 'call_agency'}
                        className="flex items-center px-1.5 py-2 bg-white hover:bg-gray-50 border border-l-0 border-gray-200 rounded-r-lg text-[#1F2937] disabled:opacity-50"
                        aria-label="Open agency options"
                        data-testid="ext-call-agency-chevron">
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      </button>
                      {showCallAgencyPopover && (
                        <div className="absolute z-40 top-[110%] left-0 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-3" data-testid="ext-call-agency-popover">
                          <p className="text-sm font-semibold text-[#1F2937]">Call Agency</p>
                          <p className="text-[11px] text-gray-500 mb-3">Use listed agency phone or enter a different number.</p>
                          <label className={`flex items-start gap-2 mb-2 cursor-pointer ${!hasAgtPhone ? 'opacity-50' : ''}`}>
                            <input type="radio" name="ext-call-agency-choice" disabled={!hasAgtPhone} checked={agencyPhoneChoice === 'listed'} onChange={() => setAgencyPhoneChoice('listed')} className="mt-0.5 accent-[#6B8F62]" data-testid="ext-call-agency-listed" />
                            <span>
                              <span className="block text-xs font-semibold text-[#1F2937]">Use agency phone</span>
                              <span className="block text-[11px] text-gray-500 font-mono">{hasAgtPhone ? agtPhoneRaw : 'no agt_phone on order'}</span>
                            </span>
                          </label>
                          <label className="flex items-start gap-2 mb-1 cursor-pointer">
                            <input type="radio" name="ext-call-agency-choice" checked={agencyPhoneChoice === 'manual'} onChange={() => setAgencyPhoneChoice('manual')} className="mt-0.5 accent-[#6B8F62]" data-testid="ext-call-agency-manual" />
                            <span className="text-xs font-semibold text-[#1F2937]">Enter different number</span>
                          </label>
                          {agencyPhoneChoice === 'manual' && (
                            <input type="tel" value={agencyManualPhone} onChange={e => setAgencyManualPhone(e.target.value)} placeholder="Enter phone number" className="w-full mb-2 px-2 py-1.5 bg-white border border-gray-200 rounded text-xs text-[#1F2937] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#6B8F62]" data-testid="ext-call-agency-manual-input" autoFocus />
                          )}
                          <button
                            onClick={async () => { setShowCallAgencyPopover(false); await doCallAgency(); }}
                            disabled={inspActionLoading === 'call_agency' || (agencyPhoneChoice === 'listed' && !hasAgtPhone) || (agencyPhoneChoice === 'manual' && agencyManualPhone.replace(/[^0-9+]/g, '').length < 7)}
                            className="w-full mt-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#6B8F62] hover:bg-[#5A7B52] text-white text-xs font-bold rounded-lg disabled:opacity-50"
                            data-testid="ext-call-agency-call-btn">
                            <Phone className="h-3.5 w-3.5" /> Call
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Denied Inspection */}
                    <button
                      onClick={() => { setInspActionMsg(null); setReportProblemPreset('deny_inspection'); setShowReportProblem(true); }}
                      disabled={inspActionLoading === 'deny'}
                      className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-60"
                      data-testid="ext-deny-btn">
                      <Ban className="h-4 w-4 text-[#8B4545]" />
                      Denied Inspection
                    </button>

                    {/* Wrong Address */}
                    <button
                      onClick={() => { setInspActionMsg(null); setReportProblemPreset('wrong_address'); setShowReportProblem(true); }}
                      disabled={inspActionLoading === 'wrong_addr'}
                      className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-[#1F2937] text-sm font-semibold transition-colors disabled:opacity-60"
                      data-testid="ext-wrong-addr-btn">
                      <MapPinOff className="h-4 w-4 text-[#7A5BA1]" />
                      Wrong Address
                    </button>
                  </div>
                </div>
              )}

              {/* Denied Inspection modal (External-only) */}
              {showDenyModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="deny-modal">
                  <div className="bg-[#162d4a] border border-[#8B4545]/40 rounded-xl p-5 max-w-md w-full shadow-2xl">
                    <h3 className="text-[#E8A0A0] font-bold text-base mb-2 flex items-center gap-2">
                      <Ban className="h-4 w-4" /> Denied Inspection
                    </h3>
                    <p className="text-[11px] text-gray-300 mb-3">
                      This sends a message to Office. Inspector does <strong>NOT</strong> change the order status. Office will handle.
                    </p>
                    <p className="text-[10px] text-gray-400 mb-2">Reason / detail (required):</p>
                    <textarea
                      value={denyComment}
                      onChange={e => setDenyComment(e.target.value)}
                      rows={4}
                      placeholder="e.g. Owner refused entry. Tenant denied access. Insurance not requested."
                      className="w-full px-3 py-2 bg-[#0d1f33] border border-white/20 rounded-lg text-sm !text-white placeholder:!text-gray-400 focus:outline-none focus:border-[#8B4545]"
                      data-testid="deny-comment-input"
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end mt-3">
                      <button onClick={() => { setShowDenyModal(false); setDenyComment(''); }} className="px-3 py-2 text-xs text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg" data-testid="deny-cancel-btn">Cancel</button>
                      <button onClick={doDenyInspection} disabled={!denyComment.trim() || inspActionLoading === 'deny'} className="px-4 py-2 text-xs font-bold text-white bg-[#8B4545] hover:bg-[#723838] rounded-lg disabled:opacity-50" data-testid="deny-send-btn">
                        {inspActionLoading === 'deny' ? 'Sending…' : 'Notify Office'}
                      </button>
                    </div>
                    <p className="text-[9px] text-gray-500 mt-3 text-center">Order status, dispatch counts, and Daily Close will not change.</p>
                  </div>
                </div>
              )}

              {/* Wrong Address modal (External-only) */}
              {showWrongAddrModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="wrong-addr-modal">
                  <div className="bg-[#162d4a] border border-[#7A5BA1]/40 rounded-xl p-5 max-w-md w-full shadow-2xl">
                    <h3 className="text-[#C9B5E0] font-bold text-base mb-2 flex items-center gap-2">
                      <MapPinOff className="h-4 w-4" /> Wrong Address
                    </h3>
                    <p className="text-[11px] text-gray-300 mb-3">
                      This sends a message to Office. Office will fix the address and reissue the route.
                    </p>
                    <p className="text-[10px] text-gray-400 mb-2">Detail (required):</p>
                    <textarea
                      value={wrongAddrComment}
                      onChange={e => setWrongAddrComment(e.target.value)}
                      rows={4}
                      placeholder="e.g. House number does not exist on this street. GPS pin is in a parking lot. Building demolished."
                      className="w-full px-3 py-2 bg-[#0d1f33] border border-white/20 rounded-lg text-sm !text-white placeholder:!text-gray-400 focus:outline-none focus:border-[#7A5BA1]"
                      data-testid="wrong-addr-comment-input"
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end mt-3">
                      <button onClick={() => { setShowWrongAddrModal(false); setWrongAddrComment(''); }} className="px-3 py-2 text-xs text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg" data-testid="wrong-addr-cancel-btn">Cancel</button>
                      <button onClick={doWrongAddress} disabled={!wrongAddrComment.trim() || inspActionLoading === 'wrong_addr'} className="px-4 py-2 text-xs font-bold text-white bg-[#7A5BA1] hover:bg-[#634885] rounded-lg disabled:opacity-50" data-testid="wrong-addr-send-btn">
                        {inspActionLoading === 'wrong_addr' ? 'Sending…' : 'Notify Office'}
                      </button>
                    </div>
                    <p className="text-[9px] text-gray-500 mt-3 text-center">Order status, dispatch counts, and Daily Close will not change.</p>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Action Buttons: Transmit & EOD Close — hidden for Inspector */}
        {!isInspector && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6" data-testid="order-actions">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Order Actions</h2>
          <div className="flex flex-wrap gap-3">
            {/* Transmit Button — only for authorized emails (hidden for Scheduling) */}

            {/* End of Day Close — hidden for restricted user and Scheduling role */}
            {!isRestrictedUser && !isScheduling && order.daily_closed === 'yes' && (
              <span className="px-4 py-2 bg-[#A7C4A0]/15 text-[#6B8F62] text-sm font-medium rounded-lg border border-[#A7C4A0]/40 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Day Closed — Sent to QA
              </span>
            )}

            {/* Remove Assignment — Admin, Finance, Operator (NOT Scheduling — Scheduling
                does not own assignment. Lock approved 2026-04-29.) */}
            {(isAdmin || isFinance || user?.role === 'Operator') && (order.assigned_operator || order.assigned_inspector) && (
              <button
                onClick={handleRemoveAssignment}
                disabled={removingAssignment}
                className="px-4 py-2 bg-[#C07070] hover:bg-[#A85A5A] text-white text-sm font-semibold rounded-lg transition-all flex items-center gap-2 disabled:opacity-40"
                data-testid="remove-assignment-btn"
              >
                <UserX className="h-4 w-4" />
                {removingAssignment ? 'Removing...' : 'Remove Assignment'}
              </button>
            )}

            {/* Send to Hold / Release Hold — hidden for restricted user and Scheduling */}
            {!isRestrictedUser && !isInspector && !isScheduling && order.hold_status ? (
              <div className="flex items-center gap-2">
                <span className="px-3 py-2 bg-[#EDE5D6] text-[#7A6535] text-xs font-medium rounded-lg border border-[#D1A054]/30 flex items-center gap-1.5">
                  <Archive className="h-3.5 w-3.5" /> On Hold until {formatDateDisplay(order.hold_until_date)} — {order.hold_reason}
                </span>
                <button
                  onClick={handleReleaseFromHold}
                  className="px-3 py-2 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
                  data-testid="release-hold-btn"
                >
                  <CheckCircle className="h-3.5 w-3.5" /> Release Hold
                </button>
              </div>
            ) : null}

            {/* Reopen Order — for closed/QA/Hold orders, non-inspector only */}
            {!isInspector && (order.status === 'EOD Closed' || order.status === 'QA' || order.status === 'On Hold' || order.daily_closed === 'yes') && (
              <button
                onClick={() => setShowReopenModal(true)}
                className="px-4 py-2 bg-[#D1A054] hover:bg-[#B08A3E] text-white text-sm font-semibold rounded-lg transition-all flex items-center gap-2"
                data-testid="reopen-order-btn"
              >
                <RotateCcw className="h-4 w-4" />
                Reopen Order
              </button>
            )}

            {/* Special Queue Actions */}
            {!isInspector && order.special_queue && (
              <div className="flex items-center gap-2">
                <span className={`px-3 py-2 text-xs font-medium rounded-lg border flex items-center gap-1.5 ${
                  order.special_queue === 'Priority Queue' ? 'bg-red-50 text-red-700 border-red-200' :
                  order.special_queue === 'Admin Review Queue' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-blue-50 text-blue-700 border-blue-200'
                }`}>
                  {order.special_queue === 'Priority Queue' && <AlertCircle className="h-3.5 w-3.5" />}
                  {order.special_queue === 'Admin Review Queue' && <Shield className="h-3.5 w-3.5" />}
                  {order.special_queue === 'SG Email Queue' && <Mail className="h-3.5 w-3.5" />}
                  In {order.special_queue}
                  {order.special_queue_reason && ` — ${order.special_queue_reason}`}
                </span>
                {(isAdmin || order.special_queue !== 'Priority Queue') && (
                  <button
                    onClick={handleRemoveFromQueue}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-colors"
                    data-testid="remove-from-queue-btn"
                  >
                    <X className="h-3.5 w-3.5" /> Remove from Queue
                  </button>
                )}
              </div>
            )}

            {/* Special Queue actions (Priority / Admin Review / SG Email) —
                hidden for Scheduling role. Lock approved 2026-04-29. */}
            {!isInspector && !isScheduling && !order.special_queue && (
              <div className="flex items-center gap-1.5">
                {canSendToPriority && (
                  <button
                    onClick={() => setShowQueueModal('Priority Queue')}
                    className="px-3 py-1.5 border border-red-300 text-red-600 text-xs font-medium rounded-lg hover:bg-red-50 flex items-center gap-1 transition-colors"
                    data-testid="send-to-priority-btn"
                  >
                    <AlertCircle className="h-3 w-3" /> Priority
                  </button>
                )}
                <button
                  onClick={() => setShowQueueModal('Admin Review Queue')}
                  className="px-3 py-1.5 border border-amber-300 text-amber-600 text-xs font-medium rounded-lg hover:bg-amber-50 flex items-center gap-1 transition-colors"
                  data-testid="send-to-admin-review-btn"
                >
                  <Shield className="h-3 w-3" /> Admin Review
                </button>
                {order.company === 'Safeguard' && (
                  <button
                    onClick={() => setShowQueueModal('SG Email Queue')}
                    className="px-3 py-1.5 border border-blue-300 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-50 flex items-center gap-1 transition-colors"
                    data-testid="send-to-sg-email-btn"
                  >
                    <Mail className="h-3 w-3" /> SG Email
                  </button>
                )}
              </div>
            )}

            {/* Call Client & Send SMS — Admin & Finance only */}
            {(isAdmin || isFinance) && (
              <div className="flex items-center gap-1.5 ml-auto pl-3 border-l border-gray-200">
                <button
                  onClick={handleCallClient}
                  disabled={callingClient || !getClientPhone()}
                  className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#264A6E] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="call-client-btn"
                  title={getClientPhone() ? `Call ${getClientPhone()}` : 'No phone number available'}
                >
                  <Phone className="h-3.5 w-3.5" />
                  {callingClient ? 'Calling...' : 'Call Client'}
                </button>
                <button
                  onClick={() => { setSmsText(''); setShowSmsModal(true); }}
                  disabled={!getClientPhone()}
                  className="px-3 py-1.5 bg-[#6B8F62] hover:bg-[#5A7B52] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="send-sms-btn"
                  title={getClientPhone() ? `SMS to ${getClientPhone()}` : 'No phone number available'}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  Send SMS
                </button>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Hold Modal */}
        {showHoldModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowHoldModal(false)}>
            <div className="bg-white rounded-lg border border-gray-200 p-5 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="hold-modal">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Place Order on Hold</h3>
                  <p className="text-gray-500 text-xs mt-0.5">Transmission Hold — {order.order_number}</p>
                </div>
                <Archive className="h-5 w-5 text-[#7A6535]" />
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Hold Reason *</label>
                  <select value={holdForm.hold_reason} onChange={e => setHoldForm(p => ({...p, hold_reason: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="hold-reason-select">
                    <option value="">Select reason...</option>
                    <option value="Transmission window not open">Transmission window not open</option>
                    <option value="Waiting for transmission date">Waiting for transmission date</option>
                    <option value="Window stop">Window stop</option>
                    <option value="Client/date restriction">Client/date restriction</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Hold Until Date *</label>
                  <input type="date" value={holdForm.hold_until_date} onChange={e => setHoldForm(p => ({...p, hold_until_date: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none" data-testid="hold-until-date" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Notes</label>
                  <textarea value={holdForm.hold_notes} onChange={e => setHoldForm(p => ({...p, hold_notes: e.target.value}))}
                    placeholder="Optional notes..." rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none resize-none" data-testid="hold-notes" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="office_paid" checked={holdForm.office_paid}
                    onChange={e => setHoldForm(p => ({...p, office_paid: e.target.checked}))}
                    className="h-4 w-4 rounded border-gray-300 text-[#2F5D8A] focus:ring-[#2F5D8A]" data-testid="hold-office-paid" />
                  <label htmlFor="office_paid" className="text-xs text-gray-600">Office Paid Hold</label>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShowHoldModal(false)} className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 font-medium" data-testid="hold-cancel-btn">Cancel</button>
                <button onClick={handlePlaceOnHold} disabled={holdingSaving}
                  className="flex-1 px-3 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="hold-submit-btn">
                  {holdingSaving ? 'Saving...' : 'Place on Hold'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reopen Modal */}
        {showReopenModal && order && (
          <ReopenModal
            orderIds={[orderId]}
            orderNumbers={[order.order_number]}
            defaultInspector={order.assigned_inspector || ""}
            onClose={() => setShowReopenModal(false)}
            onSuccess={(msg) => {
              setMessage({ type: 'success', text: msg });
              // Reload order data
              axios.get(`${API}/orders/${orderId}`).then(res => {
                setOrder(res.data);
                setEditedOrder(res.data);
              });
            }}
          />
        )}

        {/* SMS Modal */}
        {showSmsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowSmsModal(false)}>
            <div className="bg-white rounded-lg border border-gray-200 p-5 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="sms-modal">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Send SMS</h3>
                  <p className="text-gray-500 text-xs mt-0.5">Order {order?.order_number} — To: {getClientPhone() || 'N/A'}</p>
                </div>
                <MessageSquare className="h-5 w-5 text-[#6B8F62]" />
              </div>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: 'Today Appointment', text: `Hello, this is a message from AARIFI Inspection Services, a Safeguard Properties affiliate. This message is to confirm that your inspection appointment is scheduled for today between ${toAmPm24((order?.internal_start_time && order?.internal_end_time) ? `${order.internal_start_time} - ${order.internal_end_time}` : (order?.appointment_real_time || 'the scheduled time'))}. Your assigned inspector, ${order?.assigned_inspector || 'your assigned inspector'}, will contact you approximately 30 to 60 minutes prior to arrival. Please ensure the entire property is accessible at the time of the inspection. If the property has multiple units or tenants, all units must be available, or access (keys) must be provided. The inspection may include all rooms, as well as basement, furnace, electrical panel, and water tank, as applicable.` },
                    { label: 'On the way', text: 'Hello, this is AARIFI Services. The inspector is on the way and will arrive within the next hour.' },
                    { label: 'Confirmation', text: `Hello, this is AARIFI Services. Your inspection appointment has been confirmed. If you need to reschedule, please call (401) 404-6542.` },
                  ].map(t => (
                    <button key={t.label} onClick={() => setSmsText(t.text)}
                      className="px-2 py-1 text-[10px] font-medium rounded border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-[#2F5D8A]/30 transition-colors"
                      data-testid={`sms-template-${t.label.toLowerCase().replace(/\s/g,'-')}`}
                    >{t.label}</button>
                  ))}
                </div>
                <textarea value={smsText} onChange={e => setSmsText(e.target.value)} rows={4} placeholder="Type your message..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none resize-none"
                  data-testid="sms-text-input" />
                <p className="text-[10px] text-gray-400">{smsText.length}/160 characters — From: Main Office Number</p>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowSmsModal(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 font-medium" data-testid="sms-cancel-btn">Cancel</button>
                  <button onClick={handleSendSms} disabled={sendingSms || !smsText.trim()}
                    className="px-4 py-2 bg-[#6B8F62] hover:bg-[#5A7B52] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-40 flex items-center gap-1.5"
                    data-testid="sms-send-btn">
                    <MessageSquare className="h-3.5 w-3.5" />
                    {sendingSms ? 'Sending...' : 'Send SMS'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Inspector Completion Check — hidden for Inspector, restricted user
            AND Scheduling role (Scheduling does not own assignment/completion). */}
        {!isInspector && !isRestrictedUser && !isScheduling && order.assigned_inspector && ['Assigned', 'In Field'].includes(order.status) && (
          <div className="bg-white rounded-lg border border-[#D1A054]/30 p-4 mb-6" data-testid="inspector-completion-section">
            <h2 className="text-sm font-medium text-[#B08A3E] uppercase tracking-wide mb-1">Inspector Completion Check</h2>
            <p className="text-xs text-gray-500 mb-3">Assigned to: <span className="text-gray-800 font-medium">{order.assigned_inspector}</span></p>

            <p className="text-sm text-gray-600 mb-2">Did the inspector complete this order?</p>
            <div className="flex gap-3 mb-3">
              <button
                onClick={() => setReturnAnswer('yes')}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-2 ${
                  returnAnswer === 'yes'
                    ? 'bg-[#A7C4A0] text-white ring-2 ring-[#B2D4C3]'
                    : 'bg-[#C4C8CD] hover:bg-[#B2B5BA] text-[#1F2937] border border-[#B2B5BA]'
                }`}
                data-testid="completion-yes-btn"
              >
                <CheckCircle className="h-4 w-4" /> Yes
              </button>
              <button
                onClick={() => setReturnAnswer('no')}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-2 ${
                  returnAnswer === 'no'
                    ? 'bg-[#C07070] text-white ring-2 ring-[#D8C0C0]'
                    : 'bg-[#C4C8CD] hover:bg-[#B2B5BA] text-[#1F2937] border border-[#B2B5BA]'
                }`}
                data-testid="completion-no-btn"
              >
                <AlertCircle className="h-4 w-4" /> No
              </button>
            </div>

            {returnAnswer === 'yes' && (
              <p className="text-sm text-[#6B8F62] flex items-center gap-1">
                <CheckCircle className="h-4 w-4" /> Use the actions above to transmit and close this order as normal.
              </p>
            )}

            {returnAnswer === 'no' && (
              <div className="space-y-3 mt-2 p-3 bg-gray-50/50 rounded-lg border border-[#C07070]/20" data-testid="return-reason-section">
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1">Reason for return</label>
                  <select
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
                    data-testid="return-reason-select"
                  >
                    <option value="">Select a reason...</option>
                    <option value="No access">No access</option>
                    <option value="Inspector unavailable">Inspector unavailable</option>
                    <option value="Need reassignment">Need reassignment</option>
                    <option value="Time / route issue">Time / route issue</option>
                    <option value="Weather / condition issue">Weather / condition issue</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {returnReason === 'Other' && (
                  <div>
                    <label className="block text-xs text-gray-500 uppercase mb-1">Details</label>
                    <textarea
                      value={returnDetail}
                      onChange={(e) => setReturnDetail(e.target.value)}
                      placeholder="Describe the reason..."
                      className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054] min-h-[60px]"
                      data-testid="return-reason-detail"
                    />
                  </div>
                )}

                {/* Return Destination Choice */}
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1.5">Return Order To *</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setReturnDestination('dispatch')}
                      className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                        returnDestination === 'dispatch'
                          ? 'bg-[#2F5D8A] text-white ring-2 ring-[#2F5D8A]/40'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-300'
                      }`}
                      data-testid="return-dest-dispatch"
                    >
                      <Truck className="h-4 w-4" /> Dispatch
                    </button>
                    <button
                      onClick={() => setReturnDestination('scheduling')}
                      className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                        returnDestination === 'scheduling'
                          ? 'bg-[#6B5B8D] text-white ring-2 ring-[#6B5B8D]/40'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-300'
                      }`}
                      data-testid="return-dest-scheduling"
                    >
                      <Calendar className="h-4 w-4" /> Scheduling
                    </button>
                  </div>
                  {order.flow_type === 'External' && !returnDestination && (
                    <p className="text-[10px] text-[#B08A3E] mt-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> External orders typically return to Dispatch
                    </p>
                  )}
                </div>

                <button
                  onClick={handleReturnToDispatch}
                  disabled={returning || !returnReason || !returnDestination || (returnReason === 'Other' && !returnDetail.trim())}
                  className="px-4 py-2 bg-[#C07070] hover:bg-[#A85A5A] text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
                  data-testid="return-to-dispatch-btn"
                >
                  {returnDestination === 'scheduling' ? <Calendar className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
                  {returning ? 'Returning...' : `Return to ${returnDestination === 'scheduling' ? 'Scheduling' : 'Dispatch'}`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Returned badge — if order was previously returned (hidden for Inspectors) */}
        {user?.role !== 'Inspector' && order.returned_to_dispatch === 'yes' && (
          <div className="bg-[#D1A054]/15 border border-[#D1A054]/35 rounded-lg p-3 mb-6 flex items-start gap-2" data-testid="returned-badge">
            <AlertTriangle className="h-4 w-4 text-[#9A7A3E] mt-0.5 shrink-0" />
            <div className="text-sm">
              <span className="text-[#9A7A3E] font-medium">Previously returned from {order.returned_from_inspector}</span>
              {order.returned_destination && <span className="text-gray-500"> to {order.returned_destination === 'scheduling' ? 'Scheduling' : 'Dispatch'}</span>}
              <span className="text-gray-500"> — {order.returned_reason}</span>
              {order.returned_reason_detail && <span className="text-gray-400"> ({order.returned_reason_detail})</span>}
              {order.return_history?.length > 1 && (
                <span className="ml-1 text-[10px] text-gray-400">(returned {order.return_history.length}x)</span>
              )}
            </div>
          </div>
        )}

        {/* Returned from archive warning — hidden for Inspectors */}

        {/* Urgent Call Banner */}

        {/* PH Phone (Property Holder) — SINGLE SOURCE for Call/SMS actions */}
        {!isInspector && (
          <div className="bg-white rounded-lg border border-[#D1A054]/30 p-4 mb-6" data-testid="ph-phone-editor">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h2 className="text-xs font-bold text-[#B08A3E] uppercase tracking-wider mb-2">
                  PH Phone <span className="text-gray-400 normal-case font-normal">(used by Call & SMS)</span>
                </h2>
                {!editingPhPhone ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg font-bold text-gray-800" data-testid="ph-phone-resolved">
                        {order.ph_phone_manual || order.ph_phone || <span className="text-gray-400 italic text-sm font-normal">No phone on file</span>}
                      </span>
                      {order.ph_phone_manual && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#D1A054]/15 text-[#B08A3E]">MANUAL OVERRIDE</span>
                      )}
                      {!order.ph_phone_manual && order.ph_phone && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-200 text-gray-600">FROM IMPORT</span>
                      )}
                    </div>
                    {order.ph_phone_manual && order.ph_phone && order.ph_phone_manual !== order.ph_phone && (
                      <p className="text-[10px] text-gray-400">Original import: <span className="line-through">{order.ph_phone}</span></p>
                    )}
                    {order.ph_phone_manual_by && order.ph_phone_manual_at && (
                      <p className="text-[10px] text-gray-400">Updated by {order.ph_phone_manual_by} on {new Date(order.ph_phone_manual_at).toLocaleString()}</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="tel"
                      value={phPhoneDraft}
                      onChange={e => setPhPhoneDraft(e.target.value)}
                      placeholder="(401) 300-7577"
                      autoFocus
                      className="w-full max-w-xs px-3 py-2 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:border-[#D1A054]"
                      data-testid="ph-phone-input"
                    />
                    {phPhoneError && (
                      <p className="text-xs text-[#C07070] flex items-center gap-1"><AlertCircle className="h-3 w-3" />{phPhoneError}</p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={savePhPhone} disabled={phPhoneSaving || !phPhoneDraft.trim()}
                        className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#24496e] disabled:bg-gray-300 text-white text-xs font-bold rounded"
                        data-testid="ph-phone-save-btn">
                        {phPhoneSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => { setEditingPhPhone(false); setPhPhoneError(''); }}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded"
                        data-testid="ph-phone-cancel-btn">
                        Cancel
                      </button>
                      {order.ph_phone_manual && (
                        <button onClick={clearPhPhoneOverride} disabled={phPhoneSaving}
                          className="px-3 py-1.5 bg-[#C07070]/10 hover:bg-[#C07070]/20 text-[#8B4545] text-xs font-semibold rounded"
                          data-testid="ph-phone-clear-btn">
                          Remove override (use import: {order.ph_phone})
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {!editingPhPhone && (
                <button
                  onClick={() => { setPhPhoneDraft(order.ph_phone_manual || order.ph_phone || ''); setEditingPhPhone(true); setPhPhoneError(''); }}
                  className="flex-shrink-0 px-3 py-1.5 bg-[#D1A054]/10 hover:bg-[#D1A054]/20 text-[#B08A3E] text-xs font-semibold rounded flex items-center gap-1.5"
                  data-testid="ph-phone-edit-btn"
                >
                  <Edit className="h-3 w-3" /> Edit
                </button>
              )}
            </div>
          </div>
        )}

        {/* Appointment Contact Info — highlighted for Inspectors */}
        {order.appointment_set === 'yes' && (order.appointment_contact_name || order.appointment_contact_phone) && (
          <div className={`rounded-lg border p-4 mb-6 ${isInspector ? 'bg-[#D1A054]/5 border-[#D1A054]/30' : 'bg-white border-gray-200'}`} data-testid="appointment-contact-section">
            <h2 className="text-xs font-bold text-[#B08A3E] uppercase tracking-wider mb-3">Appointment Contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {order.appointment_contact_name && (
                <div data-testid="appointment-contact-name-display">
                  <p className="text-gray-400 text-[10px] uppercase">Who will be at property</p>
                  <p className="text-gray-800 text-sm font-bold">{order.appointment_contact_name}</p>
                </div>
              )}
              {order.appointment_contact_phone && (
                <div data-testid="appointment-contact-phone-display">
                  <p className="text-gray-400 text-[10px] uppercase">Scheduling Contact Phone <span className="text-gray-300 normal-case">(info only — not used for Call/SMS)</span></p>
                  <p className="text-gray-800 text-sm font-bold">{order.appointment_contact_phone}</p>
                </div>
              )}
            </div>
            {order.appointment_real_date && (
              <div className="mt-2 pt-2 border-t border-[#D1A054]/15 flex items-center gap-4">
                <div>
                  <p className="text-gray-400 text-[10px] uppercase">Appointment Date</p>
                  <p className="text-gray-700 text-sm font-medium">{order.appointment_real_date}</p>
                </div>
                {order.appointment_real_time && (
                  <div>
                    <p className="text-gray-400 text-[10px] uppercase">Window</p>
                    <p className="text-gray-700 text-sm font-medium">{order.appointment_real_time}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Layout block (Angela 2026-05-10 v2):
            • Internal/External (and any non-FINT inspector order):
                Action buttons → Training Guide → Order Information
            • FINT (vacant-house):
                Header → Order Information → FINT Property Notes → Training Guide
            All elements were already on this screen; this block only
            controls their visual order. No change to the FINT panel write
            paths, the TrainingGuide component, or the Order Information
            fields. */}
        {(() => {
          const isFINT = (order.order_type || '').toUpperCase() === 'FINT';
          const trainingGuide = isInspector && order.order_type ? (
            <div className="mb-6" data-testid="training-guide-wrapper">
              <TrainingGuide orderType={order.order_type} isAdmin={false} flowType={order.flow_type} />
            </div>
          ) : null;
          const orderInformation = (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Order Information</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {visibleReadOnlyFields.map(field => {
                  const raw = order[field.key];
                  const val = field.render ? field.render(raw) : (raw || '-');
                  return (
                    <div key={field.key} data-testid={`order-field-${field.key}`}>
                      <p className="text-gray-400 text-xs uppercase">{field.label}</p>
                      <p className="text-[#1F2937] text-sm font-medium">{val}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
          const fintPanel = isFINT ? (
            <PropertyNotesPanel orderId={orderId} isInternal={true} />
          ) : null;
          if (isFINT) {
            return <>{orderInformation}{fintPanel}{trainingGuide}</>;
          }
          return <>{trainingGuide}{orderInformation}</>;
        })()}

        {/* ═══════════════════════════════════════════════════════════════
            Order Information / National Reference — middle block
            (restored Apr 22 2026).
            Shows compact order details alongside the reference/example
            photos so the inspector knows what data and which pictures
            to capture BEFORE starting the order. Distinct from the
            National Photos upload panel below (which is for the
            inspector's own completion evidence).
            National orders only; visible to inspectors and office roles.
            ═══════════════════════════════════════════════════════════ */}
        {order.company === 'National' && ['Inspector', 'Dispatch', 'Finance', 'Admin', 'Scheduling', 'Operator', 'Operations'].includes(user?.role) && (
          <OrderReferenceBlock order={order} orderId={orderId} userRole={user?.role} />
        )}

        {/* National Photos — sits between Order Information and Additional Details
            so the inspector can review uploaded evidence while filling the order.
            Only renders for National orders and to the allowed roles. */}
        {order.company === 'National' && ['Inspector', 'Dispatch', 'Finance', 'Admin', 'Scheduling', 'Operator', 'Operations'].includes(user?.role) && (
          <div className="mb-6">
            <NationalPhotosPanel orderId={orderId} userRole={user?.role} />
          </div>
        )}

        {/* External Morning Call flag — MIRROR compliance queue (João's next-morning call
            for External orders that arrived after 5 PM). Does NOT change status/queue/route. */}
        {order.flow_type === 'External' && ['Scheduling', 'Operator', 'Admin', 'Finance', 'Dispatch', 'Operations'].includes(user?.role) && (
          <ExternalMorningCallSection
            order={order}
            orderId={orderId}
            onChange={(updates) => setOrder(prev => ({ ...prev, ...updates }))}
          />
        )}

        {/* Angela 2026-05-10 — REMOVED: legacy "External Blocked? Upload
            Pictures Here" panel. Per scope lock the External-only inspector
            action bar (Call PH · SMS PH · Call Agency · Deny Inspection ·
            Wrong Address) replaces this entry point. The NationalPhotosPanel
            component is preserved elsewhere in the app for download flows. */}

        {/* Editable Fields Section — inspector sees editable Comments + Action Comment */}
        {isInspector ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="inspector-additional-details">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Additional Details</h2>
              <span className="text-[10px] text-gray-400 italic">You can leave a comment or action note for the office</span>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-gray-500 text-xs uppercase mb-1">Comments</label>
                <textarea
                  value={editedOrder.comments || ''}
                  onChange={(e) => handleChange('comments', e.target.value)}
                  placeholder="Optional comments about this order..."
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054] min-h-[90px]"
                  data-testid="inspector-comments"
                />
              </div>
              <div>
                <label className="block text-gray-500 text-xs uppercase mb-1">
                  Action Comment
                  <span className="ml-1 text-[#DC2626] font-bold" title="Required when changing a status">*</span>
                  <span className="ml-2 text-[10px] text-gray-400 normal-case italic">required when you change the order status</span>
                </label>
                <textarea
                  value={editedOrder.action_comment || ''}
                  onChange={(e) => handleChange('action_comment', e.target.value)}
                  placeholder="Explain what you did or what needs to happen next..."
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054] min-h-[90px]"
                  data-testid="inspector-action-comment"
                />
                {order.action_comment_by && (
                  <p className="text-[10px] text-gray-400 mt-1">Last note by {order.action_comment_by}{order.action_comment_at ? `, ${new Date(order.action_comment_at).toLocaleString()}` : ''}</p>
                )}
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2.5 bg-[#D1A054] hover:bg-[#B08A3E] text-white font-semibold rounded-lg text-sm disabled:opacity-50 self-start flex items-center gap-2"
                data-testid="inspector-save-details-btn">
                {saving ? 'Saving...' : 'Save Comments'}
              </button>
            </div>
          </div>
        ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
            Editable Fields
            <span className="ml-2 text-xs font-normal text-gray-400">({flowType} flow)</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {editableFields.map(field => (
              <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-gray-500 text-xs uppercase mb-1">{field.label}</label>
                {renderField(field)}
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* National Photos panel moved up to between Order Information and
          Additional Details (Feb 2026 user request). */}

      {/* Full Order History — Priority Events + Communications (low-priority collapsed) */}
      <div className="max-w-5xl mx-auto px-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            data-testid="order-history-toggle"
          >
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <h2 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Order Timeline</h2>
              {orderHistory.length > 0 && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{orderHistory.length}</span>}
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
          </button>
          {showHistory && (
            <div className="border-t border-gray-100 max-h-[560px] overflow-y-auto">
              {historyLoading ? (
                <div className="p-6 text-center text-gray-400 text-sm">Carregando historico...</div>
              ) : orderHistory.length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">Nenhum evento registrado.</div>
              ) : (() => {
                // Classify events: communications (low priority) vs priority business events
                const COMM_KEYWORDS = ['sms', 'email sent', 'mail sent', 'reminder', 'call attempt', 'call registered', 'auto reminder', 'message sent'];
                const isCommEvent = (ev) => {
                  const t = ((ev.type || '') + ' ' + (ev.detail || '')).toLowerCase();
                  return COMM_KEYWORDS.some(k => t.includes(k));
                };
                const priority = [];
                const communications = [];
                orderHistory.forEach(ev => (isCommEvent(ev) ? communications : priority).push(ev));

                // Highlight certain events with accent colors/labels
                const PRIORITY_BADGES = [
                  { match: 'completed', bg: 'bg-[#DCEAE4]', text: 'text-[#3E7559]', label: 'Completed' },
                  { match: 'daily clos', bg: 'bg-[#FBEFD7]', text: 'text-[#A07422]', label: 'Closed' },
                  { match: 'paid', bg: 'bg-[#D7E7FA]', text: 'text-[#2F5D8A]', label: 'Paid' },
                  { match: 'follow', bg: 'bg-[#F7DADA]', text: 'text-[#9B3A3A]', label: 'Follow-Up' },
                  { match: 'reassigned', bg: 'bg-[#FBEFD7]', text: 'text-[#A07422]', label: 'Reassigned' },
                  { match: 'assigned', bg: 'bg-[#E6DFF4]', text: 'text-[#6B4E9A]', label: 'Assigned' },
                  { match: 'qa', bg: 'bg-[#D7E7FA]', text: 'text-[#2F5D8A]', label: 'QA' },
                  { match: 'billing', bg: 'bg-[#D7E7FA]', text: 'text-[#2F5D8A]', label: 'Billing' },
                  { match: 'phot', bg: 'bg-[#F4E3C8]', text: 'text-[#9A7735]', label: 'PHOT' },
                  { match: 'appointment', bg: 'bg-[#E6DFF4]', text: 'text-[#6B4E9A]', label: 'Appointment' },
                  { match: 'removed from', bg: 'bg-gray-100', text: 'text-gray-500', label: 'Removed' },
                  { match: 'returned', bg: 'bg-[#F7DADA]', text: 'text-[#9B3A3A]', label: 'Returned' },
                  { match: 'imported', bg: 'bg-[#EFF5ED]', text: 'text-[#4A7A56]', label: 'Imported' },
                  { match: 'payment_reversed', bg: 'bg-[#F7DADA]', text: 'text-[#9B3A3A]', label: 'Pay Reversed' },
                ];
                const badgeFor = (ev) => {
                  const t = ((ev.type || '') + ' ' + (ev.detail || '')).toLowerCase();
                  return PRIORITY_BADGES.find(b => t.includes(b.match)) || { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Event' };
                };

                const renderEvent = (ev, idx, isComm) => {
                  const badge = isComm
                    ? { bg: 'bg-gray-50', text: 'text-gray-400', label: 'Msg' }
                    : badgeFor(ev);
                  return (
                    <div key={idx} className="px-4 py-3 hover:bg-gray-50/50" data-testid={`history-event-${idx}`}>
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 mt-0.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm ${isComm ? 'text-gray-500' : 'font-medium text-gray-800'}`}>{ev.type}</span>
                            {ev.user && <span className="text-[10px] text-gray-400">by {ev.user}</span>}
                          </div>
                          {ev.detail && <p className="text-xs text-gray-500 mt-0.5 break-words">{ev.detail}</p>}
                          {(ev.previous_status || ev.new_status) && (
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              {ev.previous_status && <span>{ev.previous_status}</span>}
                              {ev.previous_status && ev.new_status && <span> → </span>}
                              {ev.new_status && <span className="font-semibold text-gray-700">{ev.new_status}</span>}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-[10px] text-gray-400 whitespace-nowrap">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ''}</p>
                        </div>
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="divide-y divide-gray-50">
                    {priority.length > 0 ? (
                      <div className="divide-y divide-gray-50">
                        <div className="px-4 py-2 bg-[#FAFAFB] text-[10px] font-bold text-gray-500 uppercase tracking-wider">Priority Events</div>
                        {priority.map((ev, i) => renderEvent(ev, `p-${i}`, false))}
                      </div>
                    ) : null}
                    {communications.length > 0 && (
                      <details className="group" data-testid="communications-collapsed">
                        <summary className="px-4 py-2 bg-[#FAFAFB] text-[10px] font-bold text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 flex items-center justify-between list-none">
                          <span>Communications <span className="ml-1 text-gray-500">({communications.length})</span></span>
                          <ChevronDown className="h-3 w-3 text-gray-400 group-open:rotate-180 transition-transform" />
                        </summary>
                        <div className="divide-y divide-gray-50">
                          {communications.map((ev, i) => renderEvent(ev, `c-${i}`, true))}
                        </div>
                      </details>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Transmission Modal */}
      {showTransmitModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowTransmitModal(false)}>
          <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="transmit-modal">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Transmission Details</h3>
            <p className="text-gray-500 text-sm mb-5">Order #{order.order_number}</p>

            {/* Transmitted By */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-2 font-semibold">Transmitted By *</label>
              <div className="grid grid-cols-3 gap-2">
                {['Vitória', 'Aline', 'Aly'].map(op => (
                  <button
                    key={op}
                    onClick={() => setTransmitOperator(op)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all border ${
                      transmitOperator === op
                        ? 'bg-[#D1A054] text-[#1F2937] border-[#D1A054] ring-2 ring-[#D1A054]/30'
                        : 'bg-gray-100 text-gray-600 border-gray-300 hover:border-gray-300 hover:bg-gray-200'
                    }`}
                    data-testid={`transmit-operator-${op.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`}
                  >
                    {op}
                  </button>
                ))}
              </div>
            </div>

            {/* Paid By */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-2 font-semibold">Paid By *</label>
              <div className="grid grid-cols-2 gap-2">
                {['Inspector', 'Office'].map(p => (
                  <button
                    key={p}
                    onClick={() => { setTransmitPaidBy(p); if (p === 'Inspector') { setOfficeReason(''); setOfficeReasonDetail(''); } }}
                    className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all border ${
                      transmitPaidBy === p
                        ? p === 'Inspector'
                          ? 'bg-[#2F5D8A] text-white border-[#2F5D8A] ring-2 ring-[#2F5D8A]/20'
                          : 'bg-[#5A4F7A] text-white border-[#5A4F7A] ring-2 ring-[#5A4F7A]/20'
                        : 'bg-gray-100 text-[#4B5563] border-gray-300 hover:border-gray-300 hover:bg-gray-200'
                    }`}
                    data-testid={`transmit-paid-${p.toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Office Reason (if Office selected) */}
            {transmitPaidBy === 'Office' && (
              <div className="mb-4 p-3 bg-[#F0EDF5] border border-[#6B5B8D]/25 rounded-lg">
                <label className="block text-xs text-[#6B5B8D] uppercase tracking-wide mb-2 font-semibold">Office Payment Reason *</label>
                <select
                  value={officeReason}
                  onChange={(e) => setOfficeReason(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                  data-testid="office-reason-select"
                >
                  <option value="">Select a reason...</option>
                  {['Order already closed by office','Office requested support','Inspector not responsible','Internal office decision','Billing adjustment','Other'].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {officeReason === 'Other' && (
                  <textarea
                    value={officeReasonDetail}
                    onChange={(e) => setOfficeReasonDetail(e.target.value)}
                    placeholder="Describe the reason..."
                    className="w-full mt-2 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A] min-h-[60px]"
                    data-testid="office-reason-detail"
                  />
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-5">
              <button
                onClick={handleConfirmTransmit}
                disabled={transmitting || !transmitOperator || !transmitPaidBy || (transmitPaidBy === 'Office' && !officeReason) || (transmitPaidBy === 'Office' && officeReason === 'Other' && !officeReasonDetail.trim())}
                className="flex-1 px-4 py-2.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="confirm-transmit-btn"
              >
                <Radio className="h-4 w-4" />
                {transmitting ? 'Transmitting...' : 'Confirm Transmission'}
              </button>
              <button
                onClick={() => setShowTransmitModal(false)}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm rounded-lg"
                data-testid="cancel-transmit-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send to Queue Modal */}
      {showQueueModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="queue-modal">
          <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              {showQueueModal === 'Priority Queue' && <AlertCircle className="h-5 w-5 text-red-500" />}
              {showQueueModal === 'Admin Review Queue' && <Shield className="h-5 w-5 text-amber-600" />}
              {showQueueModal === 'SG Email Queue' && <Mail className="h-5 w-5 text-blue-600" />}
              <h3 className="text-base font-bold text-gray-800">Send to {showQueueModal}</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Reason *</label>
                <input
                  value={queueReason}
                  onChange={e => setQueueReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                  placeholder="Why is this order being sent here?"
                  data-testid="queue-reason-input"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
                <textarea
                  value={queueNotes}
                  onChange={e => setQueueNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                  rows={3}
                  placeholder="Additional context..."
                  data-testid="queue-notes-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setShowQueueModal(null); setQueueReason(''); setQueueNotes(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                data-testid="queue-modal-cancel"
              >Cancel</button>
              <button
                onClick={handleSendToQueue}
                disabled={!queueReason.trim() || sendingToQueue}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-40 ${
                  showQueueModal === 'Priority Queue' ? 'bg-red-500 hover:bg-red-600' :
                  showQueueModal === 'Admin Review Queue' ? 'bg-amber-500 hover:bg-amber-600' :
                  'bg-blue-500 hover:bg-blue-600'
                }`}
                data-testid="queue-modal-confirm"
              >{sendingToQueue ? 'Sending...' : 'Send'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Angela 2026-05-19 — Unified Report Problem modal mounting. */}
      <ReportProblemModal
        open={showReportProblem}
        orderId={orderId}
        orderNumber={order?.order_number}
        presetEventType={reportProblemPreset}
        onClose={() => { setShowReportProblem(false); setReportProblemPreset(''); }}
        onSubmitted={() => {
          setInspActionMsg({ type: 'success', text: 'Problem reported to Office. They will review the photos.' });
        }}
      />
    </Layout>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <Toaster richColors position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/set-password" element={<ProtectedRoute><SetPasswordPage /></ProtectedRoute>} />
          <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["Admin", "Finance"]}><Layout><DashboardPage /></Layout></ProtectedRoute>} />
          <Route path="/scheduling" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"]}><SchedulingPage /></ProtectedRoute>} />
          <Route path="/scheduling/order/:orderId" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"]}><SchedulingOrderDetailPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/scheduling/rc-sms-inbox" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"]}><Layout><RcSmsInbox /></Layout></ProtectedRoute>} />
          <Route path="/scheduling/rc-voicemail-inbox" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"]}><Layout><RcVoicemailInbox /></Layout></ProtectedRoute>} />
          <Route path="/scheduling/email-inbox" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Operations"]}><Layout><div className="p-6 max-w-3xl mx-auto"><div className="bg-white border border-gray-200 rounded-lg p-8 text-center"><Mail className="h-10 w-10 text-blue-500 mx-auto mb-3" /><h1 className="text-xl font-bold text-gray-800 mb-2">Office / Scheduling Email Inbox</h1><p className="text-sm text-gray-600 max-w-md mx-auto">Coming soon. Waiting on IMAP credentials propagation in Emergent environment so the app can read the <b>Scheduling</b> label from <b>office@aarifi.com</b> in read-only mode.</p><p className="text-[11px] text-gray-400 mt-3">No emails are being synced yet.</p></div></div></Layout></ProtectedRoute>} />
          <Route path="/productivity" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Operations"]}><ProductivityPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/dispatch" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator", "National Transmission"]}><DispatchPage /></ProtectedRoute>} />
          <Route path="/inspector" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Operations", "Operator"]}><InspectorPage /></ProtectedRoute>} />
          <Route path="/my-weekly-closing" element={<ProtectedRoute allowedRoles={["Inspector"]}><InspectorWeeklyClosingPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/my-orders" element={<ProtectedRoute allowedRoles={["Inspector"]}><InspectorMyOrdersPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/pre-route" element={<ProtectedRoute allowedRoles={["Inspector"]}><InspectorPreRoutePage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/day-closing" element={<ProtectedRoute allowedRoles={["Inspector"]}><InspectorDayClosingPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/finance" element={<ProtectedRoute allowedRoles={["Admin", "Finance"]}><FinancePage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/finance/check-reconcile" element={<ProtectedRoute allowedRoles={["Admin", "Finance"]}><CheckReconcilePage /></ProtectedRoute>} />
          <Route path="/team" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Operations"]}><TeamPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/upload-orders" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Operations"]}><UploadOrdersPage /></ProtectedRoute>} />
          <Route path="/archive" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"]}><Layout><ArchivePage /></Layout></ProtectedRoute>} />
          <Route path="/daily-closing" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Operations"]} permission="daily_closing"><DailyClosingPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/closeout-review" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Office", "Operations", "Scheduling"]}><Layout><CloseoutReviewPage /></Layout></ProtectedRoute>} />
          <Route path="/aly-portal" element={<ProtectedRoute allowedRoles={["National Transmission", "Admin", "Finance"]}><AlyPortalPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/aly-national" element={<ProtectedRoute allowedRoles={["National Transmission", "Admin", "Finance"]}><AlyNationalViewPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/aly-calendar" element={<ProtectedRoute allowedRoles={["National Transmission", "Admin", "Finance"]}><AlyNationalCalendarPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/aly-order/:orderId" element={<ProtectedRoute allowedRoles={["National Transmission", "Admin", "Finance"]}><AlyOrderDetailPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/aly-all-national" element={<ProtectedRoute allowedRoles={["National Transmission", "Admin", "Finance"]}><AlyAllNationalOrdersPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/routes" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Operations"]}><RouteManagementPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/zone-calendar" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"]}><Layout><ZoneCalendarPage /></Layout></ProtectedRoute>} />
          <Route path="/office/hours-tracking" element={<ProtectedRoute allowedRoles={["Admin"]}><OfficeHoursTrackingPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/admin/announcements" element={<ProtectedRoute allowedRoles={["Admin"]}><AdminAnnouncementsPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/admin/inspector-rates" element={<ProtectedRoute allowedRoles={["Admin"]}><AdminInspectorRatesPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/national-inspectors-upload" element={<ProtectedRoute allowedRoles={["Admin", "National Transmission"]}><Layout><NationalInspectorsUploadPage /></Layout></ProtectedRoute>} />
          <Route path="/office" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Operator"]}><Layout><OfficeWorkforcePage /></Layout></ProtectedRoute>} />
          {/* Legacy /inspection alias — Angela/Aline's old bookmark. Now renders the
              IDENTICAL InspectorMyOrdersPage with no custom flags, so the experience
              matches a real inspector login 1:1. */}
          <Route path="/inspection" element={<ProtectedRoute allowedRoles={["Admin", "Finance"]}><InspectorMyOrdersPage Layout={Layout} /></ProtectedRoute>} />
          <Route path="/dispatch/all-orders" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Dispatch", "Operations"]}><AllCompanyOrdersPage Layout={Layout} department="Dispatch" /></ProtectedRoute>} />
          <Route path="/finance/all-orders" element={<ProtectedRoute allowedRoles={["Admin", "Finance"]}><AllCompanyOrdersPage Layout={Layout} department="Finance" /></ProtectedRoute>} />
          <Route path="/scheduling/all-orders" element={<ProtectedRoute allowedRoles={["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"]}><AllCompanyOrdersPage Layout={Layout} department="Scheduling" /></ProtectedRoute>} />
          <Route path="/order/:orderId" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
          <Route path="/inspector-upload/:token" element={<InspectorUploadPage />} />
          <Route path="/inspector-contract/:token" element={<InspectorContractPage />} />
          <Route path="/client-upload/:token" element={<ClientPhotoUploadPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
